/**
 * ChartsPage — full-viewport charting with left symbol panel,
 * 3 layout modes (single / 2-split / 4-quad), interval selector,
 * overlay toggles, auto-save preferences per symbol.
 *
 * 4-Quad layout: Q1=Underlying spot, Q2=CE option, Q3=PE option, Q4=Straddle
 */
import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { USD_TO_INR } from '../utils/constants';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup,
  Select, MenuItem, IconButton, Tooltip, Chip, TextField,
  List, CircularProgress, Divider,
  SelectChangeEvent,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  RadioGroup, Radio, FormControlLabel, Alert, Popover, Slider,
} from '@mui/material';
import type { RectZone } from '../components/charts/primitives/RectangleZonePrimitive';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ReplayIcon from '@mui/icons-material/Replay';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { optionsService } from '../services/options.service';
import { optionStrategyService } from '../services/optionStrategy.service';
import BasketReviewDialog from '../components/charts/BasketReviewDialog';
import OutlookBuilderDialog from '../components/charts/OutlookBuilderDialog';
import AiVerdictChip from '../components/ai/AiVerdictChip';
import aiService, { type AiAssessment } from '../services/ai.service';
import { tradingService } from '../services/trading.service';
import TradingChart, { CandleRow, Overlays, AuxCandles, FlowCandleSignal, type FractionView } from '../components/charts/TradingChart';
import type { HZone } from '../components/charts/primitives/HorizontalZonesPrimitive';
import { STRATEGY_PICKS, AUTO_BUILD_STRATEGIES, buildLegsFromPicks, comboSign, parseDeltaOptionSymbol, type StrategyLeg } from '../utils/optionStrategyLegs';
import { sanitizeCandles } from '../utils/sanitizeCandles';
import { projectToBarClose } from '../utils/projectSeries';
import { projectFnoBars, gapBarTimes, fmtExpiry, fmtLeft, type FnoSeriesResp, type FnoBar, type FnoSample } from '../utils/fnoBars';
import type { FnoViewProps } from '../components/charts/TradingChart';
import type { FnoSelectProps, FnoSelectOption } from '../components/charts/FnoExpiryMenu';
import BasketAdjustPanel from '../components/options/BasketAdjustPanel';
import CampaignAdjustPanel from '../components/options/CampaignAdjustPanel';
import StagedCampaignDialog from '../components/options/StagedCampaignDialog';
import { scoreFno, type FnoInterpretInputs } from '../utils/fnoInterpret';
import { usePersistedState } from '../hooks/usePersistedState';
import OrderConfirmDialog from '../components/charts/OrderConfirmDialog';
import { chartFlowService } from '../services/chartFlow.service';
import { chartFundingService } from '../services/chartFunding.service';
import ChartErrorBoundary from '../components/charts/ChartErrorBoundary';
import ChartSettingsDialog from '../components/charts/ChartSettingsDialog';
import OptionChartPicker from '../components/charts/OptionChartPicker';
import OptionsWorkspace, { WsChartOverlay } from '../components/charts/workspace/OptionsWorkspace';
import SyntheticSymbolPanes from '../components/charts/SyntheticSymbolPanes';
import { syntheticSymbolsService, SyntheticSymbolRow } from '../services/syntheticSymbols.service';
import SavedChartLayouts, { SavedLayoutState } from '../components/charts/SavedChartLayouts';
import ReplayController from '../components/charts/ReplayController';
import ToolbarMenuGroup, { type ToolbarItem, type ChipColorName } from '../components/charts/ToolbarMenuGroup';
import ToolbarIndicatorsGroup, { type IndicatorSection } from '../components/charts/ToolbarIndicatorsGroup';
import SnapshotButton from '../components/charts/SnapshotButton';
import { useHotkey, useHotkeySequence } from '../hooks/useHotkeys';
import { ChartSettingsProvider, useChartSettings } from '../contexts/ChartSettingsContext';
import { readVisibleBars, writeVisibleBars, LayoutMode3 } from '../services/chartSettings.service';
import { ctfFromLtf, htfFromCtf, cprBaseFromLtf } from '../utils/dailyBlastIndicators';
import ChartsHelpDialog from '../components/charts/ChartsHelpDialog';
import OverviewPanel from '../components/charts/OverviewPanel';
import { computeTimeframeTrends, computeTimeframeTrendSeries, type TimeframeTrends, type TrendSeriesPoint } from '../utils/overviewTrends';
import { intervalHtfMappingService } from '../services/intervalHtfMapping.service';
import { tfToMinutes } from '../utils/timeframe';
import { zoneForScore, mixHex, OVERVIEW_ZONE_COLORS } from '../theme/overviewColors';
import { useTabs } from '../contexts/TabsContext';
import { useAppTheme } from '../hooks/useAppTheme';
import { strategies, getStrategy, computeRockBandOverlay, computeSniperOverlay, computeOmOverlay, OM_SIMPLE_DEFAULT_CLN_LENGTH, resolveGandivSource, resolveLrCrossSource, type StrategyOverlayResult } from '../utils/strategies';
import { CHART_OVERLAY_CATALOGUE } from '../components/signals/overlayCatalogue';
import { bhairavGateLabel, bhairavGateProfile } from '../utils/strategies/bhairavGates';
import { transformCandles, linreg, CANDLE_TYPES, dropFormingHtfBars, type CandleType } from '../utils/candleTransforms';
import {
  planFraction, applyFraction, applyFractionLive, formingWindowStart, normaliseDenominator, fractionLabel,
  type FractionRow,
} from '../utils/candleFraction';
import {
  BUILTIN_INTERVALS, CALENDAR_MONTH, isCalendarInterval,
  intervalLabel as catalogIntervalLabel, resolveHtfInterval,
} from '../utils/intervalCatalog';
import CustomIntervalsDialog from '../components/charts/CustomIntervalsDialog';
import {
  customIntervalService, pickerIntervals, FALLBACK_VOCABULARY,
  type IntervalVocabulary,
} from '../services/customInterval.service';
import { resolveDecimals, decimalsForValue } from '../utils/decimalPrecision';
import { formatPrice } from '../utils/formatters';
import { CH_TYPE, CH_SIZE, chGroupSx, chGroupLabelSx, chChipSx, chNumeric, chPaneTitleSx, chPaneHeaderSx, type ChTone } from '../styles/appUi';
import SymbolPaneRow from '../components/charts/SymbolPaneRow';
import { alertPeriodToHours, readAlertPeriod, ALERT_PERIODS } from '../utils/alertWindow';
import { getGlobalSocket } from '../hooks/useWebSocket';
import { effectiveSegment } from '../utils/sizeOrdersAs';
import LevelOptionDialog from '../components/options/LevelOptionDialog';
import { levelOptionTriggersService, levelOptionCapable, levelArmed, LevelOptionState, attExpiryText, attExpiryShort } from '../services/levelOptionTriggers.service';
import { findTemplate } from '../utils/optionTemplates';

/** One spot pane's "important fraction" rows (POST /charts/fraction-bars). `key` is the
 *  symbol|exchange|interval|denominator they belong to: rows are joined on TIME, so rows
 *  from another symbol must never be applied to this one's bars. */
interface PaneFraction { key: string; rows: Map<number, FractionRow>; forming: FractionRow | null; loaded: boolean; ver: number }
const EMPTY_PANE_FRACTION: PaneFraction[] = [
  { key: '', rows: new Map(), forming: null, loaded: false, ver: 0 },
  { key: '', rows: new Map(), forming: null, loaded: false, ver: 0 },
];

/* Reveal a symbol row inside the symbol-pane list. Looks the row up by dataset
 * (NOT a CSS attribute selector — symbols can contain spaces/specials that would
 * break querySelector) and scrolls ONLY the list, and only when the row isn't
 * already fully visible, via scrollIntoView(block:'nearest'). Optionally moves
 * keyboard focus to the row (roving-tabindex pattern) so the browser keeps it on
 * screen and repeated arrows stay smooth — focus is moved on keyboard nav only,
 * never on programmatic symbol changes (which would steal focus from the chart). */
const SYMNAV_DEBUG = (() => { try { return localStorage.getItem('chart_symnav_debug') === '1'; } catch { return false; } })();
function revealSymRow(list: HTMLElement | null, sym: string, exch: string, doFocus: boolean) {
  if (!list) return;
  const key = `${sym}-${exch}`;
  const el = Array.from(list.querySelectorAll<HTMLElement>('[data-sym-row]'))
    .find(e => e.dataset.symRow === key);
  if (!el) { if (SYMNAV_DEBUG) console.debug('[symnav] row not found', key); return; }
  if (doFocus) { try { el.focus({ preventScroll: true }); } catch { /* older browsers */ } }
  const lr = list.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  const off = er.top < lr.top || er.bottom > lr.bottom;
  if (SYMNAV_DEBUG) console.debug('[symnav] reveal', key, { off, scrollTop: list.scrollTop, erTop: Math.round(er.top), lrTop: Math.round(lr.top), erBot: Math.round(er.bottom), lrBot: Math.round(lr.bottom) });
  if (off) el.scrollIntoView({ block: 'nearest' });
}
/**
 * Reveal a symbol row across several phases so the row stays visible even if some
 * LATER render/effect resets the list scroll to the top (the root cause of the
 * recurring "list jumps to top on select" bug was a post-commit scroll reset we
 * couldn't pin down statically). We re-assert the reveal synchronously, on the
 * next two animation frames, and again after a short delay — each pass only
 * scrolls when the row is actually off-screen, so it's a no-op once stable.
 */
function scheduleRevealSymRow(getList: () => HTMLElement | null, sym: string, exch: string, doFocus: boolean) {
  revealSymRow(getList(), sym, exch, doFocus);
  requestAnimationFrame(() => {
    revealSymRow(getList(), sym, exch, doFocus);
    requestAnimationFrame(() => revealSymRow(getList(), sym, exch, false));
  });
  window.setTimeout(() => revealSymRow(getList(), sym, exch, false), 160);
}

/* ───── constants ───── */
/* The picker is built from the shared catalog. It used to be a literal here,
   a second literal in StrategiesPage, a third in OptionStrategiesPage, a
   fourth in BacktestPage and a fifth in the HTF service — none of which
   agreed. `1M` is a calendar month, not 43200 minutes of arithmetic. */
const INTERVAL_OPTIONS = BUILTIN_INTERVALS.map(value => ({ value, label: catalogIntervalLabel(value), custom: false }));

/* Bar-replay step sizes. '1 Bar' is always offered; the rest are filtered per
   chart interval. A month step is sized at 30 days, the same approximation the
   week step already makes by assuming seven trading days. */
const REPLAY_STEP_MINUTES: Record<string, number> = {
  '1 Bar': 0, '1 Hour': 60, '4 Hour': 240, '1 Day': 1440, '1 Week': 10080, '1 Month': CALENDAR_MONTH,
};

const FILTER_MODES = [
  'ALL', 'NSE', 'BSE', 'NFO', 'NSEFNO', 'MCX', 'CDS', 'NCO', 'DELTA',
  'Tracked', 'Browsed',
  'MyFav',
  'OpenPos', 'OpenBaskets', 'PendEntry',
  // ONE alerts filter, with BUY / SHORT / COVER / SELL as sub-toggles inside it.
  // It replaced four separate chips (LongAlert / ShortAlert / CoverAlert / SellAlert),
  // which cost four slots in the toolbar to answer one question: what fired recently?
  'Alerts',
  'LongWL', 'ShortWL', 'LongTrig', 'ShortTrig',
  // Round 27 — date+threshold-driven movers from the user's tracked list.
  'Gainers', 'Losers',
  // Blast Movers — symbols whose latest Daily/Weekly candle is Inside/Engulfing
  // (auto-populated by the daily 05:31 IST Blast scan).
  'Movers',
  // R114 Stage 4 — symbols from the user's most recent adhoc strategy scan.
  'AdScanBuy', 'AdScanShort',
  // R143 — global indices & commodities (Yahoo, delayed/free, analysis-only).
  'Global',
  // Delta's tokenised real-world assets, and the listed stocks they track.
  // `RWA` is classified by Delta itself (top_tag 'tradfi'), not by a symbol
  // pattern; `RWAEq` charts the equivalent stock via the same Yahoo path Global
  // uses, for the deep history a months-old Delta listing cannot have.
  'RWA', 'RWAEq',
] as const;
type FilterMode = typeof FILTER_MODES[number];

/** R143 — Global indices & commodities (exchange 'GLOBAL', charted via Yahoo,
 *  ~15-min delayed, analysis-only). `symbol` keys mirror the backend
 *  services/replay/globalSymbols.ts map. */
const GLOBAL_SYMBOLS_FE: Array<{ symbol: string; name: string; kind: 'index' | 'commodity' }> = [
  { symbol: 'SP500', name: 'S&P 500', kind: 'index' },
  { symbol: 'DOWJONES', name: 'Dow Jones', kind: 'index' },
  { symbol: 'NASDAQ', name: 'Nasdaq Composite', kind: 'index' },
  { symbol: 'RUSSELL2000', name: 'Russell 2000', kind: 'index' },
  { symbol: 'VIX', name: 'CBOE VIX', kind: 'index' },
  { symbol: 'FTSE100', name: 'FTSE 100', kind: 'index' },
  { symbol: 'DAX', name: 'DAX (Germany)', kind: 'index' },
  { symbol: 'CAC40', name: 'CAC 40 (France)', kind: 'index' },
  { symbol: 'NIKKEI225', name: 'Nikkei 225', kind: 'index' },
  { symbol: 'HANGSENG', name: 'Hang Seng', kind: 'index' },
  { symbol: 'DXY', name: 'US Dollar Index', kind: 'index' },
  { symbol: 'GOLD', name: 'Gold (COMEX)', kind: 'commodity' },
  { symbol: 'SILVER', name: 'Silver (COMEX)', kind: 'commodity' },
  { symbol: 'WTICRUDE', name: 'WTI Crude Oil', kind: 'commodity' },
  { symbol: 'BRENT', name: 'Brent Crude Oil', kind: 'commodity' },
  { symbol: 'NATGAS', name: 'Natural Gas', kind: 'commodity' },
  { symbol: 'COPPER', name: 'Copper', kind: 'commodity' },
  { symbol: 'PLATINUM', name: 'Platinum', kind: 'commodity' },
];
const NSE_LIKE_EXCH = new Set(['NSE', 'BSE', 'NFO', 'MCX', 'CDS', 'BFO', 'NCO']);

/* R145's dropFormingHtfBars (only CLOSED HTF candles, no repaint) now lives in
   utils/candleTransforms so TradingChart's CLN HTF band can share it. */
const SYMBOL_PANEL_W = 190;

/** 'workspace' = the Options Workspace (Delta BTC/ETH chain + basket builder + payoff),
 *  with the page's single spot chart as its collapsible chart strip. */
type LayoutMode = 'single' | 'split' | 'quad' | 'workspace';

/**
 * How one row of the symbol pane is drawn. The pane used to have exactly one template
 * — symbol / change% / exchange — for all 28 filters, which meant the column that made
 * you PICK a filter was the one column it never showed. You chose 'PendEntry' to see armed
 * levels and got a change%; you chose 'OpenPos' to see your positions and got a change%.
 *
 * Four shapes, assigned per filter by FILTER_TEMPLATE below. Row height is constant
 * WITHIN a shape, so scrolling one list never jumps; it differs BETWEEN shapes, which is
 * unavoidable once a row carries a second line and is invisible anyway because the pane
 * scroll resets on every filter change.
 */
export type SymbolRowShape =
  /** symbol — change% — exchange. The instrument browsers: the move IS the distinguishing fact. */
  | 'market'
  /** + what fired and when. You opened this list because something happened. */
  | 'signal'
  /** + unrealised P&L, side, qty. Once you hold it, today's % is noise. */
  | 'position'
  /** + the armed level and the distance to it. That gap is the only question. */
  | 'level';

interface SymbolItem {
  symbol: string;
  exchange: string;
  /** 'market': a human name where the ticker is not self-explanatory (Global, RWA). */
  name?: string;
  /** 'signal': WHAT fired — BUY / SHORT / COVER / SELL / Inside / Engulfing / scan side. */
  action?: string;
  /**
   * 'signal': WHEN it fired — the START OF THE BAR when the engine recorded one,
   * otherwise the moment the alert was written. `atIsBar` says which.
   */
  at?: number;
  /** 'signal': true when `at` is a real candle boundary rather than a scan-run time. */
  atIsBar?: boolean;
  /** 'signal': when the alert was RECORDED, so the scan lag can be disclosed on hover. */
  firedAt?: number;
  /** 'signal': the scan interval in minutes, as the engine evaluated it. */
  intervalMinutes?: number;
  /** 'signal': the price at the moment the signal fired. */
  alertPrice?: number | null;
  /** 'signal': the qualifier worth one word — strategy name, or D/W for a Blast mover. */
  note?: string;
  /** 'signal': the alert this row came from, so it can be joined to a stored AI verdict. */
  alertId?: number;
  /**
   * 'signal': what the AI already thinks of this symbol, if anything is on file.
   *
   * Filled from ONE batch read for the whole list (`GET /ai/symbol-views`), never per row, and it
   * never triggers an assessment — a grid that assessed itself would spend money by being looked
   * at. Absent means nothing is stored, which is the common case while the per-signal lane is off.
   */
  ai?: { grade: string | null; state: string | null; direction: string | null; at: string; source: string } | null;
  /** 'position': unrealised P&L in the account currency. */
  pnl?: number | null;
  /** 'position' / 'level': LONG or SHORT. */
  side?: string;
  /** 'position': open quantity. */
  qty?: number | null;
  /** 'level': the armed trigger price. */
  level?: number | null;
}

/**
 * Every filter maps to EXACTLY ONE shape. Exhaustive by construction: the Record type
 * makes TypeScript reject a new FILTER_MODES entry that is not listed here, so a new
 * filter cannot silently inherit 'market' by falling through a default.
 * `verify:symbol-pane` asserts the same property at runtime.
 */
export const FILTER_TEMPLATE: Record<FilterMode, SymbolRowShape> = {
  // Instrument browsers — you are scanning a venue, so the move is the story.
  ALL: 'market', NSE: 'market', BSE: 'market', NFO: 'market', NSEFNO: 'market',
  MCX: 'market', CDS: 'market', NCO: 'market', DELTA: 'market',
  Tracked: 'market', Browsed: 'market', MyFav: 'market',
  Global: 'market', RWA: 'market', RWAEq: 'market',
  // Gainers/Losers stay 'market': the % change IS the reason the row qualified.
  Gainers: 'market', Losers: 'market',

  // Something fired.
  Alerts: 'signal', Movers: 'signal',
  AdScanBuy: 'signal', AdScanShort: 'signal',
  LongWL: 'signal', ShortWL: 'signal',

  // You hold it.
  OpenPos: 'position', OpenBaskets: 'position',

  // A level is armed.
  PendEntry: 'level', LongTrig: 'level', ShortTrig: 'level',
};

/** The alert types the clubbed `Alerts` filter can show, in display order. */
export const ALERT_TYPES = ['BUY', 'SHORT', 'COVER', 'SELL'] as const;
export type AlertType = typeof ALERT_TYPES[number];

/* ───── Multi-entry scale-in split (Round 89) ─────
   Doubling rule: weights [1,1,2,4,8…] (sum = 2^(N-1)). The last entry takes
   half the total; the first two are equal — so each fill doubles the running
   position and recenters the blended average to the midpoint of (prior avg,
   new level). N=2→½,½ · N=3→¼,¼,½ · N=4→⅛,⅛,¼,½ · N=5→1/16,1/16,⅛,¼,½. */
function entryWeights(n: number): number[] {
  const N = Math.max(1, Math.min(5, Math.floor(n)));
  if (N === 1) return [1];
  const w: number[] = [];
  for (let i = 0; i < N; i++) w.push(i <= 1 ? 1 : 2 ** (i - 1));
  return w;
}
/** Split an integer total into N entry quantities by the doubling rule. Each
 *  entry gets ≥ 1; the last (largest) entry absorbs the rounding remainder. */
/** Combine N option-leg candle series into the synthetic symbol's chart series
 *  (plan §2 orientation): V = isCredit ? Σ(sell)−Σ(buy) : Σ(buy)−Σ(sell), each
 *  leg ratio-weighted. High/low use the two extreme signed sums (same method as
 *  the 4-quad Combined pane). Iterates the UNION time grid of all legs; a leg
 *  missing a bar contributes FLAT at its last known close (forward-fill) —
 *  dropping those bars created holes that re-seeded the CLH/HA smoothing chains
 *  and made custom candle types "jump" to a stale level after the hole. */
function combineSyntheticCandles(synth: { isCredit: boolean; legs: Array<{ action: 'BUY' | 'SELL'; ratio: number }> }, legSeries: CandleRow[][]): CandleRow[] {
  if (!legSeries.length || legSeries.some(s => !s.length)) return [];
  const signs = synth.legs.map(l => {
    const base = l.action === 'BUY' ? 1 : -1;              // debit orientation Σbuy−Σsell
    return (synth.isCredit ? -base : base) * (l.ratio || 1); // credit flips: Σsell−Σbuy
  });
  const maps = legSeries.map(arr => { const m = new Map<number, CandleRow>(); for (const c of arr) m.set(c.time, c); return m; });
  const timeSet = new Set<number>();
  for (const arr of legSeries) for (const c of arr) timeSet.add(c.time);
  const times = Array.from(timeSet).sort((a, b) => a - b);
  const lastSeen: (CandleRow | null)[] = legSeries.map(() => null);
  const out: CandleRow[] = [];
  for (const t of times) {
    // Advance every leg's forward-fill state first, THEN build the bar — so a leg
    // that traded at t is never back-filled from an older close on later bars.
    for (let i = 0; i < maps.length; i++) { const c = maps[i].get(t); if (c) lastSeen[i] = c; }
    if (lastSeen.some(c => !c)) continue; // before some leg's first bar — no basis yet
    let open = 0, close = 0, e1 = 0, e2 = 0, vol = 0;
    for (let i = 0; i < maps.length; i++) {
      const real = maps[i].get(t);
      const prev = lastSeen[i]!;
      const c = real || { time: t, open: prev.close, high: prev.close, low: prev.close, close: prev.close, volume: 0 };
      const s = signs[i];
      open += s * c.open; close += s * c.close;
      e1 += s * c.high; e2 += s * c.low;
      vol += c.volume || 0;
    }
    out.push({ time: t, open, close, high: Math.max(e1, e2, open, close), low: Math.min(e1, e2, open, close), volume: vol });
  }
  return out;
}

function splitQuantity(total: number, n: number): number[] {
  const N = Math.max(1, Math.min(5, Math.floor(n)));
  const T = Math.max(0, Math.floor(total));
  if (N === 1) return [T];
  const w = entryWeights(N);
  const sum = w.reduce((a, b) => a + b, 0); // 2^(N-1)
  const unit = T / sum;
  const out: number[] = [];
  let used = 0;
  for (let i = 0; i < N - 1; i++) {
    const q = Math.max(1, Math.round(unit * w[i]));
    out.push(q);
    used += q;
  }
  out.push(Math.max(1, T - used)); // last absorbs the remainder
  return out;
}

/* ───── Last-used chart config (Round 96) ─────
   Opening any symbol restores the indicators / timeframe / layout the user
   last had on ANY symbol (persisted globally to localStorage). On the very
   first load (no saved config), DEFAULT_OVERLAYS turns on the requested
   indicator set; OverView + Bkground default on via their own keys. */
/** Horizontal lane the bar-close countdown pill must clear when the OverView
 *  panel is on. The panel is 208px wide at left:6, and the pill is positioned
 *  by PRICE — so whenever the last close sat within the panel's vertical range
 *  (most of the time) the pill was drawn on top of it, covering two indicator
 *  rows. Offsetting by a constant keeps the pill still; making it y-aware would
 *  make it jump sideways as price moved. */
const OVERVIEW_LANE_PX = 222;
const LAST_USED_KEY = 'chart_lastUsed_v1';

/* ───── Default-set version marker ─────
   The default indicator set spans FIVE independent localStorage keys — the
   last-used snapshot plus candle type, wick and hide-candles, each read on its
   own — so bumping the snapshot key alone would leave the others stale. A
   leftover `chart_hideCandles: true` in particular would paint no candles at
   all and make the new CLN default look broken. One marker clears the whole set
   exactly once; the next change to the defaults just bumps the version.

   This runs at MODULE LOAD, which is before any useState initialiser below
   reads those keys — the ordering matters, and doing it inside an effect would
   land after the first render had already restored the old values. */
const DEFAULTS_VERSION_KEY = 'chart_defaults_version';
const DEFAULTS_VERSION = '2';
(function resetChartDefaultsOnce() {
  try {
    if (localStorage.getItem(DEFAULTS_VERSION_KEY) === DEFAULTS_VERSION) return;
    for (const k of [LAST_USED_KEY, 'chart_candleType', 'chart_showWick', 'chart_hideCandles']) {
      localStorage.removeItem(k);
    }
    localStorage.setItem(DEFAULTS_VERSION_KEY, DEFAULTS_VERSION);
  } catch { /* private mode / storage disabled — the defaults below apply anyway */ }
})();

/* The default set a chart opens with when nothing is saved: CLN candles with
   wicks, MM, and AV_Band (whose own sub-toggles leave only the Slow HMA drawn —
   those live in Chart Settings, not here). Everything else is OFF, including
   payoffZones: that one is not an indicator, it auto-overlays a running option
   basket on its underlying chart, so a user with a live basket has to switch it
   back on to see it. Deliberate — the default set was specified as a complete
   replacement, not an addition. */
const DEFAULT_OVERLAYS: Overlays = {
  bollingerEnvelope: false, ema200: false, entryZones: false, cpr: false, poc: false,
  avgCloud: false, combinedMidline: false, ctfPoc: false, htfPoc: false,
  mm: true,
  bbrColor: false, spikeColor: false,
  lrHama: false, fllhh: false, fbo: false, wave: false, lrc: false, as: false, vpa: false, hlDiff: false, sr: false,
  relStrength: false, dayChange: false, macd: false, bbrOsc: false, waveOsc: false,
  vol: false, avBand: true, vob: false, fno: false,
  payoffZones: false,
  synthiya: false,
  dbcrZone: false, // DbCrZone — Debit/Credit option-market regime background zones on the underlying
  lrBody: false,   // LR body over ANY candle type (the CLN body as a switch; CLN = Regular + LR + ghost)
};
function readLastUsed(): { overlays?: Overlays; intervals?: number[]; layoutMode?: LayoutMode; candleType?: string; activeStrategy?: string; optInterval?: number | null } | null {
  try { return JSON.parse(localStorage.getItem(LAST_USED_KEY) || 'null'); } catch { return null; }
}

/* ───── Candle fetch cache (perf) ─────
   In-memory, module-level cache for /charts/candles results so that revisiting a
   symbol, flipping timeframe, or re-deriving aux series (Daily/Weekly, which are
   interval-independent; CTF/HTF; benchmark) reuses already-fetched HISTORY
   instead of re-hitting the API 7+ times per symbol change. Only closed-bar
   history is cached (never `withLive` requests); the live forming candle still
   comes from the separate live poll, and the gap-backfill (R124) tops up any
   bars that closed while cached — so cached history can't go stale-wrong.
   TTL: bounded ranges (lazy-load history) are immutable → 1h; open-ended recent
   → 30s. This is purely a frontend fetch optimisation and does NOT touch the
   backend scan engine. */
type CandleCacheEntry = { ts: number; data: any[] };
const candleCache = new Map<string, CandleCacheEntry>();
/* IDXN alias → the actual contract it resolved to (from the candle response), so the
   OHLC legend can show "IDXNGOLD → GOLD26AUGFUT · MCX". Module-level: the legend reads
   it at render time (a fetch completing always re-renders via the candles state). */
const aliasActualBySymbol = new Map<string, { actual: string; exchange: string }>();
const CANDLE_CACHE_MAX = 150;
function candleCacheKey(sym: string, exch: string, interval: number, fromSec?: number, toSec?: number): string {
  return `${sym}|${exch}|${interval}|${fromSec ?? ''}|${toSec ?? ''}`;
}
function candleCacheGet(key: string, ttlMs: number): any[] | null {
  const e = candleCache.get(key);
  if (e && (Date.now() - e.ts) < ttlMs) return e.data;
  return null;
}
function candleCacheSet(key: string, data: any[]): void {
  if (candleCache.size >= CANDLE_CACHE_MAX) {
    // Evict the oldest ~third to bound memory.
    const keys = Array.from(candleCache.keys()).slice(0, Math.floor(CANDLE_CACHE_MAX / 3));
    for (const k of keys) candleCache.delete(k);
  }
  candleCache.set(key, { ts: Date.now(), data });
}

/* Stale-while-revalidate caches (module-level → survive route unmount) so that
   re-entering the Charts page shows the symbol list + % column INSTANTLY from the
   last visit, while the existing fetches refresh them in the background. Keyed by
   filter mode for the symbol list. */
const symResultsCache: Record<string, SymbolItem[]> = {};
/** One symbol's row from /charts/symbol-pct-changes. The endpoint has always returned the
 *  last price and (for Delta) 24h turnover alongside the percent; the pane kept ONLY the
 *  percent, which is why an armed trigger level could never say how far away it was. */
export interface SymPctRow { pct: number; ltp: number | null; turnoverUsd?: number }
let symPctCache: Record<string, SymPctRow> = {};

/* ───── component ───── */
const ChartsPage: React.FC = () => {
  const { symbol: routeSymbol } = useParams<{ symbol?: string }>();
  const routeLocation = useLocation();
  const { C, isDark } = useAppTheme();

  /* symbol panel */
  const [symSearch, setSymSearch] = useState('');
  // R115 follow-up — Symbol panel sort order + per-symbol pct map for the %
  // column and the % Change sort. Pct fetched from /v1/charts/symbol-pct-changes
  // (tracked instruments only). Symbols not in the map render "—" for pct.
  const [symSort, setSymSort] = useState<'name-asc' | 'name-desc' | 'pct-desc' | 'pct-asc' | 'recent' | 'time-desc' | 'time-asc'>(() => {
    try { return (localStorage.getItem('chart_symSort') as any) || 'name-asc'; } catch { return 'name-asc'; }
  });
  const persistSymSort = (v: typeof symSort) => { setSymSort(v); try { localStorage.setItem('chart_symSort', v); } catch {} };
  /* RWA token → listed-stock mapping, loaded with the RWA / RWAEq filters. Kept
     so the symbol row can grey an UNVERIFIED equivalent and say why, and so the
     RWA rows can name the stock they track. */
  const [rwaEquivalents, setRwaEquivalents] = useState<Array<{
    symbol: string; exchange: string; name?: string; deltaSymbol: string; verified: boolean; note?: string | null;
  }>>([]);
  /* The OTHER name(s) a row is known by, so neither RWA list can hide a symbol
     the user is looking for. A Delta token maps to one stock; a stock can map to
     more than one token (PAXGUSD and XAUTUSD are both gold), so this returns a
     list. Empty for every non-RWA row, which is what keeps the label clean
     everywhere else. */
  const rwaPairsFor = useCallback((symbol: string, exchange: string): string[] => {
    const u = (symbol || '').toUpperCase();
    if ((exchange || '').toUpperCase() === 'DELTA') {
      const row = rwaEquivalents.find(e => e.deltaSymbol.toUpperCase() === u);
      return row && row.symbol ? [row.symbol] : [];
    }
    return rwaEquivalents.filter(e => (e.symbol || '').toUpperCase() === u).map(e => e.deltaSymbol);
  }, [rwaEquivalents]);
  const [rwaRefreshing, setRwaRefreshing] = useState(false);
  const [symPctMap, setSymPctMap] = useState<Record<string, SymPctRow>>(() => symPctCache);
  useEffect(() => {
    let cancelled = false;
    const fetchPct = async () => {
      try {
        const r = await api.get<any>('/charts/symbol-pct-changes');
        const items: Array<{ symbol: string; exchange: string; pctChange: number; ltp?: number; turnoverUsd?: number }> = r?.data?.data?.items || [];
        if (cancelled) return;
        const m: Record<string, SymPctRow> = {};
        for (const it of items) {
          m[`${it.exchange}:${it.symbol}`] = {
            pct: it.pctChange,
            ltp: Number.isFinite(Number(it.ltp)) ? Number(it.ltp) : null,
            turnoverUsd: Number.isFinite(Number(it.turnoverUsd)) ? Number(it.turnoverUsd) : undefined,
          };
        }
        setSymPctMap(m);
      } catch { /* silent */ }
    };
    fetchPct();
    // Refresh every 60s — matches the Dashboard cadence for tracked-symbol moves.
    const t = window.setInterval(() => { if (!document.hidden) fetchPct(); }, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  const pctFor = useCallback((sym: string, exch: string): number | null => {
    const v = symPctMap[`${exch}:${sym}`]?.pct;
    return Number.isFinite(v) ? (v as number) : null;
  }, [symPctMap]);
  /** Last price for a symbol, from the same feed as pctFor. Null when it carries none. */
  const ltpFor = useCallback((sym: string, exch: string): number | null => {
    const v = symPctMap[`${exch}:${sym}`]?.ltp;
    return Number.isFinite(v) ? (v as number) : null;
  }, [symPctMap]);
  const [symResults, setSymResults] = useState<SymbolItem[]>(() => {
    try { return symResultsCache[localStorage.getItem('chart_filterMode') || ''] || []; } catch { return []; }
  });
  const [symLoading, setSymLoading] = useState(false);
  /* Declared HERE, above sortedSymResults and the alert-type toggles, because both read it.
     It used to sit ~100 lines further down, which was fine while nothing before it needed
     the value. */
  const [filterMode, setFilterMode] = useState<FilterMode>(() => {
    const stored = localStorage.getItem('chart_filterMode');
    if (stored && (FILTER_MODES as readonly string[]).includes(stored)) return stored as FilterMode;
    return 'Tracked';
  });
  useEffect(() => {
    localStorage.setItem('chart_filterMode', filterMode);
  }, [filterMode]);
  /**
   * Which alert types the clubbed `Alerts` filter is showing. All four by default: the
   * filter replaced four chips, so its out-of-the-box behaviour has to be the union of
   * what those four did, or the change would silently hide rows the user used to see.
   *
   * The list is filtered CLIENT-SIDE from an already-fetched result (see
   * sortedSymResults), so a toggle is instant and costs no request.
   */
  const [alertTypes, setAlertTypes] = useState<AlertType[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('chart_alertTypes') || 'null');
      const kept = Array.isArray(raw) ? raw.filter((t: any) => (ALERT_TYPES as readonly string[]).includes(t)) : [];
      return kept.length ? (kept as AlertType[]) : [...ALERT_TYPES];
    } catch { return [...ALERT_TYPES]; }
  });
  useEffect(() => {
    try { localStorage.setItem('chart_alertTypes', JSON.stringify(alertTypes)); } catch { /* ignore */ }
  }, [alertTypes]);
  /**
   * The Recent-Alerts window, SHARED WITH THE DASHBOARD.
   *
   * Same `dashboard_alertPeriod` localStorage key the Dashboard's dropdown writes, so this
   * is a genuine two-way link rather than a second, competing setting: change it here and
   * the Dashboard panel shows the same window, and vice versa. That contract is the whole
   * reason the key is persisted at all -- the Charts Alerts filter exists to reproduce the
   * list the Dashboard is showing.
   *
   * The `storage` listener covers the cross-TAB case (Dashboard open in another tab):
   * the event fires only in OTHER documents, so it cannot loop back on our own write.
   */
  const [alertPeriod, setAlertPeriodState] = useState<string>(() => readAlertPeriod());
  const setAlertPeriod = useCallback((v: string) => {
    setAlertPeriodState(v);
    try { localStorage.setItem('dashboard_alertPeriod', v); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'dashboard_alertPeriod' && e.newValue) setAlertPeriodState(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleAlertType = useCallback((t: AlertType) => {
    setAlertTypes(prev => {
      const next = prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t];
      // Never let the user reach an empty selection: an empty list is indistinguishable
      // from 'no alerts fired', which is a different and much more alarming statement.
      return next.length ? next : prev;
    });
  }, []);
  // R127 — the symbol pane renders this SORTED list; ArrowUp/Down navigation must
  // use the SAME ordering (previously it walked the raw symResults, so arrows
  // jumped to the next *unsorted* symbol — landing at a random spot in the
  // sorted view). Single source of truth for both render + keyboard nav.
  const sortedSymResults = useMemo(() => {
    // The Alerts filter holds all four types; the toggles narrow the VIEW, not the fetch.
    const arr = (filterMode === 'Alerts' && alertTypes.length < ALERT_TYPES.length)
      ? symResults.filter(r => alertTypes.includes(String(r.action || '').toUpperCase() as AlertType))
      : [...symResults];
    if (symSort === 'recent') return arr; // preserve loadSymbolsByMode order
    arr.sort((a, b) => {
      if (symSort === 'time-desc' || symSort === 'time-asc') {
        // Rows without a timestamp sink to the bottom in BOTH directions. Sorting them to
        // the top of the ascending view would put 'we don't know when' where 'oldest'
        // belongs, which is the same lie as a zero standing in for a missing figure.
        const ta = a.at, tb = b.at;
        if (ta == null && tb == null) return a.symbol.localeCompare(b.symbol);
        if (ta == null) return 1;
        if (tb == null) return -1;
        const d = symSort === 'time-desc' ? tb - ta : ta - tb;
        return d !== 0 ? d : a.symbol.localeCompare(b.symbol);
      }
      if (symSort === 'pct-asc' || symSort === 'pct-desc') {
        const pa = pctFor(a.symbol, a.exchange);
        const pb = pctFor(b.symbol, b.exchange);
        if (pa == null && pb == null) return a.symbol.localeCompare(b.symbol);
        if (pa == null) return 1;
        if (pb == null) return -1;
        const d = symSort === 'pct-desc' ? pb - pa : pa - pb;
        return d !== 0 ? d : a.symbol.localeCompare(b.symbol);
      }
      const cmp = a.symbol.localeCompare(b.symbol);
      return symSort === 'name-desc' ? -cmp : cmp;
    });
    return arr;
  }, [symResults, symSort, pctFor, filterMode, alertTypes]);
  // Symbol-pane scroll preservation. Clicking a symbol re-renders the page; the
  // useLayoutEffect below keeps the selected row visible so the list doesn't jump
  // back to the top (works for every sort — see that effect).
  const symListRef = useRef<HTMLUListElement | null>(null);
  // Page-level Up/Down symbol navigation. The old List-onKeyDown approach only
  // worked while the list kept DOM focus, which the per-click re-render dropped —
  // so arrows stopped after the first press. Instead a window keydown listener
  // (subscribed once) reads the latest state from this ref and navigates the
  // sorted symbol list regardless of focus, EXCEPT while typing in a field or a
  // dialog/menu/popover is open. Assigned every render below (after handleSymbolClick).
  const symNavRef = useRef<{ rows: SymbolItem[]; symbol: string; exchange: string; click: (s: string, e: string) => void } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName) || ae.isContentEditable)) return;
      if (ae && ae.closest && ae.closest('[role="dialog"],.MuiModal-root,.MuiPopover-root,.MuiMenu-root')) return;
      const nav = symNavRef.current;
      if (!nav || !nav.rows.length) return;
      // Locate the current symbol in the SORTED list. Tolerate an exchange
      // mismatch (match by symbol) so a slightly-different stored exchange can't
      // make findIndex miss and jump the selection to row 0 (the "resets to top"
      // bug). If the current symbol genuinely isn't in this list, do nothing
      // rather than jumping to the top.
      let idx = nav.rows.findIndex(r => r.symbol === nav.symbol && r.exchange === nav.exchange);
      if (idx < 0) idx = nav.rows.findIndex(r => r.symbol === nav.symbol);
      if (idx < 0) return;
      const next = e.key === 'ArrowDown'
        ? Math.min(nav.rows.length - 1, idx + 1)
        : Math.max(0, idx - 1);
      if (next === idx) { e.preventDefault(); return; } // already at the end/start
      const t = nav.rows[next];
      if (!t) return;
      e.preventDefault();
      nav.click(t.symbol, t.exchange);
      // Reveal across several phases (see scheduleRevealSymRow) so the row stays
      // visible even if a later effect resets the list scroll. Focus the row
      // (roving tabindex) so repeated arrows stay smooth regardless of prior focus.
      scheduleRevealSymRow(() => symListRef.current, t.symbol, t.exchange, true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Keep the stale-while-revalidate caches fresh so the NEXT visit renders the
  // symbol list + % column instantly (only cache non-search default lists).
  useEffect(() => {
    if (symResults.length && !symSearch) symResultsCache[filterMode] = symResults;
  }, [symResults, filterMode, symSearch]);
  useEffect(() => {
    if (symPctMap && Object.keys(symPctMap).length) symPctCache = symPctMap;
  }, [symPctMap]);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Gainers / Losers threshold, persisted so the user's last pick is restored.
     `moversDate` used to live here too. It was removed with the date input it fed:
     the backend has never read a `date` parameter, so the whole round-trip was theatre. */
  const [moversThreshold, setMoversThreshold] = useState<string>(() => {
    return localStorage.getItem('chart_moversThreshold') || '1.0';
  });
  useEffect(() => { localStorage.setItem('chart_moversThreshold', moversThreshold); }, [moversThreshold]);
  /** Why a Gainers/Losers list is empty. Rendered under the chips; without it 'you track
   *  nothing', 'nothing moved that much' and 'your Zerodha session expired so no NSE symbol
   *  was even queried' all looked identical: a blank pane. */
  const [moversNote, setMoversNote] = useState<string>('');

  /* symbol browsing history */
  const [history, setHistory] = useState<SymbolItem[]>(() => {
    try { return JSON.parse(localStorage.getItem('chart_history') || '[]'); } catch { return []; }
  });
  const [historyIdx, setHistoryIdx] = useState(() => {
    try { const h = JSON.parse(localStorage.getItem('chart_history') || '[]'); return Math.max(h.length - 1, 0); } catch { return 0; }
  });
  const navigatingHistory = useRef(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { settings, loading: chartSettingsLoading, active: activeChartTemplate, updateTemplate: updateChartTemplate, createTemplate: createChartTemplate } = useChartSettings();
  /* chart state */
  const [symbol, setSymbol] = useState(routeSymbol?.toUpperCase() || localStorage.getItem('chart_lastSymbol') || '');
  // F-Chart-InitialExchange — read ?exchange= from the URL SYNCHRONOUSLY at
  // mount so a deep-link like /charts/XANUSD?exchange=DELTA doesn't briefly
  // mount as NSE (stale localStorage) and fire benchmark fetches for NIFTY
  // before the URL-params useEffect kicks in (4 NIFTY 404 popups).
  const [exchange, setExchange] = useState(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('exchange');
      if (fromUrl) return fromUrl.toUpperCase();
    } catch { /* SSR / no window — fall through */ }
    return localStorage.getItem('chart_lastExchange') || 'NSE';
  });
  /* True only while the DB lookup that CONFIRMS this symbol's exchange is in
   * flight. Until it lands, `exchange` is still the stale localStorage value —
   * or the 'NSE' default above — even for a Delta symbol. Anything that derives
   * a DIFFERENT symbol from `exchange` must wait, or it asks for the wrong
   * venue's data: on a cold /charts/BTCUSD the RelStrength benchmark resolved
   * to NIFTY/NSE and 404'd four times before the real exchange arrived.
   * See docs/CHARTS-FUNCTIONAL-ISSUES.md item 2. Fails OPEN — if the lookup
   * throws, this returns to false and behaviour is exactly as before.
   *
   * It must start TRUE, not false. Effects run in declaration order, and the benchmark
   * effect is declared ~3000 lines ABOVE the lookup that resolves the exchange — so on
   * mount it fired, 404'd, and only then saw the flag go true. Whether the lookup is
   * going to run is decidable synchronously from the URL, so decide it here. */
  const [exchangePending, setExchangePending] = useState<boolean>(() => {
    try {
      // An explicit ?exchange= is authoritative; no lookup runs, so nothing to wait for.
      if (new URLSearchParams(window.location.search).get('exchange')) return false;
      // /charts/<symbol> with no explicit exchange => the DB lookup below WILL run.
      return /\/charts\/[^/?#]+/.test(window.location.pathname);
    } catch { return false; }
  });

  /* ───── AI view of the charted symbol ─────
     Read-only and additive. Fetched from /ai/symbol, which returns the newer of the nightly
     board verdict and any one-off assessment — reading only ai_assessments here would miss
     every verdict the nightly run produced, which is most of them. No model call is made on
     load; the chip offers "Ask AI" when nothing exists yet, so opening a chart never spends. */
  const [aiView, setAiView] = useState<AiAssessment | null>(null);
  useEffect(() => {
    if (!symbol) { setAiView(null); return; }
    let cancelled = false;
    setAiView(null);
    aiService.getSymbolView(symbol)
      .then(v => { if (!cancelled) setAiView((v as AiAssessment) || null); })
      .catch(() => { if (!cancelled) setAiView(null); });
    return () => { cancelled = true; };
  }, [symbol, exchange]);

  /* ───── Synthetic symbols (docs/SYNTHETIC-SYMBOLS-PLAN.md) ─────
     When a synthetic is opened, symbol = its name and exchange = 'SYNTH'; the
     chart plots the COMBINED package premium (legs fetched + zipped in
     fetchCandles) and BUY/SHORT arms a premium TRIGGER instead of an order. */
  const [synth, setSynth] = useState<SyntheticSymbolRow | null>(null);
  const synthRef = useRef<SyntheticSymbolRow | null>(null);
  useEffect(() => { synthRef.current = synth; }, [synth]);
  const isSynth = exchange === 'SYNTH';
  const isSynthRef = useRef(false);
  useEffect(() => { isSynthRef.current = isSynth; }, [isSynth]);
  /** Trigger arming mode for synthetic charts — PAPER (default) or LIVE
   *  (Increment 5). LIVE additionally demands an explicit confirm at submit. */
  // LIVE-default (item 8): manual trade initiation defaults to the real account —
  // the chip still toggles and LIVE arming keeps its explicit confirmation dialog.
  const [synthMode, setSynthMode] = useState<'PAPER' | 'LIVE'>('LIVE');
  /** BUY / SHORT confirm on a normal chart: place the order LIVE (real broker order) or
   *  PAPER (simulated by the paper engine — Trade.isSimulation). Owner 2026-11-03: the
   *  dialog ALWAYS opens on LIVE; PAPER is a per-trade choice. */
  const [chartTradeMode, setChartTradeMode] = useState<'PAPER' | 'LIVE'>('LIVE');
  const synthModeRef = useRef<'PAPER' | 'LIVE'>('PAPER');
  useEffect(() => { synthModeRef.current = synthMode; }, [synthMode]);
  /** Trailing-stop toggle for the NEXT armed trigger (Increment 6): the basket
   *  monitor then ratchets the fired basket's SL with the shared HA-trail math. */
  // (The synthetic-trigger TRL trailing toggle was removed 2026-11-03: no automatic trailing.)
  // Bumped when a synthetic changes anywhere (save-as-symbol, arm, delete) so
  // the panes below the symbol list refresh immediately.
  const [synthNonce, setSynthNonce] = useState(0);
  useEffect(() => {
    const f = () => setSynthNonce(n => n + 1);
    window.addEventListener('synthetics-changed', f);
    return () => window.removeEventListener('synthetics-changed', f);
  }, []);

  /* Keep the browser/PWA title and the in-app chart tab label tracking the
     CURRENTLY displayed symbol. When the user changes the symbol from the
     pane, syncActiveChartTab renames the active chart tab in place + updates
     the URL so the tab strip no longer shows the symbol it was opened with. */
  const { syncActiveChartTab, openChartTab } = useTabs();
  useEffect(() => {
    if (!symbol) return;
    document.title = `${symbol} — Prashant Charts`;
    syncActiveChartTab(symbol, exchange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange]);

  // Keep the just-selected symbol visible in the pane after a symbol change so
  // the user can click the next symbol in place — the list never snaps back to
  // the top. We target the actual selected row element (not a saved numeric
  // offset, which goes stale whenever the list reorders), so this works for
  // EVERY sort, including % sort whose order shifts on each pct refresh. We
  // scroll ONLY the list, and ONLY when the row is off-screen, so a row that's
  // already visible (the usual case right after a click) doesn't move at all.
  // Runs before paint → no flicker.
  useLayoutEffect(() => {
    // Keep the selected row visible when the symbol changes (click / history
    // prev-next / programmatic). Multi-phase so a later scroll reset can't strand
    // it at the top. No focus move here — only keyboard nav focuses the row — so
    // this never yanks focus off the chart.
    scheduleRevealSymRow(() => symListRef.current, symbol, exchange, false);
  }, [symbol, exchange]);

  // When the LIST CONTENT itself changes (filter / search / sort), the previous
  // scroll position is no longer meaningful — start from the top.
  useEffect(() => {
    if (symListRef.current) symListRef.current.scrollTop = 0;
  }, [filterMode, symSearch, symSort]);
  // Round 96 — layout / interval / overlays all initialise from the globally
  // persisted "last used" config (whatever the user last had on ANY symbol),
  // falling back to sensible first-load defaults when none is saved.
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => readLastUsed()?.layoutMode || 'single');
  /* Quad's option panes (Q2 leg 1, Q3 combined, Q4 leg 2) get their OWN timeframe.
     They used to inherit intervals[0] from the spot pane, which is fine at 15m and useless
     at 8H: a Delta option contract listed days ago simply has no 8H history to draw, so
     three of the four panes in the layout that exists FOR options went nearly blank.
     null = "follow the spot pane, capped at 15m", which is the sensible default and still
     tracks the spot pane on low timeframes. A value = the user picked one, and it sticks. */
  const [optIntervalPref, setOptIntervalPref] = useState<number | null>(() => readLastUsed()?.optInterval ?? null);
  const [focusedSplitPane, setFocusedSplitPane] = useState<0 | 1>(0);
  const [intervals, setIntervals] = useState<number[]>(() => readLastUsed()?.intervals || [15, 60, 1440, 10080]);
  // Spread DEFAULT_OVERLAYS first so newly-added keys (e.g. payoffZones) pick up
  // their default for users whose saved config predates them; explicit user choices
  // (incl. a deliberate off) are preserved by the persisted spread on top.
  const [overlays, setOverlays] = useState<Overlays>(() => ({ ...DEFAULT_OVERLAYS, ...(readLastUsed()?.overlays || {}) }));

  // NOTE: the "last used" snapshot is persisted by a single consolidated effect
  // declared below (after candleType / activeStrategy are defined) so all five
  // dimensions — overlays, intervals, layout, candleType, strategy — are saved
  // together and restored on the next symbol / app restart.

  /* Chart preset — set from the `?preset=` URL param when a chart is opened
   * via a "Chart" button anywhere in the app. `preset=cloud` shows ONLY the
   * AvgCloud + C-POC + LR_HAMA_Band overlays and forces the AvgCloud
   * reference line to C-POC, so the chart matches what the Sniper strategy
   * scans on. `presetActiveRef` lets the saved-per-symbol-prefs hydration
   * know not to clobber the preset's overlay selection. */
  const [chartPreset, setChartPreset] = useState<string | null>(null);
  const presetActiveRef = useRef(false);

  /* candle data per pane (index 0-3) */
  const [paneData, setPaneData] = useState<CandleRow[][]>([[], [], [], []]);
  // R143 Plan A — free (Yahoo) chart data for NSE symbols when Zerodha isn't
  // connected. `freeDataActive` = panes are loaded from the free source (so the
  // live broker poll is skipped). `nseFreeOffer` = the live NSE load failed
  // (no broker) → show a "Load free data" banner.
  const [freeDataActive, setFreeDataActive] = useState(false);
  const [nseFreeOffer, setNseFreeOffer] = useState(false);
  const freeDataActiveRef = useRef(false);
  useEffect(() => { freeDataActiveRef.current = freeDataActive; }, [freeDataActive]);
  // Reset free-data mode whenever the symbol changes — re-attempt the live feed.
  useEffect(() => { setFreeDataActive(false); setNseFreeOffer(false); }, [symbol, exchange]);
  const [paneLoading, setPaneLoading] = useState([false, false, false, false]);

  /* Aux higher-timeframe candles per spot pane — populated lazily when any of
     Combined Envelope / Combined Midline / CPR / CTF POC / HTF POC is toggled on. */
  const [paneCtf, setPaneCtf] = useState<CandleRow[][]>([[], []]);
  const [paneHtf, setPaneHtf] = useState<CandleRow[][]>([[], []]);
  // S/R indicator (R137) HTF candles per pane — at the Strategies-mapped HTF
  // for each pane's interval (kept separate from the ladder-based paneHtf).
  const [paneSrHtf, setPaneSrHtf] = useState<CandleRow[][]>([[], []]);
  const [paneSrHtfMin, setPaneSrHtfMin] = useState<number[]>([0, 0]);
  /* Daily / Weekly candles (symbol-level) — for the BOLev_D / BOLev_W overlays. */
  const [dailyCandles, setDailyCandles] = useState<CandleRow[]>([]);
  const [weeklyCandles, setWeeklyCandles] = useState<CandleRow[]>([]);
  const [h4Candles, setH4Candles] = useState<CandleRow[]>([]);

  /* quad mode option state */
  const [optionExpiries, setOptionExpiries] = useState<string[]>([]);
  const [optionStrikes, setOptionStrikes] = useState<number[]>([]);
  // Bumped by the quad "refresh chain" button to re-pull the live Delta option
  // contracts (expiries + strikes) from the broker on demand.
  const [chainReloadNonce, setChainReloadNonce] = useState(0);
  const [chainLoading, setChainLoading] = useState(false);
  const [spotPrice, setSpotPrice] = useState(0);
  const [ceStrike, setCeStrike] = useState<number | null>(null);
  const [ceExpiry, setCeExpiry] = useState('');
  const [peStrike, setPeStrike] = useState<number | null>(null);
  const [peExpiry, setPeExpiry] = useState('');
  const [nfoUnderlying, setNfoUnderlying] = useState('');
  const [ceCandles, setCeCandles] = useState<CandleRow[]>([]);
  const [peCandles, setPeCandles] = useState<CandleRow[]>([]);
  const [ceLoading, setCeLoading] = useState(false);
  const [peLoading, setPeLoading] = useState(false);
  /* Resolved CE/PE Delta symbols (captured at chain load) + their HTF candles,
     fetched only when a multi-timeframe strategy (Sniper / RockBand) is active so
     the option panes can carry the same strategy colouring the spot pane does. */
  const [ceSymbol, setCeSymbol] = useState('');
  const [peSymbol, setPeSymbol] = useState('');
  const [ceOptExchange, setCeOptExchange] = useState('');
  const [peOptExchange, setPeOptExchange] = useState('');
  const [ceHtf, setCeHtf] = useState<CandleRow[]>([]);
  const [peHtf, setPeHtf] = useState<CandleRow[]>([]);
  /* Phase 2 — per-leg sizing detail (lotSize / leverage / option budgets) so a
     leg's BUY/SHORT auto-quantity matches its own instrument, not the spot's. */
  const [ceTickSize, setCeTickSize] = useState<number | undefined>(undefined);
  const [peTickSize, setPeTickSize] = useState<number | undefined>(undefined);
  /* Delta option contract value (base-asset per contract, e.g. 0.001 BTC) =
     the leg instrument's lotSize. Used to size a leg from the underlying's
     option investment budget. */
  const [ceLotSize, setCeLotSize] = useState<number>(0.001);
  const [peLotSize, setPeLotSize] = useState<number>(0.001);
  /* Live LTP (Delta mark price) for the focused CE/PE legs — polled so the LTP
     line and dashboard reflect the moving premium, not a stale candle close. */
  const [ceLtp, setCeLtp] = useState<number | null>(null);
  const [peLtp, setPeLtp] = useState<number | null>(null);
  /* Per-leg option TYPE + ACTION for the two generalized leg slots (formerly
     hardcoded CE/PE). Lets a slot hold a call OR a put (e.g. both calls for a
     call spread); the action drives the synthetic combined sign (SELL +, BUY −).
     Default: slot 1 = SELL CE, slot 2 = SELL PE (a short straddle/strangle). */
  const [ceOptType, setCeOptType] = useState<'CE' | 'PE'>('CE');
  const [peOptType, setPeOptType] = useState<'CE' | 'PE'>('PE');
  const [ceAction, setCeAction] = useState<'SELL' | 'BUY'>('SELL');
  const [peAction, setPeAction] = useState<'SELL' | 'BUY'>('SELL');
  /* When a basket is live, the Combined (Q3) panel is LOCKED to the basket's two
     legs via these independent candle series, so re-pointing the leg panels can't
     alter it. Empty when no basket is live (then Combined uses the leg panels). */
  const [basketLegCandles, setBasketLegCandles] = useState<CandleRow[][]>([]);
  /* Tracks which basket id we've already defaulted the leg panels to (so we set
     the leg selectors to the basket legs ONCE, not on every poll/re-point). */
  const basketDefaultedRef = useRef<number | null>(null);

  /* Combined (synthetic) candlestick for quad Q3 — zip the two leg series by
     timestamp and SIGN each by its action (SELL +, BUY −) so the series is the
     position's net premium: credit strategies positive, debit negative, and the
     value shrinks toward 0 as a credit position profits. For a BUY leg the high
     and low invert, so recompute the combined high/low from the signed extremes. */
  const combinedCandles = useMemo<CandleRow[]>(() => {
    if (!ceCandles.length || !peCandles.length) return [];
    const sA = comboSign(ceAction), sB = comboSign(peAction);
    const ceByTime = new Map<number, CandleRow>();
    for (const c of ceCandles) ceByTime.set(c.time, c);
    const result: CandleRow[] = [];
    for (const p of peCandles) {
      const c = ceByTime.get(p.time);
      if (!c) continue;
      const open = sA * c.open + sB * p.open;
      const close = sA * c.close + sB * p.close;
      const e1 = sA * c.high + sB * p.high, e2 = sA * c.low + sB * p.low;
      result.push({
        time: p.time, open, close,
        high: Math.max(e1, e2, open, close),
        low: Math.min(e1, e2, open, close),
        volume: (c.volume || 0) + (p.volume || 0),
      });
    }
    return result;
  }, [ceCandles, peCandles, ceAction, peAction]);

  /* Combined HTF — same signed timestamp-zip as combinedCandles, so a
     multi-timeframe strategy (Sniper / RockBand) can colour the Combined pane. */
  const combinedHtf = useMemo<CandleRow[]>(() => {
    if (!ceHtf.length || !peHtf.length) return [];
    const sA = comboSign(ceAction), sB = comboSign(peAction);
    const ceByTime = new Map<number, CandleRow>();
    for (const c of ceHtf) ceByTime.set(c.time, c);
    const result: CandleRow[] = [];
    for (const p of peHtf) {
      const c = ceByTime.get(p.time);
      if (!c) continue;
      const open = sA * c.open + sB * p.open;
      const close = sA * c.close + sB * p.close;
      const e1 = sA * c.high + sB * p.high, e2 = sA * c.low + sB * p.low;
      result.push({
        time: p.time, open, close,
        high: Math.max(e1, e2, open, close),
        low: Math.min(e1, e2, open, close),
        volume: (c.volume || 0) + (p.volume || 0),
      });
    }
    return result;
  }, [ceHtf, peHtf, ceAction, peAction]);

  /* live updates */
  const [latestCandles, setLatestCandles] = useState<(CandleRow | undefined)[]>([undefined, undefined]);
  const [isLive, setIsLive] = useState(true);

  /* Mark price (Delta perpetuals only) — polled every 10s when the toggle is on. */
  const [markPrice, setMarkPrice] = useState<number | null>(null);
  /* Synthetic per-leg LTPs (symbol → mark) from the same quote poll — legend "@LTP". */
  const [synthLegMarks, setSynthLegMarks] = useState<Record<string, number>>({});

  /* FNO metrics overlay — persisted via the shared overlays object (overlays.fno)
     so it survives page navigation like every other indicator. */
  const fnoOverlay = !!overlays.fno;
  type FnoLevel = { strike: number; oi: number | null };
  type FnoPoint = { t: number; maxPain: number | null; gexFlip: number | null; netGamma: number | null; pcr: number | null; support: FnoLevel[]; resistance: FnoLevel[]; walls: { strike: number; gex: number }[] };
  type FnoLevelsResp = { supported?: boolean; reason?: string; tiers: Record<string, { expiry: string; points: FnoPoint[] }>; requestedTier?: string | null; effectiveTier?: string | null };
  // levels-history: now only the Synthiya PCR / IV / RS staircase reads it.
  const [fnoData, setFnoData] = useState<FnoLevelsResp | null>(null);
  // The FNO per-candle series (/options/fno-series) — every FNO line / pane reads this.
  const [fnoSeries, setFnoSeries] = useState<FnoSeriesResp | null>(null);
  const [optionProfileRaw, setOptionProfileRaw] = useState<any | null>(null);
  const [futuresProfileRaw, setFuturesProfileRaw] = useState<any | null>(null);
  const [fnoOutlook, setFnoOutlook] = useState<any | null>(null);

  /* price level triggers */
  type TriggerType = 'nbuy' | 'nshort' | 'bbuy' | 'bshort';
  type TriggerLevels = { nbuy?: number; nshort?: number; bbuy?: number; bshort?: number };
  /* Additional click-modes that aren't simple trigger-level saves:
     - hvn: place a new draggable HVN line (multiple per symbol)
     - buy / short: 3-click capture (Entry → SL → Target) → confirm modal */
  // R115 Stage 5 — 'addIsl' / 'addTgt' arm a one-shot price-pick that PATCHes
  // the open trade's stop-loss / target on the next chart click.
  type ChartTool = 'hvn' | 'buy' | 'short' | 'addIsl' | 'addTgt' | 'strategy' | 'outlook';

  /* ── Rectangle zone annotations (item 3) — per-symbol, persisted in the
     chart_preferences jsonb under chartSettings.rectZones. ── */
  const [rectZones, setRectZones] = useState<RectZone[]>([]);
  const [rectDraw, setRectDraw] = useState(false);
  const [rectSel, setRectSel] = useState<{ id: string; x: number; y: number } | null>(null);
  // The per-symbol chartSettings blob as loaded — the rect saver spreads it as the
  // merge base so OTHER chartSettings keys aren't clobbered by the partial PUT.
  const loadedChartSettingsRef = useRef<any>({});
  const rectZonesLoadedRef = useRef(false); // guards the saver until the load ran
  // STABLE handlers — inline arrows made TradingChart's draw-mode effect re-run on
  // every render (live ticks), which reset the 2-click draft between clicks.
  const handleRectZoneSelect = useCallback((id: string | null, pos?: { x: number; y: number }) =>
    setRectSel(id && pos ? { id, x: pos.x, y: pos.y } : null), []);
  const handleRectDrawComplete = useCallback(() => setRectDraw(false), []);
  const [triggerMode, setTriggerMode] = useState<TriggerType | null>(null);
  const [chartTool, setChartTool] = useState<ChartTool | null>(null);
  /* Which pane/instrument an action targets. There is NO pre-selector — the
     target is decided dynamically by WHICH pane the user clicks (spot / CE / PE).
     This type is carried on orderCapture.targetPane, frozen at the first capture
     click so a multi-click BUY/SHORT can't drift across panes. */
  type TradeTarget = 'spot' | 'ce' | 'pe';
  /* Chart-side strategy BUILDER (4-quad): the dialog opens for a chosen strategy,
     previews + lets the user adjust strikes/lots live (no order), and on approve
     persists + executes. builderConfig = which strategy is open; dialogCard =
     the live (lots-scaled) preview card, used to drive the payoff-zones overlay
     on the underlying before approval. */
  const [builderConfig, setBuilderConfig] = useState<{ strategyId: string; label: string; legs?: StrategyLeg[] } | null>(null);
  const [dialogCard, setDialogCard] = useState<any | null>(null);
  /* Click-to-build capture (4-quad): clicking a Build chip arms a level pick on
     the SPOT chart (1–2 clicks per strategy). When enough levels are picked they
     are snapped to strikes → legs → the builder dialog opens seeded with them. */
  type StrategyCapture = { strategyId: string; label: string; clicksNeeded: number; roles: string[]; picks: number[] };
  const [strategyCapture, setStrategyCapture] = useState<StrategyCapture | null>(null);
  const strategyCaptureRef = useRef<((price: number) => void) | null>(null);
  /* Outlook capture (item 5): clicking an Outlook chip arms a 1–2 click level pick
     on the SPOT chart; the picked level(s) open the OutlookBuilderDialog seeded with
     the chosen boundary mode (Won't fall below / rise above / break above / below /
     stay in range). Reuses the same capture mechanic as the strategy builder. */
  type OutlookCapMode = 'WONT_FALL_BELOW' | 'WONT_RISE_ABOVE' | 'WILL_BREAK_ABOVE' | 'WILL_BREAK_BELOW' | 'WILL_BREACH' | 'STAY_IN_RANGE';
  const [outlookCapture, setOutlookCapture] = useState<{ mode: OutlookCapMode; clicksNeeded: number; picks: number[] } | null>(null);
  const outlookCaptureRef = useRef<((price: number) => void) | null>(null);
  const [outlookDialog, setOutlookDialog] = useState<{ mode: OutlookCapMode; level: number; level2?: number } | null>(null);
  // Adjustment review dialog for the running basket whose P&L badge was clicked.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [squaring, setSquaring] = useState(false);
  // Staged-campaign chart wiring — launcher + adjust dialog + the active campaign for this symbol.
  const [stageDlgOpen, setStageDlgOpen] = useState(false);
  const [campaignAdjustOpen, setCampaignAdjustOpen] = useState(false);
  const [campaignAdjustId, setCampaignAdjustId] = useState<number | null>(null);
  const [openCampaigns, setOpenCampaigns] = useState<any[]>([]);
  // Unified Outlook flow: 'single' = one ranked basket (Architect), 'staged' = an anchored-ladder
  // campaign whose per-view draw pre-defines the actual entry levels (market comes to them).
  const [outlookMode, setOutlookMode] = useState<'single' | 'staged'>('single');
  const [drawnStages, setDrawnStages] = useState<number[]>([]);   // directional entries E1..EN
  const [drawnUp, setDrawnUp] = useState<number[]>([]);           // up-ladder (range/breakout)
  const [drawnDn, setDrawnDn] = useState<number[]>([]);           // down-ladder (range/breakout)
  const [drawnRU, setDrawnRU] = useState<number | null>(null);    // range/breakout UPPER bound
  const [drawnRL, setDrawnRL] = useState<number | null>(null);    // range/breakout LOWER bound
  const [drawnDanger, setDrawnDanger] = useState<number | null>(null); // directional danger
  const [drawPhase, setDrawPhase] = useState<'entries' | 'danger' | 'rangeUpper' | 'rangeLower' | 'upLevels' | 'dnLevels'>('entries');
  const [stageSeed, setStageSeed] = useState<any>(null);
  const [stageSeedKey, setStageSeedKey] = useState(0); // bump → remount the dialog fresh per draw
  /* Phase C — the OPEN (executed, not closed) credit basket for this underlying,
     so its SL/Target can be shown + dragged on the Combined pane. Polled. */
  // A symbol can have MULTIPLE open baskets — keep them all; the PayOff-Zones overlay
  // shows the user-selected one (persisted per underlying), defaulting to the first.
  const [openBaskets, setOpenBaskets] = useState<any[]>([]);
  /* Forward declaration for the BUY/SHORT 3-click capture handler (#5).
     Implemented inside the OrderCaptureProvider block; click router calls
     `orderCaptureRef.current?.(price)` when chartTool is 'buy'/'short'. */
  const orderCaptureRef = useRef<((price: number, ctx?: { pane: TradeTarget; instrumentId: number | null; symbol: string; decimals: number }) => void) | null>(null);

  /* ── Bar Replay (Round 68a) ──────────────────────────────────────────
   * Lets the user freeze the chart at any past candle and reveal future
   * bars one at a time, with indicators recomputing as each bar appears.
   * MVP scope: single-pane only; click "Replay" to enter, the playhead
   * defaults to 200 bars from the right edge (or candles.length/2,
   * whichever is smaller) so the user has runway. Auto-refresh, lazy
   * load and BUY/SHORT capture are all gated while active.
   * State lives only in memory — exits on symbol change or page leave. */
  const [replay, setReplay] = useState<{
    active: boolean; currentIdx: number; playing: boolean; speed: number;
    // R142 — replay data feed: 'broker' (Delta/Zerodha, paginated) or 'free'
    // (Binance/Yahoo deep history). `deep` = a deep window has been loaded
    // (so paneData was replaced and must be restored to live on exit).
    source: 'broker' | 'free'; deep: boolean;
  }>({ active: false, currentIdx: 0, playing: false, speed: 1, source: 'broker', deep: false });
  const [replayDeepLoading, setReplayDeepLoading] = useState(false);
  const REPLAY_OFF = { active: false, currentIdx: 0, playing: false, speed: 1, source: 'broker' as const, deep: false };
  // GoTo-date — popover anchor + the picked datetime-local string.
  const [gotoAnchorEl, setGotoAnchorEl] = useState<HTMLElement | null>(null);
  const [gotoVal, setGotoVal] = useState<string>('');
  // Bar-replay step increment per click (label → minutes → bars at the chart interval).
  const [replayStepLabel, setReplayStepLabel] = useState('1 Bar');
  // Only offer steps LARGER than the bar being replayed. On a 1M chart the old
  // fixed menu offered "1 Week", which divided to zero bars and was clamped
  // back to one — a control that looked like it did something and did not.
  const replayStepOptions = useMemo(() => {
    const iv = intervals[0] || 15;
    return Object.entries(REPLAY_STEP_MINUTES)
      .filter(([, mins]) => mins === 0 || mins > iv)
      .map(([label]) => label);
  }, [intervals]);
  useEffect(() => {
    if (!replayStepOptions.includes(replayStepLabel)) setReplayStepLabel('1 Bar');
  }, [replayStepOptions, replayStepLabel]);
  const replayStepBars = (() => {
    const mins = REPLAY_STEP_MINUTES[replayStepLabel] ?? 0;
    if (mins <= 0) return 1;
    return Math.max(1, Math.round(mins / (intervals[0] || 15)));
  })();
  // Symbol / exchange / layout change → leave replay, since currentIdx indexes
  // a candle array that is about to be replaced. If a DEEP window was loaded,
  // restore the live series too: exitReplay and both toggle paths already do
  // this, and only this reset forgot — so switching layout mid-replay dropped
  // the panel but left the deep historical window on screen with live polling
  // resumed on top of it.
  useEffect(() => {
    setReplay(r => {
      if (!r.active) return r;
      if (r.deep) setTimeout(() => loadSpotPanesRef.current?.(), 0);
      return { ...REPLAY_OFF };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, layoutMode]);

  // Mirror replay.active into a ref so callbacks that capture earlier scope
  // (loadOlderPane, chart-click router) can read the latest value without
  // being recreated.
  const replayActiveRef = useRef(false);
  useEffect(() => { replayActiveRef.current = replay.active; }, [replay.active]);
  // Animation loop: when playing, advance currentIdx every 1000/speed ms.
  // Stops automatically when we reach the last bar. The interval re-creates
  // when speed changes so the cadence is correctly updated.
  useEffect(() => {
    if (!replay.active || !replay.playing) return;
    const stepMs = Math.max(40, 1000 / replay.speed);
    const id = window.setInterval(() => {
      setReplay(r => {
        if (!r.active || !r.playing) return r;
        const total = paneData[0]?.length || 0;
        const next = r.currentIdx + 1;
        if (next >= total) return { ...r, playing: false, currentIdx: Math.max(0, total - 1) };
        return { ...r, currentIdx: next };
      });
    }, stepMs);
    return () => clearInterval(id);
  }, [replay.active, replay.playing, replay.speed, paneData]);

  // R68b — common helper to seek the playhead to the closest candle for a
  // target time. Times are paneData[0]'s IST-shifted seconds-since-epoch.
  const seekReplayToTime = useCallback((targetTime: number) => {
    setReplay(prev => {
      if (!prev.active) return prev;
      const pd = paneData[0];
      if (!pd || pd.length === 0) return prev;
      // Binary search for first index with time >= target
      let lo = 0, hi = pd.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (pd[mid].time < targetTime) lo = mid + 1; else hi = mid;
      }
      // Compare hit with previous to pick closest
      const candA = pd[lo];
      const candB = lo > 0 ? pd[lo - 1] : null;
      const idx = candB && Math.abs(candB.time - targetTime) < Math.abs(candA.time - targetTime) ? lo - 1 : lo;
      return { ...prev, playing: false, currentIdx: idx };
    });
  }, [paneData]);

  /* Click-to-seek, but ONLY when the click belongs to nobody else. TradingChart
     fires onChartClick and onChartTimeClick from the same subscription, so while
     a drawing / capture tool was armed a single click both drew AND jumped the
     playhead — arming Rect and clicking the first corner moved the playhead from
     bar 352 to bar 220 (measured), truncating away the very bars being boxed.
     Rect draw runs its own subscribeClick, which is why gating here rather than
     inside the tool handlers is what actually fixes it. */
  const replaySeekOnClick = (replay.active && !rectDraw && !chartTool && !triggerMode)
    ? seekReplayToTime : undefined;

  /* Interval change → keep the user's PLACE instead of their index.
     `currentIdx` indexes the old bar array, so carrying it across a timeframe
     switch pointed at a completely different moment — measured: 4H bar 352
     (2 Aug 01:30) became 1D bar 352 (9 Apr 05:30) — or fell out of range
     entirely, which silently un-clamped the chart while the panel still read
     ON. Re-seek to the same instant on the new timeframe instead. */
  const replayAnchorTimeRef = useRef<number | null>(null);
  useEffect(() => {
    if (!replay.active) { replayAnchorTimeRef.current = null; return; }
    const t = paneData[0]?.[replay.currentIdx]?.time;
    if (t != null) replayAnchorTimeRef.current = t as number;
  }, [replay.active, replay.currentIdx, paneData]);

  const replayReanchorRef = useRef<number | null>(null);
  const prevIntervalRef = useRef<number>(intervals[0]);
  useEffect(() => {
    if (prevIntervalRef.current === intervals[0]) return;
    prevIntervalRef.current = intervals[0];
    if (!replayActiveRef.current) return;
    replayReanchorRef.current = replayAnchorTimeRef.current;
  }, [intervals]);

  useEffect(() => {
    const target = replayReanchorRef.current;
    if (target == null) return;
    const pd = paneData[0];
    if (!pd?.length) return;                       // new data not in yet — retry
    replayReanchorRef.current = null;
    if (target < (pd[0].time as number) || target > (pd[pd.length - 1].time as number)) {
      setReplay({ ...REPLAY_OFF });
      toast('Left Bar Replay — that moment is outside the history loaded for this timeframe',
        { icon: '⏹', duration: 5000 });
      return;
    }
    seekReplayToTime(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneData]);

  // R68b — Trades deep-link auto-start. When the chart is opened with a
  // ?focusAt=<ISO> query param (set by the Trades-page Replay button),
  // automatically enter replay mode at the closest candle. Runs once per
  // page mount AND once per symbol load — guarded by a ref so we don't
  // re-arm replay on every paneData reflow.
  // F43 — deep-link from Trade Management's "Set SL" / "Set target" icon
  // buttons opens this page with `?symbol=…&exchange=…&action=addIsl|addTgt`.
  // Switch to the requested symbol on mount, arm the matching chart tool,
  // and strip the params from the URL so a refresh doesn't re-fire the arm.
  const deepLinkArmedRef = useRef(false);
  useEffect(() => {
    if (deepLinkArmedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const sym = params.get('symbol');
    const exch = params.get('exchange') || '';
    const action = params.get('action');
    if (sym && (action === 'addIsl' || action === 'addTgt')) {
      deepLinkArmedRef.current = true;
      handleSymbolClick(sym, exch);
      // Defer the chartTool arm until the symbol's active trade has loaded,
      // otherwise the chip stays disabled. 600ms is enough for the
      // /trades?status=open&symbol=… fetch to complete in normal conditions.
      window.setTimeout(() => setChartToolExclusive(action === 'addIsl' ? 'addIsl' : 'addTgt'), 600);
      // Clean the URL.
      const url = new URL(window.location.href);
      url.searchParams.delete('symbol');
      url.searchParams.delete('exchange');
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replayAutoStartedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!symbol) return;
    if (!paneData[0]?.length) return;
    const params = new URLSearchParams(window.location.search);
    const focusAt = params.get('focusAt');
    if (!focusAt) return;
    // Don't re-arm for the same focusAt+symbol combo
    const key = `${symbol}|${focusAt}`;
    if (replayAutoStartedRef.current === key) return;
    replayAutoStartedRef.current = key;
    // Parse ISO → UTC seconds → IST shift to match paneData's time grid.
    const utcMs = Date.parse(focusAt);
    if (!Number.isFinite(utcMs)) return;
    const targetIstSec = Math.floor(utcMs / 1000) + 19800;
    const pd = paneData[0];
    let lo = 0, hi = pd.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (pd[mid].time < targetIstSec) lo = mid + 1; else hi = mid;
    }
    const candA = pd[lo];
    const candB = lo > 0 ? pd[lo - 1] : null;
    const idx = candB && Math.abs(candB.time - targetIstSec) < Math.abs(candA.time - targetIstSec) ? lo - 1 : lo;
    setReplay({ active: true, currentIdx: idx, playing: false, speed: 1, source: 'broker', deep: false });
    setChartTool(null);
    toast('Replay started at trade entry', { icon: '⏯' });
    // Strip the param (the sibling addIsl/addTgt deep-link above already does).
    // Left in place, switching symbol re-armed replay on the NEW symbol at the
    // OLD trade's timestamp, because the guard key is `symbol|focusAt`.
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('focusAt');
      window.history.replaceState({}, '', u.toString());
    } catch { /* ignore */ }
  }, [symbol, paneData]);

  /* Chart-side order capture (BUY / SHORT chips) — 3-click flow:
       awaiting_entry → awaiting_sl → awaiting_target → confirming
     Confirmation dialog shows auto-computed qty (editable), LIMIT/MARKET, and
     the Risk : Reward INR figures derived from the captured prices. */
  type OrderCaptureStage = 'idle' | 'awaiting_entry' | 'awaiting_sl' | 'awaiting_target' | 'confirming';
  const [orderCapture, setOrderCapture] = useState<{
    stage: OrderCaptureStage;
    side: 'BUY' | 'SHORT' | null;
    /** Captured entry prices E1..E? (grows to entryCount during capture). */
    entries: number[];
    /** N — number of scale-in levels, frozen when this flow started. */
    entryCount: number;
    sl?: number;
    target?: number;
    /** Per-entry split quantities, seeded on entering the confirm stage. */
    quantities?: number[];
    /** The total the budget sized before the split (a split of a small total can add up to more). */
    sizedTotal?: number;
    /** Order type for entry 1 only (E2..EN always rest as LIMIT). */
    orderType: 'LIMIT' | 'MARKET';
    submitting?: boolean;
    /** Phase 2 — which instrument this capture targets (spot or a CE/PE leg),
     *  frozen when the flow starts so a later target switch can't misroute it. */
    targetPane?: TradeTarget;
    targetInstrumentId?: number | null;
    targetSymbol?: string;
    targetDecimals?: number;
  }>({ stage: 'idle', side: null, entries: [], entryCount: 1, orderType: 'LIMIT' });

  /* Multi-entry scale-in (Round 89) — the user's default N (1..5) from
     Settings → Trading Preferences. Frozen into orderCapture.entryCount when a
     BUY/SHORT flow starts, so a later Settings change never re-shapes a live
     capture. */
  const [entrySplitCount, setEntrySplitCount] = useState(1);
  useEffect(() => {
    api.get<any>('/user/settings', { headers: { _suppressToast: 'true' } })
      .then(r => {
        const n = Number(r.data?.data?.entrySplitCount);
        if (Number.isFinite(n)) setEntrySplitCount(Math.max(1, Math.min(5, n)));
      })
      .catch(() => {});
  }, []);
  const [triggerLevels, setTriggerLevels] = useState<TriggerLevels>({});
  /* HVN levels — multi-row per (user, instrument). Loaded on symbol change.
     Click-to-create when chartTool === 'hvn'; drag and × delete on the right axis. */
  type HvnRow = { id: number; price: number; label?: string | null };
  const [hvnLevels, setHvnLevels] = useState<HvnRow[]>([]);

  /* Round 27 — pending entries for the current instrument. The chart
     renders each as a draggable line so the user can re-position the
     trigger price by dragging. tradeMonitor watches LTP and fires a
     MARKET order when the LTP crosses the entry. */
  type PendingRow = { id: number; side: 'BUY' | 'SHORT'; entryPrice: number; status: string; entryIndex?: number; entryCount?: number; stopLoss?: number | null; target?: number | null };
  const [pendingRows, setPendingRows] = useState<PendingRow[]>([]);
  /* Pending entries for the focused CE/PE legs, rendered as lines on their own
     panes (entry / SL / Target) so a leg order is visible after it's created. */
  const [cePendingRows, setCePendingRows] = useState<PendingRow[]>([]);
  const [pePendingRows, setPePendingRows] = useState<PendingRow[]>([]);
  // Always-fresh mirror so the (memoised) level-drag handler can read the
  // current pending rows without a stale closure.
  const pendingRowsRef = useRef<PendingRow[]>([]);
  useEffect(() => { pendingRowsRef.current = pendingRows; }, [pendingRows]);
  // Bump to force an immediate authoritative re-fetch of pending entries (used
  // to roll back an optimistic SL/Target/entry drag the backend rejected).
  const [pendingBump, setPendingBump] = useState(0);
  /* Subset of the active instrument's fields needed by the chart-side BUY/SHORT
     auto-quantity formula. Mirrors the backend `calculateOrderQuantity` inputs. */
  type InstrumentDetail = {
    lotSize: number;
    leverage: number;
    eqInvestmentAmount: number;
    futInvestmentAmount: number;
    /** OPTIONS budget — split by side so risk-defined long-premium plays and
     *  margin-heavy short-premium plays can be sized independently. */
    optInvestmentAmountBuy: number;
    optInvestmentAmountSell: number;
    tradeSegment: string | null;
    tradeMode: string | null;
    instrumentType: string | null;
  };
  const [instrumentDetail, setInstrumentDetail] = useState<InstrumentDetail | null>(null);
  /* Wrap setters so the two click-modes are mutually exclusive — only one
     intent active at any time (avoids "did my click set NBUY or fire BUY?"). */
  /* Bar Replay refuses the ARM, not the later chart click. Blocking at the
     chart click meant the chip lit up as armed, the user clicked the chart, and
     nothing whatsoever happened. Refusing here answers the moment they press
     the chip, which is when they can still do something about it. HVN is
     allowed through: it is a pure annotation, and marking a chart up is the
     point of replay. Rect zones never route through here at all. */
  const refuseDuringReplay = useCallback(() => {
    if (!replayActiveRef.current) return false;
    toast.error('Not available during Bar Replay — these arm or modify a real order.\nHVN lines and Rect zones still work; press R to leave replay.',
      { id: 'replay-blocked', duration: 5000 });
    return true;
  }, []);
  const setTriggerModeExclusive = useCallback((next: TriggerType | null) => {
    if (next != null && refuseDuringReplay()) return;
    setTriggerMode(next);
    if (next != null) setChartTool(null);
  }, [refuseDuringReplay]);
  const setChartToolExclusive = useCallback((next: ChartTool | null) => {
    if (next != null && next !== 'hvn' && refuseDuringReplay()) return;
    setChartTool(next);
    if (next != null) setTriggerMode(null);
  }, [refuseDuringReplay]);
  const [instrumentId, setInstrumentId] = useState<number | null>(null);
  // Option strategies attached to this symbol's NB/NS/BB/BS levels + the levels' cross-arming
  // state + the global Auto-Execute switch (one call; see levelOptionTrigger.controller.state).
  const [lvlOpt, setLvlOpt] = useState<LevelOptionState | null>(null);
  const [lvlOptDlg, setLvlOptDlg] = useState<'nbuy' | 'nshort' | 'bbuy' | 'bshort' | null>(null);
  const [spotDecimals, setSpotDecimals] = useState(2);
  const [spotTickSize, setSpotTickSize] = useState<number | undefined>(undefined);

  /* per-option instrument state (quad mode CE/PE) */
  const [ceInstrumentId, setCeInstrumentId] = useState<number | null>(null);
  const [peInstrumentId, setPeInstrumentId] = useState<number | null>(null);
  const [ceDecimals, setCeDecimals] = useState(2);
  const [peDecimals, setPeDecimals] = useState(2);
  const [ceTriggerLevels, setCeTriggerLevels] = useState<TriggerLevels>({});
  const [peTriggerLevels, setPeTriggerLevels] = useState<TriggerLevels>({});

  /* watchlist state */
  const [wlLong, setWlLong] = useState(false);
  const [wlShort, setWlShort] = useState(false);
  const [isFav, setIsFav] = useState(false);
  // Set of favourited symbols (uppercased) for the ★ markers in the symbol list.
  const [favSet, setFavSet] = useState<Set<string>>(new Set());

  /* Symbol-pane width — user-resizable (drag grip on the pane's right edge),
     clamped 160..480px, persisted per device. */
  const [symPanelW, setSymPanelW] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('chart_symPanelW_v1'));
      return Number.isFinite(v) && v >= 160 && v <= 480 ? v : SYMBOL_PANEL_W;
    } catch { return SYMBOL_PANEL_W; }
  });
  const startSymPanelDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = symPanelW;
    let lastW = startW;
    const move = (ev: MouseEvent) => {
      lastW = Math.max(160, Math.min(480, startW + (ev.clientX - startX)));
      setSymPanelW(lastW);
    };
    const up = () => {
      try { localStorage.setItem('chart_symPanelW_v1', String(lastW)); } catch { /* ignore */ }
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [symPanelW]);

  /* active OPEN trade for the current spot symbol — drives draggable SL/TSL/Target lines
   * and the live P&L pill on the LTP label. */
  type ActiveTrade = {
    id: number; symbol: string; side: 'LONG' | 'SHORT';
    /** OPEN once entry has filled, DRAFT while a LIMIT entry is still resting
     *  on the broker. The chart shows SL/Target lines and allows dragging in
     *  both states so the user can adjust the levels before fill. */
    status: 'OPEN' | 'DRAFT';
    stopLoss?: number | null; manualStopLoss?: number | null;
    target?: number | null; currentTrailingSl?: number | null;
    entryPrice?: number | null; quantity?: number | null;
    unrealizedPnl?: number | null;
    pnlCurrency?: 'INR' | 'USD';
    lotSize?: number | null;
    /** PAPER chart trade (Trade.isSimulation) — simulated, no broker position. */
    isSimulation?: boolean;
  };
  const [activeTrade, setActiveTrade] = useState<ActiveTrade | null>(null);
  const [activeTradeBump, setActiveTradeBump] = useState(0);
  // Always-fresh mirror of activeTrade. The chart drag handler is memoized with a
  // narrow dep list (so the chart's drag listeners don't re-bind every 15s poll),
  // which means it would otherwise capture a STALE activeTrade (null at mount) and
  // silently skip the SL/Target PUT — the actual cause of "drag reverts, no PUT".
  const activeTradeRef = useRef<ActiveTrade | null>(null);
  useEffect(() => { activeTradeRef.current = activeTrade; }, [activeTrade]);

  /* strategy overlay — STICKY (Round 105). Like the overlay/interval/layout
   * "last used" state, the applied strategy persists in localStorage so it
   * survives symbol changes (and any chart remount). Previously plain useState,
   * so switching symbol — which restores every other chart setting from its
   * sticky source — silently dropped the strategy back to none. A ?strategy=ID
   * URL or the toggle still overrides it. */
  const [activeStrategy, setActiveStrategyRaw] = useState<string>(() => {
    try { return readLastUsed()?.activeStrategy ?? (localStorage.getItem('chart_activeStrategy') || ''); } catch { return ''; }
  });
  const setActiveStrategy = useCallback((u: string | ((p: string) => string)) => {
    setActiveStrategyRaw(prev => {
      const next = typeof u === 'function' ? (u as any)(prev) : u;
      try { localStorage.setItem('chart_activeStrategy', next || ''); } catch {}
      return next;
    });
  }, []);

  /* Scanner signal markers — when chart is opened from a strategy signal
     (?strategy=ID), we fetch persisted StrategySignal rows for that
     (strategyId, symbol, intervalMinutes) so the chart shows EXACTLY the
     same markers the scanner emitted. Independent of `activeStrategy`. */
  const [backendStrategyId, setBackendStrategyId] = useState<number | null>(null);
  const [scannerMarkers, setScannerMarkers] = useState<Array<{ time: number; action: string }>>([]);

  /* candle type — applies across all panes, persists across sessions */
  const [candleType, setCandleType] = useState<CandleType>(() => {
    const valid = (v: any): v is CandleType => v === 'HA' || v === 'CSH' || v === 'CSL' || v === 'CLH' || v === 'CLN' || v === 'Regular';
    const fromSnapshot = readLastUsed()?.candleType;
    if (valid(fromSnapshot)) return fromSnapshot;
    const s = localStorage.getItem('chart_candleType');
    // Default candle type is CLN (see DEFAULT_OVERLAYS above). This second
    // fallback is why the reset marker has to clear `chart_candleType` too —
    // otherwise a stale per-device value would outlive the snapshot reset.
    return valid(s) ? s : 'CLN';
  });
  useEffect(() => { localStorage.setItem('chart_candleType', candleType); }, [candleType]);

  /* Candle wick visibility — quick toggle on the Charts toolbar (moved here from
   * Chart Settings for frequent analysis use). Persisted per device; applied to
   * every pane via the showWick prop (TradingChart toggles it without rebuilding). */
  const [showWick, setShowWick] = useState<boolean>(() => {
    try { return localStorage.getItem('chart_showWick') !== 'false'; } catch { return true; }
  });
  useEffect(() => { try { localStorage.setItem('chart_showWick', String(showWick)); } catch { /* ignore */ } }, [showWick]);

  /* Hide Candles — replaces the old 'HC' pseudo candle-type. Paints only the
   * indicators/signals; the candle series is hidden but its price scale stays
   * active. Quick toggle next to Wick; persisted per device. */
  const [hideCandles, setHideCandles] = useState<boolean>(() => {
    try { return localStorage.getItem('chart_hideCandles') === 'true'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('chart_hideCandles', String(hideCandles)); } catch { /* ignore */ } }, [hideCandles]);

  // Consolidated "last used" snapshot — persists ALL of overlays / intervals /
  // layout / candleType / strategy together so opening another symbol or
  // restarting the app restores the exact same chart setup. (Single writer, so
  // the partial-object effects can't clobber each other.)
  useEffect(() => {
    try {
      localStorage.setItem(LAST_USED_KEY, JSON.stringify({ overlays, intervals, layoutMode, candleType, activeStrategy, optInterval: optIntervalPref }));
    } catch { /* ignore */ }
  }, [overlays, intervals, layoutMode, candleType, activeStrategy, optIntervalPref]);

  /* Cross-pane crosshair sync — both time (vertical) and price (horizontal), so the panes
     of a split/quad mirror the exact level under the cursor.

     This used to be React state here (`syncCrosshair`), fed by an `onCrosshairMove` prop on
     every pane. That made a POINTER MOVE a state update on this component — measured at
     83–100ms per frame while dragging in split, because the re-render also rebuilt every
     chart series. It is now a module-level pub/sub the panes talk to directly
     (stores/crosshairSyncStore); this page is not involved in a mouse move at all.

     The group key is the layout, so switching layout cannot leave a pane mirroring a chart
     that is no longer on screen. `single` opts out entirely — one pane has nothing to
     mirror, and a pane with no group never touches its own crosshair. */
  const crosshairGroup = layoutMode === 'single' ? undefined : 'charts-' + layoutMode;

  /* The timeframe the quad option panes actually use. Default: follow the spot pane but
     never above 15m, because Delta option series are thin — a contract listed three days
     ago has ~9 bars at 8H and ~288 at 15m. An explicit pick in the "Opt TF" toolbar control
     overrides it, and the chosen value is shown in every option pane's title, so the spot
     pane's interval is never silently applied to half the screen either way. */
  const OPT_INTERVAL_CAP = 15;
  const optInterval = optIntervalPref ?? Math.min(intervals[0] || OPT_INTERVAL_CAP, OPT_INTERVAL_CAP);

  // Convert scanner signals → chart markers (always shown when present,
  // regardless of activeStrategy — they represent the backend's own signals).
  const scannerMarkersForChart = useMemo(() => {
    if (!scannerMarkers.length) return [];
    return scannerMarkers.map(m => {
      const isBuyLike = m.action === 'BUY' || m.action === 'COVER_FLAT';
      return {
        time: m.time,
        position: (isBuyLike ? 'belowBar' : 'aboveBar') as 'aboveBar' | 'belowBar',
        shape: (isBuyLike ? 'arrowUp' : 'arrowDown') as 'arrowUp' | 'arrowDown',
        color: isBuyLike ? '#2e7d32' : '#c62828',
        text: m.action
      };
    });
  }, [scannerMarkers]);

  // R84 — Strategies that need multi-TF data (Sniper) receive the same CTF +
  // HTF aux candles + LTF minutes that the chart's AvgCloud overlay uses.
  // Single-TF strategies (DailyBlast) ignore the opts argument.
  // OM_Simple reads TWO lengths and they are not the same thing: the one it
  // EVALUATES (settings.omSimple.clnLength, default 50) and the one the chart
  // DRAWS (settings.candle.clnLength, default 14). Both are passed so the
  // overlay can warn when they differ, rather than putting markers on colour
  // changes the user cannot see.
  // The W / C marks read their switches and thresholds from HERE. The adapter is
  // pure — it never touches the settings store — so a control that is not passed
  // through this object does nothing at all. All four were missing, which left
  // Chart Settings drawing both marks at the shipped thresholds whatever the
  // dialog said, and made "switch C off" reach the chart only as a starved HTF
  // fetch reported as "no higher-timeframe history".
  const omSimpleOpts = useMemo(() => ({
    clnLength: Number((settings as any)?.omSimple?.clnLength) || OM_SIMPLE_DEFAULT_CLN_LENGTH,
    chartClnLength: Number(settings.candle?.clnLength) || 14,
    omSimpleSuppressThinVolume: (settings as any)?.omSimple?.suppressThinVolume === true,
    omSimpleShowWatch: (settings as any)?.omSimple?.showWatch !== false,
    omSimpleShowContinuation: (settings as any)?.omSimple?.showContinuation !== false,
    // Passed raw: a missing or unparseable value arrives as NaN, which the
    // adapter reads as "not set" and answers with the calibrated default.
    omSimpleWatchThreshold: Number((settings as any)?.omSimple?.watchThreshold),
    omSimpleContinuationThreshold: Number((settings as any)?.omSimple?.continuationThreshold),
  }), [settings]);

  // Vajra reads every one of its controls from HERE. The adapter is pure and
  // never touches the settings store, so a field missing from this object is a
  // control that silently does nothing — which is exactly how OM_Simple's four
  // W/C switches shipped inert. `check-strategy-opts-wiring.mjs` fails the build
  // if a ChartStrategyOptions field has no producer in this file.
  const vajraOpts = useMemo(() => ({
    vajraBbOs: Number((settings as any)?.vajra?.bbOs),
    vajraWvOs: Number((settings as any)?.vajra?.wvOs),
    vajraRequireTurn: (settings as any)?.vajra?.requireTurn !== false,
    vajraMinHtfRun: Number((settings as any)?.vajra?.minHtfRun),
    vajraExitLevel: Number((settings as any)?.vajra?.exitLevel),
    vajraStopAtr: Number((settings as any)?.vajra?.stopAtr),
    vajraMaxHold: Number((settings as any)?.vajra?.maxHold),
    vajraRiskPct: Number((settings as any)?.vajra?.riskPct),
  }), [settings]);

  // CLN_Break reads every one of its controls from HERE, for the same reason
  // Vajra does: the adapter is pure and a field missing from this object is a
  // control that silently does nothing. Its regression length is
  // `chartClnLength`, supplied by omSimpleOpts above: CLN_Break has NO length of
  // its own, so its levels are always built from the bodies the chart draws.
  const clnBreakOpts = useMemo(() => ({
    clnBreakLookback: Number((settings as any)?.clnBreak?.lookback),
    clnBreakBufferAtr: Number((settings as any)?.clnBreak?.bufferAtr),
    clnBreakBreach: ((settings as any)?.clnBreak?.breach === 'close' ? 'close' : 'touch') as 'touch' | 'close',
    clnBreakRequireLrAgree: (settings as any)?.clnBreak?.requireLrAgree === true,
    clnBreakStopAtr: Number((settings as any)?.clnBreak?.stopAtr),
  }), [settings]);

  // Gandiv's only rule input from settings is its source candle; the band's
  // regression length is `chartClnLength` (omSimpleOpts), the CHART's own, so
  // the band it trades is the one built from the length on screen. Line colours
  // and switches are applied by TradingChart, which reads settings.gandiv itself.
  const gandivOpts = useMemo(() => ({
    gandivSource: resolveGandivSource((settings as any)?.gandiv?.source),
  }), [settings]);

  // LR_Cross: its source candle comes from Chart Settings → LR_Cross (the scan has
  // its own setting on the strategy). Its LR length is `chartClnLength`
  // (omSimpleOpts), the chart's own. The chart's DRAWN candle type and whether the
  // LR body is on are passed so the overlay can say when the crossing is not on
  // screen — `candleType` given to strategies can be a Bhairav override, so the
  // real one travels separately.
  const lrCrossOpts = useMemo(() => ({
    lrCrossSource: resolveLrCrossSource((settings as any)?.lrCross?.source),
    lrCrossChartCandleType: candleType as string,
    lrCrossLrVisible: candleType === 'CLN' || overlays.lrBody === true,
  }), [settings, candleType, overlays.lrBody]);

  const strategyOverlay0 = useMemo<StrategyOverlayResult | undefined>(() => {
    const strat = activeStrategy ? getStrategy(activeStrategy) : undefined;
    // Bhairav candle source: Chart Settings → Bhairav can pin HA/CLH; FOLLOW uses
    // the chart's selected candle type.
    const bhCt = (settings as any)?.bhairav?.candleType;
    const stratCandleType = (bhCt === 'HA' || bhCt === 'CLH') ? bhCt : candleType;
    const base = (strat && paneData[0]?.length)
      ? strat.computeOverlays(paneData[0], {
          ctfCandles: paneCtf[0],
          htfCandles: paneHtf[0],
          // The Strategies-mapped HTF (resolveHtfInterval), NOT the ctfx15 ladder
          // above. OM_Simple's continuation regime reads the same series the CLN
          // context band does; `paneHtf` is empty unless RockBand or the Combined
          // overlays happen to be on, which is not a basis for a regime gate.
          mappedHtfCandles: paneSrHtf[0],
          mappedHtfMinutes: paneSrHtfMin[0],
          ltfMinutes: intervals[0],
          candleType: stratCandleType,
          dailyCandles,
          ...omSimpleOpts,
          ...vajraOpts,
          ...clnBreakOpts,
          ...gandivOpts,
          ...lrCrossOpts,
        })
      : undefined;
    if (!scannerMarkersForChart.length) return base;
    return {
      ...(base || {}),
      markers: [...(base?.markers || []), ...scannerMarkersForChart]
    };
  }, [activeStrategy, paneData, scannerMarkersForChart, paneCtf, paneHtf, paneSrHtf, paneSrHtfMin, intervals, candleType, settings, dailyCandles, omSimpleOpts, vajraOpts, clnBreakOpts, gandivOpts, lrCrossOpts]);

  const strategyOverlay1 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (!activeStrategy || !paneData[1]?.length) return undefined;
    const strat = getStrategy(activeStrategy);
    if (!strat) return undefined;
    const bhCt = (settings as any)?.bhairav?.candleType;
    return strat.computeOverlays(paneData[1], {
      ctfCandles: paneCtf[1],
      htfCandles: paneHtf[1],
      mappedHtfCandles: paneSrHtf[1],
      mappedHtfMinutes: paneSrHtfMin[1],
      ltfMinutes: intervals[1],
      candleType: (bhCt === 'HA' || bhCt === 'CLH') ? bhCt : candleType,
      dailyCandles,
      ...omSimpleOpts,
      ...vajraOpts,
      ...clnBreakOpts,
      ...gandivOpts,
      ...lrCrossOpts,
    });
  }, [activeStrategy, paneData, paneCtf, paneHtf, paneSrHtf, paneSrHtfMin, intervals, candleType, settings, dailyCandles, omSimpleOpts, vajraOpts, clnBreakOpts, gandivOpts, lrCrossOpts]);

  /* CE/PE/Combined strategy overlays are computed further down as
     effectiveOverlayCe / effectiveOverlayPe / effectiveOverlayCombined, after
     the multi-TF (Sniper / RockBand) inputs are available. */

  /* Display candles — transformed per candleType. Regular is a no-op passthrough.
     For stateful transforms (Heikin-Ashi, smoothed HA/LinReg) the forming/live bar
     must participate in the same transform so the rightmost bar visually matches
     the rest of the series.

     Reference stability is critical: TradingChart's setData effect re-runs
     whenever its `candles` prop reference changes — and re-running setData on
     every live tick clears the chart and visibly flickers. Therefore:
       - displayPane0/1Closed depend ONLY on paneData + candleType — stable
         between bar-close events (typically minutes).
       - displayPane0/1Live recompute every tick (as latestCandles changes), but
         feed only TradingChart's incremental update() path, which doesn't redraw.
     paneData stores closed bars only; latestCandles stores the forming bar.
     Initial-fetch and live-poll code below maintain this separation. */
  const transformLiveWith = (closed: CandleRow[], live: CandleRow | undefined, ct: CandleType): CandleRow | undefined => {
    if (!live) return undefined;
    if (ct === 'Regular') return live;
    const full = closed.length && closed[closed.length - 1].time >= live.time
      ? [...closed.slice(0, -1), live]
      : [...closed, live];
    const transformed = transformCandles(full, ct);
    return transformed[transformed.length - 1];
  };
  // For Delta: overlay the latest mark_price as the live candle's close so the
  // chart visibly ticks at the user-configured live cadence even on low-volume
  // pairs where /v2/history/candles returns an unchanged close between trades.
  // mark_price is recomputed continuously by Delta and arrives over the
  // dedicated mark-price poll above. We also widen the live bar's high/low so
  // the candle wick reflects intra-bucket extremes implied by the mark price.
  const liveWithMark = useMemo(() => {
    const apply = (live?: CandleRow) => {
      // Merge the live mark into the forming candle for Delta AND synthetic charts
      // (a synthetic's "mark" is its combined premium V, polled below).
      if (!live || (exchange !== 'DELTA' && exchange !== 'SYNTH') || markPrice == null || !Number.isFinite(markPrice)) return live;
      const mp = Number(markPrice);
      return {
        ...live,
        close: mp,
        high: Math.max(live.high, mp),
        low:  Math.min(live.low,  mp),
      } as CandleRow;
    };
    return [apply(latestCandles[0]), apply(latestCandles[1])] as (CandleRow | undefined)[];
  }, [latestCandles, exchange, markPrice]);
  /* ── "Important fraction" candles (utils/candleFraction.ts) ──
     Display only: every candle type is built from the LAST 1/N of each bar. The rows
     come from POST /charts/fraction-bars (the effect next to the mapped-HTF fetch),
     are joined on TIME, and are applied BEFORE the candle-type transform. With the
     denominator at 1 `applyFraction` hands back the very same arrays, so the chart
     is exactly what it was. Spot panes only; option panes stay normal. */
  const fracDen = normaliseDenominator(settings.candleFraction?.denominator);
  const fracPlans = useMemo(() => [0, 1].map(i => planFraction(exchange, intervals[i], fracDen)), [exchange, intervals, fracDen]);
  const fracKey = useCallback((i: number) => [symbol, exchange, intervals[i], fracDen].join('|'), [symbol, exchange, intervals, fracDen]);
  const [paneFraction, setPaneFraction] = useState<PaneFraction[]>(EMPTY_PANE_FRACTION);
  const fracActive = useCallback((i: number) => {
    const plan = fracPlans[i];
    return plan.den > 1 && !plan.unavailable && paneFraction[i].loaded && paneFraction[i].key === fracKey(i);
  }, [fracPlans, paneFraction, fracKey]);
  const fracClosed = useMemo(() => [0, 1].map(i => (fracActive(i)
    ? applyFraction(paneData[i] || [], paneFraction[i].rows, fracPlans[i].den)
    : { bars: paneData[i] || [], missing: new Set<number>() })), [paneData, paneFraction, fracPlans, fracActive]);
  const fracLive = useMemo(() => [0, 1].map(i => {
    const live = liveWithMark[i];
    if (!live || !fracActive(i)) return live;
    const ws = formingWindowStart(live.time as number, intervals[i], exchange, fracPlans[i]);
    return applyFractionLive(live, paneFraction[i].forming ?? undefined, ws, Math.floor(Date.now() / 1000) + 19800);
  }), [liveWithMark, paneFraction, fracPlans, fracActive, intervals, exchange]);
  // What TradingChart needs (legend marker, CLN source, faded bars). Undefined at 1.
  const fractionView = useMemo<(FractionView | undefined)[]>(() => [0, 1].map(i => {
    const plan = fracPlans[i];
    if (plan.den <= 1) return undefined;
    const label = fractionLabel(plan.den);
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = (t: number) => { const d = new Date(t * 1000); return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`; };
    if (plan.unavailable) {
      return { label, bars: paneData[i] || [], missing: new Set<number>(), notes: [`normal candles here: ${plan.unavailable}`], inactive: true };
    }
    const notes: string[] = [];
    if (!fracActive(i)) notes.push('loading finer bars…');
    if (!plan.exact) notes.push(`window rounded to whole ${plan.subMin}-min steps`);
    const { bars, missing } = fracClosed[i];
    if (fracActive(i) && missing.size) {
      // Two different reasons a bar stays normal: history older than the finer data
      // (a run at the start), or a bar whose window saw no trade (thin data, anywhere).
      let first = -1;
      for (let k = 0; k < bars.length; k++) if (!missing.has(bars[k].time as number)) { first = k; break; }
      if (first < 0) notes.push('no finer data for these bars: shown normal (faded)');
      else {
        if (first > 0) notes.push(`fraction candles from ${day(bars[first].time as number)}; older bars normal (faded)`);
        const thin = missing.size - first;
        if (thin > 0) notes.push(`${thin} bar${thin === 1 ? '' : 's'} with no trade in the window: normal (faded)`);
      }
    }
    return { label, bars, missing, notes };
  }), [fracPlans, fracClosed, fracActive, paneData]);
  const displayPane0ClosedFull = useMemo(() => transformCandles(fracClosed[0].bars, candleType), [fracClosed, candleType]);
  const displayPane1ClosedFull = useMemo(() => transformCandles(fracClosed[1].bars, candleType), [fracClosed, candleType]);
  const displayPane0Live = useMemo(() => transformLiveWith(fracClosed[0].bars, fracLive[0], candleType), [fracClosed, fracLive, candleType]);
  const displayPane1Live = useMemo(() => transformLiveWith(fracClosed[1].bars, fracLive[1], candleType), [fracClosed, fracLive, candleType]);
  // SL Risk + Target Gain pills for the running trade (rendered along the
  // SL and Target lines on the chart). Same INR formula the Dashboard's
  // Trades Management section uses, so the numbers match exactly:
  //   risk  = (entry − SL)    × qty × lotSize × usdToInr      (LONG)
  //   risk  = (SL − entry)    × qty × lotSize × usdToInr      (SHORT)
  //   gain  = (target − entry)× qty × lotSize × usdToInr      (LONG)
  //   gain  = (entry − target)× qty × lotSize × usdToInr      (SHORT)
  // Negative SL Risk = TSL has moved beyond entry (profit locked in) — we
  // surface that with a 🔒 emoji and bullish color, matching the Dashboard.
  const tradeAmountPills = useMemo(() => {
    if (!activeTrade || !activeTrade.entryPrice || !activeTrade.quantity) return [];
    const isDelta = (exchange || '').toUpperCase() === 'DELTA';
    const usdToInr = isDelta ? USD_TO_INR : 1;
    const qty = Number(activeTrade.quantity) || 0;
    const lot = Number(activeTrade.lotSize) || 1;
    const entry = Number(activeTrade.entryPrice) || 0;
    const target = activeTrade.target;
    const isLong = activeTrade.side === 'LONG';
    // Operative SL mirrors the BACKEND breach (and the SL line on the chart):
    // trailing SL when active, else the user's manual SL (full-manual-control
    // turns trailing OFF and makes the manual SL authoritative — so a SHORT's
    // manual SL above the initial SL DOES become the effective breach), else the
    // initial SL. (Previously this used the most-protective min/max, which made
    // the SL-risk pill disagree with the actual breach + the Dashboard.)
    const operativeSL: number | null =
      (activeTrade.currentTrailingSl != null && Number.isFinite(activeTrade.currentTrailingSl)) ? activeTrade.currentTrailingSl
      : (activeTrade.manualStopLoss != null && Number.isFinite(activeTrade.manualStopLoss)) ? activeTrade.manualStopLoss
      : (activeTrade.stopLoss != null && Number.isFinite(activeTrade.stopLoss)) ? activeTrade.stopLoss
      : null;
    const out: Array<{ type: 'SL' | 'TARGET'; price: number; amountInr: number; profitLocked: boolean }> = [];
    if (operativeSL != null && entry > 0 && qty > 0) {
      const risk = (isLong ? (entry - operativeSL) : (operativeSL - entry)) * qty * lot * usdToInr;
      out.push({ type: 'SL', price: operativeSL, amountInr: risk, profitLocked: risk <= 0 });
    }
    if (target != null && entry > 0 && qty > 0) {
      const gain = (isLong ? (target - entry) : (entry - target)) * qty * lot * usdToInr;
      out.push({ type: 'TARGET', price: target, amountInr: gain, profitLocked: false });
    }
    return out;
  }, [activeTrade, exchange]);

  // Close-active-trade flow used by the LTP pill's ✕ button. Action depends
  // on trade status:
  //   DRAFT → POST /trades/:id/cancel-draft (cancels the resting LIMIT entry
  //           on the broker — there is no position to square off).
  //   OPEN  → POST /trades/:id/close (MARKET square-off).
  // Bumps activeTradeBump so the trade fetch refreshes, propagating the new
  // status to the Trades Management section on the Dashboard.
  const closeActiveTrade = useCallback(async () => {
    if (!activeTrade?.id) return;
    const isDraft = activeTrade.status === 'DRAFT';
    const ok = window.confirm(
      activeTrade.isSimulation
        ? `Close PAPER ${activeTrade.symbol} ${activeTrade.side} (qty ${activeTrade.quantity ?? '?'})?

` +
          `Simulated — it closes at the live price. No broker order is placed.`
      : isDraft
        ? `Cancel pending ${activeTrade.symbol} ${activeTrade.side} LIMIT entry (qty ${activeTrade.quantity ?? '?'})?\n\n` +
          `This will cancel the resting order on the broker.`
        : `Close ${activeTrade.symbol} ${activeTrade.side} (qty ${activeTrade.quantity ?? '?'})?\n\n` +
          `This will place a MARKET square-off order on the broker.`
    );
    if (!ok) return;
    try {
      if (isDraft) {
        await api.post(`/trades/${activeTrade.id}/cancel-draft`);
        toast.success(`Cancelling ${activeTrade.symbol} pending entry…`);
      } else {
        await tradingService.closeTrade(activeTrade.id, { closeMethod: 'manual' });
        toast.success(`Closing ${activeTrade.symbol}…`);
      }
      setActiveTrade(null);
      setActiveTradeBump(b => b + 1);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || (isDraft ? 'Failed to cancel pending entry' : 'Failed to close trade'));
    }
  }, [activeTrade]);

  // ACTUAL LTP — never transformed by candle type. For Delta we prefer the
  // continuously-updated mark_price; otherwise fall back to whichever pane has
  // the freshest live close (split layout uses one source for both panes so
  // they always plot the LTP at the same level / show the same number).
  const actualLtp: number | null = useMemo(() => {
    if (exchange === 'DELTA' && markPrice != null && Number.isFinite(markPrice)) return Number(markPrice);
    const c0 = latestCandles[0]?.close;
    const c1 = latestCandles[1]?.close;
    if (typeof c0 === 'number' && Number.isFinite(c0)) return c0;
    if (typeof c1 === 'number' && Number.isFinite(c1)) return c1;
    return null;
  }, [exchange, markPrice, latestCandles]);
  const displayPane0Full = displayPane0ClosedFull;
  const displayPane1Full = displayPane1ClosedFull;
  const displayCeFull = useMemo(() => transformCandles(ceCandles, candleType), [ceCandles, candleType]);
  const displayPeFull = useMemo(() => transformCandles(peCandles, candleType), [peCandles, candleType]);
  // Option panes stay normal under an "important fraction" (owner, 2026-09-30): they
  // are built from several legs. The legend still says the fraction is set, and why
  // these candles are not following it.
  const optFractionView = useMemo<(FractionView | undefined)[]>(() => (fracDen > 1
    ? [ceCandles, peCandles].map(b => ({ label: fractionLabel(fracDen), bars: b, missing: new Set<number>(), notes: ['option panes are drawn normal'], inactive: true }))
    : [undefined, undefined]), [fracDen, ceCandles, peCandles]);
  const displayCombinedFull = useMemo(() => transformCandles(combinedCandles, candleType), [combinedCandles, candleType]);

  /* Per-layout cap on displayed candles (settings-driven). Keeps each chart
   * readable regardless of total loaded history. Overlays/strategy use the
   * full arrays; only the *view* is trimmed.
   *
   * paneExtraBars[i] tracks how many bars were prepended via lazy-load on pane i;
   * the cap is grown by that amount so prepended bars become visible (otherwise
   * capTail would slice them off). Resets on symbol/interval change. */
  const [paneExtraBars, setPaneExtraBars] = useState<number[]>([0, 0]);
  // Per-layout, PER-PANE "remembered zoom": each pane OPENS at the candle count the user
  // last scroll-zoomed IT to (persisted in localStorage), instead of a fixed setting.
  // Recomputed on every fresh-open trigger (layout / symbol / interval) so the
  // latest remembered value is read. Zooming writes localStorage WITHOUT a
  // re-render (see handleVisibleBarsChange), so it only takes effect on the NEXT
  // open — no mid-session chart churn.
  //
  // It used to be ONE number shared by every pane in a layout, and only pane 0 reported its
  // zoom back. In an 8H/15m split that meant the 15m pane could never keep a wider view:
  // its own zoom was never recorded, and any re-fit re-applied whatever the 8H pane had
  // written. Every pane now reads and writes its own slot.
  const candleCaps = useMemo(
    () => readVisibleBars()[layoutMode as LayoutMode3],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutMode, symbol, exchange, intervals],
  );
  /** Opening candle count for one pane. Pane 0's value is the fallback for anything
   *  outside the layout's pane count, so a caller can never read undefined. */
  const capOf = useCallback((pane: number) => candleCaps[pane] ?? candleCaps[0], [candleCaps]);
  // History sizing (computeFromSec / lazy-load) uses the WIDEST pane's cap, not pane 0's.
  // Panes remember their zoom separately now, so a user who zooms the 15m pane out to 800
  // bars must get 800 bars of history fetched for it; sizing off pane 0 would leave that
  // pane short and its long-warmup overlays blank. Over-fetching is harmless here.
  const candleCap = Math.max(...candleCaps);
  const handleVisibleBarsChange = useCallback((pane: number, bars: number) => {
    writeVisibleBars(layoutMode as LayoutMode3, pane, bars);
  }, [layoutMode]);
  /* One STABLE callback per pane. TradingChart keys its remember-zoom subscription on this
     prop, so an inline `bars => handleVisibleBarsChange(i, bars)` would give it a fresh
     identity every render and make it unsubscribe/resubscribe from the time scale on every
     render — the exact class of churn this batch is removing. */
  const visBarsCbs = useMemo(
    () => [0, 1, 2, 3].map(i => (bars: number) => handleVisibleBarsChange(i, bars)),
    [handleVisibleBarsChange],
  );
  // Round 30 (revised) — capForPane always covers the FULL loaded paneData
  // length. Previously capTailFor sliced to the LAST `candleCap` bars on
  // initial load, so on low intervals (1m/3m/5m/10m/15m where ~500 bars get
  // fetched but candleCap is ~200) the front 300 bars were hidden. When the
  // user panned left and triggered onLoadMore, TradingChart passed
  // `candles[0].time` (= the oldest *displayed* bar, not the oldest
  // *loaded* bar) so the backend fetched a range that was already loaded
  // and Set-dedup left addedCount=0. Result: lazy-load appeared broken.
  // Fix: pass the entire loaded paneData to TradingChart; the chart's
  // firstFit `setVisibleLogicalRange` still controls the initial zoom
  // window, and lightweight-charts handles 1k-10k bars without any perf
  // concern. paneExtraBars/capTailFor are kept as a structural safety net
  // but become no-ops in normal cases.
  const capForPane = useCallback((i: number) =>
    Math.max(capOf(i) + (paneExtraBars[i] || 0), (paneData[i]?.length || 0)),
    [capOf, paneExtraBars, paneData]);
  const capTailFor = useCallback(<T,>(arr: T[], cap: number): T[] => (arr.length > cap ? arr.slice(arr.length - cap) : arr), []);

  /** Append the forming candle (if any, with a strictly newer time) to the
   *  capped closed-bar tail. Without this, anything that re-runs the chart's
   *  main setData effect (priceLevels ref change every 15s when activeTrade
   *  refreshes, settings ref change, theme flip, …) would briefly draw the
   *  series with closed bars only and the live candle would visibly blink out
   *  until the next live-poll tick re-added it via series.update(). Including
   *  it in setData makes the bar persistent across re-renders. */
  const withLiveAppended = useCallback((closedTail: CandleRow[], live: CandleRow | undefined): CandleRow[] => {
    if (!live) return closedTail;
    // Round 43 — when there is NO closed history yet (initial-load window
    // before the historical fetch resolves), return [] instead of [live].
    // Previously the live-poll could set a live candle before history
    // arrived, so the chart's FIRST render had exactly one candle. The
    // chart's first-fit then locked its visible-range onto that 1-bar zoom,
    // and the subsequent full 500-bar batch was treated as a "continuation"
    // (the lone candle's time falls within the new span), so the stale
    // 1-bar zoom was restored over the real data — the "opens zoomed in on
    // a few huge candles" bug. With [] the chart simply shows the loading
    // state until real candles arrive, then first-fits cleanly.
    if (closedTail.length === 0) return [];
    const lastT = closedTail[closedTail.length - 1].time;
    if (live.time > lastT) return [...closedTail, live];
    return closedTail; // live shares time with last closed — let series.update replace
  }, []);

  const displayPane0 = useMemo(
    () => withLiveAppended(capTailFor(displayPane0Full, capForPane(0)), displayPane0Live),
    [displayPane0Full, capForPane, capTailFor, displayPane0Live, withLiveAppended],
  );
  const displayPane1 = useMemo(
    () => withLiveAppended(capTailFor(displayPane1Full, capForPane(1)), displayPane1Live),
    [displayPane1Full, capForPane, capTailFor, displayPane1Live, withLiveAppended],
  );
  const displayCe = useMemo(() => capTailFor(displayCeFull, capOf(1)), [displayCeFull, capOf, capTailFor]);
  const displayPe = useMemo(() => capTailFor(displayPeFull, capOf(3)), [displayPeFull, capOf, capTailFor]);
  const displayCombined = useMemo(() => capTailFor(displayCombinedFull, capOf(2)), [displayCombinedFull, capOf, capTailFor]);

  /* prefs */
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [prefsReady, setPrefsReady] = useState(false);
  const skipFirstSave = useRef(true);

  /* how many panes are active for the spot chart? */
  const spotPaneCount = layoutMode === 'single' ? 1 : layoutMode === 'split' ? 2 : 1; // quad: only pane 0 for spot

  /* ───── load default symbols on mount / when filter changes ───── */
  const loadSymbolsByMode = useCallback(async (mode: FilterMode) => {
    /**
     * Deduplicate by symbol+exchange, KEEPING the rest of the row.
     *
     * This used to rebuild each row as `{ symbol, exchange }` and drop everything else,
     * which is precisely why the pane could never show a position's P&L or an armed trigger
     * level: the branches above fetched that data and this threw it away on the way out.
     * Spread the row instead, and only DEFAULT the exchange.
     */
    const dedupe = (rows: Array<Partial<SymbolItem> & { symbol: string }>): SymbolItem[] => {
      const seen = new Set<string>();
      const out: SymbolItem[] = [];
      for (const r of rows) {
        if (!r.symbol) continue;
        const k = `${r.symbol}|${r.exchange || ''}`;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push({ ...r, symbol: r.symbol, exchange: r.exchange || '' });
      }
      return out;
    };
    try {
      if (mode === 'Global') {
        // Static, curated list — charted via Yahoo (delayed/free). No API call.
        setSymResults(GLOBAL_SYMBOLS_FE.map(g => ({ symbol: g.symbol, exchange: 'GLOBAL', name: g.name })) as SymbolItem[]);
      } else if (mode === 'RWA' || mode === 'RWAEq') {
        // One endpoint serves both lists — the tokens and their equivalents are
        // refreshed together, so fetching them together keeps them consistent.
        const r = await api.get<any>('/charts/rwa-symbols').catch(() => null);
        const d: any = r?.data?.data || {};
        setRwaEquivalents(Array.isArray(d.equivalents) ? d.equivalents : []);
        if (mode === 'RWA') {
          setSymResults(dedupe((d.rwa || []).map((x: any) => ({ symbol: x.symbol, exchange: x.exchange || 'DELTA' }))));
        } else {
          // Only VERIFIED mappings are chartable. Unverified ones are still
          // listed (greyed, with the reason on hover) rather than dropped —
          // a missing row reads as "the feature is broken", a greyed row reads
          // as the fact it is.
          setSymResults(dedupe((d.equivalents || [])
            .filter((x: any) => x.symbol)
            .map((x: any) => ({ symbol: x.symbol, exchange: x.exchange || 'GLOBAL' }))));
        }
      } else if (mode === 'Tracked') {
        const r = await api.get<any>('/charts/default-symbols');
        setSymResults((r.data?.data || []) as SymbolItem[]);
      } else if (mode === 'Browsed') {
        const r = await api.get<any>('/charts/default-symbols', { params: { browsed: 'true' } });
        setSymResults((r.data?.data || []) as SymbolItem[]);
      } else if (mode === 'OpenPos') {
        // Symbols with active trades (OPEN or DRAFT — pending LIMIT entries), EXCLUDING
        // option-basket constituent legs — those live under the separate "Baskets" filter
        // and the Option Baskets panel, so they don't clutter the open-positions list.
        const [tr, bk] = await Promise.all([
          api.get<any>('/trades', { params: { status: 'OPEN,DRAFT' } }).catch(() => null),
          optionsService.getBaskets('EXECUTED').catch(() => null),
        ]);
        const tradeItems: any[] = tr?.data?.data?.data || [];
        const rows: any[] = (bk as any)?.data?.data || (bk as any)?.data || [];
        const legSyms = new Set<string>();
        for (const b of rows) {
          if (b.closedAt) continue;
          for (const f of (b.legFills || [])) if (f?.symbol) legSyms.add(String(f.symbol).toUpperCase());
          for (const l of (b.legs || [])) if (l?.symbol) legSyms.add(String(l.symbol).toUpperCase());
        }
        // Keep the position itself. The trade rows were already fetched and then thrown
        // away, so the pane could only show a change% for a symbol the user HOLDS — the
        // one context where the % is the least interesting number on the row.
        setSymResults(dedupe(tradeItems
          .filter((t: any) => !legSyms.has(String(t.symbol || '').toUpperCase()))
          .map((t: any) => ({
            symbol: t.symbol, exchange: t.exchange,
            pnl: Number.isFinite(Number(t.unrealizedPnl)) ? Number(t.unrealizedPnl) : null,
            side: String(t.side || '').toUpperCase() || undefined,
            qty: Number.isFinite(Number(t.quantity)) ? Number(t.quantity) : null,
          }))));
      } else if (mode === 'OpenBaskets') {
        // Underlyings that have a RUNNING option basket (legs C-BTC-… → BTCUSD on DELTA).
        const bk = await optionsService.getBaskets('EXECUTED').catch(() => null);
        const rows: any[] = (bk as any)?.data?.data || (bk as any)?.data || [];
        const basketItems = rows
          .filter((b: any) => !b.closedAt && Array.isArray(b.legFills) && b.legFills.length)
          .map((b: any) => {
            const sym = String(b.legFills[0]?.symbol || '').toUpperCase();
            const m = /^[CP]-([A-Z0-9]+)-/.exec(sym);          // Delta option → base
            if (!m) return null;
            // A basket has no LONG/SHORT -- its direction IS its strategy (Iron Condor,
            // Straddle...), so that is what the side slot carries. Live P&L comes from the
            // in-memory monitor rather than the entity, so accept whichever field the list
            // endpoint attached and render an honest dash when it attached none.
            const live = [b.unrealizedPnl, b.livePnl, b.currentPnl]
              .map((v: any) => Number(v)).find((v: number) => Number.isFinite(v));
            return {
              symbol: `${m[1]}USD`, exchange: 'DELTA',
              pnl: live == null ? null : live,
              side: b.strategy ? String(b.strategy).toUpperCase() : undefined,
              qty: Array.isArray(b.legFills) ? b.legFills.length : null,
            };
          })
          .filter(Boolean) as SymbolItem[];
        setSymResults(dedupe(basketItems));
      } else if (mode === 'PendEntry') {
        // Symbols listed in the Dashboard "Pending Entry Watch" panel —
        // user-defined entry levels still waiting for a trigger
        // (PENDING + TRIGGERED). Default backend filter covers both.
        const r = await api.get<any>('/pending-entries').catch(() => null);
        const items: any[] = r?.data?.data || [];
        // The trigger level is the entire reason a row is on this list, and it was being
        // discarded one line after it arrived.
        setSymResults(dedupe(items.map((p: any) => ({
          symbol: p.symbol, exchange: p.exchange,
          level: Number.isFinite(Number(p.triggerPrice ?? p.entryPrice ?? p.price))
            ? Number(p.triggerPrice ?? p.entryPrice ?? p.price) : null,
          side: String(p.direction || p.side || p.tradeType || '').toUpperCase() || undefined,
          note: p.status ? String(p.status) : undefined,
        }))));
      } else if (mode === 'Alerts') {
        // ONE list, TWO sources, deliberately — and the tooltip says so, because merging
        // two different time windows without saying it would be the same class of quiet
        // dishonesty as a freshness stamp that advances on a timer.
        //
        //   BUY / SHORT   — Dashboard Recent Alerts. Honours the SAME window
        //                   (dashboard_alertPeriod) and the SAME persisted strategy /
        //                   interval / confirmed / symbol filters, so this list and the
        //                   Dashboard panel always agree. That contract is why those two
        //                   localStorage keys are written by the Dashboard at all.
        //   COVER / SELL  — RockBand SIGNAL LOG, fixed 5-day window. Exit signals for a
        //                   symbol with no open trade are SUPPRESSED from Recent Alerts but
        //                   still recorded here, which is the whole reason these two chips
        //                   never shared a source.
        //
        // All four are fetched once and the BUY/SHORT/COVER/SELL toggles filter the result
        // client-side (see sortedSymResults), so flipping a toggle costs no request.
        const period = alertPeriod;
        const f: any = (() => { try { return JSON.parse(localStorage.getItem('dashboard_alertFilters') || '{}') || {}; } catch { return {}; } })();
        const hours = alertPeriodToHours(period);
        const toDate = new Date();
        const fromDate = new Date(toDate.getTime() - hours * 3600 * 1000);

        const wantSym = String(f.symbol || '').toLowerCase();
        const wantStrat = String(f.strategy || '').toLowerCase();
        const wantInterval = f.interval ? tfToMinutes(f.interval) : null;

        const [ar, sres] = await Promise.all([
          api.get<any>('/alerts', { params: { fromDate: fromDate.toISOString(), toDate: toDate.toISOString(), limit: 2000 } }).catch(() => null),
          api.get<any>('/strategies').catch(() => null),
        ]);

        // ---- entries: BUY / SHORT from Recent Alerts ----------------------------------
        const list: any[] = ar?.data?.data?.data || [];
        const latestBySym = new Map<string, any>();
        for (const a of list) {
          const symbol = a.symbol || a.instrument?.symbol || a?.parsedData?.symbol;
          if (!symbol) continue;
          const time = new Date(a.createdAt || a.triggeredAt || 0).getTime();
          const cur = latestBySym.get(symbol);
          if (!cur || time > cur._t) latestBySym.set(symbol, { ...a, _sym: symbol, _t: time });
        }
        const entryRows: SymbolItem[] = Array.from(latestBySym.values())
          .filter((a: any) => {
            const pd = a.parsedData || {};
            const act = String(pd.action || '').toUpperCase();
            // Only entry actions here; exits come from the Signal Log below.
            if (act !== 'BUY' && act !== 'SHORT') return false;
            if (wantSym && !String(a._sym).toLowerCase().includes(wantSym)) return false;
            if (wantInterval != null) { const have = tfToMinutes(pd.timeframe); if (have == null || have !== wantInterval) return false; }
            if (wantStrat) {
              const inS = String(pd.strategy || '').toLowerCase().includes(wantStrat);
              const inL = String(pd.levelType || '').toLowerCase().includes(wantStrat);
              if (!inS && !inL) return false;
            }
            if (f.confirmed && pd.confirmed !== f.confirmed) return false;
            return true;
          })
          .map((a: any) => {
            // Prefer the BAR the signal fired on over the moment the scan wrote the row.
            // Measured 2026-09-09: a 420-minute OM_Simple signal had a candle start of
            // 07:30 IST and a createdAt of 17:31 IST -- 601 minutes apart, because the scan
            // schedule is decoupled from the bar size. The engines now record candleTime;
            // sources that genuinely have no bar (a TradingView webhook) fall back to the
            // record time and the row says so rather than implying a boundary.
            const barMs = Date.parse(String(a.parsedData?.candleTime || ''));
            const hasBar = Number.isFinite(barMs);
            const tfMin = Number(a.parsedData?.timeframe);
            const alertPx = [a.triggerPrice, a.parsedData?.originalPrice, a.currentPrice]
              .map((v: any) => Number(v)).find((v: number) => Number.isFinite(v) && v > 0);
            return {
              symbol: a._sym,
              exchange: a.instrument?.exchange || a.exchange || '',
              action: String(a.parsedData?.action || '').toUpperCase(),
              at: hasBar ? barMs : a._t,
              atIsBar: hasBar,
              firedAt: a._t,
              intervalMinutes: Number.isFinite(tfMin) && tfMin > 0 ? tfMin : undefined,
              alertPrice: alertPx == null ? null : alertPx,
              // `timeframe` is no longer a fallback note -- it has its own column now, and
              // repeating it would spend the narrow pane twice on one fact.
              note: a.parsedData?.strategy || a.parsedData?.levelType || '',
              // Carried so a row can be joined to an AI verdict. It was being dropped here, which
              // is why nothing in this list could show one.
              alertId: a.id,
            };
          });

        // ---- exits: COVER / SELL from the RockBand Signal Log --------------------------
        let exitRows: SymbolItem[] = [];
        const strategies: any[] = sres?.data?.strategies || sres?.data?.data || [];
        const norm = (n?: string) => (n || '').toLowerCase().replace(/[\s_-]/g, '');
        const rb = strategies.find((st: any) => norm(st.name) === 'rockband');
        if (rb) {
          const sr = await api.get<any>(`/strategies/${rb.id}/signals`, {
            params: { days: 5, actions: 'COVER,COVER_FLAT,SELL,SELL_FLAT', limit: 2000 },
          }).catch(() => null);
          const sigs: any[] = sr?.data?.data?.data || sr?.data?.data || [];
          const latestSig = new Map<string, SymbolItem & { at: number }>();
          for (const sg of sigs) {
            const act = String(sg.action || '').toUpperCase();
            if (!act.startsWith('COVER') && !act.startsWith('SELL')) continue;
            if (!sg.symbol) continue;
            const time = new Date(sg.candleTime || sg.createdAt || 0).getTime();
            const cur = latestSig.get(sg.symbol);
            if (cur && cur.at >= time) continue;
            // The Signal Log has ALWAYS carried candleTime -- it is what the dedupe key is
            // built from -- so exits get the bar for free. `createdAt` is the record time.
            const sigBar = Date.parse(String(sg.candleTime || ''));
            const sigHasBar = Number.isFinite(sigBar);
            const sigTf = Number(sg.intervalMinutes);
            const sigPx = [sg.entryPrice, sg.currentPrice]
              .map((v: any) => Number(v)).find((v: number) => Number.isFinite(v) && v > 0);
            latestSig.set(sg.symbol, {
              symbol: sg.symbol, exchange: sg.exchange || '',
              // COVER_FLAT / SELL_FLAT collapse to their family so the toggles stay four.
              action: act.startsWith('COVER') ? 'COVER' : 'SELL',
              at: sigHasBar ? sigBar : time,
              atIsBar: sigHasBar,
              firedAt: Date.parse(String(sg.createdAt || '')) || time,
              intervalMinutes: Number.isFinite(sigTf) && sigTf > 0 ? sigTf : undefined,
              alertPrice: sigPx == null ? null : sigPx,
              note: 'RockBand',
            });
          }
          exitRows = Array.from(latestSig.values());
        }

        // A symbol can legitimately appear on both sides (an entry today, an exit
        // yesterday). Keep the NEWER event — that is the one worth looking at.
        const merged = new Map<string, SymbolItem>();
        for (const r of [...entryRows, ...exitRows]) {
          const k = `${r.symbol}|${r.exchange}`;
          const cur = merged.get(k);
          if (!cur || (r.at || 0) > (cur.at || 0)) merged.set(k, r);
        }
        const rows = Array.from(merged.values()).sort((a, c) => (c.at || 0) - (a.at || 0));
        setSymResults(rows);

        // ── What the AI already thinks, in ONE request for the whole list ──────────
        //
        // BATCH MODE ONLY, and read-only by construction. This reads what is already STORED --
        // the nightly board and any per-signal assessments -- and computes nothing: there is no
        // endpoint here that can trigger a model call, so the alerts list cannot spend money by
        // being looked at. That is the same rule that keeps AiVerdictChip click-to-fetch on its
        // six surfaces, and it matters more here because this list is 100+ rows.
        //
        // Fired after the rows are on screen, never before: the alert list must render at its
        // normal speed whether or not the AI layer is switched on, reachable, or configured.
        // A failure is swallowed entirely -- the rows simply carry no grade.
        try {
          const views = await aiService.getSymbolViews(72);
          if (Object.keys(views).length) {
            setSymResults(prev => prev.map(r => {
              const v = views[String(r.symbol || '').toUpperCase()];
              return v ? { ...r, ai: { grade: v.grade, state: v.state, direction: v.direction, at: v.at, source: v.source } } : r;
            }));
          }
        } catch { /* no AI view is a normal state, not an error worth showing */ }
      } else if (mode === 'LongWL' || mode === 'ShortWL') {
        // Watchlist entries flagged wlLong/wlShort via /dashboard/watchlist.
        const flag = mode === 'LongWL' ? 'wlLong' : 'wlShort';
        const r = await api.get<any>('/dashboard/watchlist').catch(() => null);
        const wl: any[] = r?.data?.data?.watchlist || [];
        setSymResults(dedupe(wl.filter((i: any) => !!i[flag]).map((i: any) => ({
          symbol: i.symbol, exchange: i.exchange,
          action: mode === 'LongWL' ? 'LONG' : 'SHORT',
          at: i.updatedAt ? new Date(i.updatedAt).getTime() : undefined,
          note: i.strategy || i.levelType || undefined,
        }))));
      } else if (mode === 'LongTrig' || mode === 'ShortTrig') {
        // Price-level triggers from /dashboard/watchlist: nbuy/bbuy = long trigger,
        // nshort/bshort = short trigger.
        const r = await api.get<any>('/dashboard/watchlist').catch(() => null);
        const pl: any[] = r?.data?.data?.priceLevels || [];
        const keep = mode === 'LongTrig'
          ? (p: any) => p.nbuy != null || p.bbuy != null
          : (p: any) => p.nshort != null || p.bshort != null;
        // Show the level that is actually armed. nbuy/nshort are the 'normal' levels and
        // bbuy/bshort the 'breakout' ones; when both exist the nearer one is the one the
        // price will reach first, so that is the one worth printing.
        setSymResults(dedupe(pl.filter(keep).map((p: any) => {
          const cands = (mode === 'LongTrig' ? [p.nbuy, p.bbuy] : [p.nshort, p.bshort])
            .map((v: any) => Number(v)).filter((v: number) => Number.isFinite(v));
          return {
            symbol: p.symbol, exchange: p.exchange,
            level: cands.length ? cands[0] : null,
            side: mode === 'LongTrig' ? 'LONG' : 'SHORT',
          };
        })));
      } else if (mode === 'AdScanBuy' || mode === 'AdScanShort') {
        // R114 Stage 4 — symbols from the user's MOST RECENT adhoc strategy
        // scan (any strategy). Replaces older results each new adhoc run.
        const r = await api.get<any>('/strategies/last-adhoc-symbols').catch(() => null);
        const d: any = r?.data?.data || {};
        const list: any[] = mode === 'AdScanBuy' ? (d.buy || []) : (d.short || []);
        setSymResults(dedupe(list.map((x: any) => ({
          symbol: x.symbol, exchange: x.exchange,
          action: mode === 'AdScanBuy' ? 'BUY' : 'SHORT',
          at: x.scannedAt ? new Date(x.scannedAt).getTime() : undefined,
          note: x.strategy || x.strategyName || undefined,
        }))));
      } else if (mode === 'MyFav') {
        // Symbols marked ★ Favourite on the Instruments page (any exchange).
        const r = await api.get<any>('/instruments', { params: { favourites: 'true', pageSize: 1000 } });
        const items: any[] = r.data?.data?.instruments || r.data?.data?.data || r.data?.instruments || r.data?.data || [];
        setSymResults(dedupe((Array.isArray(items) ? items : []).map((i: any) => ({ symbol: i.symbol, exchange: i.exchange || '' }))));
      } else if (mode === 'Movers') {
        // Blast Movers — symbols from the daily Inside/Engulfing scan.
        const r = await api.get<any>('/charts/blast-movers').catch(() => null);
        const movers: any[] = r?.data?.data?.movers || [];
        // `kind` (Inside / Engulfing) and `timeframe` (D / W) are the whole point of this
        // list, and the endpoint has always returned them.
        setSymResults(dedupe(movers.map((m: any) => ({
          symbol: m.symbol, exchange: m.exchange || '',
          action: m.kind ? String(m.kind).toUpperCase() : undefined,
          note: m.timeframe || undefined,
        }))));
      } else if (mode === 'Gainers' || mode === 'Losers') {
        // Tracked symbols whose move TODAY (last price vs the previous completed daily
        // close) is at least `threshold%`. No date parameter — see the note on the state
        // declaration and on the backend handler.
        const type = mode === 'Gainers' ? 'gainers' : 'losers';
        const r = await api.get<any>('/charts/movers', {
          params: { type, threshold: moversThreshold },
        }).catch(() => null);
        const d: any = r?.data?.data || {};
        const movers: any[] = d.movers || [];
        setSymResults(movers.map((m: any) => ({ symbol: m.symbol, exchange: m.exchange || '' })));
        // Say WHY the list is short, using the counts the endpoint now returns.
        const parts: string[] = [];
        if (!r) parts.push('Could not reach the server.');
        else if (!d.universeSize) parts.push('You have no tracked symbols — track some on the Instruments page.');
        else if (!movers.length) parts.push(`None of your ${d.universeSize} tracked symbols moved ≥${moversThreshold}% today.`);
        const src = d.sources || {};
        if (src.zerodha === 'session-expired') parts.push('Zerodha session expired, so NSE/BSE/MCX symbols were not checked — log in to Zerodha.');
        else if (src.zerodha === 'failed') parts.push('Zerodha did not respond, so its symbols are missing.');
        if (src.delta === 'failed') parts.push('Delta did not respond, so crypto symbols are missing.');
        setMoversNote(parts.join(' '));
      } else if (mode === 'MCX') {
        // MCX futures browser — the tradable commodity families (Gold/Silver/Crude/
        // NatGas/base metals, every lot variety), nearest TWO expiries (M0/M1) per
        // name, derived live from the imported dump. No tracking required.
        const r = await api.get<any>('/instruments', { params: { pageSize: 200, mcxFutures: 'true' } });
        const items: any[] = r.data?.data?.instruments || r.data?.instruments || [];
        setSymResults(items.map((i: any) => ({ symbol: i.symbol, exchange: i.exchange || 'MCX' })));
      } else if (mode === 'CDS') {
        // CDS currency-futures browser — USDINR/EURINR/GBPINR/JPYINR, nearest THREE
        // expiries per pair, derived live from the imported dump. No tracking required.
        const r = await api.get<any>('/instruments', { params: { pageSize: 200, cdsFutures: 'true' } });
        const items: any[] = r.data?.data?.instruments || r.data?.instruments || [];
        setSymResults(items.map((i: any) => ({ symbol: i.symbol, exchange: i.exchange || 'CDS' })));
      } else if (mode === 'NSEFNO') {
        // ALL NSE F&O-underlying STOCKS (NSE EQ whose symbol is an NFO underlying).
        // Backend derives the F&O universe LIVE from the NFO table — always current
        // after the weekly import, no user curation. (This used to also require
        // tracked=true, which showed an empty list until stocks were hand-tracked.)
        const r = await api.get<any>('/instruments', { params: { pageSize: 5000, fnoUnderlying: 'true' } });
        const items: any[] = r.data?.data?.instruments || r.data?.instruments || [];
        setSymResults(items.map((i: any) => ({ symbol: i.symbol, exchange: i.exchange || '' })));
      } else {
        // R86 — bumped pageSize from 200 to 5000 so exchange filters return
        // every matching instrument, not just the alphabetic first 200.
        // Exchange filters (NSE / BSE / NFO / MCX / NCO / DELTA) are scoped to
        // TRACKED instruments only (indTracked=true on the Instruments page);
        // the separate 'ALL' chip stays unscoped (every instrument).
        const params: any = { pageSize: 5000 };
        if (mode !== 'ALL') { params.exchange = mode; params.tracked = 'true'; }
        const r = await api.get<any>('/instruments', { params });
        const items: any[] = r.data?.data?.instruments || r.data?.instruments || [];
        setSymResults(items.map((i: any) => ({ symbol: i.symbol, exchange: i.exchange || '' })));
      }
    } catch { /* ignore */ }
  }, [moversThreshold, alertPeriod]);

  useEffect(() => { loadSymbolsByMode(filterMode); }, [filterMode, loadSymbolsByMode]);

  /**
   * Keep the Alerts list live.
   *
   * The backend already emits `alert:new` into the user's own room on every alert it
   * records; nothing on this page was listening. Riding that existing socket costs NO new
   * timer and no polling -- the same discipline the liquidation guard shipped under, and the
   * reason this app has a shared ticker cache in the first place.
   *
   * Debounced: a strategy scan can record a dozen alerts in a second, and each one would
   * otherwise trigger its own /alerts + /strategies round-trip. One refresh 1.5s after the
   * last event of a burst is both cheaper and less visually jarring than twelve.
   *
   * Only while the filter is actually showing, so the rest of the app pays nothing.
   */
  useEffect(() => {
    if (filterMode !== 'Alerts') return;
    let debounce: number | undefined;
    let attach: number | undefined;
    let sock: ReturnType<typeof getGlobalSocket> = null;
    const onNew = () => {
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => { void loadSymbolsByMode('Alerts'); }, 1500);
    };
    // The global socket is created by useWebSocket and may not exist on first paint.
    const tryAttach = () => {
      const sk = getGlobalSocket();
      if (!sk) { attach = window.setTimeout(tryAttach, 300); return; }
      sock = sk;
      sk.on('alert:new', onNew);
    };
    tryAttach();
    return () => {
      if (debounce) window.clearTimeout(debounce);
      if (attach) window.clearTimeout(attach);
      if (sock) sock.off('alert:new', onNew);
    };
  }, [filterMode, loadSymbolsByMode]);

  // Selecting Alerts defaults the sort to newest-first. 'time-desc' rather than 'recent':
  // both give the same order on a freshly loaded list, but 'recent' is passthrough, so a
  // row appended by a live alert:new event would land wherever the merge put it. An explicit
  // time sort keeps the list ordered as it grows.
  //
  // Only when the CURRENT sort cannot express time, so a user who deliberately picked
  // Name or % is not overridden every time they revisit the filter.
  useEffect(() => {
    if (filterMode === 'Alerts' && symSort !== 'time-desc' && symSort !== 'time-asc') {
      setSymSort('time-desc');
      try { localStorage.setItem('chart_symSort', 'time-desc'); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode]);

  /* ───── symbol search ───── */
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = symSearch.trim().toUpperCase();
    if (term.length < 2) {
      // When search is cleared, reload defaults per filterMode
      if (term.length === 0) loadSymbolsByMode(filterMode);
      else setSymResults([]);
      return;
    }
    /* The two RWA lists are fixed, already loaded, and — crucially — the two
       halves of one pairing that is listed under DIFFERENT names on each side.
       RWA rows are Delta perpetuals (SPYXUSD); RWA -> Stock rows are the listed
       stock (SPY). Searching either list for the other side's name found
       nothing, which is what "SPYXUSD and QQQXUSD are missing from the RWA
       filters" actually was. So match BOTH names, locally — sending these to
       /instruments would search a different universe entirely. */
    if (filterMode === 'RWA' || filterMode === 'RWAEq') {
      const all = symResultsCache[filterMode] || [];
      const hit = (s: SymbolItem) => {
        const u = s.symbol.toUpperCase();
        if (u.includes(term)) return true;
        return rwaPairsFor(s.symbol, s.exchange).some(p => p.toUpperCase().includes(term));
      };
      setSymResults(all.filter(hit));
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSymLoading(true);
      try {
        const params: any = { search: term, pageSize: 40 };
        // Only apply exchange filter when filterMode is an exchange code
        const isExch = ['NSE', 'BSE', 'NFO', 'MCX', 'NCO', 'DELTA'].includes(filterMode);
        if (isExch) params.exchange = filterMode;
        const r = await api.get<any>('/instruments', { params });
        const items: any[] = r.data?.data?.instruments || r.data?.instruments || [];
        setSymResults(items.map((i: any) => ({ symbol: i.symbol, exchange: i.exchange || '' })));
      } catch { setSymResults([]); }
      setSymLoading(false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symSearch, filterMode, loadSymbolsByMode, rwaEquivalents]);

  /* ───── fetch candles helper ───── */
  // Self-reference for the SYNTH branch below (a const can't call itself while
  // it is being defined) — assigned right after the definition.
  const fetchCandlesRef = useRef<((sym: string, exch: string, interval: number, opts?: { fromSec?: number; toSec?: number; withLive?: boolean; silentError?: boolean; signal?: AbortSignal; onRateLimited?: () => void }) => Promise<CandleRow[]>) | null>(null);
  const fetchCandles = useCallback(async (sym: string, exch: string, interval: number, opts?: { fromSec?: number; toSec?: number; withLive?: boolean; silentError?: boolean; signal?: AbortSignal; onRateLimited?: () => void }): Promise<CandleRow[]> => {
    if (!sym) return [];
    // ── SYNTHETIC symbol: fetch each leg's candles (existing per-leg path,
    // including cache + IST shift) and zip them into the combined package
    // premium. Self-recursion goes through the ref (TDZ-safe).
    if (exch === 'SYNTH') {
      const sd = synthRef.current;
      // Compare case-insensitively: the tab/route round-trip upper-cases the symbol
      // (openChartTab → /charts/D0_CR_L_BTC), while the synthetic's stored name is
      // mixed-case (D0_Cr_L_BTC). A strict compare silently returned no candles.
      if (!sd || sd.name.toUpperCase() !== String(sym).toUpperCase() || !sd.legs?.length || sd.legs.some(l => !l.symbol)) return [];
      const feed = String(sd.underlying?.feedSource || '').toUpperCase();
      const legExch = feed === 'DELTA' ? 'DELTA' : (sd.underlying?.zerodhaExchange || 'NFO');
      const legSeries: CandleRow[][] = [];
      for (let i = 0; i < sd.legs.length; i++) {
        const data = await fetchCandlesRef.current!(sd.legs[i].symbol as string, legExch, interval, { ...opts, withLive: false, silentError: true });
        if (!data.length) return [];
        legSeries.push(data);
        if (i < sd.legs.length - 1) await new Promise(r => setTimeout(r, 250)); // broker rate limit
      }
      return combineSyntheticCandles(sd, legSeries);
    }
    // Serve history from cache when possible (never for withLive — that carries
    // the live LTP). Bounded ranges are immutable history (long TTL); open-ended
    // recent fetches use a short TTL so the newest bars stay fresh.
    const useCache = !opts?.withLive;
    const ckey = candleCacheKey(sym, exch, interval, opts?.fromSec, opts?.toSec);
    // Bounded historical ranges are immutable → 1h. Daily/Weekly aux series
    // change only once a day/week → 10 min (the big win when flipping timeframe,
    // since they're interval-independent). Other open-ended fetches → 30s.
    const ttl = opts?.toSec != null ? 3_600_000 : (interval >= 1440 ? 600_000 : 30_000);
    if (useCache) {
      const hit = candleCacheGet(ckey, ttl);
      if (hit) return hit as CandleRow[];
    }
    try {
      const params: any = { symbol: sym, exchange: exch, interval };
      // Backend expects `from`/`to` in seconds (UTC) for Delta, or ISO strings for Zerodha.
      // For Zerodha we pass ISO; for Delta we pass seconds. Detect by exchange.
      // Delta + GLOBAL (Yahoo) expect epoch seconds; Zerodha expects ISO.
      const secsRange = exch === 'DELTA' || exch === 'GLOBAL';
      if (opts?.fromSec != null) {
        params.from = secsRange ? opts.fromSec : new Date(opts.fromSec * 1000).toISOString();
      }
      if (opts?.toSec != null) {
        params.to = secsRange ? opts.toSec : new Date(opts.toSec * 1000).toISOString();
      }
      // For non-Delta (Zerodha NSE/BSE/NFO/MCX) symbols, ask the backend to
      // merge the live LTP into the last/forming candle. Delta has its own
      // mark_price poll on the frontend so we don't redundantly request
      // withLive there. Caller controls via opts.withLive.
      if (opts?.withLive && exch !== 'DELTA') {
        params.withLive = 'true';
      }
      const resp = await api.get<any>('/charts/candles', {
        params,
        headers: { _suppressToast: 'true' },
        signal: opts?.signal,
      });
      // IDXN alias metadata — remember what the alias resolved to for the legend title.
      const aliasSym = resp.data?.data?.aliasSymbol;
      if (aliasSym && resp.data?.data?.actualSymbol) {
        aliasActualBySymbol.set(String(aliasSym).toUpperCase(), {
          actual: String(resp.data.data.actualSymbol), exchange: String(resp.data.data.actualExchange || ''),
        });
      }
      // Clamp spurious single-bar wick spikes ("bad ticks") so one corrupt print doesn't
      // blow the Y-scale / high-low indicators (see the reported giant-wick candle).
      const candles = sanitizeCandles(resp.data?.data?.candles || []);
      if (useCache && candles.length) candleCacheSet(ckey, candles);
      return candles;
    } catch (err: any) {
      // Superseded / cancelled (user moved to another symbol) → silent, never a toast.
      if (opts?.signal?.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return [];
      const status = err?.response?.status;
      // 429: a transient rate-limit (already retried server-side). Don't pop an
      // error toast — let the caller surface a subtle, self-healing inline hint
      // (onRateLimited). Falls back to the legacy toast only if no handler given.
      if (status === 429) {
        if (opts?.onRateLimited) { opts.onRateLimited(); return []; }
        if (!opts?.silentError) {
          const provider = (exch || '').toUpperCase() === 'DELTA' ? 'Delta' : 'the data provider';
          toast.error(`Rate-limited by ${provider} for ${sym} ${interval}m. Please wait a few seconds.`, { id: 'chart-rate-limit' });
        }
        return [];
      }
      // Genuine failure (400/404/500…) → keep the informative toast.
      const msg = err?.response?.data?.error?.message || `Failed to load candles for ${sym} ${interval}m`;
      if (!opts?.silentError) toast.error(msg, { id: `chart-${sym}-${interval}` });
      return [];
    }
  }, []);
  fetchCandlesRef.current = fetchCandles;

  /* ───── replay deep-history fetch (R142) ─────
     Pulls a deep candle window for bar-replay only, from the broker (paginated)
     or a free source (Binance/Yahoo). anchorSec = IST-shifted seconds of the
     replay start bar; the backend window covers ~`bars` before it up to now. */
  const fetchReplayCandles = useCallback(async (
    sym: string, exch: string, interval: number, anchorIstSec: number,
    source: 'broker' | 'free', bars = 1500,
  ): Promise<CandleRow[]> => {
    if (!sym) return [];
    try {
      const resp = await api.get<any>('/charts/replay-candles', {
        params: { symbol: sym, exchange: exch, interval, anchor: anchorIstSec - 19800, source, bars },
        headers: { _suppressToast: 'true' },
      });
      return resp.data?.data?.candles || [];
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || `Failed to load replay history for ${sym} ${interval}m`,
        { id: `replay-${sym}-${interval}` });
      return [];
    }
  }, []);

  /* Delta rate-limit UX: a subtle inline hint (not an error toast) + bounded
     self-healing auto-retry. loadAbortRef cancels a superseded symbol's in-flight
     candle fetches so they never surface a stale rate-limit. */
  const [rateLimitHint, setRateLimitHint] = useState<string | null>(null);
  const loadAbortRef = useRef<AbortController | null>(null);
  const rlRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; attempts: number }>({ timer: null, attempts: 0 });
  const loadSpotPanesRef = useRef<() => void>(() => {});

  /* Prepend older candles when user pans the chart left. Called from TradingChart's
     onLoadMore subscription with the oldest currently-loaded candle time (IST-shifted seconds). */
  const loadingMoreRef = useRef<Set<number>>(new Set());
  const loadOlderPane = useCallback(async (paneIdx: 0 | 1, oldestTimeIst: number, reason?: 'pan' | 'edge') => {
    // R68a — lazy-loading more history mid-replay would shift the bar indices
    // out from under the playhead. Disable while replay is active — but say so:
    // silently doing nothing when the user drags left reads as a dead chart,
    // and the control that DOES load more history is on the replay bar itself.
    if (replayActiveRef.current) {
      // Only for a DELIBERATE drag past the left edge. The 'edge' heuristic also
      // fires whenever a pane merely holds few bars — which is the normal state
      // of the shorter-timeframe pane in a split — and toasted on its own the
      // moment replay started.
      if (reason === 'pan') {
        toast('Panning back for more history is paused during Bar Replay — it would move the bars out from under the playhead. Use Data: Broker / Free on the replay bar to load a deeper window.',
          { id: 'replay-loadmore', icon: '⏸', duration: 6000 });
      }
      return;
    }
    if (loadingMoreRef.current.has(paneIdx)) return;
    loadingMoreRef.current.add(paneIdx);
    try {
      // Backend times are UTC seconds; candle.time is IST-shifted (+19800)
      const oldestUtc = oldestTimeIst - 19800;
      const intervalMin = intervals[paneIdx];
      // Request ~300 more bars worth of history ending just before the current oldest
      const fromSec = oldestUtc - intervalMin * 60 * 300;
      const toSec = oldestUtc - 1;
      const older = await fetchCandles(symbol, exchange, intervalMin, { fromSec, toSec, signal: loadAbortRef.current?.signal, onRateLimited: () => setRateLimitHint('Delta is rate-limiting — older bars will load shortly.') });
      if (older.length) {
        let addedCount = 0;
        let mergedLength = 0;
        setPaneData(prev => {
          const n = [...prev];
          const existing = new Set(n[paneIdx].map(c => c.time));
          const fresh = older.filter(c => !existing.has(c.time));
          addedCount = fresh.length;
          const merged = [...fresh, ...n[paneIdx]];
          mergedLength = merged.length;
          n[paneIdx] = merged;
          return n;
        });
        if (addedCount > 0) {
          // Round 30 — capForPane(i) must cover the *entire* loaded paneData
          // length so the newly-prepended bars appear in the rendered slice.
          // capTailFor(arr, cap) keeps the LAST `cap` entries — if the initial
          // load already had more bars than candleCap (common on low intervals
          // 1m/3m/5m/10m/15m where ~500 bars get fetched but candleCap is
          // ~200), then prepending N more bars at index 0 still leaves them
          // trimmed off the head. Pre-R30 we only added `addedCount`, which
          // didn't account for the existing oversized buffer, so lazy-load
          // appeared broken on those intervals.
          setPaneExtraBars(prev => {
            const n = [...prev];
            const required = Math.max(0, mergedLength - candleCap);
            n[paneIdx] = Math.max((n[paneIdx] || 0) + addedCount, required);
            return n;
          });
        }
      }
    } finally {
      loadingMoreRef.current.delete(paneIdx);
    }
  }, [symbol, exchange, intervals, fetchCandles, candleCap]);

  /* ───── load spot panes sequentially (avoids Zerodha rate-limit) ─────
     Scale the `fromSec` lookback per pane so every pane gets ~500 bars
     regardless of its interval. Prevents HTF panes from looking sparse
     when an LTF pane sits beside it. */
  const computeFromSec = useCallback((exch: string, intervalMin: number, minBars?: number): number => {
    // Round 43 — fetch enough WARMUP bars beyond what's displayed so
    // long-warmup overlays (EMA200 needs 200 bars, EMA128 128, LR_HAMA 45)
    // are valid across the ENTIRE visible window. Previously this was a
    // fixed 500: if the user's candle-count setting (candleCap) was ≥ ~300,
    // the displayed range reached back past EMA200's 200-bar warmup, so the
    // AvgCloud rendered blank on the left third of the screen until the
    // user dragged left to lazy-load more history. candleCap + 250 leaves a
    // full EMA200 warmup (200) + buffer (50) ahead of the displayed tail.
    // R70 — `minBars` lets specific call-sites demand more history. CMid
    // needs EMA(200) at CTF/HTF cadence to be CONVERGED (not just seeded),
    // so the aux-fetch passes minBars=1000 to give the EMA ~800 bars
    // beyond the warmup. Without that the CTF basis stays stuck on the
    // seed-SMA of stale data and CMid floats away from price (the R41 bug).
    const TARGET_BARS = Math.max(500, candleCap + 250, minBars ?? 0);
    const CAL_OVERHEAD = 1.5; // weekends / holidays cushion
    const isDelta = (exch || '').toUpperCase() === 'DELTA';
    // Bars-per-calendar-day. NSE: 375 min/day of trading. Delta: 24×7.
    // A calendar month is treated as ~30.44 days for sizing only — the actual
    // bucketing is calendar-exact, this just decides how far back to ask.
    const barsPerCalDay = isCalendarInterval(intervalMin)
      ? (1 / 30.44)
      : isDelta
      ? (1440 / intervalMin)
      : (intervalMin < 375 ? (375 / intervalMin) : (1 / Math.ceil(intervalMin / 375)));
    let daysBack = Math.max(7, Math.ceil((TARGET_BARS / barsPerCalDay) * (isDelta ? 1.0 : CAL_OVERHEAD)));
    // Cap lookback to provider limits — Zerodha rejects `from` beyond these
    // ranges with a 400 that surfaces as "Request failed with status code 400".
    // Delta accepts deeper ranges, so keep a larger ceiling there.
    if (!isDelta) {
      // Monthly needs the deepest window Kite will serve — 720 days would be
      // twenty-four bars. Matches the backend's own 1900-day monthly lookback.
      if (isCalendarInterval(intervalMin)) daysBack = Math.min(daysBack, 1900);
      else if (intervalMin >= 10080) daysBack = Math.min(daysBack, 720);   // weekly → ~2y
      else if (intervalMin >= 1440) daysBack = Math.min(daysBack, 1800); // daily → ~5y
      else if (intervalMin >= 60) daysBack = Math.min(daysBack, 400);   // hourly → ~1y
      else daysBack = Math.min(daysBack, 60);                           // intraday → 2mo
    } else {
      daysBack = Math.min(daysBack, 1825); // Delta ceiling ~5y
    }
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec - daysBack * 24 * 60 * 60;
  }, [candleCap]);

  const loadRef = useRef(0);
  const loadSpotPanes = useCallback(async () => {
    if (!symbol || !prefsReady) return;
    const loadId = ++loadRef.current;
    // Cancel the previous symbol/interval's in-flight candle fetches (browsing fast)
    // so their requests/rate-limits don't land after we've moved on.
    try { loadAbortRef.current?.abort(); } catch { /* ignore */ }
    const ac = new AbortController();
    loadAbortRef.current = ac;
    const signal = ac.signal;
    let rateLimited = false;
    const onRateLimited = () => { rateLimited = true; };
    // Round 32 — track which pane indices THIS invocation flipped to
    // loading=true. If we exit early because a newer invocation took
    // over (loadRef.current !== loadId), we still need to release any
    // panes WE flipped on so the loading overlay doesn't get stuck
    // forever in races where the newer invocation never gets to those
    // panes (e.g., it returns before iterating them). The newer
    // invocation will re-flip them on its own iteration anyway.
    const ownedLoadingPanes = new Set<number>();
    const releaseOwnedLoading = () => {
      if (!ownedLoadingPanes.size) return;
      setPaneLoading(prev => {
        const n = [...prev];
        ownedLoadingPanes.forEach(idx => { n[idx] = false; });
        return n;
      });
    };

    const nseLike = NSE_LIKE_EXCH.has((exchange || '').toUpperCase());
    let pane0Empty = false;
    try {
      for (let i = 0; i < spotPaneCount; i++) {
        if (loadRef.current !== loadId) return;
        setPaneLoading(prev => { const n = [...prev]; n[i] = true; return n; });
        ownedLoadingPanes.add(i);
        // Plan A — free (Yahoo) chart data: fetch a deep window anchored at now
        // (no broker needed). Otherwise the normal broker fetch; for NSE without
        // a Zerodha link we suppress the error toast and surface a banner instead.
        let data: CandleRow[];
        if (freeDataActive) {
          const nowIst = Math.floor(Date.now() / 1000) + 19800;
          data = await fetchReplayCandles(symbol, exchange, intervals[i], nowIst, 'free', candleCap + 250);
        } else {
          const fromSec = computeFromSec(exchange, intervals[i]);
          data = await fetchCandles(symbol, exchange, intervals[i], { fromSec, silentError: nseLike, signal, onRateLimited });
        }
        if (loadRef.current !== loadId) return;
        if (i === 0) pane0Empty = data.length === 0;
        // Split: paneData holds closed bars only; latestCandles holds the forming bar.
        // (Free data has no live forming bar — treat all as closed.)
        const closed = (!freeDataActive && data.length > 1) ? data.slice(0, -1) : data;
        const live = (!freeDataActive && data.length > 1) ? data[data.length - 1] : undefined;
        setPaneData(prev => { const n = [...prev]; n[i] = closed; return n; });
        setLatestCandles(prev => { const n = [...prev]; n[i] = live; return n; });
        setPaneLoading(prev => { const n = [...prev]; n[i] = false; return n; });
        ownedLoadingPanes.delete(i); // released — don't reset again in finally
        if (i < spotPaneCount - 1) await new Promise(r => setTimeout(r, 350));
      }
      // Offer free data when the live NSE feed returned nothing (no Zerodha link).
      if (!freeDataActive) setNseFreeOffer(nseLike && pane0Empty);
      // Rate-limit UX: if the primary pane couldn't load because Delta is limiting,
      // show a subtle inline hint and auto-retry (bounded backoff). On success, clear.
      if (loadRef.current === loadId) {
        if (rateLimited && pane0Empty) {
          const st = rlRef.current;
          if (!st.timer) {
            if (st.attempts >= 3) {
              setRateLimitHint('Delta is busy — it’ll keep retrying; click the symbol again to force a reload.');
            } else {
              setRateLimitHint('Delta is rate-limiting — retrying…');
              const delay = [1800, 3500, 6000][st.attempts] ?? 6000;
              st.attempts += 1;
              st.timer = setTimeout(() => { st.timer = null; loadSpotPanesRef.current?.(); }, delay);
            }
          }
        } else if (!pane0Empty) {
          rlRef.current.attempts = 0;
          if (rlRef.current.timer) { clearTimeout(rlRef.current.timer); rlRef.current.timer = null; }
          setRateLimitHint(null);
        }
      }
    } finally {
      // If we exited early (cancelled by a newer load OR an exception
      // bubbled up), release any panes WE had set to loading. Skipping
      // this would strand paneLoading[i] = true and the loading overlay
      // would obscure the chart until the next manual refresh.
      releaseOwnedLoading();
    }
    // `synth?.id` is a dep: a SYNTH chart can't fetch until its detail (leg broker
    // symbols + feed) has loaded, so the first pass after a tab open/reload bails
    // and this re-runs once the synthetic resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals, spotPaneCount, fetchCandles, fetchReplayCandles, prefsReady, computeFromSec, freeDataActive, candleCap, synth?.id]);
  useEffect(() => { loadSpotPanesRef.current = loadSpotPanes; }, [loadSpotPanes]);

  /* ───── lazy-fetch benchmark candles for RelStrength overlay ───── */
  // Auto-resolve a sensible benchmark from the current chart symbol:
  //   • Delta crypto: BTCUSD → ETHUSD, anything else → BTCUSD
  //   • NSE/BSE/NFO: NIFTY → BANKNIFTY, anything else → NIFTY
  //   • MCX / fallback: NIFTY (NSE)
  // User overrides (chartSettings.relStrength.benchmarkSymbol+benchmarkExchange) always win.
  const resolveBenchmark = useCallback((sym: string, exch: string): { symbol: string; exchange: string } => {
    const s = (sym || '').toUpperCase();
    const e = (exch || '').toUpperCase();
    const rs = (settings as any)?.relStrength || {};
    if (rs.benchmarkSymbol && rs.benchmarkSymbol.trim()) {
      return { symbol: rs.benchmarkSymbol.trim().toUpperCase(), exchange: (rs.benchmarkExchange || e).toUpperCase() };
    }
    if (e === 'DELTA') {
      return { symbol: s === 'BTCUSD' ? 'ETHUSD' : 'BTCUSD', exchange: 'DELTA' };
    }
    if (e === 'NSE' || e === 'BSE' || e === 'NFO') {
      return { symbol: s === 'NIFTY' || s === 'NIFTY 50' ? 'BANKNIFTY' : 'NIFTY', exchange: 'NSE' };
    }
    // GLOBAL (Yahoo-charted) and any other venue have no sensible in-DB benchmark —
    // return none so RelStrength is simply skipped rather than fetching a NIFTY/NSE
    // benchmark that isn't in the instrument table (popped "NIFTY not found").
    return { symbol: '', exchange: '' };
  }, [settings]);
  /* Aux-series refresh tick. The benchmark (RelStrength / Synthiya RS) and the synthetic's
     underlying candles were fetched ONCE per symbol/interval, so on a chart left open for
     hours those lines silently stopped extending (they only plot on bars where the aux
     series has data). Re-fetch every 5 min — same cadence as the FNO history poll. */
  const [auxRefreshTick, setAuxRefreshTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => { if (!document.hidden) setAuxRefreshTick(t => t + 1); }, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, []);
  const [paneBenchmarkCandles, setPaneBenchmarkCandles] = useState<CandleRow[][]>([[], []]);
  const benchLoadRef = useRef(0);
  useEffect(() => {
    // Synthiya (staircase RelStrength line) needs the benchmark too.
    if (!symbol || (!overlays.relStrength && !overlays.synthiya)) {
      setPaneBenchmarkCandles([[], []]);
      return;
    }
    // Item 2 — the benchmark is derived from `exchange`; asking before it is
    // confirmed fetches the wrong venue's index. Wait, keeping what we have.
    if (exchangePending) return;
    const loadId = ++benchLoadRef.current;
    (async () => {
      const { symbol: bSym, exchange: bExch } = resolveBenchmark(symbol, exchange);
      for (let i = 0; i < spotPaneCount; i++) {
        if (benchLoadRef.current !== loadId) return;
        const interval = intervals[i];
        const fromSec = computeFromSec(exchange, interval);
        // Auxiliary RelStrength benchmark — never block the user with an error
        // toast if it rate-limits / 404s; RelStrength just won't draw.
        const benchData = bSym ? await fetchCandles(bSym, bExch, interval, { fromSec, silentError: true }) : [];
        if (benchLoadRef.current !== loadId) return;
        setPaneBenchmarkCandles(prev => { const n = [...prev]; n[i] = benchData; return n; });
        await new Promise(r => setTimeout(r, 250));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, exchangePending, intervals.join(','), overlays.relStrength, overlays.synthiya, spotPaneCount, resolveBenchmark, auxRefreshTick]);

  /* ───── per-pane Flow history (Delta only, when overlays.flow is on) ─────
     Pulls per-candle bias from /v1/order-flow/history at the pane's current
     interval and uses the chart's existing fromSec window so the strip lines
     up with whatever bars are loaded. Re-fetches on symbol/exchange/interval
     change. NSE/MCX symbols skip the call entirely (Delta-only data source). */
  const [paneFlowCandles, setPaneFlowCandles] = useState<FlowCandleSignal[][]>([[], [], [], []]);
  const flowLoadRef = useRef(0);
  useEffect(() => {
    if (!symbol || !overlays.flow || (exchange || '').toUpperCase() !== 'DELTA') {
      setPaneFlowCandles([[], [], [], []]);
      return;
    }
    // R67i — chart candles arrive from /charts/candles already IST-shifted
    // (UTC + 19800s) so the time axis renders in local Indian time. The
    // /order-flow/history endpoint returns raw UTC seconds, so every flow
    // bar was sitting 5.5 hours off from its candle on the X axis — this
    // is the alignment bug the user kept reporting. Apply the same shift
    // here so flow times join the chart's time grid exactly.
    const IST_SHIFT_SEC = 19800;
    const loadFlow = async () => {
      const loadId = ++flowLoadRef.current;
      const nowSec = Math.floor(Date.now() / 1000);
      for (let i = 0; i < spotPaneCount; i++) {
        if (flowLoadRef.current !== loadId) return;
        const intervalMin = intervals[i];
        const fromSec = Math.max(computeFromSec(exchange, intervalMin), nowSec - 7 * 24 * 3600);
        const data = await chartFlowService.getHistory(symbol, fromSec, nowSec, intervalMin);
        if (flowLoadRef.current !== loadId) return;
        const shifted = (data as FlowCandleSignal[]).map(f => ({ ...f, time: f.time + IST_SHIFT_SEC }));
        setPaneFlowCandles(prev => { const n = [...prev]; n[i] = shifted; return n; });
        await new Promise(r => setTimeout(r, 120));
      }
    };
    loadFlow(); // immediate-on-mount
    // Re-poll so the histogram keeps pace with newly-formed candles instead of
    // freezing at whatever bar was current when Flow was switched on (the
    // unattended-chart stale-histogram bug). Floor at 15s — order-flow buckets
    // are per-minute, so a tighter cadence just re-fetches the same 3-day
    // window for no gain. flowLoadRef abandons any run still in flight.
    const cadenceMs = Math.max(15, settings.liveUpdateSec) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) loadFlow(); }, cadenceMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals.join(','), overlays.flow, spotPaneCount, computeFromSec, settings.liveUpdateSec]);

  /* ───── Funding-rate history (Delta only, when overlays.funding is on) ─────
     Symbol-level series (same regardless of timeframe); each chart pane
     forward-fills it onto its own candle grid. Fetched over the widest visible
     pane window and IST-shifted to align with candle.time. Re-polled gently —
     funding only moves at the 8h settlements. */
  const [fundingSeries, setFundingSeries] = useState<Array<{ time: number; rate: number }>>([]);
  const fundingLoadRef = useRef(0);
  useEffect(() => {
    if (!symbol || !overlays.funding || (exchange || '').toUpperCase() !== 'DELTA') {
      setFundingSeries([]);
      return;
    }
    const IST_SHIFT_SEC = 19800;
    const loadFunding = async () => {
      const loadId = ++fundingLoadRef.current;
      const nowSec = Math.floor(Date.now() / 1000);
      let fromSec = nowSec - 30 * 24 * 3600;
      for (let i = 0; i < spotPaneCount; i++) fromSec = Math.min(fromSec, computeFromSec(exchange, intervals[i]));
      const data = await chartFundingService.getHistory(symbol, fromSec, nowSec);
      if (fundingLoadRef.current !== loadId) return;
      setFundingSeries(data.map(p => ({ time: p.time + IST_SHIFT_SEC, rate: p.rate })));
    };
    loadFunding();
    const cadenceMs = Math.max(60, settings.liveUpdateSec) * 1000; // funding moves slowly
    const id = window.setInterval(() => { if (!document.hidden) loadFunding(); }, cadenceMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals.join(','), overlays.funding, spotPaneCount, computeFromSec, settings.liveUpdateSec]);

  /* ───── lazy-fetch CTF/HTF aux candles per spot pane, only when needed ───── */
  const auxLoadRef = useRef(0);
  const needsCtf = !!(overlays.avgCloud || overlays.combinedMidline || overlays.cpr || overlays.ctfPoc || activeStrategy === 'rockband');
  // R70/R72 — CombinedMidline blends LTF + CTF + HTF (Pine's f_ctf_context).
  // HTF is therefore required when EITHER CMid OR AvgCloud is enabled — R72
  // wired AvgCloud to use CMid as its reference boundary (replacing the
  // dropped cpoc/ema200/ema128 dropdown). R114 — RockBand now reads the
  // AvgCloud cloud state, so it needs CTF + HTF too.
  const needsHtf = !!(overlays.htfPoc || overlays.combinedMidline || overlays.avgCloud || activeStrategy === 'rockband');
  // When CMid (or AvgCloud, which reads CMid) is on we need ≥1000 bars at
  // CTF/HTF cadence so the EMA(200) used inside the f_ctf_context blend
  // actually converges and doesn't drift CMid away from price.
  const cmidMinBars = (overlays.combinedMidline || overlays.avgCloud) ? 1000 : undefined;
  useEffect(() => {
    if (!symbol || !prefsReady) { setPaneCtf([[], []]); setPaneHtf([[], []]); return; }
    if (!needsCtf && !needsHtf) { setPaneCtf([[], []]); setPaneHtf([[], []]); return; }
    const loadId = ++auxLoadRef.current;
    // Drop the PREVIOUS symbol/interval's CTF/HTF immediately so aux-dependent
    // overlays (esp. the Combined Midline / AvgCloud, which is gated on aux being
    // present) never draw against stale context while the new aux loads. They
    // reappear, correct, once the new CTF + HTF arrive below.
    setPaneCtf([[], []]); setPaneHtf([[], []]);
    (async () => {
      for (let i = 0; i < spotPaneCount; i++) {
        if (auxLoadRef.current !== loadId) return;
        const ltf = intervals[i];
        if (needsCtf) {
          const ctfMin = ctfFromLtf(ltf);
          const fromSec = computeFromSec(exchange, ctfMin, cmidMinBars);
          const ctfData = await fetchCandles(symbol, exchange, ctfMin, { fromSec, silentError: true });
          if (auxLoadRef.current !== loadId) return;
          setPaneCtf(prev => { const n = [...prev]; n[i] = ctfData; return n; });
          await new Promise(r => setTimeout(r, 350));
        }
        if (needsHtf) {
          const ctfMin = ctfFromLtf(ltf);
          const htfMin = htfFromCtf(ctfMin);
          const fromSec = computeFromSec(exchange, htfMin, cmidMinBars);
          const htfData = await fetchCandles(symbol, exchange, htfMin, { fromSec, silentError: true });
          if (auxLoadRef.current !== loadId) return;
          setPaneHtf(prev => { const n = [...prev]; n[i] = htfData; return n; });
          await new Promise(r => setTimeout(r, 350));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals, spotPaneCount, prefsReady, needsCtf, needsHtf, cmidMinBars]);

  /* CPR base period per pane — sourced from the Pine resolution table. */
  const auxFor = useCallback((paneIdx: number): AuxCandles => ({
    ctf: paneCtf[paneIdx] || [],
    htf: paneHtf[paneIdx] || [],
    daily: dailyCandles,
    weekly: weeklyCandles,
    h4: h4Candles,
    srHtf: paneSrHtf[paneIdx] || [],
    srHtfMinutes: paneSrHtfMin[paneIdx] || 0,
    cprPeriod: cprBaseFromLtf(intervals[paneIdx]),
    ltfMinutes: intervals[paneIdx],
    exchange,
  }), [paneCtf, paneHtf, paneSrHtf, paneSrHtfMin, dailyCandles, weeklyCandles, h4Candles, intervals, exchange]);
  /* auxFor is memoised, but CALLING it builds a fresh object — so calling it inline in the
     JSX handed TradingChart a new `auxCandles` identity on EVERY render of this page, and
     `auxCandles` is in that component's master data effect's dependency list. Every render
     therefore tore down and re-added every overlay series on every pane, then re-issued
     setData and re-applied the visible range. Combined with the crosshair state that used to
     live here, a pan-drag did that at pointer rate.
     Memoise the RESULT per pane so the identity changes only when the aux data does. */
  const aux0 = useMemo(() => auxFor(0), [auxFor]);
  const aux1 = useMemo(() => auxFor(1), [auxFor]);

  /* ───── load option chain (strikes/expiries/underlying) ─────
     Runs in ALL layouts (not just quad) so the Build / Outlook option-basket
     chips work in single & 2-split too — they need nfoUnderlying + optionStrikes
     + ceExpiry. Reads stored snapshots server-side (no live NSE scrape); returns
     empty for symbols without an option chain (chips then stay hidden). */
  const chainLoadRef = useRef(0);
  useEffect(() => {
    if (!symbol) {
      setOptionExpiries([]);
      setOptionStrikes([]);
      return;
    }
    const chainId = ++chainLoadRef.current;
    (async () => {
      setChainLoading(true);
      try {
        const resp = await api.get<any>('/charts/option-chain', { params: { symbol } });
        if (chainLoadRef.current !== chainId) return; // stale
        const d = resp.data?.data;
        if (d) {
          const strikes: number[] = d.strikes || [];
          const expiries: string[] = d.expiries || [];
          const sp = d.spotPrice || 0;
          setOptionExpiries(expiries);
          setOptionStrikes(strikes);
          setNfoUnderlying(d.underlying || '');
          setSpotPrice(sp);

          // Compute ATM strike (nearest to spot price)
          const atm = sp && strikes.length
            ? strikes.reduce((prev, curr) => Math.abs(curr - sp) < Math.abs(prev - sp) ? curr : prev)
            : strikes[Math.floor(strikes.length / 2)] || null;

          // Default CE and PE to ATM and nearest expiry if not already set
          if (strikes.length && ceStrike === null) setCeStrike(atm);
          if (strikes.length && peStrike === null) setPeStrike(atm);
          // Set the nearest expiry when unset OR when the held value is no longer
          // offered (e.g. it expired since the last load) — otherwise a stale
          // selection would stamp a dead expiry onto built basket legs.
          if (expiries.length && (!ceExpiry || !expiries.includes(ceExpiry))) setCeExpiry(expiries[0]);
          if (expiries.length && (!peExpiry || !expiries.includes(peExpiry))) setPeExpiry(expiries[0]);
        }
      } catch {
        if (chainLoadRef.current !== chainId) return;
        setOptionExpiries([]);
        setOptionStrikes([]);
      } finally {
        if (chainLoadRef.current === chainId) setChainLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutMode, symbol, chainReloadNonce]);

  /* ───── load CE/PE candles for quad mode (separate strikes/expiries) ───── */
  const optLoadRef = useRef(0);
  useEffect(() => {
    if (layoutMode !== 'quad' || !nfoUnderlying) {
      // Invalidate any in-flight run AND clear the flags it owns. A load that was
      // mid-flight when the layout changed used to leave ceLoading/peLoading true
      // forever — TradingChart renders its skeleton for `loading && !candles.length`,
      // which is why quad Q3/Q4 sat on a skeleton indefinitely.
      optLoadRef.current++;
      setCeCandles([]);
      setPeCandles([]);
      setCeLoading(false);
      setPeLoading(false);
      return;
    }

    const loadId = ++optLoadRef.current;

    /* Load ONE option leg. CE and PE used to run strictly one after the other,
       behind a hardcoded 350ms sleep, so the quad layout took ~16s to fill:
       option-chain 3.9s, option-symbol CE 7.0s, CE candles 10.9s, sleep,
       option-symbol PE 13.0s, PE candles 16.0s. Nothing in the PE leg depends on
       the CE leg, and neither does the sleep -- it was spacing a serial chain
       that no longer exists. Both legs now run concurrently, and inside each leg
       the trigger-level lookup runs alongside the candle fetch rather than
       before it.

       SAFETY: these panes carry BUY / SHORT controls, and ceInstrumentId /
       ceTriggerLevels are what those controls arm with. So every write is behind
       a FRESH supersede check, and a superseded run writes nothing and clears no
       loading flag -- the run that replaced it already owns them. */
    const loadLeg = async (side: 'CE' | 'PE') => {
      const isCe = side === 'CE';
      const expiry = isCe ? ceExpiry : peExpiry;
      const strike = isCe ? ceStrike : peStrike;
      const optType = isCe ? ceOptType : peOptType;
      const setLoading = isCe ? setCeLoading : setPeLoading;
      const stale = () => optLoadRef.current !== loadId;

      const clearLeg = () => {
        if (stale()) return;
        if (isCe) { setCeCandles([]); setCeInstrumentId(null); setCeSymbol(''); setCeOptExchange(''); setCeTriggerLevels({}); }
        else { setPeCandles([]); setPeInstrumentId(null); setPeSymbol(''); setPeOptExchange(''); setPeTriggerLevels({}); }
      };

      if (!expiry || strike === null) { clearLeg(); return; }

      setLoading(true);
      try {
        const resp = await api.get<any>('/charts/option-symbol', {
          params: { underlying: nfoUnderlying, strike, expiry, type: optType },
        });
        if (stale()) return;
        const info = resp.data?.data;
        if (!info?.symbol) return;

        if (isCe) {
          setCeInstrumentId(info.id ?? null);
          setCeSymbol(info.symbol);
          setCeOptExchange(info.exchange || 'DELTA');
          setCeDecimals(info.decimalPrecision ?? 2);
          setCeTickSize(info.tickSize != null && Number(info.tickSize) > 0 ? Number(info.tickSize) : undefined);
          if (info.lotSize != null && Number(info.lotSize) > 0) setCeLotSize(Number(info.lotSize));
        } else {
          setPeInstrumentId(info.id ?? null);
          setPeSymbol(info.symbol);
          setPeOptExchange(info.exchange || 'DELTA');
          setPeDecimals(info.decimalPrecision ?? 2);
          setPeTickSize(info.tickSize != null && Number(info.tickSize) > 0 ? Number(info.tickSize) : undefined);
          if (info.lotSize != null && Number(info.lotSize) > 0) setPeLotSize(Number(info.lotSize));
        }

        // The merged-instrument lookup (for the pane's trigger levels) and the
        // candle fetch are independent -- run them together, not in sequence.
        const [levels, data] = await Promise.all([
          (async (): Promise<TriggerLevels> => {
            if (!info.id) return {};
            try {
              const instResp = await api.get<any>('/instruments', { params: { search: info.symbol, exchange: info.exchange, pageSize: 5 } });
              const items = instResp.data?.data?.instruments || instResp.data?.instruments || [];
              const matched = items.find((it: any) => it.id === info.id) || items[0];
              if (!matched) return {};
              return {
                nbuy: matched.nbuy != null ? Number(matched.nbuy) : undefined,
                nshort: matched.nshort != null ? Number(matched.nshort) : undefined,
                bbuy: matched.bbuy != null ? Number(matched.bbuy) : undefined,
                bshort: matched.bshort != null ? Number(matched.bshort) : undefined,
              };
            } catch { return {}; }
          })(),
          fetchCandles(info.symbol, info.exchange, optInterval),
        ]);
        if (stale()) return;
        if (isCe) { setCeTriggerLevels(levels); setCeCandles(data); }
        else { setPeTriggerLevels(levels); setPeCandles(data); }
      } catch {
        clearLeg();
      } finally {
        // Each leg clears its OWN flag as soon as it finishes, so a pane renders
        // the moment its data lands instead of waiting for the other leg.
        if (!stale()) setLoading(false);
      }
    };

    (async () => {
     try {
      await Promise.all([loadLeg('CE'), loadLeg('PE')]);
     } finally {
      // The NEWEST run owns both flags on EVERY exit path -- normal completion, the
      // no-strike/no-expiry early returns, a throw, and the supersede returns above.
      // A superseded run must NOT clear them: the run that replaced it has already
      // set them true and will clear them itself. Before this, a supersede returned
      // straight out of the async function with peLoading still true and nothing
      // left to turn it off. See docs/CHARTS-FUNCTIONAL-ISSUES.md item 4.
      if (optLoadRef.current === loadId) { setCeLoading(false); setPeLoading(false); }
     }
    })();
    // `intervals` is a fresh array every render; joining it stops this effect
    // re-firing on unrelated renders, which is what created the supersede races.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutMode, nfoUnderlying, ceExpiry, ceStrike, ceOptType, peExpiry, peStrike, peOptType, optInterval, fetchCandles]);

  /* ───── load prefs on symbol change ───── */
  useEffect(() => {
    if (!symbol) return;
    // Round 96 — layout / interval / overlays are now GLOBAL "last used" state
    // (persisted to localStorage, sticky across symbols), so we no longer apply
    // per-symbol saved overlays/interval/layout here. We still load the symbol-
    // specific option-chain context (CE/PE strike/expiry).
    if (chartSettingsLoading) return;
    setPrefsReady(false);
    skipFirstSave.current = true;
    (async () => {
      try {
        const resp = await api.get<any>(`/charts/preferences/${encodeURIComponent(symbol)}`);
        const d = resp.data?.data;
        // Rectangle zones (item 3) — per-symbol annotations under chartSettings.
        loadedChartSettingsRef.current = d?.chartSettings || {};
        rectZonesLoadedRef.current = false;
        setRectZones(Array.isArray(d?.chartSettings?.rectZones) ? d.chartSettings.rectZones : []);
        setRectSel(null); setRectDraw(false);
        setTimeout(() => { rectZonesLoadedRef.current = true; }, 0);
        if (d) {
          // Separate CE/PE fields (fall back to legacy shared fields).
          // Option-chain context is symbol-specific by definition.
          // A saved expiry that has SETTLED since the preference was written must not
          // be restored — it would stamp a dead expiry onto built basket legs (the
          // "NIFTY 2026-06-30 in July" failure). Restore only today-or-later dates.
          const liveExp = (e?: string) => !!e && String(e).slice(0, 10) >= new Date().toISOString().slice(0, 10);
          if (d.ceStrike != null) setCeStrike(Number(d.ceStrike));
          else if (d.optionStrike != null) setCeStrike(Number(d.optionStrike));
          if (liveExp(d.ceExpiry)) setCeExpiry(d.ceExpiry);
          else if (liveExp(d.optionExpiry)) setCeExpiry(d.optionExpiry);
          if (d.peStrike != null) setPeStrike(Number(d.peStrike));
          else if (d.optionStrike != null) setPeStrike(Number(d.optionStrike));
          if (liveExp(d.peExpiry)) setPeExpiry(d.peExpiry);
          else if (liveExp(d.optionExpiry)) setPeExpiry(d.optionExpiry);
        }
      } catch { /* defaults */ }
      setPrefsReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, chartSettingsLoading]);

  /* ───── auto-save prefs (debounced, skips initial load) ───── */
  useEffect(() => {
    if (!symbol || !prefsReady) return;
    if (skipFirstSave.current) { skipFirstSave.current = false; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/charts/preferences/${encodeURIComponent(symbol)}`, {
          exchange, layoutMode,
          primaryInterval: intervals[0], secondaryInterval: intervals[1],
          overlays, activeStrategy,
          ceStrike, ceExpiry, peStrike, peExpiry,
        });
      } catch { /* silent */ }
    }, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, layoutMode, intervals, overlays, ceStrike, ceExpiry, peStrike, peExpiry, activeStrategy, prefsReady]);

  /* Rect zones saver (item 3) — its own debounced PUT that spreads the loaded
     chartSettings as merge base (the main prefs save intentionally does NOT send
     chartSettings, so the two savers can't clobber each other's keys). */
  const rectSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!symbol || !prefsReady || !rectZonesLoadedRef.current) return;
    if (rectSaveTimer.current) clearTimeout(rectSaveTimer.current);
    rectSaveTimer.current = setTimeout(async () => {
      try {
        const merged = { ...(loadedChartSettingsRef.current || {}), rectZones };
        await api.put(`/charts/preferences/${encodeURIComponent(symbol)}`, { chartSettings: merged });
        loadedChartSettingsRef.current = merged;
      } catch { /* silent */ }
    }, 1200);
    return () => { if (rectSaveTimer.current) clearTimeout(rectSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rectZones, symbol, prefsReady]);

  /* Del/Backspace deletes the selected rect zone (guarded when typing in inputs). */
  useEffect(() => {
    if (!rectSel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (document.activeElement?.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as any)?.isContentEditable) return;
      setRectZones(zs => zs.filter(z => z.id !== rectSel.id));
      setRectSel(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rectSel]);

  /* ───── reload spot panes when relevant state changes ───── */
  // Debounced so rapidly browsing symbols (or arrowing through the list) fires ONE
  // load for the symbol you settle on — not a request burst per glanced symbol
  // (the main cause of Delta rate-limits). A single click is effectively instant.
  useEffect(() => {
    const t = setTimeout(() => loadSpotPanes(), 200);
    return () => clearTimeout(t);
  }, [loadSpotPanes]);

  /* Enter / leave Bar Replay.
   *
   * The two call sites (the `R` hotkey and the toolbar chip) used to carry identical inline
   * copies of this; they are one function now.
   *
   * RUNWAY. The playhead wants 200 bars of pane-0 history behind it,
   * but it also refuses to start before the SHORTEST pane's first bar — otherwise pane 1
   * filters to nothing and renders "No candles for this selection". In the owner's 8H/15m
   * split those two pull hard against each other: the normal load fetches ~500 bars PER PANE
   * AT ITS OWN INTERVAL, which is ~166 days at 8H but only ~5 days at 15m, so the playhead
   * was dragged forward to roughly 16 bars from the end and there was nothing to replay.
   *
   * So: before starting, if pane 1 cannot reach back to where the playhead wants to be,
   * deepen PANE 1 (only) using the replay-history endpoint that GoTo-date and the deep-window
   * button already use. One extra request, on entering replay, and no new timer. `deep` is
   * then true so leaving replay restores the live series exactly as the other deep paths do. */
  const enterOrExitReplay = useCallback(async () => {
    setChartTool(null);
    setTriggerMode(null);
    if (replayActiveRef.current) {
      setReplay(r => {
        if (!r.active) return r;
        if (r.deep) setTimeout(() => loadSpotPanesRef.current?.(), 0);
        return { ...REPLAY_OFF };
      });
      return;
    }
    const p0 = paneData[0] || [];
    if (p0.length < 2) return;

    const RUNWAY_BARS = 200;
    const wantIdx = Math.max(0, Math.min(p0.length - 1, p0.length - RUNWAY_BARS));
    const wantTime = p0[wantIdx].time as number;
    const panes = layoutMode === 'split' ? 2 : 1;

    let deepened: CandleRow[] | null = null;
    if (panes > 1) {
      const p1 = paneData[1] || [];
      const earliest1 = p1.length ? (p1[0].time as number) : Infinity;
      if (earliest1 > wantTime) {
        setReplayDeepLoading(true);
        try {
          const nowIst = Math.floor(Date.now() / 1000) + 19800;
          const src: 'broker' | 'free' = freeDataActive ? 'free' : 'broker';
          const rows = await fetchReplayCandles(symbol, exchange, intervals[1], nowIst, src, 1500);
          if (rows.length) deepened = rows;
        } finally {
          setReplayDeepLoading(false);
        }
      }
    }

    // Recompute the start with whatever pane 1 can now actually cover.
    let startIdx = wantIdx;
    if (panes > 1) {
      const p1 = deepened || paneData[1] || [];
      const earliest = p1.length ? (p1[0].time as number) : 0;
      if (earliest > 0) while (startIdx < p0.length - 1 && (p0[startIdx].time as number) < earliest) startIdx++;
    }
    if (startIdx >= p0.length - 1) { toast.error('Not enough loaded history to replay this pairing'); return; }

    if (deepened) {
      setPaneData(prev => { const n = [...prev]; n[1] = deepened as CandleRow[]; return n; });
      setLatestCandles(prev => { const n = [...prev]; n[1] = undefined; return n; });
    }
    setReplay({
      active: true, currentIdx: startIdx, playing: false, speed: 1,
      source: freeDataActive ? 'free' : 'broker', deep: !!deepened,
    });
    const runway = p0.length - 1 - startIdx;
    if (panes > 1) {
      toast(`Replay: ${runway} × ${catalogIntervalLabel(intervals[0])} bars of runway`
        + (runway < RUNWAY_BARS ? ` (limited by the ${catalogIntervalLabel(intervals[1])} pane's history)` : ''),
        { icon: '⏯', id: 'replay-runway' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneData, layoutMode, symbol, exchange, intervals, freeDataActive, fetchReplayCandles]);

  /* R142 — load a DEEP replay window from the chosen source and swap it into
     paneData for the duration of replay. Anchored at the current replay bar so
     there's plenty of history to its LEFT for analysis. */
  const loadReplayDeep = useCallback(async (source: 'broker' | 'free') => {
    if (!symbol) return;
    const p0 = paneData[0] || [];
    const anchor = p0[replay.currentIdx]?.time || p0[p0.length - 1]?.time || (Math.floor(Date.now() / 1000) + 19800);
    setReplayDeepLoading(true);
    try {
      const results: CandleRow[][] = [];
      for (let i = 0; i < spotPaneCount; i++) {
        results[i] = await fetchReplayCandles(symbol, exchange, intervals[i], anchor, source, 1500);
        if (i < spotPaneCount - 1) await new Promise(r => setTimeout(r, 200));
      }
      if (!results[0]?.length) { toast.error('No replay history returned for this symbol/source'); return; }
      setPaneData(prev => { const n = [...prev]; for (let i = 0; i < spotPaneCount; i++) if (results[i]?.length) n[i] = results[i]; return n; });
      setLatestCandles(prev => { const n = [...prev]; for (let i = 0; i < spotPaneCount; i++) n[i] = undefined; return n; });
      // Re-anchor currentIdx to the bar nearest `anchor` in the new pane-0 data.
      const np0 = results[0];
      let idx = np0.length - 1, lo = 0, hi = np0.length - 1;
      while (lo <= hi) { const mid = (lo + hi) >> 1; if (np0[mid].time === anchor) { idx = mid; lo = hi + 1; break; } if (np0[mid].time < anchor) lo = mid + 1; else hi = mid - 1; }
      if (np0[idx]?.time !== anchor && np0.length) {
        const a = np0[Math.max(hi, 0)], b = np0[Math.min(lo, np0.length - 1)];
        idx = Math.abs(a.time - anchor) <= Math.abs(b.time - anchor) ? np0.indexOf(a) : np0.indexOf(b);
      }
      setReplay(r => ({ ...r, source, deep: true, currentIdx: Math.max(0, idx), playing: false }));
    } finally {
      setReplayDeepLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals, spotPaneCount, replay.currentIdx, paneData, fetchReplayCandles]);

  /* GoTo-date — jump the chart to a chosen historical date instead of dragging
     back repeatedly. Loads a deep window anchored a bit AFTER the date (so there
     are bars to step/scroll into) and positions the (frozen) replay playhead at
     the date. History to the left is loaded; Exit (replay controls) returns to
     live. Single/2-split only (like Replay). */
  const GOTO_FWD_BARS = 200;
  const goToDate = useCallback(async (dateUtcSec: number) => {
    if (!symbol) return;
    const source: 'broker' | 'free' = freeDataActive ? 'free' : 'broker';
    setReplayDeepLoading(true);
    try {
      const targetIst = dateUtcSec + 19800; // candle.time is IST-shifted seconds
      const results: CandleRow[][] = [];
      for (let i = 0; i < spotPaneCount; i++) {
        const anchorIst = targetIst + GOTO_FWD_BARS * intervals[i] * 60;
        results[i] = await fetchReplayCandles(symbol, exchange, intervals[i], anchorIst, source, 1500);
        if (i < spotPaneCount - 1) await new Promise(r => setTimeout(r, 200));
      }
      if (!results[0]?.length) { toast.error('No history returned for that date / symbol / source'); return; }
      setPaneData(prev => { const n = [...prev]; for (let i = 0; i < spotPaneCount; i++) if (results[i]?.length) n[i] = results[i]; return n; });
      setLatestCandles(prev => { const n = [...prev]; for (let i = 0; i < spotPaneCount; i++) n[i] = undefined; return n; });
      // Position the playhead at the bar nearest the target date in pane-0.
      const np0 = results[0];
      let idx = np0.length - 1;
      let best = Infinity;
      for (let k = 0; k < np0.length; k++) { const d = Math.abs(np0[k].time - targetIst); if (d < best) { best = d; idx = k; } else if (np0[k].time > targetIst) break; }
      setReplay({ active: true, currentIdx: Math.max(0, idx), playing: false, speed: 1, source, deep: true });
      toast.success(`Jumped to ${new Date(dateUtcSec * 1000).toLocaleString('en-IN')} — use the Replay bar to step/play, or Exit to return to live`, { id: 'goto', duration: 6000 });
    } finally {
      setReplayDeepLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals, spotPaneCount, fetchReplayCandles, freeDataActive]);

  /* Exit replay; if a deep window was loaded, restore the live broker data. */
  const exitReplay = useCallback(() => {
    const wasDeep = replay.deep;
    setReplay({ ...REPLAY_OFF });
    if (wasDeep) loadSpotPanes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay.deep, loadSpotPanes]);

  /* Reset lazy-load extension counters whenever the underlying series resets
   * (symbol/interval/exchange change → fresh fetch in loadSpotPanes). */
  useEffect(() => { setPaneExtraBars([0, 0]); }, [symbol, exchange, intervals]);

  /* ───── fetch FNO running levels (per expiry tier) for the overlay ───── */
  const fcfg = (settings as any).fno || {};
  const fnoTiersKey = `${fcfg.tierD ? 'D' : ''}${fcfg.tierW ? 'W' : ''}${fcfg.tierM ? 'M' : ''}`;
  /* Toggle an FNO expiry tier (Day0/Week0/Month0) straight from the toolbar so the
     user can flip between expiry profiles without opening Chart Settings. Persists to
     the active chart-settings template (creates one if none exists). */
  const [fnoTierBusy, setFnoTierBusy] = useState(false);
  const setFnoTier = useCallback(async (tier: 'D' | 'W' | 'M', on: boolean) => {
    const key = tier === 'D' ? 'tierD' : tier === 'W' ? 'tierW' : 'tierM';
    const nextPayload: any = { ...(settings as any), fno: { ...(settings as any).fno, [key]: on } };
    setFnoTierBusy(true);
    try {
      if (activeChartTemplate) await updateChartTemplate(activeChartTemplate.id, { payload: nextPayload });
      else await createChartTemplate('My chart settings', nextPayload, true);
    } catch {
      toast.error('Could not update expiry tier', { duration: 4000 });
    } finally { setFnoTierBusy(false); }
  }, [settings, activeChartTemplate, updateChartTemplate, createChartTemplate]);
  // Expiry tiers are SINGLE-select (exactly one of D0 / W0 / M0) — selecting one turns
  // the others off, so the running FNO lines / PCR / OI stay on a single, clear expiry.
  const selectFnoTier = useCallback(async (tier: 'D' | 'W' | 'M') => {
    const nextPayload: any = { ...(settings as any), fno: { ...(settings as any).fno, tierD: tier === 'D', tierW: tier === 'W', tierM: tier === 'M' } };
    setFnoTierBusy(true);
    try {
      if (activeChartTemplate) await updateChartTemplate(activeChartTemplate.id, { payload: nextPayload });
      else await createChartTemplate('My chart settings', nextPayload, true);
    } catch {
      toast.error('Could not update expiry tier', { duration: 4000 });
    } finally { setFnoTierBusy(false); }
  }, [settings, activeChartTemplate, updateChartTemplate, createChartTemplate]);

  // Bar-replay upper time bound (chart-space epoch seconds, incl. the forming candle).
  // Defined HERE (before the FNO memos/fetches) so every FNO overlay — levels, PCR, IV,
  // left levels, and the option-profile rail — reflects the last VISIBLE candle during
  // replay instead of the live latest. undefined = not replaying (use live latest).
  const replayMaxTime = useMemo<number | undefined>(() => {
    if (!replay.active) return undefined;
    const pd = paneData[0];
    if (!pd?.length) return undefined;
    // CLAMP rather than bail. An out-of-range currentIdx (a stale index for one
    // frame after new pane data lands) used to return undefined, which does not
    // mean "no replay bound" downstream - it un-masks every future bar AND
    // restores the live LTP / mark price while the panel still reads ON.
    // Clamping keeps the chart internally consistent until the re-anchor lands.
    const c = pd[Math.max(0, Math.min(pd.length - 1, replay.currentIdx))];
    if (!c) return undefined;
    const iv = intervals[0] || 1;
    return (c.time as number) + iv * 60 - 1;
  }, [replay.active, replay.currentIdx, paneData, intervals]);
  // Same bound as a UTC epoch-ms (candle.time is epoch-sec + IST 19800) for the backend asOf.
  const replayAsOfMs = useMemo<number | undefined>(
    () => (replayMaxTime != null ? (replayMaxTime - 19800) * 1000 : undefined),
    [replayMaxTime],
  );

  useEffect(() => {
    // Only Synthiya still reads the levels-history PCR / IV series (the FNO overlay reads
    // /options/fno-series below).
    if (!symbol || !overlays.synthiya) { setFnoData(null); return; }
    // On a SYNTHETIC chart the FNO series belong to the UNDERLYING, not the name.
    const fnoSymbol = exchange === 'SYNTH' ? (synthRef.current?.underlying?.symbol || '') : symbol;
    if (!fnoSymbol) { setFnoData(null); return; }
    const tiers = [fcfg.tierD !== false ? 'D' : '', fcfg.tierW !== false ? 'W' : '', fcfg.tierM ? 'M' : ''].filter(Boolean).join(',') || 'D,W';
    const days = Math.max(1, Math.min(90, Number(fcfg.days) || 30));
    const fetchFno = async () => {
      try {
        const r = await api.get<any>(`/options/levels-history/${encodeURIComponent(fnoSymbol)}`, {
          params: { tiers, days }, headers: { _suppressToast: 'true' },
        });
        setFnoData((r.data?.data as FnoLevelsResp) || null);
      } catch { setFnoData(null); }
    };
    fetchFno();
    const id = window.setInterval(() => { if (!document.hidden) fetchFno(); }, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, overlays.synthiya, fnoTiersKey, fcfg.days]);

  /* ───── FNO per-candle series (2026-11-03) ─────
     ONE expiry at a time: a ROLLING tier (D0 = front contract, W0 = front weekly incl. the
     monthly, M0 = front monthly; each rolls at its settlement instant) or a PINNED date
     (that contract's own history). The pin is per underlying, kept in this browser, and a
     pin whose expiry has settled is dropped on the next load. */
  const fnoSym = isSynth ? (synth?.underlying?.symbol || '') : symbol;
  const fnoRollTier: 'D' | 'W' | 'M' = fcfg.tierM && !(fcfg.tierD !== false) && !(fcfg.tierW !== false) ? 'M'
    : (fcfg.tierW !== false) && !(fcfg.tierD !== false) ? 'W' : 'D';
  const fnoPinKey = fnoSym ? `chart.fnoPin.${fnoSym.toUpperCase()}` : '';
  const [fnoPin, setFnoPinState] = useState<string | null>(null);
  useEffect(() => {
    if (!fnoPinKey) { setFnoPinState(null); return; }
    let v: string | null = null;
    try { v = localStorage.getItem(fnoPinKey); } catch { /* storage blocked */ }
    // Settled (12:00 UTC Delta; NSE settles earlier, 10:00) → back to the rolling tier.
    if (v && (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Date.parse(`${v}T12:00:00Z`) <= Date.now())) {
      v = null;
      try { localStorage.removeItem(fnoPinKey); } catch { /* ignore */ }
    }
    setFnoPinState(v);
  }, [fnoPinKey]);
  const setFnoPin = useCallback((v: string | null) => {
    setFnoPinState(v);
    try { if (fnoPinKey) { if (v) localStorage.setItem(fnoPinKey, v); else localStorage.removeItem(fnoPinKey); } } catch { /* ignore */ }
  }, [fnoPinKey]);
  const fnoSel = fnoPin || fnoRollTier;
  // 5-min samples for sub-30m charts (split: the finer pane decides), 30-min otherwise.
  const fnoMinIv = layoutMode === 'split' ? Math.min(intervals[0] || 15, intervals[1] || 15) : (intervals[0] || 15);
  const fnoBucket = fnoMinIv >= 30 ? 1800 : 300;
  const fnoSrN = Math.max(1, Math.min(6, Number(fcfg.srCount ?? 1) || 1));
  // ≥3 γ walls always: the crosshair interpreter reads them even when the lines are off.
  const fnoWallsN = Math.max(3, fcfg.showWalls === true ? Math.min(6, Number(fcfg.wallCount ?? 2) || 0) : 0);
  const fnoDays = Math.max(1, Math.min(fnoBucket === 300 ? 10 : 90, Number(fcfg.days) || 30));
  useEffect(() => {
    if (!fnoOverlay || !fnoSym) { setFnoSeries(null); return; }
    let cancelled = false;
    const run = async () => {
      try {
        const r = await api.get<any>(`/options/fno-series/${encodeURIComponent(fnoSym)}`, {
          params: { sel: fnoSel, bucket: fnoBucket, days: fnoDays, sr: fnoSrN, walls: fnoWallsN },
          headers: { _suppressToast: 'true' },
        });
        if (!cancelled) setFnoSeries((r.data?.data as FnoSeriesResp) || null);
      } catch { if (!cancelled) setFnoSeries(null); }
    };
    run();
    // The collector analyses every ~5 min; the server caches 60 s.
    const id = window.setInterval(() => { if (!document.hidden) run(); }, 2 * 60 * 1000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [fnoOverlay, fnoSym, fnoSel, fnoBucket, fnoDays, fnoSrN, fnoWallsN]);

  /* Option Profile rail (item 3) — fetch the latest per-strike OI/ΔOI/GEX for the
   * nearest enabled tier whenever FNO is on (the rail itself renders only when a
   * profile mode is chosen, but the per-strike ΔOI also feeds the crosshair FNO
   * interpreter, so we fetch it regardless of the rail toggle). */
  const profileMode: string = fcfg.profile || 'off';
  useEffect(() => {
    if (!symbol || !fnoOverlay) { setOptionProfileRaw(null); return; }
    // Same expiry as the FNO lines: the rolling tier, or the pinned date.
    const params: any = { tier: fnoRollTier };
    if (fnoPin) params.expiry = fnoPin;
    // Bar Replay: fetch the profile AS-OF the last visible candle so the rail moves with
    // the playhead instead of showing the live latest.
    if (replayAsOfMs != null) params.asOf = Math.round(replayAsOfMs);
    const fetchProfile = async () => {
      try {
        const r = await api.get<any>(`/options/option-profile/${encodeURIComponent(symbol)}`, { params, headers: { _suppressToast: 'true' } });
        setOptionProfileRaw(r.data?.data?.supported === false ? null : (r.data?.data || null));
      } catch { setOptionProfileRaw(null); }
    };
    if (replayAsOfMs != null) {
      // Debounce so fast stepping / playback coalesces into one fetch per settle.
      const t = window.setTimeout(fetchProfile, 250);
      return () => window.clearTimeout(t);
    }
    fetchProfile();
    const id = window.setInterval(() => { if (!document.hidden) fetchProfile(); }, 60 * 1000); // 1-min refresh (live only)
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, fnoOverlay, fnoRollTier, fnoPin, replayAsOfMs]);

  /* Futures OI-by-price footprint — feeds the 4th profile lane + the crosshair/score
   * futures dimension. Fetched whenever FNO is on (fills as capture accumulates). */
  useEffect(() => {
    if (!symbol || !fnoOverlay) { setFuturesProfileRaw(null); return; }
    const fetchFut = async () => {
      try {
        const r: any = await optionsService.getFuturesProfile(symbol, 7);
        setFuturesProfileRaw(r?.data?.data?.supported === false ? null : (r?.data?.data || null));
      } catch { setFuturesProfileRaw(null); }
    };
    fetchFut();
    const id = window.setInterval(() => { if (!document.hidden) fetchFut(); }, 60 * 1000);
    return () => clearInterval(id);
  }, [symbol, fnoOverlay]);

  // Shape the profile for the chart primitive (mode + width + strikes).
  const optionProfileData = useMemo(() => {
    if (!fnoOverlay || profileMode === 'off' || !optionProfileRaw?.strikes?.length) return null;
    const mode = (['OI', 'OIChg', 'GEX', 'Combined'].includes(profileMode) ? profileMode : 'GEX');
    const futures = Array.isArray(futuresProfileRaw?.buckets) && futuresProfileRaw.buckets.length
      ? futuresProfileRaw.buckets.map((b: any) => ({ price: Number(b.price), net: Number(b.net) })).filter((b: any) => Number.isFinite(b.price))
      : undefined;
    return {
      mode, widthPx: Math.max(60, Math.min(220, Number(fcfg.profileWidth) || 110)),
      points: optionProfileRaw.strikes,
      spot: Number(optionProfileRaw.spot) || 0,
      maxPain: optionProfileRaw.maxPain ?? null,
      gexFlip: optionProfileRaw.gexFlip ?? null,
      futures,
      deltaFrom: optionProfileRaw.deltaFrom ?? null,
      deltaTo: optionProfileRaw.deltaTo ?? null,
    } as any;
  }, [fnoOverlay, profileMode, optionProfileRaw, futuresProfileRaw, fcfg.profileWidth]);

  /* ───── load instrument trigger levels ───── */
  useEffect(() => {
    if (!symbol) { setTriggerLevels({}); setInstrumentId(null); setSpotDecimals(2); setSpotTickSize(undefined); setWlLong(false); setWlShort(false); return; }
    (async () => {
      try {
        // pageSize=10 lets us prefer an exact symbol+exchange match instead of the first
        // fuzzy hit. Critical for Delta crypto routing where a similarly-prefixed NSE
        // instrument can otherwise grab the chart and saved levels never reach the
        // intended Delta row.
        let r = await api.get<any>('/instruments', { params: { search: symbol, exchange, pageSize: 10 } });
        let items = r.data?.data?.instruments || r.data?.instruments || [];
        let inst = items.find((i: any) => i.symbol === symbol && i.exchange === exchange);
        // No exact symbol+exchange row — the requested exchange is wrong for this symbol
        // (e.g. AAPLXUSD opened under BSE because of a stale localStorage filter). Re-query
        // without the exchange filter, take the unique exact-symbol match, and switch the
        // chart's exchange to that row's actual exchange so candles/levels load correctly.
        if (!inst) {
          const r2 = await api.get<any>('/instruments', { params: { search: symbol, pageSize: 10 } });
          const items2 = r2.data?.data?.instruments || r2.data?.instruments || [];
          const exact = items2.find((i: any) => i.symbol === symbol);
          if (exact && exact.exchange && String(exact.exchange).toUpperCase() !== exchange) {
            const fixedExchange = String(exact.exchange).toUpperCase();
            setExchange(fixedExchange);
            localStorage.setItem('chart_lastExchange', fixedExchange);
            inst = exact;
            items = items2;
          } else {
            inst = exact || items2[0] || items[0];
            if (inst) items = items2;
          }
        }
        inst = inst || items.find((i: any) => i.symbol === symbol) || items[0];
        if (inst) {
          setInstrumentId(inst.id);
          setInstrumentDetail({
            lotSize: inst.lotSize != null ? Number(inst.lotSize) : 1,
            leverage: inst.leverage != null ? Number(inst.leverage) : 1,
            eqInvestmentAmount: inst.eqInvestmentAmount != null ? Number(inst.eqInvestmentAmount) : 0,
            futInvestmentAmount: inst.futInvestmentAmount != null ? Number(inst.futInvestmentAmount) : 0,
            optInvestmentAmountBuy:  inst.optInvestmentAmountBuy  != null ? Number(inst.optInvestmentAmountBuy)  : 0,
            optInvestmentAmountSell: inst.optInvestmentAmountSell != null ? Number(inst.optInvestmentAmountSell) : 0,
            tradeSegment: inst.tradeSegment || null,
            tradeMode: inst.tradeMode || null,
            instrumentType: inst.instrumentType || null,
          });
          /* Decimals — delegate to the shared resolver.
           *
           * This block used to REIMPLEMENT the same priority ladder inline, and
           * the copies drifted: `resolveDecimals` gained a tick-size ceiling (a
           * stored precision can never exceed what the tick can express) and this
           * copy did not, so every Delta chart went on rendering
           * `80000.00000000` on the axis and `77222.11279897` on the LTP badge
           * while Trades and Alerts had already been corrected. One resolver, one
           * behaviour — see utils/decimalPrecision.ts. */
          const tick = inst.tickSize != null ? Number(inst.tickSize) : 0;
          const resolved = resolveDecimals({
            decimalPrecision: inst.decimalPrecision,
            tickSize: inst.tickSize,
            exchange,
          });
          setSpotDecimals(Math.max(0, Math.min(8, resolved)));
          setSpotTickSize(tick > 0 ? tick : undefined);
          setTriggerLevels({
            nbuy: inst.nbuy != null ? Number(inst.nbuy) : undefined,
            nshort: inst.nshort != null ? Number(inst.nshort) : undefined,
            bbuy: inst.bbuy != null ? Number(inst.bbuy) : undefined,
            bshort: inst.bshort != null ? Number(inst.bshort) : undefined,
          });
          setWlLong(!!inst.wlLong);
          setWlShort(!!inst.wlShort);
          setIsFav(!!inst.isFavourite);
        }
      } catch { /* ignore */ }
    })();
  }, [symbol, exchange]);

  /* ───── load HVN levels for current instrument ───── */
  useEffect(() => {
    if (!instrumentId) { setHvnLevels([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<any>(`/instruments/${instrumentId}/hvn`, {
          headers: { _suppressToast: 'true' },
        });
        if (cancelled) return;
        const rows = (r.data?.data || []) as Array<{ id: number; price: string | number; label?: string | null }>;
        setHvnLevels(rows.map(x => ({ id: x.id, price: Number(x.price), label: x.label })));
      } catch { /* ignore — endpoint will 404 if migration not yet applied */ }
    })();
    return () => { cancelled = true; };
  }, [instrumentId]);

  /* ───── load pending entries for current instrument (round 27) ─────
     Polled at the same 15s cadence as activeTrade so dragging stays
     responsive without hammering the API. The chart renders these as
     draggable lines (see priceLevelsProp). */
  useEffect(() => {
    if (isSynth) return; // synthetic charts render the ARMED trigger instead (below)
    if (!instrumentId && !ceInstrumentId && !peInstrumentId) {
      setPendingRows([]); setCePendingRows([]); setPePendingRows([]); return;
    }
    let cancelled = false;
    const mapRow = (p: any): PendingRow => ({ id: p.id, side: p.side, entryPrice: Number(p.entryPrice), status: p.status, entryIndex: p.entryIndex, entryCount: p.entryCount, stopLoss: p.stopLoss != null ? Number(p.stopLoss) : null, target: p.target != null ? Number(p.target) : null });
    const fetchPending = async () => {
      try {
        const r = await api.get<any>('/pending-entries', {
          params: { status: 'PENDING' },
          headers: { _suppressToast: 'true' },
        });
        if (cancelled) return;
        const all = (r.data?.data || []) as Array<any>;
        // Bucket by instrument so each pane (spot / CE / PE) renders its own
        // pending-entry lines.
        setPendingRows(instrumentId ? all.filter(p => p.instrumentId === instrumentId).map(mapRow) : []);
        setCePendingRows(ceInstrumentId ? all.filter(p => p.instrumentId === ceInstrumentId).map(mapRow) : []);
        setPePendingRows(peInstrumentId ? all.filter(p => p.instrumentId === peInstrumentId).map(mapRow) : []);
      } catch { /* non-critical */ }
    };
    fetchPending();
    const id = window.setInterval(() => { if (!document.hidden) fetchPending(); }, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instrumentId, ceInstrumentId, peInstrumentId, pendingBump, isSynth]);

  /* Synthetic chart — render the ARMED trigger as the pending-entry lines
     (entry draggable + shared SL/Target), reusing the exact same primitives.
     Drags route to PATCH /synthetic-symbols/:id/trigger (see the level-change
     handler); the × cancels the trigger. */
  useEffect(() => {
    if (!isSynth) return;
    const armed = (synth?.triggers && synth.triggers.length ? synth.triggers : (synth?.trigger ? [synth.trigger] : []))
      .filter(t => t.status === 'ARMED');
    setPendingRows(armed.map(t => ({
      id: t.id,
      side: t.side === 'LONG' ? 'BUY' as const : 'SHORT' as const,
      entryPrice: Number(t.entryPremium),
      status: 'PENDING',
      entryIndex: t.entryIndex ?? 1,
      entryCount: t.entryCount ?? armed.length,
      stopLoss: t.slPremium != null ? Number(t.slPremium) : null,
      target: t.targetPremium != null ? Number(t.targetPremium) : null,
    })));
    setCePendingRows([]); setPePendingRows([]);
  }, [isSynth, synth]);

  /* Live LTP poll for the focused CE/PE legs (Delta mark price). Feeds the LTP
     line on the option panes so it ticks with the premium instead of sitting at
     a stale candle close. Quad mode only; paused during replay. */
  useEffect(() => {
    if (layoutMode !== 'quad' || (!ceSymbol && !peSymbol) || replay.active) {
      setCeLtp(null); setPeLtp(null); return;
    }
    let cancelled = false;
    const fetchLeg = async (sym: string) => {
      try {
        const r = await api.get<any>('/charts/mark-price', { params: { symbol: sym }, headers: { _suppressToast: 'true' } });
        const mp = r.data?.data?.markPrice;
        return mp == null ? null : Number(mp);
      } catch { return null; }
    };
    const poll = async () => {
      if (ceSymbol) { const v = await fetchLeg(ceSymbol); if (!cancelled) setCeLtp(v); } else if (!cancelled) setCeLtp(null);
      if (peSymbol) { const v = await fetchLeg(peSymbol); if (!cancelled) setPeLtp(v); } else if (!cancelled) setPeLtp(null);
    };
    poll();
    const cadenceMs = Math.max(3, settings.liveUpdateSec) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) poll(); }, cadenceMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [layoutMode, ceSymbol, peSymbol, settings.liveUpdateSec, replay.active]);

  /* Auto-quantity formula — mirrors backend `calculateOrderQuantity`.
     Used to seed the qty field in the BUY/SHORT confirmation modal.
     The optional `side` argument selects the OPTIONS Buy vs Sell budget
     when the segment is OPTIONS (Round 67a). */
  const computeAutoQty = useCallback((entryPrice: number, side?: 'BUY' | 'SHORT', pane: TradeTarget = 'spot'): number => {
    // Synthetic symbol — "quantity" is basket LOTS; default 1, edited in the confirm.
    if (exchange === 'SYNTH') {
      // Same shape as the equity/futures rule (budget × leverage ÷ per-unit capital), but
      // against the underlying's OPTION budget: a CREDIT package consumes the OPT Sell
      // budget (margin), a DEBIT package the OPT Buy budget (premium paid). `marginPerLot`
      // is the priced per-lot capital for THIS structure, from the server.
      const sz = synthRef.current?.sizing;
      const isCredit = !!synthRef.current?.isCredit;
      const budget = isCredit ? Number(sz?.optInvestmentAmountSell || 0) : Number(sz?.optInvestmentAmountBuy || 0);
      const lev = Number(sz?.leverage) > 0 ? Number(sz!.leverage) : 1;
      const perLot = Number(sz?.marginPerLot) || 0;
      if (!(budget > 0) || !(perLot > 0)) return 1;   // no budget configured → 1 lot
      return Math.max(1, Math.floor((budget * lev) / perLot));
    }
    // Phase 2.1 — size a CE/PE leg from the UNDERLYING's (e.g. BTCUSD) option
    // investment budget, NOT the spot perp's futures budget/leverage:
    //   BUY  (long premium): contracts = budgetUSD / (premium × contractValue)
    //   SHORT (margin)     : contracts = budgetUSD / marginPerContract, where
    //     marginPerContract ≈ contractValue × (premium + IM% × spot). Delta's
    //     real short-option initial margin is dynamic, so IM% is a deliberately
    //     conservative estimate (under-deploys rather than over-leverages); Delta
    //     re-checks margin at order time and rejects if short. The dialog shows
    //     the figure for the user to review/edit before placing.
    if (pane !== 'spot') {
      if (!instrumentDetail || !Number.isFinite(entryPrice) || entryPrice <= 0) return 1;
      const budgetInr = side === 'SHORT'
        ? instrumentDetail.optInvestmentAmountSell
        : instrumentDetail.optInvestmentAmountBuy;
      if (!(budgetInr > 0)) return 1;
      const contractVal = pane === 'ce' ? ceLotSize : peLotSize;
      if (!(contractVal > 0)) return 1;
      const budgetUsd = budgetInr / USD_TO_INR;
      const spot = (paneData[0]?.length ? Number(paneData[0][paneData[0].length - 1].close) : 0) || spotPrice || 0;
      const SHORT_OPT_MARGIN_PCT = 0.10; // ≈10% of underlying notional (tunable estimate)
      const perContractUsd = side === 'SHORT'
        ? contractVal * (entryPrice + SHORT_OPT_MARGIN_PCT * spot)
        : contractVal * entryPrice;
      if (!(perContractUsd > 0)) return 1;
      return Math.max(1, Math.floor(budgetUsd / perContractUsd));
    }
    if (!instrumentDetail || !Number.isFinite(entryPrice) || entryPrice <= 0) return 1;
    const isUsd = (exchange || '').toUpperCase() === 'DELTA';
    const inrToUsd = isUsd ? USD_TO_INR : 1;
    const lot = instrumentDetail.lotSize > 0 ? instrumentDetail.lotSize : 1;
    const lev = instrumentDetail.leverage > 0 ? instrumentDetail.leverage : 1;
    // "Size orders as" — the same Auto rule the server uses (sizingInstrument.effectiveSegment):
    // an explicit Equity/Futures wins, else Futures on a derivatives venue, Equity otherwise.
    const seg = effectiveSegment(exchange, instrumentDetail.instrumentType, instrumentDetail.tradeSegment);
    const mode = instrumentDetail.tradeMode || 'Naked';
    if (seg === 'EQUITY' && mode === 'Naked' && instrumentDetail.eqInvestmentAmount > 0) {
      const buying = (instrumentDetail.eqInvestmentAmount / inrToUsd) * lev;
      return Math.max(1, Math.floor(buying / (entryPrice * lot)));
    }
    if (seg === 'FUTURES' && mode === 'Naked' && instrumentDetail.futInvestmentAmount > 0) {
      const buying = (instrumentDetail.futInvestmentAmount / inrToUsd) * lev;
      return Math.max(1, Math.floor(buying / (entryPrice * lot)));
    }
    return 1;
  }, [instrumentDetail, exchange, ceLotSize, peLotSize, paneData, spotPrice]);

  /* Seed per-entry quantities when entering the confirm stage. Total is sized
     from entry 1 (same formula as the single-entry flow), then split by the
     doubling rule across the captured levels. */
  useEffect(() => {
    if (orderCapture.stage === 'confirming' && !orderCapture.quantities && orderCapture.entries.length > 0) {
      const total = computeAutoQty(orderCapture.entries[0], orderCapture.side || undefined, orderCapture.targetPane || 'spot');
      const qtys = splitQuantity(total, orderCapture.entries.length);
      setOrderCapture(prev => ({ ...prev, quantities: qtys, sizedTotal: total }));
    }
  }, [orderCapture.stage, orderCapture.quantities, orderCapture.entries, orderCapture.side, orderCapture.targetPane, computeAutoQty]);

  /* Sanity-check the captured prices. SL must be on the losing side of
     entry; Target must be on the winning side. Off-side captures could
     mean the user clicked SL and Target in the wrong order (we ask for
     SL second, Target third) or simply mis-clicked. Blocks submit. */
  const orderCaptureWarning = useMemo<string | null>(() => {
    if (orderCapture.stage !== 'confirming') return null;
    const { side, entries, sl, target } = orderCapture;
    if (!entries.length || sl == null || target == null) return null;
    const E1 = entries[0];
    const EN = entries[entries.length - 1];
    if (side === 'BUY') {
      for (let i = 1; i < entries.length; i++) {
        if (entries[i] >= entries[i - 1]) {
          return `For BUY, each next entry must be BELOW the previous (averaging down). Entry ${i + 1} (${entries[i]}) ≥ Entry ${i} (${entries[i - 1]}).`;
        }
      }
      if (sl >= EN) return `For BUY, Stop-Loss (${sl}) must be BELOW the last entry (${EN}).`;
      if (target <= E1) return `For BUY, Target (${target}) must be ABOVE entry 1 (${E1}).`;
    } else if (side === 'SHORT') {
      for (let i = 1; i < entries.length; i++) {
        if (entries[i] <= entries[i - 1]) {
          return `For SHORT, each next entry must be ABOVE the previous. Entry ${i + 1} (${entries[i]}) ≤ Entry ${i} (${entries[i - 1]}).`;
        }
      }
      if (sl <= EN) return `For SHORT, Stop-Loss (${sl}) must be ABOVE the last entry (${EN}).`;
      if (target >= E1) return `For SHORT, Target (${target}) must be BELOW entry 1 (${E1}).`;
    }
    return null;
  }, [orderCapture]);

  /* G7 — non-blocking note when an entry sits on the BREAKOUT side of the
     live price. The backend auto-classifies such entries as STOP (fire when
     price breaks THROUGH the level) instead of the classic DIP behaviour
     (fire when price comes back to it), so tell the user which they'll get. */
  const orderCaptureInfo = useMemo<string | null>(() => {
    if (orderCapture.stage !== 'confirming' || orderCapture.orderType !== 'LIMIT') return null;
    const { side, entries, targetPane } = orderCapture;
    if (!side || !entries.length) return null;
    // Reference price must be the TARGET instrument's LTP — for a CE/PE leg the
    // option premium, NOT the spot/underlying price (comparing a ~₹350 premium
    // against the ~64,000 spot mis-classified every leg as a breakout STOP).
    const lastClose = (rows?: CandleRow[]) => (rows && rows.length ? rows[rows.length - 1].close : null);
    const ltp = targetPane === 'ce' ? lastClose(ceCandles)
      : targetPane === 'pe' ? lastClose(peCandles)
      : (latestCandles[0]?.close ?? lastClose(paneData[0]));
    if (ltp == null || !Number.isFinite(ltp)) return null;
    const stopLegs = entries
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => side === 'BUY' ? p > ltp : p < ltp);
    if (!stopLegs.length) return null;
    const legTxt = stopLegs.map(({ p, i }) => `E${i + 1} (${p})`).join(', ');
    const dirTxt = side === 'BUY' ? 'ABOVE' : 'BELOW';
    const fireTxt = side === 'BUY' ? 'RISES to' : 'FALLS to';
    return `${legTxt} ${stopLegs.length === 1 ? 'is' : 'are'} ${dirTxt} the current price (~${ltp}). ` +
      `${stopLegs.length === 1 ? 'It' : 'They'} will be queued as Breakout (STOP) ${stopLegs.length === 1 ? 'entry' : 'entries'} — firing when price ${fireTxt} the level, not immediately.`;
  }, [orderCapture, latestCandles, paneData, ceCandles, peCandles]);

  /* Per-stage risk/reward (Round 89). For each cumulative fill stage (after
     E1, after E1+E2, …) compute the blended average, the loss if SL hits and
     the gain if Target hits at that stage. INR conversion mirrors the
     SL/Target pills. The final stage = the fully-loaded position. */
  const orderStages = useMemo(() => {
    if (orderCapture.stage !== 'confirming') return null;
    const { side, entries, sl, target, quantities } = orderCapture;
    if (!entries.length || !quantities || quantities.length !== entries.length || sl == null || target == null) return null;
    const isLong = side === 'BUY';
    const isUsd = (exchange || '').toUpperCase() === 'DELTA';
    const usdToInr = isUsd ? USD_TO_INR : 1;
    const lot = instrumentDetail?.lotSize ?? 1;
    const rows: { index: number; price: number; qty: number; cumQty: number; avg: number; slLoss: number; tgtGain: number; rr: number | null }[] = [];
    let cumQty = 0, cumCost = 0;
    for (let i = 0; i < entries.length; i++) {
      cumQty += quantities[i];
      cumCost += quantities[i] * entries[i];
      const avg = cumQty > 0 ? cumCost / cumQty : entries[i];
      const slLoss = (isLong ? (avg - sl) : (sl - avg)) * cumQty * lot * usdToInr;
      const tgtGain = (isLong ? (target - avg) : (avg - target)) * cumQty * lot * usdToInr;
      const rr = slLoss > 0 ? Math.abs(tgtGain / slLoss) : null;
      rows.push({ index: i + 1, price: entries[i], qty: quantities[i], cumQty, avg, slLoss, tgtGain, rr });
    }
    return rows;
  }, [orderCapture, instrumentDetail, exchange]);

  /* Overall Risk : Reward = the fully-loaded (final) stage. Works for N=1
     (single entry) and N>1 (blended average across all levels). */
  const orderRiskReward = useMemo(() => {
    if (!orderStages || !orderStages.length) return null;
    const last = orderStages[orderStages.length - 1];
    return { risk: last.slLoss, gain: last.tgtGain, rr: last.rr };
  }, [orderStages]);

  /* Submit handler — POST /trades/quick-execute.
     LIMIT orders are NOT pushed to the broker (round 27). They are saved
     as a Pending Entry which the app monitors locally; when LTP crosses
     the entry level the app fires a MARKET order. The toast wording
     reflects which path the order took. */
  // Owner 2026-11-03: every BUY / SHORT confirm opens on LIVE — PAPER is chosen per trade.
  useEffect(() => { if (orderCapture.stage === 'confirming') setChartTradeMode('LIVE'); }, [orderCapture.stage]);

  const submitQuickOrder = useCallback(async () => {
    const { side, entries, sl, target, quantities, orderType, targetInstrumentId } = orderCapture;
    // ── SYNTHETIC symbol: the 3 captured levels ARM a combined-premium TRIGGER
    // (PAPER in this increment) — no broker order and no PendingEntry row. The
    // backend monitor fires the multi-leg basket when the premium crosses.
    if (isSynthRef.current && synthRef.current) {
      const sd = synthRef.current;
      if (!side || !entries.length || sl == null || target == null) return;
      const mode = synthModeRef.current;
      // LIVE = real multi-leg orders when the trigger fires — explicit sign-off.
      if (mode === 'LIVE') {
        const lotsN = Math.max(1, Math.floor(quantities?.[0] || 1));
        const ok = window.confirm(
          `Arm a LIVE ${side} trigger on ${sd.name}?\n\n` +
          `When the combined premium crosses ${entries[0]}, the app will place REAL multi-leg option orders (×${lotsN} lot${lotsN === 1 ? '' : 's'}) on your broker — ` +
          `after a wallet margin check and your kill-switch / daily-loss gates.\n\nSL ${sl} · Target ${target} · auto square-off 15 min before expiry.`,
        );
        if (!ok) return;
      }
      setOrderCapture(prev => ({ ...prev, submitting: true }));
      try {
        // E1..EN each carry their own lots; SL/Target are shared across the group.
        const entryRows = entries.map((p, i) => ({ entryPremium: p, lots: Math.max(1, Math.floor(quantities?.[i] || 1)) }));
        const r = await syntheticSymbolsService.armTrigger(sd.id, {
          side: side === 'BUY' ? 'LONG' : 'SHORT',
          entries: entryRows, slPremium: sl, targetPremium: target, tradingMode: mode,
        });
        if (!r.ok) throw new Error(r.reason || 'Could not arm the trigger');
        const totalLots = entryRows.reduce((a, e) => a + e.lots, 0);
        toast.success(entryRows.length === 1
          ? `⏳ [${mode}] ${side} trigger armed on ${sd.name} @ ${entries[0]} ×${totalLots} lot${totalLots === 1 ? '' : 's'} — the multi-leg basket fires when the combined premium crosses (${r.direction}).`
          : `⏳ [${mode}] ${side} scale-in armed on ${sd.name} — ${entryRows.length} entries (${entryRows.map(e => `${e.entryPremium}×${e.lots}`).join(', ')}), shared SL ${sl} / target ${target}.`,
          { id: 'quick-order', duration: 9000 });
        setOrderCapture({ stage: 'idle', side: null, entries: [], entryCount: 1, orderType: 'LIMIT' });
        setChartTool(null);
        const d = await syntheticSymbolsService.getOne(sd.id);
        if (d) setSynth(d);
        try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
      } catch (err: any) {
        toast.error(err?.message || 'Could not arm the trigger', { id: 'quick-order', duration: 8000 });
        setOrderCapture(prev => ({ ...prev, submitting: false }));
      }
      return;
    }
    // Phase 2 — route to the captured target leg (CE/PE) or the spot instrument.
    const orderInstrumentId = targetInstrumentId ?? instrumentId;
    if (!orderInstrumentId || !side || !entries.length || sl == null || target == null || !quantities) return;
    const tradingMode = chartTradeMode;
    const tag = tradingMode === 'PAPER' ? '📝 PAPER ' : '';
    setOrderCapture(prev => ({ ...prev, submitting: true }));
    try {
      if (entries.length === 1) {
        // Single entry — unchanged path (preserves N=1 behaviour exactly).
        await api.post('/trades/quick-execute', {
          instrumentId: orderInstrumentId, side,
          entryPrice: orderType === 'LIMIT' ? entries[0] : undefined,
          stopLoss: sl, target, quantity: quantities[0], orderType, tradingMode,
        });
        toast.success(
          orderType === 'LIMIT'
            ? `${tag}${side} queued — entry @ ${entries[0]}. ${tradingMode === 'PAPER' ? 'Fills (simulated) when price reaches it.' : 'Will fire MARKET when reached.'}`
            : tradingMode === 'PAPER' ? `${tag}${side} filled at the live price (simulated — no broker order)` : `${side} order placed`,
          { id: 'quick-order' },
        );
      } else {
        // Multi-entry scale-in group. Only entry 1 may be MARKET.
        await api.post('/trades/quick-execute-multi', {
          instrumentId: orderInstrumentId, side, stopLoss: sl, target,
          entries: entries.map((p, i) => ({ price: p, quantity: quantities[i], orderType: i === 0 ? orderType : 'LIMIT' })),
          tradingMode,
        });
        toast.success(
          `${tag}${side} scale-in queued — ${entries.length} entries (${orderType === 'MARKET' ? 'E1 market, rest resting' : 'all resting'}).`,
          { id: 'quick-order' },
        );
      }
      setOrderCapture({ stage: 'idle', side: null, entries: [], entryCount: 1, orderType: 'LIMIT' });
      setChartTool(null);
      setActiveTradeBump(b => b + 1);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Order failed';
      toast.error(msg, { id: 'quick-order' });
      setOrderCapture(prev => ({ ...prev, submitting: false }));
    }
  }, [instrumentId, orderCapture, chartTradeMode]);

  const cancelQuickOrder = useCallback(() => {
    setOrderCapture({ stage: 'idle', side: null, entries: [], entryCount: 1, orderType: 'LIMIT' });
    setChartTool(null);
  }, []);

  /* Order capture — wire the click handler via ref so handleChartClickSpot's
     useCallback doesn't need to depend on this state and re-create on every
     stage change. The ref reads the latest setter via setState updater. */
  useEffect(() => {
    orderCaptureRef.current = (price: number, ctx) => {
      setOrderCapture(prev => {
        if (prev.stage === 'idle' || prev.stage === 'confirming') return prev;
        // Dynamic targeting: the FIRST capture click locks the target instrument
        // to whichever pane was clicked; later clicks from a DIFFERENT pane are
        // ignored so a multi-click BUY/SHORT can't straddle two instruments.
        if (ctx && prev.targetPane && prev.targetPane !== ctx.pane) return prev;
        const lock = (ctx && prev.targetPane == null)
          ? { targetPane: ctx.pane, targetInstrumentId: ctx.instrumentId, targetSymbol: ctx.symbol, targetDecimals: ctx.decimals }
          : {};
        if (prev.stage === 'awaiting_entry') {
          // Collect entry levels until N are captured, then advance to SL.
          const entries = [...prev.entries, price];
          if (entries.length >= prev.entryCount) return { ...prev, ...lock, entries, stage: 'awaiting_sl' };
          return { ...prev, ...lock, entries };
        }
        if (prev.stage === 'awaiting_sl') return { ...prev, ...lock, sl: price, stage: 'awaiting_target' };
        if (prev.stage === 'awaiting_target') return { ...prev, ...lock, target: price, stage: 'confirming' };
        return prev;
      });
    };
  }, []);

  /* When the user toggles BUY or SHORT, kick off the capture flow at the
     entry stage. Toggling off mid-capture cancels everything. */
  useEffect(() => {
    if (chartTool === 'buy' || chartTool === 'short') {
      setOrderCapture(prev => {
        // Don't reset if already mid-flow with same side
        if (prev.side === (chartTool === 'buy' ? 'BUY' : 'SHORT') && prev.stage !== 'idle') return prev;
        // Synthetic triggers are single-entry (no scale-in groups on a combo premium).
        // Synthetic symbols support the SAME scale-in split as equities/futures — E1..EN
        // on the combined premium, one shared SL/Target (backend arms them as a group).
        const n = Math.max(1, Math.min(5, entrySplitCount));
        // Target instrument is NOT frozen here — it's set by the FIRST capture
        // click (whichever pane the user clicks). See orderCaptureRef below.
        return {
          stage: 'awaiting_entry', side: chartTool === 'buy' ? 'BUY' : 'SHORT',
          entries: [], entryCount: n, orderType: 'LIMIT',
        };
      });
    } else if (chartTool == null) {
      setOrderCapture(prev => prev.stage === 'idle' ? prev : { stage: 'idle', side: null, entries: [], entryCount: 1, orderType: 'LIMIT' });
    }
  }, [chartTool, entrySplitCount]);

  /* HVN CRUD helpers */
  const addHvn = useCallback(async (price: number) => {
    if (!instrumentId || !Number.isFinite(price) || price <= 0) return;
    try {
      const r = await api.post<any>(`/instruments/${instrumentId}/hvn`, { price });
      const row = r.data?.data;
      if (row) setHvnLevels(prev => [...prev, { id: row.id, price: Number(row.price), label: row.label }]);
      toast.success(`HVN added at ${price}`, { id: `hvn-add-${row?.id ?? Date.now()}` });
    } catch { toast.error('Failed to add HVN'); }
    // One-click semantics: deactivate the HVN tool after a single capture so
    // a second click on the chart does not silently create another HVN. The
    // user must click the HVN chip again to add another level.
    setChartTool(null);
  }, [instrumentId]);

  const updateHvn = useCallback(async (id: number, price: number) => {
    if (!Number.isFinite(price) || price <= 0) return;
    try {
      await api.put(`/hvn/${id}`, { price });
      setHvnLevels(prev => prev.map(x => x.id === id ? { ...x, price } : x));
    } catch { toast.error('Failed to update HVN'); }
  }, []);

  const removeHvn = useCallback(async (id: number) => {
    try {
      await api.delete(`/hvn/${id}`);
      setHvnLevels(prev => prev.filter(x => x.id !== id));
      toast.success('HVN removed', { id: `hvn-del-${id}` });
    } catch { toast.error('Failed to delete HVN'); }
  }, []);

  /* ───── load active trade (OPEN or DRAFT) for current symbol —
       drives SL/TSL/Target overlay AND the dragability of those lines.
       DRAFT covers LIMIT entries that haven't filled yet — the user
       must still be able to see and drag SL/Target while they wait.

       Drag-revert race protection (round 25): the 15s poll used to
       blindly replace activeTrade with the server's view. If a write
       (drag-end PUT or Dashboard edit) hadn't fully propagated to the
       next GET — e.g. positionSync ran a `save(trade)` between the user's
       PUT and the next poll — the server returned an older value and
       the user's edit appeared to "snap back".

       Two guards now apply:
         1. A per-field edit-lock window. After a successful user write
            we stamp the field's edit time. Subsequent polls keep the
            local value for that field for the next 30s, even if the
            server returns something different. After 30s the server
            is authoritative again.
         2. Drag-pause. While the user is mid-drag (TradingChart's
            draggingTypeRef is set), the chart calls back through
            setIsDragging(true) and the poll skips the field-replace
            step. The drag handler unsets it on mouse-up. This stops
            the 15s tick from forcing a re-render mid-drag and
            collapsing the user's gesture.
  */
  type EditableField = 'manualStopLoss' | 'target' | 'entryPrice';
  const editLockRef = useRef<Record<EditableField, number>>({ manualStopLoss: 0, target: 0, entryPrice: 0 });
  const isDraggingRef = useRef<boolean>(false);
  const EDIT_LOCK_MS = 30_000;
  // Sticky operative-SL override (SL-drag-revert fix). After a CONFIRMED manual
  // SL drag we pin the SL line to the dragged price for a window, regardless of
  // which server field (currentTrailingSl / manualStopLoss / stopLoss) the next
  // poll or candle refresh reports. Cleared early once the server's operative SL
  // converges to it. This defeats every revert vector at the display layer.
  const slOverrideRef = useRef<{ price: number; until: number } | null>(null);
  useEffect(() => {
    if (!symbol) { setActiveTrade(null); return; }
    let cancelled = false;
    const fetchActive = async () => {
      // While a drag is in progress, skip the poll entirely. The drag
      // gesture's mouse-move handlers set local chart state directly;
      // overwriting activeTrade mid-drag visibly snaps the line back.
      if (isDraggingRef.current) return;
      try {
        const r = await api.get<any>('/trades', { params: { status: 'OPEN,DRAFT' } });
        const list: any[] = r.data?.data?.data || r.data?.data || [];
        const sym = symbol.toUpperCase();
        // A REAL position on this symbol wins the overlay; a PAPER one shows only when
        // there is no real one (the two books are independent and can coexist).
        const matches = list.filter((t: any) => {
          const st = String(t.status || '').toUpperCase();
          return String(t.symbol || '').toUpperCase() === sym && (st === 'OPEN' || st === 'DRAFT');
        });
        const match = matches.find((t: any) => !t.isSimulation) || matches[0];
        if (cancelled) return;
        const now = Date.now();
        const isLocked = (f: EditableField) => (now - (editLockRef.current[f] || 0)) < EDIT_LOCK_MS;
        setActiveTrade(prev => {
          if (!match) return null;
          const next: ActiveTrade = {
            id: match.id,
            symbol: match.symbol,
            side: match.side,
            status: (String(match.status || '').toUpperCase() === 'DRAFT') ? 'DRAFT' : 'OPEN',
            stopLoss: match.stopLoss != null ? Number(match.stopLoss) : null,
            manualStopLoss: match.manualStopLoss != null ? Number(match.manualStopLoss) : null,
            target: match.target != null ? Number(match.target) : null,
            currentTrailingSl: match.currentTrailingSl != null ? Number(match.currentTrailingSl) : null,
            entryPrice: match.averageEntryPrice != null ? Number(match.averageEntryPrice) : (match.entryPrice != null ? Number(match.entryPrice) : null),
            quantity: match.quantity != null ? Number(match.quantity) : null,
            // unrealizedPnl is stored in INR for Delta (positionSync × 85) and INR
            // for NSE/BSE/NFO/MCX, so the display unit is always INR.
            unrealizedPnl: match.unrealizedPnl != null ? Number(match.unrealizedPnl) : null,
            pnlCurrency: 'INR',
            lotSize: match.lotSize != null ? Number(match.lotSize) : 1,
            isSimulation: !!match.isSimulation,
          };
          // Preserve user-edited fields for the lock window. If the same
          // trade is loaded (id matches), prefer the local value when its
          // edit-lock is still active.
          if (prev && prev.id === next.id) {
            // While the user's SL edit is locked, preserve the WHOLE operative-SL
            // triple from the local state — not just manualStopLoss. Otherwise a
            // stale/re-ratcheted currentTrailingSl (or a momentarily-missing
            // manualStopLoss in the list payload) flips the precedence in
            // priceLevelsProp and the line snaps back to the initial SL.
            if (isLocked('manualStopLoss')) {
              next.manualStopLoss    = prev.manualStopLoss;
              next.currentTrailingSl = prev.currentTrailingSl;
              next.stopLoss          = prev.stopLoss;
            }
            if (isLocked('target'))         next.target         = prev.target;
            if (isLocked('entryPrice'))     next.entryPrice     = prev.entryPrice;
          }
          return next;
        });
      } catch { if (!cancelled) setActiveTrade(null); }
    };
    fetchActive();
    const id = window.setInterval(() => { if (!document.hidden) fetchActive(); }, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, activeTradeBump]);

  /* Called by TradingChart when a drag begins / ends — pauses the 15s
     activeTrade poll while the user is interacting with the line. */
  const setIsDragging = useCallback((v: boolean) => { isDraggingRef.current = v; }, []);

  /* ───── save trigger level to backend (pane-aware) ───── */
  type TriggerPane = 'spot' | 'ce' | 'pe';
  const saveTriggerLevelFor = useCallback(async (pane: TriggerPane, type: TriggerType, price: number) => {
    const id = pane === 'spot' ? instrumentId : pane === 'ce' ? ceInstrumentId : peInstrumentId;
    if (!id) return;
    try {
      await api.put(`/instruments/${id}`, { [type]: price });
      if (pane === 'spot') setTriggerLevels(prev => ({ ...prev, [type]: price }));
      else if (pane === 'ce') setCeTriggerLevels(prev => ({ ...prev, [type]: price }));
      else setPeTriggerLevels(prev => ({ ...prev, [type]: price }));
      const laneLabel = pane === 'spot' ? '' : pane === 'ce' ? ' [CE]' : ' [PE]';
      toast.success(`${type.toUpperCase()}${laneLabel} set at ${price}`, { id: `trigger-${pane}-${type}` });
    } catch {
      toast.error(`Failed to save ${type.toUpperCase()}`);
    }
    setTriggerMode(null);
  }, [instrumentId, ceInstrumentId, peInstrumentId]);

  const removeTriggerLevel = useCallback(async (type: TriggerType) => {
    if (!instrumentId) return;
    try {
      await api.put(`/instruments/${instrumentId}`, { [type]: null });
      setTriggerLevels(prev => { const n = { ...prev }; delete n[type]; return n; });
    } catch { /* ignore */ }
  }, [instrumentId]);

  const removeCeTriggerLevel = useCallback(async (type: TriggerType) => {
    if (!ceInstrumentId) return;
    try {
      await api.put(`/instruments/${ceInstrumentId}`, { [type]: null });
      setCeTriggerLevels(prev => { const n = { ...prev }; delete n[type]; return n; });
    } catch { /* ignore */ }
  }, [ceInstrumentId]);

  const removePeTriggerLevel = useCallback(async (type: TriggerType) => {
    if (!peInstrumentId) return;
    try {
      await api.put(`/instruments/${peInstrumentId}`, { [type]: null });
      setPeTriggerLevels(prev => { const n = { ...prev }; delete n[type]; return n; });
    } catch { /* ignore */ }
  }, [peInstrumentId]);

  /* ───── watchlist toggle ───── */
  const toggleWatchlist = useCallback(async (side: 'wlLong' | 'wlShort') => {
    if (!instrumentId) return;
    const newVal = side === 'wlLong' ? !wlLong : !wlShort;
    try {
      await api.put(`/instruments/${instrumentId}`, { [side]: newVal });
      if (side === 'wlLong') setWlLong(newVal);
      else setWlShort(newVal);
      toast.success(`${symbol} ${newVal ? 'added to' : 'removed from'} WL ${side === 'wlLong' ? 'Long' : 'Short'}`, { id: `wl-${side}` });
    } catch { toast.error('Failed to update watchlist'); }
  }, [instrumentId, wlLong, wlShort, symbol]);

  const toggleFavourite = useCallback(async () => {
    if (!instrumentId) return;
    const newVal = !isFav;
    setIsFav(newVal); // optimistic
    const up = (symbol || '').toUpperCase();
    setFavSet(prev => { const n = new Set(prev); if (newVal) n.add(up); else n.delete(up); return n; });
    try {
      await api.put(`/instruments/${instrumentId}`, { isFavourite: newVal });
      toast.success(`${symbol} ${newVal ? 'marked ★ favourite' : 'removed from favourites'}`, { id: 'fav-toggle' });
    } catch { setIsFav(!newVal); setFavSet(prev => { const n = new Set(prev); if (newVal) n.delete(up); else n.add(up); return n; }); toast.error('Failed to update favourite'); }
  }, [instrumentId, isFav, symbol]);

  // Favourited-symbols set (★ markers in the symbol list). Loaded once; updated on toggle.
  const loadFavSet = useCallback(async () => {
    try {
      const r: any = await api.get('/instruments', { params: { favourites: 'true', pageSize: 1000 }, headers: { _suppressToast: 'true' } });
      const items: any[] = r.data?.data?.data || r.data?.data || r.data?.instruments || [];
      setFavSet(new Set((Array.isArray(items) ? items : []).map((i: any) => String(i.symbol || '').toUpperCase())));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadFavSet(); }, [loadFavSet]);

  // Toggle favourite for ANY list row (no instrument id at hand) — symbol-based.
  const toggleFavSymbol = useCallback(async (sym: string, exch: string) => {
    const up = (sym || '').toUpperCase();
    const newVal = !favSet.has(up);
    setFavSet(prev => { const n = new Set(prev); if (newVal) n.add(up); else n.delete(up); return n; });
    if (up === (symbol || '').toUpperCase()) setIsFav(newVal);
    try { await api.post('/instruments/favourite', { symbol: sym, exchange: exch, isFavourite: newVal }); }
    catch {
      setFavSet(prev => { const n = new Set(prev); if (newVal) n.delete(up); else n.add(up); return n; });
      toast.error('Failed to update favourite');
    }
  }, [favSet, symbol]);

  /* ───── chart click handlers for trigger placement (per pane) ─────
     The price arriving here is ALREADY tick-snapped by TradingChart.snapPrice
     so we must NOT re-round it (was the TSTUSD calibration bug — a hard-coded
     `Math.round(price * 100) / 100` truncated low-decimal symbols to 2dp).

     Routing on the spot pane:
       - triggerMode set      → save NBUY/NSHORT/BBUY/BSHORT
       - chartTool === 'hvn'  → create a new HVN row at the clicked price
       - chartTool === 'buy'/'short' → 3-click capture flow (handled in #5) */
  /* Chart-side strategy builder (4-quad). Clicking a Build chip ARMS a level
     pick on the SPOT chart (1–2 clicks depending on the strategy). The picked
     levels are snapped to strikes → legs → the builder dialog opens seeded with
     them. NOT a broker order — the dialog previews payoff/economics and lets the
     user adjust strikes/lots before Approve & Execute. */
  const openStrategyBuilder = useCallback((strategyId: string, label: string) => {
    if (!nfoUnderlying) { toast.error('Load the option chain first'); return; }
    if (!optionStrikes.length) { toast.error('Option chain not loaded yet'); return; }
    if (!ceExpiry) { toast.error('Select an expiry first'); return; }
    // Auto-built strategies (e.g. premium-matched ratio back-spreads) need no click-to-pick —
    // the backend chooses the strikes. Open the review dialog directly with no seed legs.
    if (AUTO_BUILD_STRATEGIES.has(strategyId)) {
      setStrategyCapture(null);
      setChartTool(null);
      setDialogCard(null);
      setBuilderConfig({ strategyId, label });
      return;
    }
    const spec = STRATEGY_PICKS[strategyId];
    if (!spec) { toast.error(`Unknown strategy ${strategyId}`); return; }
    // Arm the capture; clicks land on the spot pane (handleChartClickSpot).
    setDialogCard(null);
    setBuilderConfig(null);
    setChartTool('strategy');
    setStrategyCapture({ strategyId, label, clicksNeeded: spec.clicks, roles: spec.roles, picks: [] });
    toast(`${label}: click the Spot chart to set ${spec.roles[0]} (1 of ${spec.clicks})`, { id: 'strat-capture', icon: '🎯', duration: 4000 });
  }, [nfoUnderlying, optionStrikes, ceExpiry]);

  /* Cancel an in-progress strategy capture. */
  const cancelStrategyCapture = useCallback(() => {
    setStrategyCapture(null);
    setChartTool(prev => (prev === 'strategy' ? null : prev));
  }, []);

  /* Strategy capture click handler, wired via ref so the spot-click callback
     needn't depend on the capture state. Each spot click appends a level; once
     clicksNeeded are collected, snap → legs → open the builder seeded with them. */
  useEffect(() => {
    strategyCaptureRef.current = (price: number) => {
      setStrategyCapture(prev => {
        if (!prev) return prev;
        const picks = [...prev.picks, price];
        if (picks.length < prev.clicksNeeded) {
          toast(`${prev.label}: click the Spot chart to set ${prev.roles[picks.length]} (${picks.length + 1} of ${prev.clicksNeeded})`, { id: 'strat-capture', icon: '🎯', duration: 4000 });
          return { ...prev, picks };
        }
        // Enough levels — build legs and open the dialog (or reject + re-arm).
        const res = buildLegsFromPicks(prev.strategyId, picks, optionStrikes, ceExpiry);
        if (!res.ok || !res.legs) {
          toast.error(res.reason || 'Could not build the strategy from those levels — pick again', { id: 'strat-capture', duration: 6000 });
          return { ...prev, picks: [] }; // keep armed for a fresh pick
        }
        setBuilderConfig({ strategyId: prev.strategyId, label: prev.label, legs: res.legs });
        setChartTool(null);
        return null; // disarm
      });
    };
  }, [optionStrikes, ceExpiry]);

  /* If the user switches to a different chart tool while a strategy capture is
     armed, cancel the capture so stray clicks don't build a basket. */
  useEffect(() => {
    if (chartTool !== 'strategy') setStrategyCapture(prev => (prev ? null : prev));
    if (chartTool !== 'outlook') setOutlookCapture(prev => (prev ? null : prev));
  }, [chartTool]);

  /* Arm an outlook capture: 1 level (floor/ceiling/break) or 2 (range). */
  const OUTLOOK_LABEL: Record<string, string> = {
    WONT_FALL_BELOW: "Won't fall below", WONT_RISE_ABOVE: "Won't rise above",
    WILL_BREAK_ABOVE: 'Will break above', WILL_BREAK_BELOW: 'Will break below',
    WILL_BREACH: 'Will break out either way', STAY_IN_RANGE: 'Will stay in range',
  };
  // Per-view ladder shape (drives the draw flow) + whether staged campaigns are supported here.
  const ladderShape = (v: string): 'dir-down' | 'dir-up' | 'range' | 'brk-up' | 'brk-dn' | 'brk-both' =>
    v === 'WONT_FALL_BELOW' ? 'dir-down' : v === 'WONT_RISE_ABOVE' ? 'dir-up' : v === 'STAY_IN_RANGE' ? 'range'
    : v === 'WILL_BREAK_ABOVE' ? 'brk-up' : v === 'WILL_BREAK_BELOW' ? 'brk-dn' : 'brk-both';
  const campaignsSupported = /^(BTC|ETH)/.test((nfoUnderlying || symbol || '').toUpperCase());
  const resetDraw = useCallback(() => { setDrawnStages([]); setDrawnUp([]); setDrawnDn([]); setDrawnRU(null); setDrawnRL(null); setDrawnDanger(null); }, []);
  const openOutlookCapture = useCallback((mode: OutlookCapMode) => {
    if (!nfoUnderlying) { toast.error('This symbol has no option chain (FNO) loaded'); return; }
    setOutlookDialog(null);
    setChartTool('outlook');
    const clicksNeeded = mode === 'STAY_IN_RANGE' ? 2 : 1;
    setOutlookCapture({ mode, clicksNeeded, picks: [] });
    resetDraw();
    // STAGED is the default (per your ask) when campaigns are supported here. Initial phase by view.
    const staged = /^(BTC|ETH)/.test((nfoUnderlying || symbol || '').toUpperCase());
    setOutlookMode(staged ? 'staged' : 'single');
    const shape = ladderShape(mode);
    setDrawPhase(shape === 'dir-down' || shape === 'dir-up' ? 'entries' : shape === 'brk-dn' ? 'rangeLower' : 'rangeUpper');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfoUnderlying, symbol, resetDraw]);

  /* Outlook capture click handler (wired via ref like the strategy capture). */
  useEffect(() => {
    outlookCaptureRef.current = (price: number) => {
      setOutlookCapture(prev => {
        if (!prev) return prev;
        const picks = [...prev.picks, price];
        if (picks.length < prev.clicksNeeded) {
          toast(`${OUTLOOK_LABEL[prev.mode]}: click the chart to set the UPPER level (2 of 2)`, { id: 'outlook-capture', icon: '🎯', duration: 4000 });
          return { ...prev, picks };
        }
        // Enough levels — open the builder dialog seeded with the mode + level(s).
        const sorted = [...picks].sort((a, b) => a - b);
        const dec = spotDecimals ?? 2;
        const rnd = (v: number) => Number(v.toFixed(dec));
        if (prev.mode === 'STAY_IN_RANGE') setOutlookDialog({ mode: prev.mode, level: rnd(sorted[0]), level2: rnd(sorted[1]) });
        else setOutlookDialog({ mode: prev.mode, level: rnd(picks[0]) });
        setChartTool(null);
        return null; // disarm
      });
    };
  }, [spotDecimals]);

  // The basket the PayOff-Zones overlay locks to — user-selected (persisted per
  // underlying), defaulting to the first open basket for this symbol.
  const basketBase = (nfoUnderlying || symbol || '').toUpperCase().replace(/(USDT|USDC|USD|INR|PERP)$/, '');
  const [selectedBasketId, setSelectedBasketId] = usePersistedState<number | null>(`chart.selectedBasket.${basketBase || 'none'}`, null);
  const openBasket = useMemo(() => {
    if (!openBaskets.length) return null;
    return openBaskets.find((b: any) => b.id === selectedBasketId) || openBaskets[0];
  }, [openBaskets, selectedBasketId]);

  /* Poll for the OPEN baskets on this underlying so the Combined pane can lock to
     one + let the user drag its SL/Target. Generalized to ANY 2-leg structure
     (straddle / strangle / call & put credit/debit spreads) — match on the
     underlying tag in the leg symbols, not a CE+PE shape. */
  useEffect(() => {
    // Match OPEN baskets to the CURRENT chart symbol (any layout) so their payoff
    // zones / break-evens overlay on the underlying — in quad we know the underlying;
    // elsewhere derive the base from the chart symbol (BTCUSD → BTC).
    const baseSym = (nfoUnderlying || symbol || '').toUpperCase().replace(/(USDT|USDC|USD|INR|PERP)$/, '');
    if (!baseSym) { setOpenBaskets([]); return; }
    let cancelled = false;
    const tag = `-${baseSym}-`;
    const fetchOpen = async () => {
      try {
        const r: any = await optionsService.getBaskets('EXECUTED');
        if (cancelled) return;
        const rows: any[] = r?.data?.data || r?.data || [];
        // Any OPEN (executed, not closed) basket for this underlying — don't require
        // monitorEnabled, so every running basket appears in the payoff selector.
        const matches = rows.filter(b => !b.closedAt && b.entryCredit != null
          && Array.isArray(b.legFills) && b.legFills.length >= 2
          && b.legFills.every((l: any) => String(l.symbol || '').toUpperCase().includes(tag)));
        setOpenBaskets(matches);
      } catch { /* keep last */ }
    };
    fetchOpen();
    const id = window.setInterval(() => { if (!document.hidden) fetchOpen(); }, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [layoutMode, nfoUnderlying, symbol]);

  /* Active STAGED CAMPAIGNS for the charted underlying — powers the on-chart campaign badge
     (adjust) + the "Stage campaign" launcher. Polled every 30s. */
  useEffect(() => {
    const baseSym = (nfoUnderlying || symbol || '').toUpperCase().replace(/(USDT|USDC|USD|INR|PERP)$/, '');
    if (!baseSym) { setOpenCampaigns([]); return; }
    let cancelled = false;
    const fetchC = async () => {
      try {
        const r: any = await optionsService.listCampaigns();
        if (cancelled) return;
        const rows: any[] = r?.data?.data || [];
        setOpenCampaigns(rows.filter((c: any) => c.status === 'ACTIVE' && String(c.symbol || '').toUpperCase() === baseSym));
      } catch { /* keep last */ }
    };
    fetchC();
    const id = window.setInterval(() => { if (!document.hidden) fetchC(); }, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [nfoUnderlying, symbol]);

  const openCampaignAdjust = useCallback((id: number) => { setCampaignAdjustId(id); setCampaignAdjustOpen(true); }, []);
  // On-chart P&L badge click → campaign adjust when the tranche belongs to a campaign, else basket adjust.
  const handleBadgeClick = useCallback(() => {
    if (!openBasket) return;
    if (openBasket.campaignId) openCampaignAdjust(Number(openBasket.campaignId));
    else setAdjustOpen(true);
  }, [openBasket, openCampaignAdjust]);
  const squareOffBasket = useCallback(async (basket: any) => {
    if (!basket) return;
    if (!window.confirm(`Square off basket #${basket.id} now? This closes ALL its legs at market.`)) return;
    setSquaring(true);
    try { await optionsService.closeBasket(basket.id); toast.success(`Basket #${basket.id} squared off`); setOpenBaskets(prev => prev.filter(b => b.id !== basket.id)); setAdjustOpen(false); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Square-off failed'); }
    finally { setSquaring(false); }
  }, []);
  const squareOffCampaign = useCallback(async (id: number) => {
    if (!window.confirm('Square off ALL tranches of this campaign now?')) return;
    setSquaring(true);
    try { await optionsService.closeCampaign(id); toast.success('Campaign squared off'); setOpenCampaigns(prev => prev.filter(c => c.id !== id)); setCampaignAdjustOpen(false); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Square-off failed'); }
    finally { setSquaring(false); }
  }, []);
  // Disarm the Outlook capture (single or staged) and clear the draw sub-state.
  const cancelOutlookDraw = useCallback(() => { resetDraw(); setDrawPhase('entries'); setOutlookMode('single'); setOutlookCapture(null); setChartTool(null); }, [resetDraw]);
  // Number of ladder levels per side comes from Settings (entry-split count, clamped 2–5).
  const targetStages = Math.max(2, Math.min(5, entrySplitCount || 2));
  // Build the ladder seed from the completed draw + open the dialog. `d` carries the just-drawn
  // values (setState is async, so the final click passes them in) — falls back to current state.
  const buildLadderSeed = useCallback((d: { entries?: number[]; up?: number[]; dn?: number[]; ru?: number | null; rl?: number | null; danger?: number | null }) => {
    const base = (nfoUnderlying || symbol) ? `${(nfoUnderlying || symbol).toUpperCase().replace(/(USDT|USDC|USD|INR|PERP)$/, '')}USD` : 'BTCUSD';
    const sp = Number(actualLtp) || spotPrice || 0;
    const view = outlookCapture?.mode;
    const entries = d.entries ?? drawnStages, up = d.up ?? drawnUp, dn = d.dn ?? drawnDn;
    const ru = d.ru !== undefined ? d.ru : drawnRU, rl = d.rl !== undefined ? d.rl : drawnRL, danger = d.danger !== undefined ? d.danger : drawnDanger;
    const flat = [...entries, ...up, ...dn, ...(ru != null ? [ru] : []), ...(rl != null ? [rl] : [])].filter((x): x is number => x != null && x > 0);
    setStageSeed({ underlyingSymbol: base, view, spot: sp, ladder: true, entries, upLevels: up, dnLevels: dn, viewLevel: rl ?? undefined, viewLevel2: ru ?? undefined, hardSl: danger ?? undefined, stageLevels: flat });
    setStageSeedKey(k => k + 1);
    resetDraw(); setDrawPhase('entries'); setOutlookMode('single'); setOutlookCapture(null); setChartTool(null); setStageDlgOpen(true);
  }, [nfoUnderlying, symbol, actualLtp, spotPrice, outlookCapture, drawnStages, drawnUp, drawnDn, drawnRU, drawnRL, drawnDanger, resetDraw]);

  // A single chart click during a STAGED Outlook draw — validate against the monotonic rule for
  // the current phase; reject (toast) + ignore an out-of-order click; auto-advance / auto-open.
  const handleLadderClick = useCallback((price: number) => {
    const mode = outlookCapture?.mode; if (!mode) return;
    const shape = ladderShape(mode);
    const sp = Number(actualLtp) || spotPrice || 0;
    const p = Math.round(price);
    const N = targetStages;
    if (drawPhase === 'entries') {
      const down = shape === 'dir-down';
      const ref = drawnStages.length ? drawnStages[drawnStages.length - 1] : sp;
      if (down ? !(p < ref) : !(p > ref)) { toast.error(`E${drawnStages.length + 1} must be ${down ? 'below' : 'above'} ${drawnStages.length ? 'the previous level' : 'spot'}`); return; }
      const next = [...drawnStages, p]; setDrawnStages(next);
      if (next.length >= N) setDrawPhase('danger');
    } else if (drawPhase === 'danger') {
      const down = shape === 'dir-down'; const ref = drawnStages[drawnStages.length - 1] ?? sp;
      if (down ? !(p < ref) : !(p > ref)) { toast.error(`The danger level must be ${down ? 'below' : 'above'} E${drawnStages.length}`); return; }
      buildLadderSeed({ danger: p });
    } else if (drawPhase === 'rangeUpper') {
      if (!(p > sp)) { toast.error('The upper level must be above spot'); return; }
      setDrawnRU(p); setDrawPhase(shape === 'brk-up' ? 'upLevels' : 'rangeLower');
    } else if (drawPhase === 'rangeLower') {
      if (!(p < sp) || (drawnRU != null && p >= drawnRU)) { toast.error('The lower level must be below spot (and below the upper)'); return; }
      setDrawnRL(p); setDrawPhase(shape === 'brk-dn' ? 'dnLevels' : 'upLevels');
    } else if (drawPhase === 'upLevels') {
      const ref = drawnUp.length ? drawnUp[drawnUp.length - 1] : (drawnRU ?? sp);
      if (!(p > ref)) { toast.error(`E${drawnUp.length + 1}↑ must be above ${drawnUp.length ? 'the previous level' : 'the upper bound'}`); return; }
      const next = [...drawnUp, p]; setDrawnUp(next);
      if (next.length >= N) { if (shape === 'brk-up') buildLadderSeed({ up: next }); else setDrawPhase('dnLevels'); }
    } else if (drawPhase === 'dnLevels') {
      const ref = drawnDn.length ? drawnDn[drawnDn.length - 1] : (drawnRL ?? sp);
      if (!(p < ref)) { toast.error(`E${drawnDn.length + 1}↓ must be below ${drawnDn.length ? 'the previous level' : 'the lower bound'}`); return; }
      const next = [...drawnDn, p]; setDrawnDn(next);
      if (next.length >= N) buildLadderSeed({ dn: next });
    }
  }, [outlookCapture, actualLtp, spotPrice, targetStages, drawPhase, drawnStages, drawnUp, drawnDn, drawnRU, drawnRL, buildLadderSeed]);

  /* Live-basket leg metadata (parsed from the Delta leg symbols) — ALL legs of the
     basket (was limited to the first two, which mis-built the Combined synthetic for
     3-4 leg structures like iron condors/butterflies). Each carries
     action/optionType/strike/expiry; used to (1) build the locked Combined synthetic
     from EVERY leg and (2) default + populate the two leg-inspection panels. */
  const basketLegsAll = useMemo(() => {
    if (!openBasket || !Array.isArray(openBasket.legFills)) return null;
    const legs = openBasket.legFills.map((lf: any) => {
      const parsed = parseDeltaOptionSymbol(String(lf.symbol || ''));
      if (!parsed) return null;
      return {
        symbol: String(lf.symbol),
        action: (String(lf.action || 'SELL').toUpperCase() === 'BUY' ? 'BUY' : 'SELL') as 'BUY' | 'SELL',
        optionType: parsed.optionType,
        strike: parsed.strike,
        expiry: parsed.expiryISO,
        exchange: 'DELTA',
      };
    });
    if (!legs.length || legs.some((l: any) => l == null)) return null;
    return legs as Array<{ symbol: string; action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; expiry: string; exchange: string }>;
  }, [openBasket]);

  /* The two legs the inspection panels (Q2/Q4) default to: the SHORT legs first
     (the legs most likely to be tested/managed), then the rest — capped at two. */
  const basketPrimaryLegs = useMemo(() => {
    if (!basketLegsAll) return null;
    const shorts = basketLegsAll.filter(l => l.action === 'SELL');
    const others = basketLegsAll.filter(l => l.action !== 'SELL');
    const picked = [...shorts, ...others].slice(0, 2);
    return (picked.length === 2 ? picked : basketLegsAll.slice(0, 2)) as typeof basketLegsAll;
  }, [basketLegsAll]);

  /* Point a leg-inspection panel (Q2 = 'ce', Q4 = 'pe') at a specific basket leg. */
  const pointLegPanel = useCallback((which: 'ce' | 'pe', leg: { optionType: 'CE' | 'PE'; strike: number; expiry: string; action: 'BUY' | 'SELL' }) => {
    if (which === 'ce') { setCeOptType(leg.optionType); setCeStrike(leg.strike); setCeExpiry(leg.expiry); setCeAction(leg.action); }
    else { setPeOptType(leg.optionType); setPeStrike(leg.strike); setPeExpiry(leg.expiry); setPeAction(leg.action); }
  }, []);

  /* Default the two leg panels to the basket's primary legs — ONCE per basket, so
     the user can still re-point a panel afterwards. Cleared when the basket goes. */
  useEffect(() => {
    if (basketPrimaryLegs && openBasket && basketDefaultedRef.current !== openBasket.id) {
      basketDefaultedRef.current = openBasket.id;
      pointLegPanel('ce', basketPrimaryLegs[0]);
      pointLegPanel('pe', basketPrimaryLegs[1]);
    } else if (!openBasket) {
      basketDefaultedRef.current = null;
    }
  }, [basketPrimaryLegs, openBasket, pointLegPanel]);

  /* Load independent candle series for ALL the basket's legs so the locked Combined
     pane reflects the WHOLE structure and never changes when a leg panel is
     re-pointed. Polled. The array is index-aligned with basketLegsAll. */
  useEffect(() => {
    if (!basketLegsAll) { setBasketLegCandles([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const series = await Promise.all(basketLegsAll.map(l => fetchCandles(l.symbol, l.exchange, optInterval)));
        if (!cancelled) setBasketLegCandles(series.map(s => s || []));
      } catch { /* keep last */ }
    };
    load();
    const id = window.setInterval(() => { if (!document.hidden) load(); }, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [basketLegsAll, optInterval, fetchCandles]);

  /* Locked Combined series for a live basket — signed sum of ALL the basket's leg
     series (SELL +, BUY −), aligned by bar time. Independent of the leg panels, so
     re-pointing a panel never alters it. Correct for any N-leg structure. */
  const lockedCombinedCandles = useMemo<CandleRow[]>(() => {
    if (!basketLegsAll || basketLegCandles.length !== basketLegsAll.length) return [];
    if (!basketLegCandles.length || basketLegCandles.some(arr => !arr.length)) return [];
    const signs = basketLegsAll.map(l => comboSign(l.action));
    const maps = basketLegCandles.map(arr => { const m = new Map<number, CandleRow>(); for (const c of arr) m.set(c.time, c); return m; });
    const baseTimes = basketLegCandles[0].map(c => c.time);
    const result: CandleRow[] = [];
    for (const t of baseTimes) {
      const rows = maps.map(m => m.get(t));
      if (rows.some(r => !r)) continue;
      let open = 0, close = 0, hi = 0, lo = 0, vol = 0;
      rows.forEach((r, i) => { const s = signs[i]; open += s * r!.open; close += s * r!.close; hi += s * r!.high; lo += s * r!.low; vol += (r!.volume || 0); });
      result.push({ time: t, open, close, high: Math.max(open, close, hi, lo), low: Math.min(open, close, hi, lo), volume: vol });
    }
    return result;
  }, [basketLegsAll, basketLegCandles]);
  const lockedCombinedDisplay = useMemo(
    () => capTailFor(transformCandles(lockedCombinedCandles, candleType), capOf(2)),
    [lockedCombinedCandles, candleType, capOf, capTailFor],
  );

  /* Q3 effective source: the locked basket synthetic when a basket is live, else
     the leg-panel synthetic. `q3Cur` is the live signed net premium per unit. */
  const q3IsLocked = !!basketLegsAll && lockedCombinedCandles.length > 0;
  const q3RawCandles = q3IsLocked ? lockedCombinedCandles : combinedCandles;
  const q3DisplayCandles = q3IsLocked ? lockedCombinedDisplay : displayCombined;
  const q3Cur = q3IsLocked
    ? (lockedCombinedCandles.length ? Number(lockedCombinedCandles[lockedCombinedCandles.length - 1].close) : null)
    : (ceLtp != null && peLtp != null ? comboSign(ceAction) * ceLtp + comboSign(peAction) * peLtp
        : (combinedCandles.length ? Number(combinedCandles[combinedCandles.length - 1].close) : null));

  /* Per-unit (combined-chart) SL/Target/entry levels for the open basket. The
     basket stores qty-weighted premiums; the combined pane plots per-unit
     (CE+PE) premium, so divide by the leg quantity. */
  const openBasketChart = useMemo(() => {
    if (!openBasket || !Array.isArray(openBasket.legFills) || !openBasket.legFills.length) return null;
    const q = Number(openBasket.legFills[0].quantity) || 1;
    const cv = Number(openBasket.legFills[0].contractValue) || 0.001;
    const entry = openBasket.entryCredit != null ? Number(openBasket.entryCredit) : null;
    const tgt = openBasket.targetPremium != null ? Number(openBasket.targetPremium) : null;
    const sl = openBasket.slPremium != null ? Number(openBasket.slPremium) : null;
    return {
      q, cv,
      entryPerUnit: entry != null ? entry / q : null,
      targetPerUnit: tgt != null ? tgt / q : null,
      slPerUnit: sl != null ? sl / q : null,
      entry, tgt, sl,
    };
  }, [openBasket]);

  /* Take-profit / stop-loss P&L thresholds (INR, full position) for the RUNNING
     basket, derived from the persisted premium levels so they LIVE-UPDATE when the
     user edits TP%/SL% (updateBasketLevels recomputes target/slPremium; the 15s poll
     refreshes openBasket). Same units as payoffData.expiry (×contractValue×fx).
     Null for the under-review dialog card (no fills yet) → falls back to −50% margin. */
  const basketPnlThresholds = useMemo(() => {
    if (!openBasket || dialogCard) return null;
    const e = openBasket.entryCredit != null ? Number(openBasket.entryCredit) : null;
    const tp = openBasket.targetPremium != null ? Number(openBasket.targetPremium) : null;
    const sl = openBasket.slPremium != null ? Number(openBasket.slPremium) : null;
    const cv = Number(openBasket.legFills?.[0]?.contractValue) || null;
    if (e == null || cv == null) return null;
    const fx = 85; // USD→INR, matching payoffData / monitor P&L convention
    // Fixed-₹ baskets exit on TOTAL P&L (adjustment bookings + open legs). The curve here
    // is the OPEN legs' P&L, so the open-leg P&L that fires each exit = level − booked.
    const fb = openBasket as any;
    // Levels are NET (2026-11-03): they fire when the P&L at the marks (this curve) is the
    // level PLUS the costs (exit slippage + fees, from the monitor's live state).
    const cost = Number(fb.live?.costInr) > 0 ? Number(fb.live.costInr) : 0;
    if (fb.exitMode === 'FIXED' && Number(fb.tpAmountInr) > 0 && Number(fb.slAmountInr) !== 0 && Number.isFinite(Number(fb.slAmountInr))) {   // sl signed: <0 = profit floor
      const booked = Number(fb.adjustmentsRealizedPnl || 0);
      return { tpInr: Number(fb.tpAmountInr) - booked + cost, slInr: -Number(fb.slAmountInr) - booked + cost,
        tpShow: Number(fb.tpAmountInr) as number | null, slShow: -Number(fb.slAmountInr) as number | null, booked };
    }
    return {
      tpInr: tp != null ? (e - tp) * cv * fx + cost : null,   // P&L at the marks when the net target fires
      slInr: sl != null ? (e - sl) * cv * fx + cost : null,
      tpShow: cost && tp != null ? (e - tp) * cv * fx : null as number | null,
      slShow: cost && sl != null ? (e - sl) * cv * fx : null as number | null, booked: 0,
    };
  }, [openBasket, dialogCard]);

  /* Live P&L-vs-spot curve for the running basket, used to map the premium-based
     TP/SL amounts to APPROXIMATE underlying trigger prices (item #7). The basket
     exits on net-to-close at ANY time, so the at-expiry curve would only be right
     at expiry — we use the "today" curve and ANCHOR it to the live (spot, P&L)
     observation so the levels reflect the position right now. Re-anchors every poll. */
  const basketLiveCurve = useMemo(() => {
    const b = openBasket;
    if (!overlays.payoffZones || dialogCard || !b?.payoffData?.spotRange?.length) return null;
    const xs = b.payoffData.spotRange as number[];
    const today = (b.payoffData.today as number[]) || [];
    const expiry = (b.payoffData.expiry as number[]) || [];
    const ys = today.length === xs.length ? today : expiry; // prefer the live "today" curve
    if (ys.length !== xs.length || xs.length < 2) return null;
    const interpAt = (s: number): number | null => {
      if (s <= xs[0]) return ys[0];
      if (s >= xs[xs.length - 1]) return ys[ys.length - 1];
      for (let i = 1; i < xs.length; i++) {
        if (xs[i] >= s) { const t = (s - xs[i - 1]) / (xs[i] - xs[i - 1]); return ys[i - 1] + t * (ys[i] - ys[i - 1]); }
      }
      return null;
    };
    const liveSpot = b.live?.spot != null ? Number(b.live.spot) : null;
    const livePnl = b.live?.unrealizedPnl != null ? Number(b.live.unrealizedPnl) : null;
    let delta = 0;
    if (liveSpot != null && livePnl != null) { const at = interpAt(liveSpot); if (at != null && Number.isFinite(at)) delta = livePnl - at; }
    return { xs, adj: ys.map(v => v + delta), liveSpot };
  }, [overlays.payoffZones, openBasket, dialogCard]);

  /* Payoff-zones overlay levels (per the user's request): instead of the underlying
     TP/SL/BE/Max-profit price lines, draw a single ENTRY reference line — the underlying
     price at which the running basket was executed. The ₹ P&L / SL / TP amounts and the
     adjustment flag are shown in the on-chart badge (payoffPnlBadge). Only for the
     RUNNING basket (post-approval) that carries an entrySpot. */
  const payoffLeftLevels = useMemo(() => {
    const b = openBasket;
    if (!overlays.payoffZones || dialogCard || !b || b.entrySpot == null || !(Number(b.entrySpot) > 0)) return [] as any[];
    const px = Number(b.entrySpot);
    const fmtPx = (p: number) => (Math.abs(p) >= 1000 ? Math.round(p).toLocaleString('en-IN') : p.toFixed(2));
    return [{ price: px, color: '#90a4ae', lineStyle: 2, label: `Entry ${fmtPx(px)}` }];
  }, [overlays.payoffZones, openBasket, dialogCard]);


  /* Running-campaign ladder LABELS — LEFT-pinned (so they clear the right-side option-profile
     rail) + concise (no #id / cr-db). Label only (noLine): the draggable line is drawn by the
     right-axis price-levels primitive, so we don't double it. */
  const campaignLeftLevels = useMemo(() => {
    const out: any[] = [];
    for (const c of openCampaigns) {
      for (const s of (c.stages || [])) {
        const p = Number(s.anchorLevel ?? s.triggerPrice); if (!(p > 0)) continue;
        const deployed = s.status === 'DEPLOYED';
        const arrow = s.side === 'UP' ? '↑' : s.side === 'DOWN' ? '↓' : '';
        out.push({ price: p, color: deployed ? '#5e35b1' : '#9575cd', lineStyle: deployed ? 0 : 2, noLine: true, label: `E${s.index}${arrow}${deployed ? ' ✓' : ''}` });
      }
      if (Number(c.hardSlPrice) > 0) out.push({ price: Number(c.hardSlPrice), color: '#c62828', lineStyle: 2, noLine: true, label: `Danger${c.hardSlPriceUpper ? '↓' : ''}` });
      if (Number(c.hardSlPriceUpper) > 0) out.push({ price: Number(c.hardSlPriceUpper), color: '#c62828', lineStyle: 2, noLine: true, label: 'Danger↑' });
      if (Number(c.profitLevelUp) > 0) out.push({ price: Number(c.profitLevelUp), color: '#2e7d32', lineStyle: 0, noLine: true, label: 'Profit↑' });
      if (Number(c.profitLevelDn) > 0) out.push({ price: Number(c.profitLevelDn), color: '#2e7d32', lineStyle: 0, noLine: true, label: 'Profit↓' });
    }
    return out;
  }, [openCampaigns]);
  /* Combined LEFT-pinned levels for the chart (payoff entry + campaign ladder). The FNO γ
     flip is a running line per candle now, not a single latest-value label. */
  const chartLeftLevels = useMemo(() => [...payoffLeftLevels, ...campaignLeftLevels], [payoffLeftLevels, campaignLeftLevels]);


  /* Shaded payoff ZONES on the underlying spot chart (the user asked for zones,
     not just lines): the green profit region (at-expiry P&L ≥ 0, bounded by the
     break-evens / zero-profit edges) and the red −50%-of-investment danger
     region (P&L ≤ −0.5 × margin). A spot range [lo,hi] maps to a horizontal band
     between those two prices. Sources from the live builder card pre-approval,
     else the open basket post-approval. */
  // Aggregate payoff of a RUNNING campaign's deployed tranches (for the profit-zone fill) — used
  // when there's no single open basket selected. Null until a tranche has actually deployed.
  const campaignPayoffCard = useMemo(() => {
    const c = openCampaigns.find((x: any) => x.aggregate?.combinedPayoff?.payoffData?.spotRange?.length);
    if (!c) return null;
    const cp = c.aggregate.combinedPayoff;
    return { payoffData: cp.payoffData, maxProfit: cp.maxProfit, maxLoss: cp.maxLoss, breakEvens: cp.breakEvens || [], marginEstimate: c.aggregate.deployedInr || 0, entrySpot: null } as any;
  }, [openCampaigns]);
  const payoffZoneBands = useMemo(() => {
    const b = dialogCard || openBasket || campaignPayoffCard;
    if (!overlays.payoffZones || !b?.payoffData?.spotRange?.length) return [] as HZone[];
    const xs = b.payoffData.spotRange as number[];
    const ys = b.payoffData.expiry as number[];
    const margin = Number(b.marginEstimate || 0);
    // Contiguous spot segments where pred(y) holds for a given curve, with linear
    // interpolation of the crossing spot at the threshold for clean band edges.
    const segments = (yArr: number[], thr: number, above: boolean): Array<[number, number]> => {
      const segs: Array<[number, number]> = [];
      const ok = (y: number) => above ? y >= thr : y <= thr;
      const cross = (i: number) => {
        const dy = yArr[i] - yArr[i - 1];
        if (!dy) return xs[i];
        const t = (thr - yArr[i - 1]) / dy;
        return xs[i - 1] + t * (xs[i] - xs[i - 1]);
      };
      let start: number | null = null;
      for (let i = 0; i < yArr.length; i++) {
        const inside = ok(yArr[i]);
        if (inside && start == null) start = i > 0 ? cross(i) : xs[i];
        else if (!inside && start != null) { segs.push([start, cross(i)]); start = null; }
      }
      if (start != null) segs.push([start, xs[xs.length - 1]]);
      return segs;
    };
    const inr = (v: number) => `₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
    const bands: HZone[] = [];
    // TIERED profit zone (item): the region nearest MAX profit is a darker green, fading
    // lighter toward break-even. Drawn as nested semi-transparent bands (wide→narrow) so
    // the greens accumulate: the max-profit core (≥75% of max) overlaps all three tiers →
    // darkest; the outer ≥0 band → lightest. Base tier carries the edge + "Profit zone" label.
    const maxP = ys.length ? Math.max(...ys) : 0;
    if (maxP > 0) {
      const tiers = [
        { frac: 0, alpha: 0.08, base: true },
        { frac: 0.4, alpha: 0.10, base: false },
        { frac: 0.75, alpha: 0.13, base: false },
      ];
      for (const tier of tiers) {
        for (const [lo, hi] of segments(ys, tier.frac * maxP, true)) {
          if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
            bands.push({
              bottom: lo, top: hi, fill: `rgba(38,166,154,${tier.alpha})`,
              edge: tier.base ? 'rgba(38,166,154,0.45)' : undefined,
              label: tier.base ? 'Profit zone' : undefined, labelColor: '#26a69a',
            });
          }
        }
      }
    } else {
      // No positive-profit region (rare) → single flat green over any ≥0 area.
      for (const [lo, hi] of segments(ys, 0, true)) {
        if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
          bands.push({ bottom: lo, top: hi, fill: 'rgba(38,166,154,0.10)', edge: 'rgba(38,166,154,0.45)', label: 'Profit zone', labelColor: '#26a69a' });
        }
      }
    }
    // Running basket → SL danger zone (underlying region where the basket would be
    // at/beyond the stop-loss loss NOW), off the live-anchored curve so it tracks the
    // position. The TP/SL trigger PRICES are drawn as lines (payoffLevels). Else the
    // −50%-of-margin preview guide on the at-expiry curve.
    if (basketLiveCurve && basketPnlThresholds?.slInr != null) {
      for (const [lo, hi] of segments(basketLiveCurve.adj, basketPnlThresholds.slInr, false)) {
        if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
          bands.push({ bottom: lo, top: hi, fill: 'rgba(239,83,80,0.12)', edge: 'rgba(239,83,80,0.5)', label: `SL −${inr(basketPnlThresholds.slShow ?? basketPnlThresholds.slInr)}${basketPnlThresholds.slShow != null ? ' total' : ''}`, labelColor: '#ef5350' });
        }
      }
    } else if (margin > 0) {
      for (const [lo, hi] of segments(ys, -0.5 * margin, false)) {
        if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
          bands.push({ bottom: lo, top: hi, fill: 'rgba(239,83,80,0.10)', edge: 'rgba(239,83,80,0.4)', label: '−50% of invest', labelColor: '#ef5350' });
        }
      }
    }
    return bands;
  }, [overlays.payoffZones, openBasket, dialogCard, campaignPayoffCard, basketPnlThresholds, basketLiveCurve]);

  /* Live P&L headline badge (item #1) — the selected running basket's unrealised
     P&L, pinned to the spot pane's top-left so it catches the eye and isn't blocked
     by the price axis / option-profile rail. Only while the payoff overlay is on. */
  const payoffPnlBadge = useMemo(() => {
    if (!overlays.payoffZones || !openBasket?.live || !Number.isFinite(openBasket.live.unrealizedPnl)) return null;
    const inr = (v: number) => `₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
    const lines: string[] = [];
    // SL / TP amounts in ₹ (from the persisted premium thresholds).
    const th = basketPnlThresholds;
    if (th) {
      const parts: string[] = [];
      if (th.slInr != null) parts.push(`SL −${inr(th.slShow ?? th.slInr)}`);
      if (th.tpInr != null && (th.tpShow ?? th.tpInr) > 0) parts.push(`TP +${inr(th.tpShow ?? th.tpInr)}`);
      if (parts.length) lines.push(parts.join(' · ') + (th.booked ? ' (net total P&L)' : ' (net)'));
      if (th.booked) lines.push(`Adjustments booked ${th.booked >= 0 ? '+' : '−'}${inr(th.booked)}`);
    }
    // Underlying price at which the basket was executed.
    if (openBasket.entrySpot != null && Number(openBasket.entrySpot) > 0) {
      lines.push(`Entry ${Math.round(Number(openBasket.entrySpot)).toLocaleString('en-IN')}`);
    }
    const warn = openBasket.adjustmentNeeded
      ? `⚠ Adjustment ${openBasket.adjustmentInfo?.severity === 'urgent' ? 'URGENT' : 'suggested'}`
      : undefined;
    // Headline = the basket's TOTAL (open legs + adjustment bookings) — what its exits judge.
    const bookedAdj = Number((openBasket as any).adjustmentsRealizedPnl || 0);
    return { value: Number(openBasket.live.unrealizedPnl) + bookedAdj, sub: `#${openBasket.id} · ${openBasket.strategy || 'basket'}${bookedAdj ? ' · total' : ''}`, lines, warn };
  }, [overlays.payoffZones, openBasket, basketPnlThresholds]);

  /* Close the synthetic chart's LIVE position at market — an ENGINE-mirror cycle
     (multi-leg runner) via the per-cycle close endpoint, or a basket-backed LIVE
     synthetic via the existing basket close. Confirm first; LIVE mode warns about
     real orders. */
  const [closingSynth, setClosingSynth] = useState(false);
  const closeSynthPosition = useCallback(async () => {
    const sd = synthRef.current;
    if (!sd || closingSynth) return;
    const isEngine = !!sd.engineCycle && sd.strategyCycleId != null;
    const live = isEngine ? sd.engineCycle!.mode === 'LIVE' : false;
    const ok = window.confirm(
      `Close ${sd.name} now at market?` + (live ? '\n\nThis places REAL close orders on your broker.' : ''));
    if (!ok) return;
    setClosingSynth(true);
    try {
      if (isEngine) {
        const r = await optionStrategyService.closeCycle(sd.strategyCycleId!);
        const d = r.data?.data;
        toast.success(`${sd.name} closed — realised ₹${Math.round(Number(d?.realizedPnl || 0)).toLocaleString('en-IN')}`, { id: 'synth-close', duration: 8000 });
      } else if (sd.basketId != null) {
        await optionsService.closeBasket(sd.basketId);
        toast.success(`${sd.name} — basket close placed`, { id: 'synth-close', duration: 8000 });
      } else {
        toast.error('No live position behind this symbol.', { id: 'synth-close' });
        return;
      }
      const d2 = await syntheticSymbolsService.getOne(sd.id);
      if (d2) setSynth(d2);
      try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Close failed', { id: 'synth-close', duration: 8000 });
    } finally { setClosingSynth(false); }
  }, [closingSynth]);

  /* Drag handler for the Combined pane's SL/Target lines → update the basket
     (price is per-unit; the basket stores qty-weighted, so × leg quantity). */
  const handleCombinedLevelChange = useCallback((type: string, price: number) => {
    if (!openBasket || !openBasketChart) return;
    const abs = price * openBasketChart.q;
    const t = type.toUpperCase();
    const body = t === 'TARGET' ? { targetPremium: abs } : t === 'SL' ? { slPremium: abs } : null;
    if (!body) return;
    optionsService.updateBasketLevels(openBasket.id, body)
      .then(() => {
        const patch = t === 'TARGET' ? { targetPremium: abs } : { slPremium: abs };
        setOpenBaskets((prev: any[]) => prev.map(b => b.id === openBasket.id ? { ...b, ...patch } : b));
        toast.success(`${t === 'TARGET' ? 'Target' : 'Stop'} moved to ${price.toFixed(2)} (combined premium)`, { id: 'basket-level' });
      })
      .catch((e: any) => toast.error(e?.response?.data?.error?.message || 'Failed to update level', { id: 'basket-level' }));
  }, [openBasket, openBasketChart]);

  const handleChartClickSpot = useCallback((price: number) => {
    /* Bar Replay policy. Marking a chart up IS the point of replay, so the pure
       annotations — HVN lines here, Rect zones via their own handler — go
       through. Anything that arms or modifies a REAL order must not take its
       price off a frozen historical bar: trigger levels fire live orders,
       BUY/SHORT opens an order capture, Add ISL / Add Tgt PATCHes an open
       trade, and the Strategy / Outlook chips build baskets.
       Those used to hit a blanket `return` that swallowed the click in total
       silence while the chip stayed lit as armed — which is why replay looked
       like "you cannot plot anything". Now they say why, and disarm. */
    if (replayActiveRef.current && chartTool !== 'hvn') {
      toast.error('Not available during Bar Replay — exit replay to arm a trigger or an order.\nHVN lines and Rect zones still work.',
        { id: 'replay-blocked', duration: 5000 });
      setChartToolExclusive(null);
      setTriggerModeExclusive(null);
      return;
    }
    if (chartTool === 'hvn') {
      addHvn(price);
      return;
    }
    if (chartTool === 'strategy') {
      // Click-to-build: feed the picked level into the strategy capture.
      strategyCaptureRef.current?.(price);
      return;
    }
    if (chartTool === 'outlook') {
      if (outlookMode === 'staged') handleLadderClick(price);           // anchored-ladder draw (validated)
      else outlookCaptureRef.current?.(price);                          // single basket (Architect)
      return;
    }
    if (chartTool === 'buy' || chartTool === 'short') {
      // Dynamic targeting — clicking the spot pane targets the underlying.
      orderCaptureRef.current?.(price, { pane: 'spot', instrumentId, symbol, decimals: spotDecimals });
      return;
    }
    // R115 Stage 5 — Add ISL / Add Tgt: PATCH the open trade for this symbol.
    if (chartTool === 'addIsl' || chartTool === 'addTgt') {
      const t = activeTrade;
      if (!t || (t.status !== 'OPEN' && t.status !== 'DRAFT')) {
        toast.error('No open trade for this symbol to set ISL / Target on');
        setChartToolExclusive(null);
        return;
      }
      const path = chartTool === 'addIsl' ? 'stop-loss' : 'target';
      const body = chartTool === 'addIsl' ? { stopLoss: price } : { target: price };
      api.put(`/trades/${t.id}/${path}`, body)
        .then(() => {
          toast.success(`${chartTool === 'addIsl' ? 'ISL' : 'Target'} set @ ${price}`);
          setActiveTradeBump(b => b + 1);
        })
        .catch(e => toast.error(e?.response?.data?.error?.message || `Failed to set ${chartTool === 'addIsl' ? 'ISL' : 'Target'}`));
      setChartToolExclusive(null);
      return;
    }
    if (!triggerMode) return;
    saveTriggerLevelFor('spot', triggerMode, price);
  }, [triggerMode, chartTool, outlookMode, handleLadderClick, saveTriggerLevelFor, addHvn, activeTrade, setChartToolExclusive, setTriggerModeExclusive, instrumentId, symbol, spotDecimals]);

  // CE/PE leg clicks: set a trigger level on the leg, OR drive the BUY/SHORT
  // 3-click capture against the leg's instrument. The clicked pane IS the target
  // — no pre-selector. (HVN / Add ISL / Add Tgt remain underlying-only.)
  const handleChartClickCe = useCallback((price: number) => {
    // Option panes offer only order actions (BUY / SHORT capture, trigger
    // levels), none of which may take a price off a frozen historical bar.
    // Explain rather than swallowing the click.
    if (replayActiveRef.current) {
      toast.error('Not available during Bar Replay - exit replay to arm a trigger or an order.',
        { id: 'replay-blocked', duration: 5000 });
      setChartToolExclusive(null);
      setTriggerModeExclusive(null);
      return;
    }
    if (chartTool === 'buy' || chartTool === 'short') {
      orderCaptureRef.current?.(price, { pane: 'ce', instrumentId: ceInstrumentId, symbol: ceSymbol, decimals: ceDecimals });
      return;
    }
    if (!triggerMode) return;
    saveTriggerLevelFor('ce', triggerMode, price);
  }, [chartTool, triggerMode, saveTriggerLevelFor, setChartToolExclusive, setTriggerModeExclusive, ceInstrumentId, ceSymbol, ceDecimals]);

  const handleChartClickPe = useCallback((price: number) => {
    // Option panes offer only order actions (BUY / SHORT capture, trigger
    // levels), none of which may take a price off a frozen historical bar.
    // Explain rather than swallowing the click.
    if (replayActiveRef.current) {
      toast.error('Not available during Bar Replay - exit replay to arm a trigger or an order.',
        { id: 'replay-blocked', duration: 5000 });
      setChartToolExclusive(null);
      setTriggerModeExclusive(null);
      return;
    }
    if (chartTool === 'buy' || chartTool === 'short') {
      orderCaptureRef.current?.(price, { pane: 'pe', instrumentId: peInstrumentId, symbol: peSymbol, decimals: peDecimals });
      return;
    }
    if (!triggerMode) return;
    saveTriggerLevelFor('pe', triggerMode, price);
  }, [chartTool, triggerMode, saveTriggerLevelFor, setChartToolExclusive, setTriggerModeExclusive, peInstrumentId, peSymbol, peDecimals]);

  /* Called by TradingChart when user drags an axis handle and releases —
     `type` is the uppercase label ('NBUY', 'NSHORT', 'BBUY', 'BSHORT', 'SL', 'TARGET').
     FNO overlay lines ('MaxPain', 'GEX Flip') and 'TSL' are read-only.
     Price is already tick-snapped by TradingChart — no extra rounding. */
  const makeLevelChangeHandler = (pane: 'spot' | 'ce' | 'pe') => (type: string, price: number, id?: string | number) => {
    const upper = type.toUpperCase();
    if (upper === 'NBUY' || upper === 'NSHORT' || upper === 'BBUY' || upper === 'BSHORT') {
      saveTriggerLevelFor(pane, upper.toLowerCase() as TriggerType, price);
      return;
    }
    if (upper === 'HVN' && id != null) {
      const numId = typeof id === 'number' ? id : parseInt(String(id), 10);
      if (Number.isFinite(numId)) updateHvn(numId, price);
      return;
    }
    if (upper === 'PENDING_ENTRY' && id != null) {
      const numId = typeof id === 'number' ? id : parseInt(String(id), 10);
      if (!Number.isFinite(numId)) return;
      // Synthetic chart — the "pending entry" line is the ARMED trigger's entry
      // premium; drag routes to the trigger PATCH (₹↔premium stays in sync since
      // the axis IS the premium).
      if (isSynthRef.current && synthRef.current) {
        const sd = synthRef.current;
        setPendingRows(prev => prev.map(p => p.id === numId ? { ...p, entryPrice: price } : p));
        syntheticSymbolsService.patchTrigger(sd.id, { entryPremium: price, triggerId: numId })
          .then(r => {
            if (!r.ok) throw new Error(r.reason);
            toast.success(`Trigger entry moved to ${price}`, { id: `synth-drag-${numId}` });
          })
          .catch((e: any) => {
            toast.error(e?.message || 'Failed to move the trigger entry', { id: `synth-drag-${numId}`, duration: 7000 });
            syntheticSymbolsService.getOne(sd.id).then(d => { if (d) setSynth(d); });
          });
        return;
      }
      // Optimistic local update so the line stays where the user dropped it.
      // On a backend rejection (e.g. SL/Target now on the wrong side of the new
      // entry) we ROLL BACK and force an authoritative re-fetch so the chart
      // and the dashboard never disagree, and we surface the real reason.
      setPendingRows(prev => prev.map(p => p.id === numId ? { ...p, entryPrice: price } : p));
      api.put(`/pending-entries/${numId}`, { entryPrice: price }, { headers: { _suppressToast: 'true' } })
        .then(() => toast.success(`Pending entry updated to ${price}`, { id: `pe-drag-${numId}` }))
        .catch((e: any) => {
          toast.error(e?.response?.data?.error?.message || 'Failed to update pending entry', { id: `pe-drag-${numId}`, duration: 7000 });
          setPendingBump(b => b + 1); // re-sync line back to the persisted value
        });
      return;
    }
    // Staged-campaign level drag — a PENDING entry, a danger, or a profit-close level. Encoded
    // id `camp:{campaignId}:{stageIndex|dlo|dhi|pup|pdn}`. Optimistic + PUT; the 30s poll re-syncs.
    if (upper.startsWith('CAMP_') && typeof id === 'string' && id.startsWith('camp:')) {
      const [, campStr, key] = id.split(':'); const campId = Number(campStr);
      if (!campId) return;
      const body: any = {};
      if (upper === 'CAMP_STAGE') { body.stageIndex = Number(key); body.level = price; }
      else if (upper === 'CAMP_DLO') body.hardSlPrice = price;
      else if (upper === 'CAMP_DHI') body.hardSlPriceUpper = price;
      else if (upper === 'CAMP_PUP') body.profitLevelUp = price;
      else if (upper === 'CAMP_PDN') body.profitLevelDn = price;
      setOpenCampaigns(prev => prev.map(c => {
        if (c.id !== campId) return c;
        const nc: any = { ...c };
        if (upper === 'CAMP_STAGE') nc.stages = (c.stages || []).map((s: any) => s.index === Number(key) ? { ...s, anchorLevel: price, triggerPrice: price } : s);
        else if (upper === 'CAMP_DLO') nc.hardSlPrice = price;
        else if (upper === 'CAMP_DHI') nc.hardSlPriceUpper = price;
        else if (upper === 'CAMP_PUP') nc.profitLevelUp = price;
        else if (upper === 'CAMP_PDN') nc.profitLevelDn = price;
        return nc;
      }));
      optionsService.updateCampaignLevels(campId, body)
        .then(() => toast.success(`Campaign #${campId} level → ${Math.round(price).toLocaleString('en-IN')}`, { id: `camp-${id}` }))
        .catch((e: any) => toast.error(e?.response?.data?.error?.message || 'Could not move that level', { id: `camp-${id}`, duration: 6000 }));
      return;
    }
    // Basket-backed LIVE synthetic — dragging SL/Target PATCHes the BASKET's exit
    // levels (chart premium units → the monitor's signed, lots-weighted units).
    if ((upper === 'BSL' || upper === 'BTGT') && isSynthRef.current && synthRef.current?.basketLive) {
      const sd = synthRef.current;
      const bl = sd.basketLive!;
      const monitorUnits = price * bl.lots * (sd.isCredit ? 1 : -1);
      const body = upper === 'BSL' ? { slPremium: monitorUnits } : { targetPremium: monitorUnits };
      optionsService.updateBasketLevels(bl.basketId, body)
        .then(() => {
          setSynth(prev => prev && prev.id === sd.id && prev.basketLive
            ? { ...prev, basketLive: { ...prev.basketLive, [upper === 'BSL' ? 'slPremium' : 'targetPremium']: price } }
            : prev);
          toast.success(`${upper === 'BSL' ? 'Stop' : 'Target'} moved to ${price.toFixed(2)} (combined premium)`, { id: 'synth-basket-level' });
        })
        .catch((e: any) => toast.error(e?.response?.data?.error?.message || 'Failed to update the level', { id: 'synth-basket-level' }));
      return;
    }
    // Pending-group shared SL / Target drag (Round 89). All still-PENDING legs
    // of the symbol share one SL/Target, so update every one and reflect it in
    // the dashboard (which re-polls). Uses the ref to avoid a stale closure.
    if (pane === 'spot' && (upper === 'PSL' || upper === 'PTGT')) {
      const field: 'stopLoss' | 'target' = upper === 'PSL' ? 'stopLoss' : 'target';
      const label = upper === 'PSL' ? 'Stop-Loss' : 'Target';
      // Synthetic chart — SL/Target drags update the armed trigger's premium levels.
      if (isSynthRef.current && synthRef.current) {
        const sd = synthRef.current;
        setPendingRows(prev => prev.map(p => p.status === 'PENDING' ? { ...p, [field]: price } : p));
        syntheticSymbolsService.patchTrigger(sd.id, field === 'stopLoss' ? { slPremium: price } : { targetPremium: price })
          .then(r => {
            if (!r.ok) throw new Error(r.reason);
            toast.success(`Trigger ${label.toLowerCase()} moved to ${price}`, { id: `synth-${field}-drag` });
          })
          .catch((e: any) => {
            toast.error(e?.message || `Failed to move the trigger ${label.toLowerCase()}`, { id: `synth-${field}-drag`, duration: 7000 });
            syntheticSymbolsService.getOne(sd.id).then(d => { if (d) setSynth(d); });
          });
        return;
      }
      const targets = pendingRowsRef.current.filter(p => p.status === 'PENDING');
      if (targets.length === 0) return;
      // Optimistic update across the shared-SL/Target group. If ANY leg's PUT
      // is rejected by the backend (validation — e.g. SL on the wrong side of
      // entry, the exact cause of "chart updated but dashboard didn't"), roll
      // the chart back to the persisted value and surface the real reason, so
      // the chart and the dashboard's Pending Manual Entry Watch stay in sync.
      setPendingRows(prev => prev.map(p => p.status === 'PENDING' ? { ...p, [field]: price } : p));
      Promise.all(targets.map(p => api.put(`/pending-entries/${p.id}`, { [field]: price }, { headers: { _suppressToast: 'true' } })))
        .then(() => { toast.success(`${label} updated to ${price}`, { id: `pe-${field}-drag` }); setPendingBump(b => b + 1); })
        .catch((e: any) => {
          toast.error(e?.response?.data?.error?.message || `Failed to update ${label.toLowerCase()}`, { id: `pe-${field}-drag`, duration: 7000 });
          setPendingBump(b => b + 1); // re-fetch authoritative state → line snaps back, dashboard matches
        });
      return;
    }
    // Spot-only: SL / Target on the active OPEN trade. Read activeTrade from the
    // ref so a stale closure can't make this branch a no-op (the SL-revert bug).
    const at = activeTradeRef.current;
    if (pane === 'spot' && (upper === 'SL' || upper === 'TARGET')) {
      // eslint-disable-next-line no-console
      console.debug('[sl-drag] drop', { upper, price, hasActiveTrade: !!at, tradeId: at?.id });
    }
    if (pane === 'spot' && at && (upper === 'SL' || upper === 'TARGET')) {
      const tradeId = at.id;
      const apply = async () => {
        if (upper === 'SL') {
          // Stamp the edit-lock BEFORE the PUT so a poll firing mid-request
          // can't snap the line back to the old value.
          editLockRef.current.manualStopLoss = Date.now();
          try {
            // Issue 4 — a manual SL is now AUTHORITATIVE: the backend pauses
            // auto-trail and hands the breach to manualStopLoss. Mirror the
            // returned values (currentTrailingSl is cleared) so the operative
            // SL line lands exactly where the user dropped it.
            const resp = await api.put<any>(`/trades/${tradeId}/stop-loss`, { manualStopLoss: price }, { headers: { _suppressToast: 'true' } });
            const data = resp?.data?.data || {};
            const newManual = data.manualStopLoss != null ? Number(data.manualStopLoss) : price;
            const newTsl = data.currentTrailingSl != null ? Number(data.currentTrailingSl) : null;
            // Pin the SL line to exactly where the user dropped it until the server
            // state converges (defeats the auto-refresh "snap back to initial" bug).
            slOverrideRef.current = { price: newManual, until: Date.now() + EDIT_LOCK_MS };
            // eslint-disable-next-line no-console
            console.debug('[sl-drag] set ok', { tradeId, price, newManual, newTsl, trailDisabled: data.trailDisabled ?? data.trailPaused });
            setActiveTrade(prev => prev ? { ...prev, manualStopLoss: newManual, currentTrailingSl: newTsl } : prev);
            if (data.trailDisabled ?? data.trailPaused) {
              toast.success(`SL set at ${price} (manual). Auto-Trail turned OFF — re-enable any time from the Trail switch in Trade Management.`, { id: `trade-sl-${tradeId}`, duration: 6000 });
            } else {
              toast.success(`SL set at ${price}`, { id: `trade-sl-${tradeId}` });
            }
          } catch (e: any) {
            // Issues 5/6 — surface the REAL reason (e.g. "would trigger
            // immediately") and roll the line back to the persisted value
            // instead of leaving a phantom that reverts on the next poll.
            editLockRef.current.manualStopLoss = 0;
            slOverrideRef.current = null; // rejected — let the line snap back to the real value
            toast.error(e?.response?.data?.error?.message || 'Failed to update SL', { id: `trade-sl-${tradeId}`, duration: 7000 });
            setActiveTradeBump(b => b + 1);
          }
        } else {
          editLockRef.current.target = Date.now();
          try {
            await api.put(`/trades/${tradeId}/target`, { target: price }, { headers: { _suppressToast: 'true' } });
            setActiveTrade(prev => prev ? { ...prev, target: price } : prev);
            toast.success(`Target set at ${price}`, { id: `trade-tgt-${tradeId}` });
          } catch (e: any) {
            editLockRef.current.target = 0;
            toast.error(e?.response?.data?.error?.message || 'Failed to update target', { id: `trade-tgt-${tradeId}`, duration: 7000 });
            setActiveTradeBump(b => b + 1);
          }
        }
      };
      apply();
    }
  };
  const handleLevelChangeSpot = useCallback(makeLevelChangeHandler('spot'), [saveTriggerLevelFor, updateHvn]);
  const handleLevelChangeCe = useCallback(makeLevelChangeHandler('ce'), [saveTriggerLevelFor, ceDecimals]);
  const handleLevelChangePe = useCallback(makeLevelChangeHandler('pe'), [saveTriggerLevelFor, peDecimals]);

  /* Delete handler — only HVN levels expose a × on hover. */
  const handleLevelDeleteSpot = useCallback((type: string, id?: string | number) => {
    const upper = type.toUpperCase();
    // Watch triggers are single-per-symbol (no id) — the line × clears them just
    // like the toolbar cross (safe, reversible).
    if (upper === 'NBUY' || upper === 'NSHORT' || upper === 'BBUY' || upper === 'BSHORT') {
      removeTriggerLevel(upper.toLowerCase() as TriggerType);
      return;
    }
    if (id == null) return;
    const numId = typeof id === 'number' ? id : parseInt(String(id), 10);
    if (!Number.isFinite(numId)) return;
    if (upper === 'HVN') {
      removeHvn(numId);
    } else if (upper === 'PENDING_ENTRY') {
      // Synthetic chart — the × removes THIS armed level only (one E1/E2/… line);
      // the rest of a scale-in group stays armed. Disarming the whole group lives
      // on the Dashboard / right-click.
      if (isSynthRef.current && synthRef.current) {
        const sd = synthRef.current;
        syntheticSymbolsService.cancelTrigger(sd.id, numId)
          .then(r => {
            if (!r.ok) throw new Error(r.reason);
            setPendingRows(prev => prev.filter(p => p.id !== numId));
            toast.success('Trigger level removed');
            syntheticSymbolsService.getOne(sd.id).then(d => { if (d) setSynth(d); });
            try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
          })
          .catch((e: any) => toast.error(e?.message || 'Failed to remove the trigger level'));
        return;
      }
      // Soft-delete via the pending-entries DELETE endpoint (marks CANCELLED).
      api.delete(`/pending-entries/${numId}`)
        .then(() => {
          setPendingRows(prev => prev.filter(p => p.id !== numId));
          toast.success('Pending entry cancelled');
        })
        .catch(() => toast.error('Failed to cancel pending entry'));
    }
  }, [removeHvn, removeTriggerLevel]);

  /* ───── FNO on the chart (2026-11-03) ─────
     One expiry's option market, per candle AS OF its close (projectFnoBars):
       price pane — call wall(s) red, put wall(s) green, max pain amber, γ flip purple,
                    optional GEX walls, call→put wall range fill, expected-move band and
                    γ-regime tint; expiry rolls marked; collector outages shaded.
       own panes  — PCR (baseline 1.0: green put-heavy / red call-heavy), ATM IV, and the
                    OI change per candle (green = puts added = support, red = calls added).
     A candle with no FNO sample within 20 min is EMPTY: every line breaks there. */
  const fnoColors = useMemo(() => ({
    call: fcfg.srResistanceColor || '#f23645',
    put: fcfg.srSupportColor || '#089981',
    maxPain: fcfg.maxPainColor || '#ffb300',
    gexFlip: fcfg.gexFlipColor || '#ab47bc',
    wallPos: fcfg.wallPosColor || '#26a69a',
    wallNeg: fcfg.wallNegColor || '#ef5350',
    pcr: fcfg.pcrColor || '#7e57c2',
    pcrEma: fcfg.pcrEmaColor || '#ff9800',
    iv: fcfg.ivColor || '#26c6da',
    ivEma: fcfg.ivEmaColor || '#ec407a',
    oiRange: fcfg.oiRangeColor || '#5c6bc0',
    expMove: fcfg.expMoveColor || '#90a4ae',
  }), [fcfg.srResistanceColor, fcfg.srSupportColor, fcfg.maxPainColor, fcfg.gexFlipColor, fcfg.wallPosColor, fcfg.wallNegColor, fcfg.pcrColor, fcfg.pcrEmaColor, fcfg.ivColor, fcfg.ivEmaColor, fcfg.oiRangeColor, fcfg.expMoveColor]);

  const buildFnoChart = useCallback((rows: CandleRow[]): { lines: NonNullable<React.ComponentProps<typeof TradingChart>['fnoLevels']>; view: FnoViewProps | null; bars: FnoBar[] } => {
    const none = { lines: [] as any[], view: null, bars: [] as FnoBar[] };
    if (!fnoOverlay || !fnoSeries || fnoSeries.supported === false || !fnoSeries.samples?.length || !rows?.length) return none;
    const times = rows.map(c => c.time as number);
    const bars = projectFnoBars(fnoSeries.samples, times, { tolSec: Math.round((fnoSeries.tolMs ?? 20 * 60_000) / 1000), maxTime: replayMaxTime ?? null });
    if (!bars.some(b => b.s)) return { ...none, bars };
    const col = fnoColors;
    const hexA = (hex: string, a: number) => {
      const h = (hex || '').replace('#', '');
      if (h.length !== 6) return hex;
      const n = parseInt(h, 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
    };
    const latestMode = fcfg.lineMode === 'latest';
    let lastS: FnoSample | null = null;
    for (let i = bars.length - 1; i >= 0; i--) if (bars[i].s) { lastS = bars[i].s; break; }
    const lines: any[] = [];
    // lineStyle: 0 solid · 1 dotted · 2 dashed · 3 large dashed
    const line = (key: string, label: string, color: string, lineWidth: number, lineStyle: number, pick: (s: FnoSample) => number | null | undefined) => {
      let data: Array<{ time: number; value: number | null }>;
      if (latestMode) {
        const v = lastS ? pick(lastS) : null;
        if (v == null || !Number.isFinite(v)) return;
        data = [{ time: bars[0].time, value: v }, { time: bars[bars.length - 1].time, value: v }];
      } else {
        data = bars.map(b => {
          const v = b.s ? pick(b.s) : null;
          return { time: b.time, value: v != null && Number.isFinite(v) ? v : null };
        });
        if (!data.some(d => d.value != null)) return;
      }
      lines.push({ key, label, color, lineStyle, lineWidth, data });
    };
    const rankAlpha = (i: number) => (i === 0 ? 1 : i === 1 ? 0.78 : i === 2 ? 0.62 : 0.5);
    const priceLines = !isSynth; // a synthetic name's price axis is not the underlying's
    if (priceLines) {
      if (fcfg.showSR !== false) {
        const n = Math.max(1, Math.min(6, Number(fcfg.srCount ?? 1) || 1));
        for (let i = 0; i < n; i++) {
          line(`call${i + 1}`, i === 0 ? 'Call wall' : `Call wall ${i + 1}`, hexA(col.call, rankAlpha(i)), i === 0 ? 2 : 1, 0, s => s.res?.[i]?.[0]);
          line(`put${i + 1}`, i === 0 ? 'Put wall' : `Put wall ${i + 1}`, hexA(col.put, rankAlpha(i)), i === 0 ? 2 : 1, 0, s => s.sup?.[i]?.[0]);
        }
      }
      if (fcfg.showMaxPain !== false) line('maxPain', 'Max pain', col.maxPain, 1, 2, s => s.mp);
      if (fcfg.showGexFlip !== false) line('gexFlip', 'γ flip', col.gexFlip, 2, 3, s => s.gf);
      if (fcfg.showWalls === true) {
        const n = Math.max(0, Math.min(6, Number(fcfg.wallCount ?? 2) || 0));
        for (let i = 0; i < n; i++) {
          line(`gexPos${i + 1}`, `+γ wall ${i + 1}`, hexA(col.wallPos, rankAlpha(i)), 1, 1, s => s.wp?.[i]?.[0]);
          line(`gexNeg${i + 1}`, `−γ wall ${i + 1}`, hexA(col.wallNeg, rankAlpha(i)), 1, 1, s => s.wn?.[i]?.[0]);
        }
      }
    }
    // Stepped bands, one run per stretch of candles with data (they break at gaps).
    const bands: NonNullable<FnoViewProps['bands']> = [];
    const runs = (key: string, color: string, pick: (s: FnoSample) => [number, number] | null) => {
      let cur: Array<{ time: number; upper: number; lower: number }> = [];
      let k = 0;
      const flush = () => { if (cur.length >= 2) bands.push({ key: `${key}${k++}`, color, points: cur }); cur = []; };
      for (const b of bars) {
        const r = b.s ? pick(b.s) : null;
        if (!r || !Number.isFinite(r[0]) || !Number.isFinite(r[1])) { flush(); continue; }
        cur.push({ time: b.time, upper: Math.max(r[0], r[1]), lower: Math.min(r[0], r[1]) });
      }
      flush();
    };
    if (priceLines && !latestMode) {
      if (fcfg.showOiRange !== false) runs('oiRange', hexA(col.oiRange, 0.07), s => (s.res?.[0]?.[0] != null && s.sup?.[0]?.[0] != null ? [s.res[0][0], s.sup[0][0]] : null));
      if (fcfg.showExpMove === true) runs('expMove', hexA(col.expMove, 0.10), s => (s.sp != null && s.st != null && s.st > 0 ? [s.sp + s.st, s.sp - s.st] : null));
    }
    // EMA over the candles that HAVE data; restarts after a gap so a stale value never bleeds in.
    const ema = (pick: (s: FnoSample) => number | null, lenRaw: any, scale = 1) => {
      const len = Math.max(2, Math.min(200, Math.round(Number(lenRaw) || 7)));
      const k = 2 / (len + 1);
      let prev: number | null = null;
      return bars.map(b => {
        const v = b.s ? pick(b.s) : null;
        if (v == null || !Number.isFinite(v)) { prev = null; return { time: b.time, value: null }; }
        prev = prev == null ? v * scale : v * scale * k + prev * (1 - k);
        return { time: b.time, value: Number(prev.toFixed(4)) };
      });
    };
    const showPcrPane = fcfg.showPcr !== false || fcfg.showPcrEma !== false;
    const showIvPane = fcfg.showIv !== false || fcfg.showIvEma !== false;
    // Roll markers: only where they stay readable (a 1D chart on D0 rolls every bar).
    const tierName = fnoSeries.pinned ? '' : `${fnoSeries.sel || fnoRollTier}0`;
    let rolls = fcfg.showRolls === false || fnoSeries.pinned ? [] : bars.filter(b => b.roll).map(b => ({ time: b.time, text: `${tierName} → ${fmtExpiry(b.roll!)}` }));
    if (rolls.length > Math.max(2, bars.length / 8)) rolls = [];
    const gaps = fcfg.showGaps === false ? [] : Array.from(gapBarTimes(bars, fnoSeries.gaps || []));
    const view: FnoViewProps = {
      bars,
      title: fnoSeries.pinned ? `FNO ${fmtExpiry(String(fnoSeries.sel))}` : `FNO ${tierName}`,
      colors: { call: col.call, put: col.put, maxPain: col.maxPain, gexFlip: col.gexFlip, pcr: col.pcr, iv: col.iv, ivEma: col.ivEma, pcrEma: col.pcrEma },
      pcr: showPcrPane ? { line: fcfg.showPcr !== false, ema: fcfg.showPcrEma !== false ? ema(s => s.pcr, fcfg.pcrEmaLength) : null } : null,
      iv: showIvPane ? { line: fcfg.showIv !== false, ema: fcfg.showIvEma !== false ? ema(s => s.iv, fcfg.ivEmaLength, 100) : null } : null,
      oi: fcfg.showOi !== false ? { mode: fcfg.oiMode === 'total' ? 'total' : 'net' } : null,
      rolls,
      gapTimes: gaps,
      bands,
      regime: fcfg.showRegime === true ? bars.filter(b => b.s?.ng != null && b.s.ng !== 0).map(b => ({ time: b.time, sign: (b.s!.ng! > 0 ? 1 : -1) as 1 | -1 })) : null,
    };
    return { lines, view, bars };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fnoOverlay, fnoSeries, replayMaxTime, fnoColors, isSynth, fnoRollTier, fcfg.lineMode, fcfg.showSR, fcfg.srCount, fcfg.showMaxPain, fcfg.showGexFlip, fcfg.showWalls, fcfg.wallCount,
      fcfg.showOiRange, fcfg.showExpMove, fcfg.showPcr, fcfg.showPcrEma, fcfg.pcrEmaLength, fcfg.showIv, fcfg.showIvEma, fcfg.ivEmaLength, fcfg.showOi, fcfg.oiMode, fcfg.showRolls, fcfg.showGaps, fcfg.showRegime]);
  // Pane 0 (single / workspace / split-left / quad-Q1) and pane 1 (split-right, own interval).
  const fnoChart0 = useMemo(() => buildFnoChart(paneData[0] || []), [buildFnoChart, paneData]);
  const fnoChart1 = useMemo(() => (layoutMode === 'split' ? buildFnoChart(paneData[1] || []) : null), [buildFnoChart, paneData, layoutMode]);

  /* The expiry switch on the chart legend (and the toolbar): rolling tiers + every live date. */
  const onFnoSelect = useCallback((v: string) => {
    if (v === 'D' || v === 'W' || v === 'M') {
      setFnoPin(null);
      if (v !== fnoRollTier && !fnoTierBusy) selectFnoTier(v);
    } else {
      setFnoPin(v);
    }
  }, [setFnoPin, fnoRollTier, fnoTierBusy, selectFnoTier]);
  const fnoSelect = useMemo<FnoSelectProps | null>(() => {
    if (!fnoOverlay || !fnoSeries || fnoSeries.supported === false) return null;
    const exps = fnoSeries.expiries || [];
    const live = exps.filter(e => e.live);
    const kindName = (k: string) => (k === 'M' ? 'monthly' : k === 'W' ? 'weekly' : 'daily');
    const front = (t: 'D' | 'W' | 'M') => live.find(e => (t === 'D' ? true : t === 'W' ? e.kind === 'W' || e.kind === 'M' : e.kind === 'M'));
    const rolling: FnoSelectOption[] = ([
      ['D', 'D0', 'front expiry'],
      ['W', 'W0', 'front weekly (incl. the monthly)'],
      ['M', 'M0', 'front monthly'],
    ] as Array<['D' | 'W' | 'M', string, string]>).map(([v, label, what]) => {
      const e = front(v);
      return { value: v, label, group: 'rolling' as const, disabled: !e, sub: e ? `${what} · now ${fmtExpiry(e.date)} · ${fmtLeft(e.settleMs)}` : `${what} · none listed`,
        title: 'Follows the front contract and rolls to the next one the moment it settles' };
    });
    const pinnedOpts: FnoSelectOption[] = exps.filter(e => e.live || e.date === fnoPin).map(e => ({
      value: e.date, label: fmtExpiry(e.date), group: 'pinned' as const,
      sub: `${kindName(e.kind)} · ${e.live ? fmtLeft(e.settleMs) : 'settled'}${e.count ? '' : ' · no history yet'}`,
      title: e.firstMs ? `History from ${new Date(e.firstMs).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Recorded from now on',
    }));
    // The chip names what the right edge shows: the tier + the contract it is on now.
    let lastX: string | null = null;
    const smp = fnoSeries.samples || [];
    if (smp.length) lastX = smp[smp.length - 1].x;
    const chip = fnoPin ? `FNO ${fmtExpiry(fnoPin)}` : `FNO ${fnoRollTier}0${lastX ? ` · ${fmtExpiry(lastX)}` : ''}`;
    return { value: fnoSel, chip, pinned: !!fnoPin, options: [...rolling, ...pinnedOpts], onSelect: onFnoSelect };
  }, [fnoOverlay, fnoSeries, fnoPin, fnoRollTier, fnoSel, onFnoSelect]);
  // The expiry the right edge shows (Workspace pill marker).
  const fnoShownExpiry = useMemo(() => {
    if (fnoPin) return fnoPin;
    const smp = fnoSeries?.samples || [];
    return smp.length ? smp[smp.length - 1].x : null;
  }, [fnoPin, fnoSeries]);

  /* ───── Synthiya (docs/SYNTHETIC-SYMBOLS-PLAN.md §5) ─────
     Staircase PCR / ATM-IV / Relative-Strength of the UNDERLYING in the lower
     oscillator pane. Step-projected (not interpolated) onto the candle grid so
     each series reads as discrete regime steps; per-step up/down colouring +
     styling happen in TradingChart (Chart Settings → Synthiya). */
  const [synthUnderCandles, setSynthUnderCandles] = useState<CandleRow[]>([]);
  useEffect(() => {
    if (!isSynth || !overlays.synthiya || !synth?.underlying) { setSynthUnderCandles([]); return; }
    let cancelled = false;
    (async () => {
      const feed = String(synth.underlying!.feedSource || '').toUpperCase();
      const base = synth.underlying!.symbol;
      // Underlying chart series: Delta base → its perp; NSE → the symbol itself
      // (index display-name variants may not resolve — RS is then simply absent).
      const data = await fetchCandles(feed === 'DELTA' ? `${base}USD` : base, feed === 'DELTA' ? 'DELTA' : 'NSE', intervals[0], { silentError: true });
      if (!cancelled) setSynthUnderCandles(data);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSynth, overlays.synthiya, synth, intervals.join(','), auxRefreshTick]);

  const synthiyaSeries = useMemo(() => {
    if (!overlays.synthiya) return null;
    const IST = 19800;
    const candleTimes: number[] = (paneData[0] || []).map(c => c.time as number);
    if (!candleTimes.length) return null;
    // As-of-bar-close projection (projectToBarClose) — right-edge value is the
    // freshest sample on every TF; replay-clamped like the other FNO series.
    const stepProject = (raw: { time: number; value: number }[]) =>
      projectToBarClose(raw, candleTimes, { maxTime: replayMaxTime });
    const pcrRaw = (((fnoData as any)?.pcrSeries as Array<{ t: number; pcr: number }> | undefined) || [])
      .map(d => ({ time: Math.floor(d.t / 1000) + IST, value: Number(d.pcr) })).filter(d => Number.isFinite(d.value));
    const ivRaw = (((fnoData as any)?.ivSeries as Array<{ t: number; iv: number }> | undefined) || [])
      .map(d => ({ time: Math.floor(d.t / 1000) + IST, value: Number(d.iv) * 100 })).filter(d => Number.isFinite(d.value) && d.value > 0);
    // Relative strength of the UNDERLYING vs the benchmark — IDENTICAL definition to the
    // RelStrength chip (spec): LinReg(close,14) of the CSH-transformed closes on both sides,
    // ratio, normalised to 100 at the first shared bar. (It used to be a raw close ratio,
    // which drifted from the RelStrength line it is supposed to mirror.)
    const under = isSynth ? synthUnderCandles : (paneData[0] || []);
    const bench = paneBenchmarkCandles[0] || [];
    const rsRaw: { time: number; value: number }[] = [];
    if (under.length > 14 && bench.length > 14) {
      const uCsh = transformCandles(under, 'CSH');
      const bCsh = transformCandles(bench, 'CSH');
      const uLR = linreg(uCsh.map(c => Number(c.close)), 14);
      const bLR = linreg(bCsh.map(c => Number(c.close)), 14);
      const bByTime = new Map<number, number>();
      bCsh.forEach((c, i) => { const v = bLR[i]; if (Number.isFinite(v) && v > 0) bByTime.set(c.time as number, v); });
      let first: number | null = null;
      uCsh.forEach((c, i) => {
        const uv = uLR[i]; const bv = bByTime.get(c.time as number);
        if (!Number.isFinite(uv) || !(uv > 0) || bv == null) return;
        const ratio = uv / bv;
        if (first == null) first = ratio;
        rsRaw.push({ time: c.time as number, value: Number(((ratio / first!) * 100).toFixed(3)) });
      });
    }
    const pcr = stepProject(pcrRaw), iv = stepProject(ivRaw), rs = stepProject(rsRaw);
    if (!pcr.length && !iv.length && !rs.length) return null;
    return { pcr, iv, rs };
  }, [overlays.synthiya, fnoData, paneData, replayMaxTime, isSynth, synthUnderCandles, paneBenchmarkCandles]);

  /* DbCrZone — fetch the Debit/Credit option-market regime change-points for the underlying,
     covering the loaded candle span. Regime is time-based, so one series serves any TF; the
     chart forward-fills between change-points. Underlying is resolved server-side (BTCUSD→BTC). */
  const [dbcrZonesData, setDbcrZonesData] = useState<Array<{ ts: number; zone: 'DEBIT' | 'CREDIT' | 'NEUTRAL'; score?: number | null }>>([]);
  useEffect(() => {
    if (!overlays.dbcrZone || !symbol) { setDbcrZonesData([]); return; }
    const p0 = paneData[0] || [];
    if (!p0.length) return;
    const IST = 19800;
    const fromMs = ((p0[0].time as number) - IST) * 1000;
    const toMs = ((p0[p0.length - 1].time as number) - IST) * 1000 + 24 * 3600 * 1000; // pad forward for the live edge
    const dbcrSymbol = isSynth ? (synth?.underlying?.symbol || symbol) : symbol;
    let cancelled = false;
    (async () => {
      try {
        const r: any = await api.get('/charts/dbcr-zones', { params: { symbol: dbcrSymbol, from: fromMs, to: toMs }, headers: { _suppressToast: 'true' } });
        if (!cancelled) setDbcrZonesData((r?.data?.data?.zones || []) as any);
      } catch { if (!cancelled) setDbcrZonesData([]); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlays.dbcrZone, symbol, isSynth, synth, intervals.join(','), paneData[0]?.length, auxRefreshTick]);


  /* Latest FNO structure for the crosshair interpreter (hovered-price read). Built
     from the latest point of the nearest enabled tier + the option-outlook (futures)
     + the profile's per-strike ΔOI. Null disables the FNO crosshair section. */
  const fnoInterpInputs = useMemo<FnoInterpretInputs | null>(() => {
    if (!fnoOverlay) return null;
    // The newest candle that has FNO data (replay: up to the playhead) — the same sample
    // the lines end on, so the read and the lines always describe one contract.
    const bars = fnoChart0.bars;
    let last: FnoSample | null = null;
    for (let i = bars.length - 1; i >= 0; i--) if (bars[i].s) { last = bars[i].s; break; }
    if (!last) return null;
    const o = fnoOutlook?.options || null;
    const f = fnoOutlook?.futures || null;
    const deltaByStrike = (optionProfileRaw?.strikes || []).map((s: any) => ({ strike: Number(s.strike), ceOiChg: Number(s.ceOiChg) || 0, peOiChg: Number(s.peOiChg) || 0 })).filter((d: any) => Number.isFinite(d.strike));
    return {
      spot: o?.spot ?? (optionProfileRaw?.spot != null ? Number(optionProfileRaw.spot) : (last.sp ?? null)),
      gexFlip: last.gf ?? null,
      netGamma: last.ng ?? null,
      pcr: last.pcr ?? (o?.pcr ?? null),
      maxPain: last.mp ?? (o?.maxPain ?? null),
      walls: [...(last.wp || []), ...(last.wn || [])].map(([strike, gex]) => ({ strike, gex })),
      support: (last.sup || []).map(([strike, oi]) => ({ strike, oi })),
      resistance: (last.res || []).map(([strike, oi]) => ({ strike, oi })),
      deltaByStrike,
      futures: f ? { oiPattern: f.oiPattern ?? null, basis: Number(f.basis) || 0, bias: f.bias } : null,
      futuresBuckets: Array.isArray(futuresProfileRaw?.buckets) ? futuresProfileRaw.buckets.map((b: any) => ({ price: Number(b.price), net: Number(b.net) })) : undefined,
      flow: o?.flow || null,
      pinRisk: o?.pinRisk || null,
    };
  }, [fnoOverlay, fnoChart0, fnoOutlook, optionProfileRaw, futuresProfileRaw]);

  /* Composite FNO Bull/Bear score at the live spot → OverView speedometer. */
  const fnoScore = useMemo(() => {
    const spot = fnoInterpInputs?.spot;
    if (!fnoInterpInputs || !(Number(spot) > 0)) return null;
    return scoreFno(Number(spot), fnoInterpInputs);
  }, [fnoInterpInputs]);

  /* ───── option strategies on levels + arm state (polled while any level is set) ───── */
  const anyTriggerLevel = !!(triggerLevels.nbuy || triggerLevels.nshort || triggerLevels.bbuy || triggerLevels.bshort);
  const reloadLvlOpt = useCallback(async () => {
    if (!instrumentId) { setLvlOpt(null); return; }
    try { setLvlOpt(await levelOptionTriggersService.state(instrumentId)); } catch { /* keep the last state */ }
  }, [instrumentId]);
  useEffect(() => {
    if (!instrumentId) { setLvlOpt(null); return; }
    reloadLvlOpt();
    if (!anyTriggerLevel) return;
    const t = window.setInterval(() => { if (!document.hidden) reloadLvlOpt(); }, 30_000);
    return () => window.clearInterval(t);
    // Re-read when a level is set/moved/removed: its arm state starts over.
  }, [instrumentId, anyTriggerLevel, triggerLevels.nbuy, triggerLevels.nshort, triggerLevels.bbuy, triggerLevels.bshort, reloadLvlOpt]);
  const lvlOptCapable = levelOptionCapable(symbol, exchange);
  // Does this symbol have listed options? → the order dialog offers its Strategy tab.
  // (Was: Trade segment = OPTIONS on the Instruments page — a flag that said nothing
  // about whether options exist.)
  const [hasOptionChain, setHasOptionChain] = useState(false);
  useEffect(() => {
    let dead = false;
    setHasOptionChain(false);
    if (!symbol) return;
    api.get<any>('/options/has-chain', { params: { symbol, exchange }, headers: { _suppressToast: 'true' } as any })
      .then(r => { if (!dead) setHasOptionChain(!!r.data?.data?.hasChain); })
      .catch(() => { /* no Strategy tab */ });
    return () => { dead = true; };
  }, [symbol, exchange]);
  const lvlAttachment = useCallback((k: 'nbuy' | 'nshort' | 'bbuy' | 'bshort') =>
    (lvlOpt?.attachments || []).find(a => a.levelKey === k && (a.status === 'ARMED' || a.status === 'FIRING')) || null, [lvlOpt]);
  /** Right-axis title for a trigger line: the type, "· waiting" when it is held disarmed
   *  (set inside its own zone — fires only after price leaves and re-crosses), and the
   *  attached option strategy ("· paused" while Auto-Execute is OFF). */
  const trigAxisLabel = useCallback((k: 'nbuy' | 'nshort' | 'bbuy' | 'bshort', type: string, level: number) => {
    let t = type;
    if (levelArmed(lvlOpt?.arm, k, level) === false) t += ' · waiting';
    const a = lvlAttachment(k);
    if (a) {
      const nm = (findTemplate(a.templateId)?.name || a.templateId).replace(/ \(credit\)$/, '');
      t += ` ⚡${nm} ${attExpiryShort(a)}×${a.lots}${a.tradingMode === 'LIVE' ? ' LIVE' : ''}${lvlOpt?.autoExecute ? '' : ' · paused'}`;
    }
    return t;
  }, [lvlOpt, lvlAttachment]);

  /* ───── compute price levels for TradingChart ───── */
  const priceLevelsProp = useMemo(() => {
    const levels: { type: string; id?: string | number; price: number; color: string; lineStyle?: number; draggable?: boolean; deletable?: boolean; axisLabel?: string; noAxisLabel?: boolean }[] = [];
    if (triggerLevels.nbuy) levels.push({ type: 'NBUY', price: triggerLevels.nbuy, color: '#4caf50', lineStyle: 0, deletable: true, axisLabel: trigAxisLabel('nbuy', 'NBUY', triggerLevels.nbuy) });
    if (triggerLevels.nshort) levels.push({ type: 'NSHORT', price: triggerLevels.nshort, color: '#f44336', lineStyle: 0, deletable: true, axisLabel: trigAxisLabel('nshort', 'NSHORT', triggerLevels.nshort) });
    if (triggerLevels.bbuy) levels.push({ type: 'BBUY', price: triggerLevels.bbuy, color: '#00bcd4', lineStyle: 2, deletable: true, axisLabel: trigAxisLabel('bbuy', 'BBUY', triggerLevels.bbuy) });
    if (triggerLevels.bshort) levels.push({ type: 'BSHORT', price: triggerLevels.bshort, color: '#ff9800', lineStyle: 2, deletable: true, axisLabel: trigAxisLabel('bshort', 'BSHORT', triggerLevels.bshort) });
    // HVN — multiple per symbol; each gets a stable id so drag/delete targets the right row.
    for (const h of hvnLevels) {
      levels.push({ type: 'HVN', id: h.id, price: h.price, color: '#9c27b0', lineStyle: 0, draggable: true, deletable: true });
    }
    // Pending Entry lines (round 27) — draggable; drop fires PUT to update
    // entryPrice. BUY = green, SHORT = red. Status TRIGGERED is rendered
    // as a dim/dashed line; drag is disabled by the controller anyway.
    for (const pe of pendingRows) {
      // Round 89 — label scale-in legs E1/E2/… and dim the ones already fired.
      const isGroup = (pe.entryCount ?? 1) > 1 && pe.entryIndex != null;
      const fired = pe.status === 'TRIGGERED';
      const tag = isGroup ? `E${pe.entryIndex}/${pe.entryCount}` : 'PENDING';
      levels.push({
        type: 'PENDING_ENTRY', id: pe.id, price: pe.entryPrice,
        color: pe.side === 'BUY' ? '#26a69a' : '#ef5350',
        lineStyle: 2, // dashed — visually distinct from filled SL/Target
        draggable: pe.status === 'PENDING',
        deletable: true,
        axisLabel: `${tag}${fired ? ' ✓' : ''}`,
      });
    }
    // Pending-group shared SL / Target (Round 89). All legs of a scale-in
    // group carry the same SL/Target; render them so the user sees and can
    // drag the protective levels even before any entry fires. Only when no
    // active trade is driving its own SL/Target lines (avoids duplicates —
    // once a leg fills, the activeTrade block below renders them instead).
    // SYNTH analogue: once a trigger fires, `basketLive` owns THE single
    // money-labelled SL/TGT pair — the still-armed E2..EN rows must not
    // draw a second plain pair (they'd also drift, since the two drags
    // persisted to different stores; the backend now keeps them synced).
    if (!activeTrade && !(isSynth && synth?.basketLive) && pendingRows.length > 0) {
      const slRow = pendingRows.find(p => p.stopLoss != null);
      const tgtRow = pendingRows.find(p => p.target != null);
      if (slRow?.stopLoss != null) {
        levels.push({ type: 'PSL', price: slRow.stopLoss, color: '#e53935', lineStyle: 0, draggable: true, axisLabel: 'SL' });
      }
      if (tgtRow?.target != null) {
        levels.push({ type: 'PTGT', price: tgtRow.target, color: '#1e88e5', lineStyle: 0, draggable: true, axisLabel: 'TGT' });
      }
    }
    // Ghost preview lines for the BUY/SHORT capture flow — render whatever has
    // been captured so far, before the user lands on the confirm modal.
    if (orderCapture.stage !== 'idle') {
      orderCapture.entries.forEach((p, i) => levels.push({
        type: orderCapture.entryCount > 1 ? `E${i + 1}` : 'ENTRY',
        price: p, color: '#3f51b5', lineStyle: 0, draggable: false,
      }));
      if (orderCapture.sl != null)    levels.push({ type: 'SL?',   price: orderCapture.sl,    color: '#e53935', lineStyle: 1, draggable: false });
      if (orderCapture.target != null) levels.push({ type: 'TGT?', price: orderCapture.target, color: '#1e88e5', lineStyle: 1, draggable: false });
    }
    // Active-trade SL / Target — round 32 unification.
    //
    // The OPERATIVE breach level is currentTrailingSl whenever it exists
    // (the trail manager seeds it from the initial alert SL on the very
    // first poll, then ratchets monotonically). manualStopLoss only acts
    // as the R-multiple anchor inside the engine — it does NOT directly
    // control breach. Showing two lines (red "SL" at manual + orange "TSL"
    // at current) confused users into dragging the wrong line.
    //
    // Round 32 collapses this into ONE bold "SL" line at the *most
    // protective* of (currentTrailingSl, manualStopLoss, stopLoss) — i.e.
    // exactly the level the trade will close at if breached. The user's
    // manual override (when it is not the active level — typically less
    // protective than the ratcheted TSL) is rendered as a faint dashed
    // line so it remains visible without competing for attention. Drag
    // updates manualStopLoss; the backend (round 32) now also bumps
    // currentTrailingSl when the new manual is more protective, so a
    // drag actually moves the breach instead of getting overridden by
    // the next monitor cycle.
    if (activeTrade) {
      // Issue 4 — FULL MANUAL CONTROL. A manual SL is AUTHORITATIVE: the backend
      // pauses auto-trail when the user sets one, so it is the real breach in
      // either direction. We therefore show exactly ONE bold draggable SL line:
      //   • manual SL when the user has set one (label [M]); else
      //   • the most-protective of (trailing SL, initial SL) while auto-trail
      //     is driving the breach (label [TSL] / [I]).
      // No more faint grey "Manual SL" companion line — the operative line and
      // the user's value are now always the same thing.
      // Operative breach mirrors the backend (getInitialSL / checkBreach):
      //   • trail ACTIVE  → currentTrailingSl is the breach  → show TSL
      //   • trail OFF / pre-activation → getInitialSL = manualStopLoss ?? stopLoss
      // So once the user re-enables auto-trail, the line correctly follows the
      // live trail again instead of being stuck on the old manual value.
      const tsl = activeTrade.currentTrailingSl;
      const mSl = activeTrade.manualStopLoss;
      const iSl = activeTrade.stopLoss;
      let operativeSL: number | null = null;
      let source: 'TSL' | 'M' | 'I' = 'I';
      if (tsl != null && Number.isFinite(tsl)) { operativeSL = tsl; source = 'TSL'; }
      else if (mSl != null && Number.isFinite(mSl)) { operativeSL = mSl; source = 'M'; }
      else if (iSl != null && Number.isFinite(iSl)) { operativeSL = iSl; source = 'I'; }
      // Sticky override — pin the line to the just-dragged price until the server
      // converges or the window expires. Clears early on convergence so it never
      // lingers. This is the actual revert fix: it ignores whichever stale field
      // a post-drag refresh reports.
      const ov = slOverrideRef.current;
      if (ov) {
        if (Date.now() >= ov.until) {
          slOverrideRef.current = null; // expired
        } else if (operativeSL != null && Math.abs(operativeSL - ov.price) <= Math.max(ov.price * 5e-4, 1e-9)) {
          slOverrideRef.current = null; // server caught up — release
        } else {
          operativeSL = ov.price; source = 'M'; // hold the user's value
        }
      }
      if (operativeSL != null) {
        levels.push({
          type: 'SL',
          price: operativeSL,
          color: '#e53935',
          lineStyle: 0,
          draggable: true,
          axisLabel: `SL [${source}]`,
        });
      }
      if (activeTrade.target != null) {
        levels.push({ type: 'TARGET', price: activeTrade.target, color: '#1e88e5', lineStyle: 0, draggable: true });
      }
    }
    // FNO GEX walls + S/R / MaxPain / GEX-flip all draw as lines via the fnoLevels
    // prop now (ranked, thickness-by-rank, historical or latest mode) — nothing to
    // push here.
    // Payoff-zones levels (break-even / max-profit / TP / SL) now draw via the
    // LEFT-labelled primitive (payoffLeftLevels) so they don't clutter the right
    // price axis / option-profile rail — NOT pushed here.
    // Campaign click-to-draw preview lines (E1..En = deploy levels, danger = hard stop).
    drawnStages.forEach((p, i) => levels.push({ type: 'STAGE', price: p, color: '#7e57c2', lineStyle: 0, axisLabel: `E${i + 1}` }));
    drawnUp.forEach((p, i) => levels.push({ type: 'STAGE', price: p, color: '#7e57c2', lineStyle: 0, axisLabel: `E${i + 1}↑` }));
    drawnDn.forEach((p, i) => levels.push({ type: 'STAGE', price: p, color: '#7e57c2', lineStyle: 0, axisLabel: `E${i + 1}↓` }));
    if (drawnRU != null) levels.push({ type: 'RANGE', price: drawnRU, color: '#26a69a', lineStyle: 0, axisLabel: 'Range ↑' });
    if (drawnRL != null) levels.push({ type: 'RANGE', price: drawnRL, color: '#26a69a', lineStyle: 0, axisLabel: 'Range ↓' });
    if (drawnDanger != null) levels.push({ type: 'DANGER', price: drawnDanger, color: '#c62828', lineStyle: 2, axisLabel: 'Danger' });

    // RUNNING campaign ladder — armed (PENDING) entries are draggable dashed lines; deployed ones
    // are solid + fixed; danger/profit levels are draggable too. The LABELS render LEFT-pinned
    // (campaignLeftLevels) so they never sit under the option-profile rail — here we only draw the
    // (draggable) lines, with NO right-axis label to keep the chart clean.
    for (const c of openCampaigns) {
      for (const s of (c.stages || [])) {
        const p = Number(s.anchorLevel ?? s.triggerPrice); if (!(p > 0)) continue;
        const deployed = s.status === 'DEPLOYED';
        levels.push({ type: 'CAMP_STAGE', id: `camp:${c.id}:${s.index}`, price: p, color: deployed ? '#5e35b1' : '#9575cd', lineStyle: deployed ? 0 : 2, draggable: !deployed, noAxisLabel: true });
      }
      if (Number(c.hardSlPrice) > 0) levels.push({ type: 'CAMP_DLO', id: `camp:${c.id}:dlo`, price: Number(c.hardSlPrice), color: '#c62828', lineStyle: 2, draggable: true, noAxisLabel: true });
      if (Number(c.hardSlPriceUpper) > 0) levels.push({ type: 'CAMP_DHI', id: `camp:${c.id}:dhi`, price: Number(c.hardSlPriceUpper), color: '#c62828', lineStyle: 2, draggable: true, noAxisLabel: true });
      if (Number(c.profitLevelUp) > 0) levels.push({ type: 'CAMP_PUP', id: `camp:${c.id}:pup`, price: Number(c.profitLevelUp), color: '#2e7d32', lineStyle: 0, draggable: true, noAxisLabel: true });
      if (Number(c.profitLevelDn) > 0) levels.push({ type: 'CAMP_PDN', id: `camp:${c.id}:pdn`, price: Number(c.profitLevelDn), color: '#2e7d32', lineStyle: 0, draggable: true, noAxisLabel: true });
    }
    // Basket-backed LIVE synthetic — Entry/SL/Target in combined-premium units with
    // MONEY labels: Entry shows the position's current gain/loss (live premium vs
    // fill), SL the loss if hit, Target the gain if hit. SL/Target are draggable —
    // drops PATCH the basket's exit levels (the drawn trigger levels ARE the exits).
    if (isSynth && synth?.basketLive && !synth.engineCycle) {
      const bl = synth.basketLive;
      const sideSign = bl.side === 'LONG' ? 1 : -1;
      const money = (v: number | null) => v == null ? '' : `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
      // ONE blended Entry line for the whole scale-in — the lots-weighted average
      // of EVERY fired tranche's fill (a fired E1 becomes the Entry line; when E2
      // fires it re-anchors to the blended E1+E2 average, still ONE line). SL and
      // Target stay a single shared pair. inrPerChartPoint sums across tranches so
      // the ₹ P&L reflects the WHOLE position (not just the latest basket's lots).
      const fe = (synth as any).firedEntries as Array<{ entryPremium: number; lots: number; inrPerChartPoint: number }> | undefined;
      let blendedEntry: number | null = bl.entryPremium;
      let posInrPerPoint = bl.inrPerChartPoint;
      let nFills = 1;
      if (fe && fe.length) {
        const totLots = fe.reduce((s, e) => s + (Number(e.lots) || 0), 0);
        if (totLots > 0) {
          blendedEntry = fe.reduce((s, e) => s + Number(e.entryPremium) * (Number(e.lots) || 0), 0) / totLots;
          posInrPerPoint = fe.reduce((s, e) => s + (Number(e.inrPerChartPoint) || 0), 0);
          nFills = fe.length;
        }
      }
      const inrOf = (fromP: number | null, toP: number | null) =>
        fromP == null || toP == null ? null : (toP - fromP) * sideSign * posInrPerPoint;
      if (blendedEntry != null) {
        const pnl = Number(markPrice) > 0 ? inrOf(blendedEntry, Number(markPrice)) : null;
        levels.push({
          type: 'B_ENTRY', price: blendedEntry, color: '#3f51b5', lineStyle: 0, draggable: false,
          axisLabel: `Entry ${bl.side === 'LONG' ? 'B' : 'S'}${nFills > 1 ? ' avg' : ''} ${money(pnl)}`.trim(),
        });
      }
      if (bl.slPremium != null) {
        levels.push({ type: 'BSL', price: bl.slPremium, color: '#e53935', lineStyle: 0, draggable: true, axisLabel: `SL ${money(inrOf(blendedEntry, bl.slPremium))} net`.trim() });
      }
      if (bl.targetPremium != null) {
        levels.push({ type: 'BTGT', price: bl.targetPremium, color: '#1e88e5', lineStyle: 0, draggable: true, axisLabel: `TGT ${money(inrOf(blendedEntry, bl.targetPremium))} net`.trim() });
      }
    }
    // ENGINE-mirror synthetic (a running ThetaMiner/Rudra/… cycle): read-only ENTRY /
    // STOP lines in COMBINED-PREMIUM units — the engine manages the position, so
    // nothing here is draggable or deletable. STOP is min(hard SL, CLH trail) and
    // ratchets down as the trail tightens (the 30s getOne poll refreshes it).
    if (isSynth && synth?.engineCycle) {
      const ec = synth.engineCycle;
      if (ec.entryPremium != null && ec.entryPremium > 0) {
        levels.push({ type: 'ENGINE_ENTRY', price: ec.entryPremium, color: '#3f51b5', lineStyle: 0, draggable: false, axisLabel: 'ENTRY' });
      }
      if (ec.stopPremium != null && ec.stopPremium > 0) {
        levels.push({ type: 'ENGINE_STOP', price: ec.stopPremium, color: '#e53935', lineStyle: 2, draggable: false, axisLabel: ec.trailSl != null ? 'STOP [T]' : 'STOP' });
      }
    }
    return levels;
  }, [triggerLevels, trigAxisLabel, activeTrade, hvnLevels, orderCapture, pendingRows, drawnStages, drawnUp, drawnDn, drawnRU, drawnRL, drawnDanger, openCampaigns, isSynth, synth, markPrice]);
  /* ── Synthetic charts plot the BASKET'S OWN PREMIUM (tens/hundreds), not the
     underlying (tens of thousands). Every underlying-scale overlay — FNO levels
     (GEX flip / OI walls / S-R), the option-profile rail, payoff zones, the basket
     P&L badge, the perp mark line — must therefore be suppressed, otherwise a
     64,000-price line joins the same price scale and squashes the candles into a
     flat line at the bottom. Synthiya (own pane/scale) and the premium-unit trigger
     lines stay. */
  const chartFnoLines     = fnoChart0.lines;
  const chartFnoLines1    = fnoChart1 ? fnoChart1.lines : fnoChart0.lines;
  const chartFnoView1     = fnoChart1 ? fnoChart1.view : fnoChart0.view;
  const chartLeftLevelsUi = useMemo(() => (isSynth ? [] : chartLeftLevels), [isSynth, chartLeftLevels]);
  const chartZones        = isSynth ? undefined : payoffZoneBands;
  const chartOptProfile   = isSynth ? null : optionProfileData;
  const chartFnoInterp    = isSynth ? undefined : fnoInterpInputs;
  /* Engine-mirror synthetic P&L badge (Payoff Zones chip) — the basket badge can't apply
     here (an engine cycle has no OptionBasket), so compute the open P&L from the cycle's
     entry premium vs the LIVE combined premium (the quote poll feeds markPrice on synth
     charts). All-short structures only (ThetaMiner strangle/straddle) — for mixed legs
     the unsigned entry-premium sum isn't a chart-scale value. */
  const synthEnginePnlBadge = useMemo(() => {
    if (!isSynth || !overlays.payoffZones || !synth?.engineCycle) return null;
    const ec = synth.engineCycle;
    if (!(Number(ec.entryPremium) > 0) || !(Number(markPrice) > 0)) return null;
    const legs = ec.legs || [];
    if (!legs.length || !legs.every(l => l.side === 'SELL')) return null;
    const qty = Number(legs[0]?.qty) || 1;
    const feed = String(synth.underlying?.feedSource || '').toUpperCase();
    const base = String(synth.underlying?.symbol || '').toUpperCase();
    const mult = feed === 'DELTA'
      ? (base === 'ETH' ? 0.01 : 0.001) * USD_TO_INR              // Delta option contract value × fx
      : (Number(synth.underlying?.lotSize) || 1);                  // NSE: premiums already ₹ × lot
    const open = (Number(ec.entryPremium) - Number(markPrice)) * qty * mult;
    const inr = (v: number) => `₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
    const lines: string[] = [`Entry ${Number(ec.entryPremium).toFixed(1)} · Now ${Number(markPrice).toFixed(1)}`];
    if (ec.stopPremium != null && Number(ec.stopPremium) > 0) lines.push(`Stop ${Number(ec.stopPremium).toFixed(1)}${ec.trailSl != null ? ' (trail)' : ''}`);
    if (Number(ec.realizedPnl)) lines.push(`Booked ${Number(ec.realizedPnl) >= 0 ? '+' : '−'}${inr(Number(ec.realizedPnl))}`);
    return { value: open, sub: `${synth.name} · ${ec.mode}`, lines };
  }, [isSynth, overlays.payoffZones, synth, markPrice]);
  /* Bhairav simulation badge — replay-aware paper walk of the strategy's own
     signals (NO trades/alerts): entry/exit at the RAW close of each signal bar,
     ₹1,00,000 notional, marks the open trade to the playhead close. Uses
     strategyOverlay0 directly (for 'bhairav' the effective overlay IS the
     generic one — no special-cased variant exists). */
  const bhairavSimBadge = useMemo(() => {
    if (activeStrategy !== 'bhairav' || !paneData[0]?.length) return null;
    // Gate/notice states (sub-4H timeframe, missing daily history) — explain
    // instead of simulating: autotrade emits nothing here.
    if (strategyOverlay0?.notice) {
      return { value: 0, sub: 'Bhairav — inactive', lines: [strategyOverlay0.notice] };
    }
    if (!strategyOverlay0?.signalsByTime) return null;
    const INVEST = Math.max(1000, Number((settings as any)?.bhairav?.investInr) || 100000);
    const maxT = replayMaxTime ?? Infinity;
    const closed: number[] = [];
    let dir: 1 | -1 | 0 = 0; let entry = 0; let lastClose = 0;
    for (const c of paneData[0]) {
      const t = c.time as number;
      if (t > maxT) break;
      lastClose = Number(c.close);
      const sig = strategyOverlay0.signalsByTime.get(t);
      if (!sig) continue;
      const px = Number(c.close);
      if (!(px > 0)) continue;
      const wantDir = sig.pos === 'LONG' ? 1 : sig.pos === 'SHORT' ? -1 : 0;
      if (dir !== 0 && wantDir !== dir && entry > 0) { closed.push(INVEST * (px / entry - 1) * dir); dir = 0; entry = 0; }
      if (wantDir !== 0 && dir === 0) { dir = wantDir as 1 | -1; entry = px; }
    }
    const openPnl = dir !== 0 && entry > 0 && lastClose > 0 ? INVEST * (lastClose / entry - 1) * dir : 0;
    const cum = closed.reduce((s, x) => s + x, 0) + openPnl;
    const inr = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
    const last5 = closed.slice(-5).map(x => (x >= 0 ? '✓' : '✗')).join(' ') || '—';
    const roiOpen = (openPnl / INVEST) * 100;
    const roiCum = (cum / INVEST) * 100;
    return {
      value: openPnl,
      sub: dir !== 0 ? `${dir === 1 ? 'LONG' : 'SHORT'} · Bhairav @ ${entry >= 1000 ? entry.toFixed(0) : entry.toFixed(2)}` : 'FLAT · Bhairav',
      lines: [
        `Invested ₹${INVEST.toLocaleString('en-IN')} (sim)`,
        `ROI ${roiOpen >= 0 ? '+' : ''}${roiOpen.toFixed(2)}% · cum ${inr(cum)} (${roiCum >= 0 ? '+' : ''}${roiCum.toFixed(2)}%)`,
        `Last 5: ${last5} · ${closed.length} closed`,
        `Gate: ${bhairavGateLabel(bhairavGateProfile(intervals[0] || 0))}`,
      ],
    };
  }, [activeStrategy, strategyOverlay0, paneData, replayMaxTime, settings, intervals]);

  // chartPnlBadge is assembled further down, once Om's overlay exists — a
  // strategy `notice` has to be able to reach the badge, and Om's overlay is
  // declared after the OverView score series it depends on.
  const chartMarkPrice    = isSynth ? null : markPrice;

  // Build a leg pane's price-level lines: trigger levels + its own pending-entry
  // lines (E1..EN dashed + shared SL/Target) + the live BUY/SHORT capture ghosts.
  const buildLegLevels = (
    levelsTrig: TriggerLevels,
    pendingRowsForLeg: PendingRow[],
    pane: 'ce' | 'pe',
  ) => {
    const levels: { type: string; id?: string | number; price: number; color: string; lineStyle?: number; draggable?: boolean; axisLabel?: string }[] = [];
    if (levelsTrig.nbuy) levels.push({ type: 'NBUY', price: levelsTrig.nbuy, color: '#4caf50', lineStyle: 0 });
    if (levelsTrig.nshort) levels.push({ type: 'NSHORT', price: levelsTrig.nshort, color: '#f44336', lineStyle: 0 });
    if (levelsTrig.bbuy) levels.push({ type: 'BBUY', price: levelsTrig.bbuy, color: '#00bcd4', lineStyle: 2 });
    if (levelsTrig.bshort) levels.push({ type: 'BSHORT', price: levelsTrig.bshort, color: '#ff9800', lineStyle: 2 });
    // Pending-entry lines for this leg (so a created leg order is visible). Each
    // scale-in leg is labelled E1/E2/…; fired legs get a ✓. Shared SL/Target are
    // shown once (display lines).
    for (const pe of pendingRowsForLeg) {
      const isGroup = (pe.entryCount ?? 1) > 1 && pe.entryIndex != null;
      const fired = pe.status === 'TRIGGERED';
      levels.push({
        type: 'PENDING_ENTRY', id: pe.id, price: pe.entryPrice,
        color: pe.side === 'BUY' ? '#26a69a' : '#ef5350', lineStyle: 2,
        axisLabel: `${isGroup ? `E${pe.entryIndex}/${pe.entryCount}` : 'PENDING'}${fired ? ' ✓' : ''}`,
      });
    }
    if (pendingRowsForLeg.length > 0) {
      const slRow = pendingRowsForLeg.find(p => p.stopLoss != null);
      const tgtRow = pendingRowsForLeg.find(p => p.target != null);
      if (slRow?.stopLoss != null) levels.push({ type: 'LEGSL', price: slRow.stopLoss, color: '#e53935', lineStyle: 0, axisLabel: 'SL' });
      if (tgtRow?.target != null) levels.push({ type: 'LEGTGT', price: tgtRow.target, color: '#1e88e5', lineStyle: 0, axisLabel: 'TGT' });
    }
    // Ghost preview lines for an in-progress BUY/SHORT capture on this leg.
    if (orderCapture.stage !== 'idle' && orderCapture.targetPane === pane) {
      orderCapture.entries.forEach((p, i) => levels.push({
        type: orderCapture.entryCount > 1 ? `E${i + 1}` : 'ENTRY', price: p, color: '#3f51b5', lineStyle: 0,
      }));
      if (orderCapture.sl != null) levels.push({ type: 'SL?', price: orderCapture.sl, color: '#e53935', lineStyle: 1 });
      if (orderCapture.target != null) levels.push({ type: 'TGT?', price: orderCapture.target, color: '#1e88e5', lineStyle: 1 });
    }
    return levels;
  };
  const ceLevelsProp = useMemo(() => buildLegLevels(ceTriggerLevels, cePendingRows, 'ce'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ceTriggerLevels, cePendingRows, orderCapture]);
  const peLevelsProp = useMemo(() => buildLegLevels(peTriggerLevels, pePendingRows, 'pe'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [peTriggerLevels, pePendingRows, orderCapture]);

  /* Phase C — open-basket SL/Target/entry lines for the Combined pane (per-unit
     premium). TP/SL are draggable; the entry-credit line is reference-only. */
  const combinedLevelsProp = useMemo(() => {
    const levels: { type: string; price: number; color: string; lineStyle?: number; draggable?: boolean; axisLabel?: string }[] = [];
    if (!openBasketChart) return levels;
    if (openBasketChart.entryPerUnit != null) levels.push({ type: 'ENTRY', price: openBasketChart.entryPerUnit, color: '#90a4ae', lineStyle: 2, axisLabel: openBasketChart.entryPerUnit >= 0 ? 'Credit' : 'Debit' });
    if (openBasketChart.targetPerUnit != null) levels.push({ type: 'TARGET', price: openBasketChart.targetPerUnit, color: '#1e88e5', lineStyle: 0, draggable: true, axisLabel: 'TP' });
    if (openBasketChart.slPerUnit != null) levels.push({ type: 'SL', price: openBasketChart.slPerUnit, color: '#e53935', lineStyle: 0, draggable: true, axisLabel: 'SL' });
    return levels;
  }, [openBasketChart]);

  /* ───── live polling — cadence from user settings, fires immediately ─────
     In addition to updating `latestCandles` (drives chart.update of the last
     bar), we merge any newly-closed bars into paneData so the capped tail
     view stays correct if the user leaves the page open across multiple
     candle closes. */
  // R124 — last CLOSED bar time per pane, kept current so the poll can backfill
  // the whole gap (not just ~3 bars) after the tab was hidden / throttled.
  const lastClosedRef = useRef<number[]>([]);
  useEffect(() => {
    lastClosedRef.current = paneData.map(p => (p && p.length) ? (p[p.length - 1].time as number) : 0);
  }, [paneData]);
  const pollInFlightRef = useRef(false);
  const lastPollAtRef = useRef(0);
  useEffect(() => {
    if (!symbol || !prefsReady || !isLive) return;
    // R68a — pause the live-candle poll while bar replay is active so the
    // frozen window doesn't get fresh bars appended underneath the user.
    if (replay.active) return;
    // R143 Plan A — skip the broker live poll when showing free (Yahoo) data.
    if (freeDataActive) return;
    const poll = async () => {
      // R124b — never let polls overlap (focus + interval could otherwise stack
      // concurrent requests and pile load on the backend).
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      lastPollAtRef.current = Date.now();
      try {
        for (let i = 0; i < spotPaneCount; i++) {
          const nowSec = Math.floor(Date.now() / 1000);
          // R124 — fetch from the last bar we already have (so a gap that opened
          // while the tab was hidden is fully backfilled), not a fixed 3-bar
          // window. Clamp the look-back to ~1500 bars so an extreme absence
          // (days) still issues a bounded request.
          // IMPORTANT: candle.time is IST-shifted (+19800), but the `from` param
          // expects UTC seconds — convert before using it, or the request asks
          // for candles 5.5h in the future and comes back empty (no backfill).
          const IST_SHIFT_SEC = 19800;
          const lastClosedUtc = (lastClosedRef.current[i] || 0) - IST_SHIFT_SEC;
          const minFrom = nowSec - intervals[i] * 1500 * 60;
          const fromSec = lastClosedUtc > 0
            ? Math.max(lastClosedUtc - intervals[i] * 60, minFrom)
            : nowSec - intervals[i] * 3 * 60;
          // Backend expects `from` in seconds for Delta, ISO for Zerodha (see fetchCandles).
          const fromParam = exchange === 'DELTA' ? fromSec : new Date(fromSec * 1000).toISOString();
          // For Delta we already poll mark_price separately, but for NSE
          // (Zerodha) the historical-data endpoint excludes the forming
          // candle. Ask the backend to merge LTP into the last bar so
          // intraday charts tick during market hours instead of freezing
          // at yesterday's close.
          let candles: CandleRow[];
          if (exchange === 'SYNTH') {
            // Synthetic charts have no broker symbol — re-zip the legs' recent candles
            // (fetchCandles' SYNTH branch). A short from-window keeps it to a couple of
            // light leg-candle calls per tick for the ONE visible synthetic.
            candles = sanitizeCandles(await fetchCandlesRef.current!(symbol, exchange, intervals[i], { fromSec, silentError: true }));
          } else {
            const liveMergeParams = exchange === 'DELTA' ? {} : { withLive: 'true' };
            const resp = await api.get<any>('/charts/candles', {
              params: { symbol, exchange, interval: intervals[i], from: fromParam, ...liveMergeParams },
              headers: { _suppressToast: 'true' },
            });
            candles = sanitizeCandles(resp.data?.data?.candles || []);
          }
          if (!candles.length) continue;
          // Last bar is the forming/live candle; everything before it is closed.
          const live = candles[candles.length - 1];
          const closedFromPoll = candles.slice(0, -1);
          setLatestCandles(prev => { const n = [...prev]; n[i] = live; return n; });
          // Append any newly-closed bars (time > current closed-set tail) to
          // paneData. paneData reference only changes when this happens — i.e.,
          // at bar-close events, NOT every tick.
          //
          // Round 44 — CRITICAL: do NOT populate an EMPTY paneData from the
          // live poll. The live poll only fetches ~3 recent candles
          // (`from = now - interval*3min`). On a fresh symbol open it can
          // resolve BEFORE loadSpotPanes' full ~500-bar fetch — populating
          // paneData with just 3 bars. The chart then first-fits onto those
          // 3 huge candles, and when the full batch lands it's treated as a
          // "continuation" so the 3-bar zoom is restored over real data.
          // The live poll's job is to APPEND to an existing dataset; the
          // INITIAL load is exclusively loadSpotPanes' responsibility.
          setPaneData(prev => {
            const existing = prev[i] || [];
            if (existing.length === 0) return prev; // wait for loadSpotPanes
            const lastExistingT = existing[existing.length - 1].time;
            const toAppend = closedFromPoll.filter(c => c.time > lastExistingT);
            if (!toAppend.length) return prev;
            const n = [...prev];
            n[i] = [...existing, ...toAppend];
            return n;
          });
        }
      } catch { /* silent */ }
      finally { pollInFlightRef.current = false; }
    };
    poll(); // immediate-on-mount — no waiting for first interval tick
    const cadenceMs = Math.max(5, settings.liveUpdateSec) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) poll(); }, cadenceMs);
    // R124 — when the user returns to the tab (it was hidden, so the interval was
    // skipped and the browser throttled timers), poll IMMEDIATELY so the missing
    // candles backfill at once. R124b — debounced so rapid alt-tabbing can't fire
    // a request storm; only re-poll if it's been a few seconds since the last one.
    const onVisible = () => {
      if (document.hidden) return;
      if (Date.now() - lastPollAtRef.current < 3000) return;
      poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [symbol, exchange, intervals, spotPaneCount, prefsReady, isLive, settings.liveUpdateSec, replay.active, freeDataActive]);

  /* Live "tick" poll for Delta — fetches the latest mark/last price from the
     ticker endpoint and (a) feeds the optional markPrice dotted line, and
     (b) updates the live candle's `close` so the chart visibly ticks even on
     low-volume pairs where the candle endpoint returns the same close
     repeatedly between actual trades. Cadence follows liveUpdateSec so the
     setting in Chart Settings actually controls how fluidly the chart updates.
     Runs whenever the symbol is on Delta — independent of the markPrice line
     toggle, because the live-candle merge below benefits everyone. */
  useEffect(() => {
    // Synthetic charts drive markPrice from their own combined-premium tick (above),
    // so this Delta poll must not run — or clear it — for them.
    if (isSynth) return;
    if (!symbol || exchange !== 'DELTA') {
      setMarkPrice(null);
      return;
    }
    // R68a — drop the live mark-price line during bar replay so the chart
    // stays anchored to the frozen historical view.
    if (replay.active) { setMarkPrice(null); return; }
    let cancelled = false;
    const fetchMp = async () => {
      try {
        const r = await api.get<any>('/charts/mark-price', {
          params: { symbol },
          headers: { _suppressToast: 'true' },
        });
        if (!cancelled) {
          const mp = r.data?.data?.markPrice;
          setMarkPrice(mp == null ? null : Number(mp));
        }
      } catch { if (!cancelled) setMarkPrice(null); }
    };
    fetchMp();
    const cadenceMs = Math.max(3, settings.liveUpdateSec) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) fetchMp(); }, cadenceMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, exchange, settings.liveUpdateSec, replay.active, isSynth]);

  /* Synthetic smooth-tick — the combined premium V from the lightweight quote endpoint
     (ONE call), used to tick the forming candle between the heavier leg re-zips, exactly
     like the Delta mark-price poll. Cadence follows liveUpdateSec (min 3s). Delta legs are
     24/7; an NSE synthetic returns null off-hours and the line simply holds. */
  useEffect(() => {
    if (!isSynth || !synth || replay.active) { setSynthLegMarks({}); return; }
    let cancelled = false;
    const tick = async () => {
      const q = await syntheticSymbolsService.quote(synth.id);
      if (!cancelled && q && Number.isFinite(q.v)) {
        setMarkPrice(Number(q.v));
        // Per-leg marks → legend "@LTP" beside each constituent (item: leg contribution).
        if (Array.isArray(q.perLeg)) {
          setSynthLegMarks(Object.fromEntries(q.perLeg.filter(p => p?.symbol && Number.isFinite(Number(p.mark))).map(p => [p.symbol, Number(p.mark)])));
        }
      }
    };
    tick();
    const cadenceMs = Math.max(3, settings.liveUpdateSec) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) tick(); }, cadenceMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [isSynth, synth, settings.liveUpdateSec, replay.active]);

  /* LIVE synthetic (engine mirror OR fired basket) — re-poll the detail every 30s so
     the STOP/SL/Target lines track server-side changes (trail ratchets, monitor exits,
     level PATCHes from another tab) and a closed position drops the lines. setSynth
     only on REAL change so the other synth effects don't churn every poll. */
  const synthIdForPoll = isSynth && (synth?.strategyCycleId != null || synth?.basketLive != null) ? synth.id : null;
  useEffect(() => {
    if (synthIdForPoll == null || replay.active) return;
    let cancelled = false;
    const poll = async () => {
      const d = await syntheticSymbolsService.getOne(synthIdForPoll);
      if (cancelled || !d) return;
      setSynth(prev => {
        if (!prev || prev.id !== d.id) return prev; // user switched away meanwhile
        const changed = JSON.stringify({ e: prev.engineCycle, b: prev.basketLive, l: prev.legs, s: prev.status })
          !== JSON.stringify({ e: d.engineCycle, b: d.basketLive, l: d.legs, s: d.status });
        return changed ? d : prev;
      });
    };
    const id = window.setInterval(() => { if (!document.hidden) void poll(); }, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [synthIdForPoll, replay.active]);

  /* TRADE NOW — place the synthetic's basket immediately at the live combined premium
     (no trigger lines needed). Server applies default SL/Target (±50%) when omitted;
     they land on the chart as draggable lines. */
  const [tradingNowBusy, setTradingNowBusy] = useState(false);
  const tradeNowSynth = useCallback(async () => {
    const sd = synthRef.current;
    if (!sd || tradingNowBusy || sd.engineCycle || sd.status === 'LIVE' || sd.status === 'LIVE_PARTIAL') return;
    const mode = synthModeRef.current;
    const v = Number(markPrice) > 0 ? Number(markPrice) : null;
    const side = sd.isCredit ? 'SHORT' : 'LONG';
    const lots = Math.max(1, computeAutoQty(v || 1, side === 'SHORT' ? 'SHORT' : 'BUY'));
    const slDef = v != null ? (side === 'SHORT' ? v * 1.5 : v * 0.5) : null;
    const tgtDef = v != null ? (side === 'SHORT' ? v * 0.5 : v * 1.5) : null;
    const ok = window.confirm(
      `Trade ${sd.name} NOW at market — ${side} the package${v != null ? ` @ ≈${v.toFixed(2)}` : ''} ×${lots} lot${lots === 1 ? '' : 's'} [${mode}].\n\n` +
      `Default SL ${slDef != null ? slDef.toFixed(2) : '+50%'} / Target ${tgtDef != null ? tgtDef.toFixed(2) : '−50%'} — drag the lines to your levels after placement.` +
      (mode === 'LIVE' ? '\n\nThis places REAL multi-leg orders on your broker.' : ''));
    if (!ok) return;
    setTradingNowBusy(true);
    try {
      const r = await syntheticSymbolsService.tradeNow(sd.id, { lots, tradingMode: mode });
      if (!r.ok) throw new Error(r.reason);
      toast.success(`[${mode}] ${sd.name} placed — basket #${r.basketId}. Entry ${Number(r.entryPremium).toFixed(2)}, SL ${Number(r.slPremium).toFixed(2)}, Target ${Number(r.targetPremium).toFixed(2)} (drag to adjust).`, { id: 'synth-trade-now', duration: 9000 });
      const d = await syntheticSymbolsService.getOne(sd.id);
      if (d) setSynth(d);
      try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
    } catch (e: any) {
      toast.error(e?.message || 'Trade-now failed', { id: 'synth-trade-now', duration: 9000 });
    } finally { setTradingNowBusy(false); }
  }, [tradingNowBusy, markPrice, computeAutoQty]);

  /* ───── reset trigger mode on layout/interval changes ───── */
  useEffect(() => { setTriggerMode(null); }, [layoutMode, intervals]);

  /* ───── reset trigger mode when clicking outside chart canvas or trigger chips ───── */
  useEffect(() => {
    if (!triggerMode) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Allow clicks inside a chart wrapper (where the trigger is placed) or on trigger chips
      if (target.closest('[data-trigger-chart="true"]')) return;
      if (target.closest('[data-trigger-chip="true"]')) return;
      setTriggerMode(null);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [triggerMode]);

  /* ───── initial route param ─────
   * When the user lands on /charts/SYMBOL we MUST re-derive the exchange from the
   * URL or DB rather than reusing localStorage's last-viewed exchange. Otherwise a
   * Delta crypto symbol like AAPLXUSD opens with whatever filter was sticky (NSE
   * or BSE), the instrument query for that wrong exchange returns nothing, and the
   * chart displays "AAPLXUSD · BSE" with no data. */
  useEffect(() => {
    if (routeSymbol && routeSymbol.toUpperCase() !== symbol) {
      setSymbol(routeSymbol.toUpperCase());
    }
    if (!routeSymbol) return;
    const sym = routeSymbol.toUpperCase();
    const params = new URLSearchParams(routeLocation.search);
    const exchangeParam = params.get('exchange');
    if (exchangeParam) return; // explicit ?exchange= wins; URL-param effect handles it
    // No explicit exchange in URL — resolve from DB so we don't get stuck on a stale
    // localStorage value pointing at the wrong exchange.
    let cancelled = false;
    setExchangePending(true);
    (async () => {
      try {
        const r = await api.get<any>('/instruments', { params: { search: sym, pageSize: 10 } });
        const items = r.data?.data?.instruments || r.data?.instruments || [];
        const exact = items.find((i: any) => i.symbol === sym);
        if (cancelled || !exact?.exchange) return;
        const resolved = String(exact.exchange).toUpperCase();
        setExchange(resolved);
        localStorage.setItem('chart_lastExchange', resolved);
      } catch { /* leave as-is */ }
      finally { if (!cancelled) setExchangePending(false); }
    })();
    return () => { cancelled = true; setExchangePending(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSymbol]);

  /* Query params: ?interval=N&strategy=ID&exchange=NSE — applied once on mount per route change */
  const queryParamsAppliedRef = useRef<string>('');
  useEffect(() => {
    const key = `${routeSymbol}|${routeLocation.search}`;
    if (!routeLocation.search || queryParamsAppliedRef.current === key) return;
    queryParamsAppliedRef.current = key;

    const params = new URLSearchParams(routeLocation.search);
    const intervalParam = params.get('interval');
    const strategyParam = params.get('strategy');
    const exchangeParam = params.get('exchange');
    const candleParam = params.get('candle');
    const presetParam = params.get('preset');
    const overlaysParam = params.get('overlays');

    // `?preset=cloud` — opened via a "Chart" button. Round 96: the overlay
    // SELECTION now comes from the user's global "last used" config (sticky),
    // so the preset no longer force-sets overlays. We keep the preset flag for
    // the AvgCloud reference behaviour, but the user's last indicators win.
    // `preset=cloud` deliberately does NOT set any overlay, and must not start.
    // TabsContext appends it to EVERY chart URL it builds (contexts/TabsContext.tsx:171
    // and :192), so it is not a preset the user opts into any more — it is on every
    // chart open. Round 96 already stopped it force-setting overlays; anything that
    // turned overlays on here would therefore override the default indicator set on
    // every single navigation and it could never be seen. The flag survives only for
    // the two things that still read it: forcing the AvgCloud reference line to C-POC,
    // and telling the per-symbol prefs hydration not to clobber the selection.
    if (presetParam === 'cloud') {
      presetActiveRef.current = true;
      setChartPreset('cloud');
    }

    if (candleParam) {
      const c = candleParam.toUpperCase();
      if (c === 'REGULAR' || c === 'HA' || c === 'CSH' || c === 'CSL' || c === 'CLH' || c === 'CLN') {
        setCandleType(c === 'REGULAR' ? 'Regular' : (c as CandleType));
      }
    }

    if (intervalParam) {
      const n = parseInt(intervalParam, 10);
      if (Number.isFinite(n) && n > 0) {
        setIntervals(prev => [n, prev[1] ?? 60, prev[2] ?? 1440, prev[3] ?? 10080]);
      }
    }
    /* `?overlays=bbrOsc,waveOsc` — ADDITIVE. Turns the named indicator chips
     * ON and touches nothing else.
     *
     * Built for the Golden Signals library, whose whole point is "show me this
     * signal on a chart": several signals only draw when a particular chip is
     * on (the BW mark needs BBR or Wave), so a link that cannot switch chips on
     * lands the reader on a chart with nothing to see.
     *
     * WHY THIS IS NOT `preset=cloud` ALL OVER AGAIN. That flag was neutered in
     * Round 96 because TabsContext appends it to EVERY chart URL, so anything
     * it set would silently override the user's own indicator selection on
     * every navigation and could never be seen. `overlays` appears only on a
     * link something deliberately built, so it cannot do that.
     *
     * It DOES persist, through the single-writer snapshot effect, exactly as if
     * the chips had been clicked — the same thing `interval` and `candle`
     * already do, and what a reader following the link wants. The library says
     * so in as many words next to the link.
     *
     * Keys are checked against a known set: an unknown one would otherwise sit
     * in the saved snapshot for ever, read by nothing. */
    if (overlaysParam) {
      const known = new Set<string>([
        ...Object.keys(DEFAULT_OVERLAYS),
        ...CHART_OVERLAY_CATALOGUE.map(o => o.key),
      ]);
      const wanted = overlaysParam.split(',')
        .map(k => k.trim())
        .filter(k => known.has(k));
      if (wanted.length) {
        setOverlays(prev => {
          const next = { ...prev };
          for (const k of wanted) (next as any)[k] = true;
          return next;
        });
      }
    }
    if (exchangeParam) {
      setExchange(exchangeParam.toUpperCase());
    }
    if (strategyParam) {
      const strategyId = parseInt(strategyParam, 10);
      if (/^\d+$/.test(strategyParam.trim()) && Number.isFinite(strategyId)) {
        // Numeric → a backend strategy id (gives us persisted scanner markers too).
        setBackendStrategyId(strategyId);
        (async () => {
          try {
            const resp = await api.get<any>(`/strategies/${strategyId}`);
            const strat = (resp.data as any)?.data || resp.data;
            const type = (strat?.type || strat?.name || '').toString().toLowerCase();
            // Map backend strategy.type/name to a chart overlay id
            const match = strategies.find(s =>
              type.includes(s.id) || type.replace(/\s+/g, '').includes(s.id)
            );
            if (match) setActiveStrategy(match.id);
          } catch { /* strategy lookup failed — leave overlay unset */ }
        })();
      } else {
        // Non-numeric → a strategy NAME ('RockBand', 'Sniper', 'Daily Blast',
        // 'Shaved Doji', …). Normalise to a chart overlay id and apply it. We
        // don't have a backend id here, so the client-side overlay (same engine)
        // renders the markers — no persisted scanner fetch.
        const normalized = strategyParam.toLowerCase().replace(/[^a-z]/g, '');
        const match = strategies.find(s => s.id !== 'none' && s.id === normalized);
        setBackendStrategyId(null);
        setScannerMarkers([]);
        if (match) setActiveStrategy(match.id);
      }
    } else {
      setBackendStrategyId(null);
      setScannerMarkers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation.search, routeSymbol]);

  /* Fetch persisted scanner signals so chart markers exactly match the scanner.
     Runs whenever the user lands on a strategy-linked chart (?strategy=ID). */
  useEffect(() => {
    if (!backendStrategyId || !symbol || !intervals[0]) {
      setScannerMarkers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params: any = {
          symbol,
          intervalMinutes: intervals[0],
          days: 30,
          limit: 100
        };
        if (exchange) params.exchange = exchange;
        const resp = await api.get<any>(`/strategies/${backendStrategyId}/signals`, { params });
        if (cancelled) return;
        const rows: any[] = (resp.data as any)?.data?.data || (resp.data as any)?.data || [];
        const markers = rows
          .filter(r => r.candleTime && (r.symbol || '').toUpperCase() === symbol.toUpperCase())
          .map(r => ({ time: Math.floor(new Date(r.candleTime).getTime() / 1000), action: r.action }));
        setScannerMarkers(markers);
      } catch {
        if (!cancelled) setScannerMarkers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [backendStrategyId, symbol, exchange, intervals]);

  /* ───────────────────── OverView trend dashboard (Round 92) ─────────────────────
     "OverView" shows a per-timeframe trend meter (chart TF + mapped HTF).
     "Bkground" tints the chart with the blended trend zone color. Both are
     chart-side toggles persisted to localStorage. */
  const [overviewEnabled, setOverviewEnabledRaw] = useState<boolean>(() => {
    // Default ON for first load; remembers an explicit OFF ('0').
    try { return localStorage.getItem('chart_overviewEnabled') !== '0'; } catch { return true; }
  });
  const setOverviewEnabled = useCallback((u: boolean | ((p: boolean) => boolean)) => {
    setOverviewEnabledRaw(prev => {
      const next = typeof u === 'function' ? (u as any)(prev) : u;
      try { localStorage.setItem('chart_overviewEnabled', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);
  const [bkgroundEnabled, setBkgroundEnabledRaw] = useState<boolean>(() => {
    // Default ON for first load; remembers an explicit OFF ('0').
    try { return localStorage.getItem('chart_bkgroundEnabled') !== '0'; } catch { return true; }
  });
  const setBkgroundEnabled = useCallback((u: boolean | ((p: boolean) => boolean)) => {
    setBkgroundEnabledRaw(prev => {
      const next = typeof u === 'function' ? (u as any)(prev) : u;
      try { localStorage.setItem('chart_bkgroundEnabled', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);
  // R98 — "Trend" band: overlay the chart-TF (thin) and HTF (thick) trend score
  // lines on the price pane. Default OFF (new feature). Shares the OverView
  // score series + aux data, so it forces ovData loading like the others.
  const [trendEnabled, setTrendEnabledRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('chart_trendEnabled') === '1'; } catch { return false; }
  });
  const setTrendEnabled = useCallback((u: boolean | ((p: boolean) => boolean)) => {
    setTrendEnabledRaw(prev => {
      const next = typeof u === 'function' ? (u as any)(prev) : u;
      try { localStorage.setItem('chart_trendEnabled', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);
  // RockBand consumes the OverView score series (TF + HTF + benchmark), so it
  // must force the same aux-data loading even when the OverView/Bkground/Trend
  // toggles are all off — otherwise its candle colours + BUY/SHORT markers go
  // stale/empty after a symbol switch.
  const ovActive = overviewEnabled || bkgroundEnabled || trendEnabled || activeStrategy === 'rockband' || activeStrategy === 'sniper' || activeStrategy === 'om' || !!overlays.avBand;

  /* FNO Outlook (futures + options bias) for the OverView panel — only while the
   * OverView gauge is shown. Cheap (2 metric lookups); refreshes every 60s. */
  useEffect(() => {
    if (!symbol || !overviewEnabled) { setFnoOutlook(null); return; }
    let cancelled = false;
    const fetchOutlook = async () => {
      try {
        const r: any = await optionsService.getFnoOutlook(symbol);
        if (!cancelled) setFnoOutlook(r?.data?.data?.supported ? r.data.data : null);
      } catch { if (!cancelled) setFnoOutlook(null); }
    };
    fetchOutlook();
    const id = window.setInterval(() => { if (!document.hidden) fetchOutlook(); }, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, overviewEnabled]);

  /* BOLev_D / BOLev_W — fetch Daily / Weekly candles (symbol-level, shared by
     all panes). Needed when the BOLev overlay is drawn AND, since R120, whenever
     the OverView score is computed (ovActive) because BO_D / BO_W are now score
     voters (close vs the Daily / Weekly breakout levels). Non-repainting. */
  useEffect(() => {
    // Bhairav's 4H profile gates on Daily+Weekly Bhairav alignment → the chip
    // needs symbol-level daily candles whenever it is active.
    const wantDaily = !!(overlays.bolevD || overlays.blast || ovActive || activeStrategy === 'bhairav' || activeStrategy === 'om');
    const wantWeekly = !!(overlays.bolevW || overlays.blast || ovActive);
    const wantH4 = !!overlays.bolev4h;
    if (!symbol || !prefsReady || (!wantDaily && !wantWeekly && !wantH4)) {
      setDailyCandles([]); setWeeklyCandles([]); setH4Candles([]); return;
    }
    let cancelled = false;
    (async () => {
      // R127 PERF — fetch Daily + Weekly (+4H) IN PARALLEL and set together, so
      // the score series recomputes ONCE (not once per arrival) on symbol switch.
      const [d, w, h4] = await Promise.all([
        wantDaily ? fetchCandles(symbol, exchange, 1440, { fromSec: computeFromSec(exchange, 1440, 400), onRateLimited: () => setRateLimitHint('Delta is rate-limiting — indicators will catch up shortly.') }).catch(() => [] as CandleRow[]) : Promise.resolve([] as CandleRow[]),
        wantWeekly ? fetchCandles(symbol, exchange, 10080, { fromSec: computeFromSec(exchange, 10080, 200), onRateLimited: () => setRateLimitHint('Delta is rate-limiting — indicators will catch up shortly.') }).catch(() => [] as CandleRow[]) : Promise.resolve([] as CandleRow[]),
        wantH4 ? fetchCandles(symbol, exchange, 240, { fromSec: computeFromSec(exchange, 240, 400), onRateLimited: () => setRateLimitHint('Delta is rate-limiting — indicators will catch up shortly.') }).catch(() => [] as CandleRow[]) : Promise.resolve([] as CandleRow[]),
      ]);
      if (cancelled) return;
      setDailyCandles(d);
      setWeeklyCandles(w);
      setH4Candles(h4);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, prefsReady, overlays.bolevD, overlays.bolevW, overlays.bolev4h, overlays.blast, ovActive, activeStrategy]);

  // interval → HTF mapping (Strategies → Timeframe Mapping), loaded once.
  const [htfMap, setHtfMap] = useState<Record<number, number>>({});
  useEffect(() => {
    intervalHtfMappingService.list()
      .then(rows => { const m: Record<number, number> = {}; for (const r of rows) m[r.interval] = r.htfInterval; setHtfMap(m); })
      .catch(() => {});
  }, []);

  // R145 — per-strategy "closed HTF candles only" flags (set on the Strategies
  // page). RockBand defaults OFF (forming HTF, current behavior); Sniper
  // defaults ON (already non-repainting). The chart overlay honours these.
  const [strategyClosedHtf, setStrategyClosedHtf] = useState<{ rockband: boolean; sniper: boolean }>({ rockband: false, sniper: true });
  useEffect(() => {
    api.get<any>('/strategies', { headers: { _suppressToast: 'true' } })
      .then(res => {
        const items: any[] = res.data?.strategies || [];
        const rb = items.find(s => s.engine === 'rockband' && /rockband/i.test(s.name || '')) || items.find(s => s.engine === 'rockband');
        const sn = items.find(s => s.engine === 'sniper');
        setStrategyClosedHtf({
          rockband: !!(rb?.configuration?.rockband?.closedHtfOnly),
          sniper: (sn?.configuration?.sniper?.closedHtfOnly) !== false,
        });
      })
      .catch(() => {});
  }, []);
  /* Strategies-mapped HTF for any interval.
     This used to be a hand-copy of intervalCatalog's ladder walk that was
     missing its off-ladder branch: `HTF_LADDER.indexOf(base)` is -1 for a
     CUSTOM interval (7H = 420m is not a rung), and the copy then returned
     `base`. htfMin === base fails both the fetch gate below and the draw gate
     in TradingChart, so a custom timeframe silently had NO higher-timeframe
     context band and no HTF S/R at all. resolveHtfInterval already handles it —
     smallest rung ≥ 3× base, so 7H maps to 1D and 12h to 1W. */
  const mappedHtfFor = useCallback(
    (base: number): number => resolveHtfInterval(base, htfMap),
    [htfMap],
  );
  const htfInterval = useMemo(() => mappedHtfFor(intervals[0]), [mappedHtfFor, intervals]);
  /* The option panes' higher timeframe. Derived from THEIR interval, not the spot pane's:
     with the two decoupled, mapping an 8H spot pane to 1D and then handing that to a 15m
     option chart would compare a 15m series against daily bars. */
  const optHtfInterval = useMemo(() => mappedHtfFor(optInterval), [mappedHtfFor, optInterval]);

  /* The timeframe vocabulary: the builtin ladder plus whatever the user has
     defined. Falls back to the builtins if the call fails, so the picker is
     never empty and an offline chart still works. */
  const [intervalVocab, setIntervalVocab] = useState<IntervalVocabulary>(FALLBACK_VOCABULARY);
  const [intervalsDialogOpen, setIntervalsDialogOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    customIntervalService.list()
      .then(v => { if (!cancelled) setIntervalVocab(v); })
      .catch(() => { /* keep the builtin fallback */ });
    return () => { cancelled = true; };
  }, []);
  const intervalOptions = useMemo(() => {
    const opts = pickerIntervals(intervalVocab);
    return opts.length ? opts : INTERVAL_OPTIONS;
  }, [intervalVocab]);

  // S/R indicator (R137) — fetch the Strategies-mapped HTF candles per pane for
  // the HLDiff-SR (and future) HTF stream. Gated on the S/R overlay + the
  // method needing HTF, so it costs nothing when S/R is off.
  const srNeedsHtf = !!(overlays.sr
    && (settings.sr?.methods?.hlDiff?.enabled !== false)
    && (settings.sr?.methods?.hlDiff?.useHtf !== false));
  // The CLN HTF context band wants the SAME series — the Strategies-mapped HTF
  // candles for this pane's interval — so it shares this fetch rather than
  // opening a second one. Widening the gate is the whole data-layer change the
  // band needs; `paneSrHtf` / `paneSrHtfMin` already reach TradingChart through
  // auxFor() as `srHtf` / `srHtfMinutes`.
  // OM_Simple's C (continuation) mark reads this series for its regime gate, so
  // selecting the strategy has to turn the fetch on. Gated on the setting too:
  // switching C off switches the request off with it.
  const omSimpleNeedsHtf = activeStrategy === 'om_simple'
    && (settings as any)?.omSimple?.showContinuation !== false;
  // Vajra's gate IS the mapped higher timeframe, so selecting it must turn the
  // fetch on or the strategy correctly refuses to mark anything and the chart
  // looks broken for a reason that has nothing to do with the rule.
  const vajraNeedsHtf = activeStrategy === 'vajra';
  // Gandiv's band IS the mapped higher timeframe; same reason as Vajra.
  const gandivNeedsHtf = activeStrategy === 'gandiv';
  // The CLH Band's HTF half reads the mapped higher timeframe, so switching it
  // on has to turn that fetch on too - otherwise the indicator correctly draws
  // nothing and the chart looks broken for a reason unrelated to the levels.
  const clhBandNeedsHtf = !!overlays.clhBand && (settings as any)?.clhBand?.showHtf === true;
  // The Bkground indicator's 'clhMid' and 'stack' methods read the same mapped
  // higher timeframe, with the CLH Band overlay off - so they have to turn the
  // fetch on in their own right, or the background silently paints nothing.
  const bkgroundMethod = (settings as any)?.bkground?.method;
  const bkgroundNeedsHtf = bkgroundEnabled && (bkgroundMethod === 'clhMid' || bkgroundMethod === 'stack');
  // The BBR / Wave oscillators' higher-timeframe curves (utils/oscHtf.ts) read
  // this same series, so each switches the fetch on only while its chip AND its
  // "show higher timeframe" setting are both on.
  const oscNeedsHtf = (!!overlays.bbrOsc && (settings as any)?.bbr?.showHtf === true)
    || (!!overlays.waveOsc && (settings as any)?.wave?.showHtf === true);
  // The HTF Band chip. Until it is first clicked its value is the legacy Chart
  // Settings switch, so a template that had the band on keeps it on.
  const htfBandOn = overlays.clnHtfBand ?? (settings.candle?.clnHtfBand === true);
  // MM's higher-timeframe staircase reads the same mapped-HTF series.
  const mmNeedsHtf = !!overlays.mm && (settings as any)?.mm?.showHtf === true;
  // P-Zone reads the same mapped higher timeframe (its anchor is the HTF Band).
  const profitZoneNeedsHtf = !!overlays.profitZone;
  const needsMappedHtf = srNeedsHtf || htfBandOn || mmNeedsHtf || omSimpleNeedsHtf || vajraNeedsHtf || gandivNeedsHtf || clhBandNeedsHtf || bkgroundNeedsHtf || oscNeedsHtf || profitZoneNeedsHtf;
  const srHtfLoadRef = useRef(0);
  const srHtfKeyRef = useRef('');
  useEffect(() => {
    if (!symbol || !prefsReady || !needsMappedHtf) {
      setPaneSrHtf([[], []]); setPaneSrHtfMin([0, 0]); srHtfKeyRef.current = ''; return;
    }
    // This series was fetched ONCE per symbol/interval, so on a chart left open
    // the HTF bars went stale and every consumer stopped at the last bar that
    // had been fetched — which is a second, slower way the CLN context band
    // stopped short of the last candle. auxRefreshTick re-runs this every 5 min
    // (the same seam the benchmark series already uses). A REFRESH must not
    // blank the series first, or the band would blink out every five minutes;
    // only a genuine identity change clears it.
    const key = symbol + '|' + exchange + '|' + intervals.join(',') + '|' + spotPaneCount;
    const isRefresh = srHtfKeyRef.current === key;
    srHtfKeyRef.current = key;
    const loadId = ++srHtfLoadRef.current;
    if (!isRefresh) setPaneSrHtf([[], []]);
    (async () => {
      for (let i = 0; i < spotPaneCount; i++) {
        if (srHtfLoadRef.current !== loadId) return;
        const htfMin = mappedHtfFor(intervals[i]);
        setPaneSrHtfMin(prev => { const n = [...prev]; n[i] = htfMin; return n; });
        if (!htfMin || htfMin <= intervals[i]) continue;
        const fromSec = computeFromSec(exchange, htfMin);
        const data = await fetchCandles(symbol, exchange, htfMin, { fromSec, silentError: true });
        if (srHtfLoadRef.current !== loadId) return;
        setPaneSrHtf(prev => { const n = [...prev]; n[i] = data; return n; });
        await new Promise(r => setTimeout(r, 300));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals, spotPaneCount, prefsReady, needsMappedHtf, htfMap, auxRefreshTick]);

  /* "Important fraction" rows. Asked per spot pane for the CLOSED bars not asked yet
     (plus the last two again: a venue can publish a sub-bar a little late) and, once
     its window has opened, the forming bar. paneData changes only at bar close, so
     this runs per bar close, per lazy-load, and on a 60 s tick while a fraction is
     on (the forming bar's window). The server builds the rows (4H at 1/3 needs ~14k
     5m sub-bars; only one row per bar comes back). */
  const fracLoadRef = useRef(0);
  const fracCacheRef = useRef(new Map<string, { rows: Map<number, FractionRow>; asked: Set<number>; ver: number }>());
  const latestCandlesRef = useRef(latestCandles);
  latestCandlesRef.current = latestCandles;
  const [fracTick, setFracTick] = useState(0);
  useEffect(() => {
    if (fracDen <= 1) return;
    const id = window.setInterval(() => { if (!document.hidden) setFracTick(t => t + 1); }, 60_000);
    return () => window.clearInterval(id);
  }, [fracDen]);
  useEffect(() => {
    if (!symbol || !prefsReady || fracDen <= 1) { setPaneFraction(EMPTY_PANE_FRACTION); return; }
    const loadId = ++fracLoadRef.current;
    let wake: number | undefined;
    const put = (i: number, v: PaneFraction) => setPaneFraction(prev => { const n = [...prev]; n[i] = v; return n; });
    (async () => {
      for (let i = 0; i < spotPaneCount; i++) {
        const plan = fracPlans[i];
        const key = fracKey(i);
        const bars = paneData[i] || [];
        if (plan.unavailable || !plan.subMin || !bars.length) continue;
        const cache = fracCacheRef.current;
        let c = cache.get(key);
        if (!c) {
          c = { rows: new Map(), asked: new Set(), ver: 0 };
          cache.set(key, c);
          while (cache.size > 12) cache.delete(cache.keys().next().value as string);
        }
        const times = bars.map(b => b.time as number);
        const ask = times.filter((t, k) => !c!.asked.has(t) || k >= times.length - 2);
        // The forming bar, only once its window has opened (it is drawn normal until then).
        const live = replay.active ? undefined : latestCandlesRef.current[i];
        const nowIst = Date.now() / 1000 + 19800;
        const ws = live ? formingWindowStart(live.time as number, intervals[i], exchange, plan) : null;
        const forming = live && ws != null && nowIst >= ws ? { time: live.time as number, windowStart: ws } : undefined;
        if (live && ws != null && nowIst < ws && ws - nowIst < 6 * 3600) {
          wake = window.setTimeout(() => setFracTick(t => t + 1), (ws - nowIst) * 1000 + 3000);
        }
        try {
          const r = await api.post('/charts/fraction-bars', {
            symbol, exchange, interval: intervals[i], den: plan.den, times: ask, forming,
          });
          // Keep the rows even if a newer run has started: they are keyed to this
          // symbol/interval/fraction, so the next run need not ask for them again.
          const d = r.data?.data || {};
          for (const t of ask) c.asked.add(t);
          for (const row of (d.rows || []) as FractionRow[]) {
            const was = c.rows.get(row.time);
            if (!was || was.open !== row.open || was.high !== row.high || was.low !== row.low || was.volume !== row.volume) {
              c.rows.set(row.time, row);
              c.ver++;
            }
          }
          if (fracLoadRef.current !== loadId) return;
          const cc = c;
          setPaneFraction(prev => {
            const n = [...prev];
            // A new Map (and so a redraw) only when the cached rows moved on since this
            // pane last took them, including rows a superseded run brought in.
            const same = prev[i].key === key && prev[i].loaded && prev[i].ver === cc.ver;
            n[i] = { key, rows: same ? prev[i].rows : new Map(cc.rows), forming: (d.forming as FractionRow) || null, loaded: true, ver: cc.ver };
            return n;
          });
        } catch {
          // Keep whatever rows this pane has; the next tick asks again. A pane with
          // nothing yet is marked loaded so it shows the "normal (faded)" state
          // rather than "loading" forever.
          if (fracLoadRef.current !== loadId) return;
          put(i, { key, rows: new Map(c.rows), forming: null, loaded: true, ver: c.ver });
        }
      }
    })();
    return () => { if (wake) window.clearTimeout(wake); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, intervals, spotPaneCount, prefsReady, fracDen, fracPlans, paneData, fracTick, replay.active]);

  // OverView aux candle series: chart-TF benchmark + HTF symbol/benchmark.
  const [ovData, setOvData] = useState<{ tfBench: CandleRow[]; htf: CandleRow[]; htfBench: CandleRow[] }>({ tfBench: [], htf: [], htfBench: [] });
  const [ovLoadingHtf, setOvLoadingHtf] = useState(false);
  const ovLoadRef = useRef(0);
  useEffect(() => {
    if (!ovActive || !symbol) { setOvData({ tfBench: [], htf: [], htfBench: [] }); return; }
    if (exchangePending) return;   // item 2 — see the benchmark effect above
    let cancelled = false;
    // Re-poll the HTF + benchmark series so their indicator LEVELS stay current
    // through the session (the chart-TF gauge already updates every tick via
    // ovLtp). HTF bars move slowly, so a 30s+ cadence is plenty and avoids
    // hammering the candles endpoint. Spinner shows only on the first load.
    const loadOv = async (showSpinner: boolean) => {
      const loadId = ++ovLoadRef.current;
      if (showSpinner) setOvLoadingHtf(true);
      try {
        const { symbol: bSym, exchange: bExch } = resolveBenchmark(symbol, exchange);
        const baseInt = intervals[0];
        const [tfBench, htf, htfBench] = await Promise.all([
          fetchCandles(bSym, bExch, baseInt, { fromSec: computeFromSec(bExch, baseInt), silentError: true }).catch(() => [] as CandleRow[]),
          fetchCandles(symbol, exchange, htfInterval, { fromSec: computeFromSec(exchange, htfInterval), silentError: true }).catch(() => [] as CandleRow[]),
          fetchCandles(bSym, bExch, htfInterval, { fromSec: computeFromSec(bExch, htfInterval), silentError: true }).catch(() => [] as CandleRow[]),
        ]);
        if (cancelled || ovLoadRef.current !== loadId) return;
        setOvData({ tfBench, htf, htfBench });
      } finally {
        if (!cancelled && ovLoadRef.current === loadId) setOvLoadingHtf(false);
      }
    };
    loadOv(true);
    const cadenceMs = Math.max(30, settings.liveUpdateSec || 15) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) loadOv(false); }, cadenceMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [ovActive, symbol, exchange, exchangePending, intervals, htfInterval, resolveBenchmark, fetchCandles, computeFromSec, settings.liveUpdateSec]);

  // R98 — the playhead candle time during bar replay (pane 0). Used to clamp the
  // OverView dashboard / bkground tint / trend series to the last VISIBLE candle
  // so they recompute as the user scrubs the replay (previously they stayed
  // pinned to the latest bar). Undefined when not replaying.
  // Replay reveal cutoff = the END of pane-0's current bar (open + interval − 1s),
  // NOT its open. With mixed timeframes (e.g. 1D pane-0 + 15m pane-1) an open-time
  // cutoff revealed the full 1D bar (day close) while the 15m pane only reached the
  // day's open — so the LTP differed across panes. Using the bar END reveals every
  // pane through the same wall-clock instant, so each pane's last close matches.
  // Best live price for trend determination. During replay this is the close of
  // the playhead candle so the OverView reflects that historical bar.
  const ovLtp = useMemo(() => {
    if (replayMaxTime != null) {
      const c = paneData[0]?.[replay.currentIdx];
      return c && Number.isFinite(c.close) ? c.close : null;
    }
    if (markPrice != null && Number.isFinite(markPrice)) return markPrice;
    const lc = latestCandles[0]; if (lc && Number.isFinite(lc.close)) return lc.close;
    const pd = paneData[0]; if (pd && pd.length) return pd[pd.length - 1].close;
    return null;
  }, [replayMaxTime, replay.currentIdx, markPrice, latestCandles, paneData]);

  const ovCfg = (settings as any).overview || {};
  // Merge over an all-equal default so indicators added later (macd, jhm) get a
  // weight for users whose saved template predates them, while explicit user
  // overrides (incl. a deliberate 0) are preserved.
  const ovWeights = { ema: 1, avgCloud: 1, cpr: 1, cpoc: 1, hpoc: 1, relStrength: 1, chgN: 1, macd: 1, jhm: 1, wrPct: 1, bolevD: 1, bolevW: 1, vol: 1, avBand: 1, ...(ovCfg.weights || {}) };
  const ovStrong = ovCfg.strongThreshold ?? 60;
  const ovProbable = ovCfg.probableThreshold ?? 20;

  // R127 PERF — compute each per-bar series ONCE (raw, no LTP override) and
  // reuse it for both (a) the gauge's avBand baseScore and (b) the overlay.
  // Previously the gauge recomputed a full O(n) series internally AND the overlay
  // computed another → ~4 series passes per render (each doing bolev/wvol/hma),
  // which made symbol switches slow. Now: 2 series passes (TF + HTF) total.
  const ovSeriesTfRaw = useMemo<TrendSeriesPoint[]>(() => {
    if (!overviewEnabled && !trendEnabled && !bkgroundEnabled && activeStrategy !== 'rockband' && !overlays.avBand) return [];
    let candles = paneData[0];
    if (replayMaxTime != null && candles) candles = candles.slice(0, replay.currentIdx + 1);
    if (!candles || candles.length < 5) return [];
    return computeTimeframeTrendSeries({
      candles, benchmarkCandles: ovData.tfBench, intervalMin: intervals[0],
      exchange, settings, weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable },
      dailyCandles, weeklyCandles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewEnabled, trendEnabled, bkgroundEnabled, activeStrategy, overlays.avBand, paneData, ovData.tfBench, intervals, exchange, settings, ovStrong, ovProbable, replayMaxTime, replay.currentIdx, dailyCandles, weeklyCandles]);

  const ovSeriesHtfRaw = useMemo<TrendSeriesPoint[]>(() => {
    if (!overviewEnabled && !trendEnabled && !bkgroundEnabled && activeStrategy !== 'rockband') return [];
    // R145 — RockBand "closed HTF only": drop the forming HTF bar so the zone
    // doesn't repaint as the current HTF bar fills.
    let candles = (activeStrategy === 'rockband' && strategyClosedHtf.rockband) ? dropFormingHtfBars(ovData.htf, htfInterval) : ovData.htf;
    if (replayMaxTime != null && candles) candles = candles.filter(c => c.time <= replayMaxTime);
    if (!candles || candles.length < 5) return [];
    return computeTimeframeTrendSeries({
      candles, benchmarkCandles: ovData.htfBench, intervalMin: htfInterval,
      exchange, settings, weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable },
      dailyCandles, weeklyCandles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewEnabled, trendEnabled, bkgroundEnabled, activeStrategy, ovData.htf, ovData.htfBench, htfInterval, exchange, settings, ovStrong, ovProbable, replayMaxTime, dailyCandles, weeklyCandles, strategyClosedHtf.rockband]);

  const baseScoreMapFrom = (series: TrendSeriesPoint[]): Map<number, number> | undefined => {
    if (!series.length) return undefined;
    const m = new Map<number, number>();
    for (const p of series) if (Number.isFinite((p as any).scoreBase)) m.set(p.time, (p as any).scoreBase);
    return m;
  };

  const tfTrends = useMemo<TimeframeTrends | null>(() => {
    if (!ovActive) return null;
    let candles = paneData[0];
    // R98 — during replay only the bars up to the playhead are "visible".
    if (replayMaxTime != null && candles) candles = candles.slice(0, replay.currentIdx + 1);
    if (!candles || candles.length < 5) return null;
    return computeTimeframeTrends({
      candles, ltp: ovLtp, benchmarkCandles: ovData.tfBench, intervalMin: intervals[0],
      exchange, settings, weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable }, decimals: spotDecimals,
      dailyCandles, weeklyCandles, baseScoreByTime: baseScoreMapFrom(ovSeriesTfRaw),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovActive, paneData, ovLtp, ovData.tfBench, intervals, exchange, settings, spotDecimals, ovStrong, ovProbable, replayMaxTime, replay.currentIdx, dailyCandles, weeklyCandles, ovSeriesTfRaw]);

  const htfTrends = useMemo<TimeframeTrends | null>(() => {
    if (!ovActive) return null;
    let candles = ovData.htf;
    if (replayMaxTime != null && candles) candles = candles.filter(c => c.time <= replayMaxTime);
    if (!candles || candles.length < 5) return null;
    return computeTimeframeTrends({
      candles, ltp: ovLtp, benchmarkCandles: ovData.htfBench, intervalMin: htfInterval,
      exchange, settings, weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable }, decimals: spotDecimals,
      dailyCandles, weeklyCandles, baseScoreByTime: baseScoreMapFrom(ovSeriesHtfRaw),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovActive, ovData.htf, ovData.htfBench, ovLtp, htfInterval, exchange, settings, spotDecimals, ovStrong, ovProbable, replayMaxTime, dailyCandles, weeklyCandles, ovSeriesHtfRaw]);

  // Per-bar series with the forming (last) bar's score aligned to the LTP-aware
  // gauge, so the live candle colour / Bkground / Trend / AV_Band match the
  // dashboard. Closed bars untouched → Scan/Backtest unaffected.
  const ovSeriesTf = useMemo<TrendSeriesPoint[]>(() => {
    if (!ovSeriesTfRaw.length) return [];
    if (tfTrends && Number.isFinite(tfTrends.score)) {
      const out = ovSeriesTfRaw.slice();
      const last = out[out.length - 1];
      out[out.length - 1] = { ...last, score: tfTrends.score, zone: zoneForScore(tfTrends.score, ovStrong, ovProbable) };
      return out;
    }
    return ovSeriesTfRaw;
  }, [ovSeriesTfRaw, tfTrends, ovStrong, ovProbable]);

  const ovSeriesHtf = useMemo<TrendSeriesPoint[]>(() => {
    if (!ovSeriesHtfRaw.length) return [];
    if (htfTrends && Number.isFinite(htfTrends.score)) {
      const out = ovSeriesHtfRaw.slice();
      const last = out[out.length - 1];
      out[out.length - 1] = { ...last, score: htfTrends.score, zone: zoneForScore(htfTrends.score, ovStrong, ovProbable) };
      return out;
    }
    return ovSeriesHtfRaw;
  }, [ovSeriesHtfRaw, htfTrends, ovStrong, ovProbable]);

  // RockBand chart overlay (R119) — candles coloured by the chart-TF OverView
  // zone (regime), and BUY/SHORT/SELL/COVER markers from the candle-colour +
  // ALWAYS-HTF-background state machine (rockBand.evaluateRockBandV2). The HTF
  // background zone is derived from ovSeriesHtf (the mapped Higher Timeframe's
  // per-bar OverView score) forward-filled by start time — independent of the
  // chart's Bkground display setting. No AvgCloud / Cond3 / split inputs.
  const rockBandOverlay0 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (activeStrategy !== 'rockband') return undefined;
    const candles = paneData[0];
    if (!candles?.length || !ovSeriesTf.length) return { candleColors: [], markers: scannerMarkersForChart };
    const base = computeRockBandOverlay(candles, ovSeriesTf, ovSeriesHtf, { strong: ovStrong, probable: ovProbable });
    // The chart appends a LIVE/forming bar (displayPane0) when its bucket is
    // newer than the last closed bar; that bar isn't in ovSeriesTf, so RockBand
    // gave it no zone colour and it fell back to default red/green-by-price —
    // which is why the last candle showed red while the OverView gauge read
    // bullish. Colour the forming bar with the live gauge zone (tfTrends) so it
    // matches the speedometer.
    let candleColors = base.candleColors || [];
    const formed = displayPane0[displayPane0.length - 1];
    const lastClosedT = candles[candles.length - 1]?.time as number | undefined;
    if (formed && lastClosedT != null && (formed.time as number) > lastClosedT && tfTrends && Number.isFinite(tfTrends.score)) {
      const zc = OVERVIEW_ZONE_COLORS[zoneForScore(tfTrends.score, ovStrong, ovProbable)];
      candleColors = [...candleColors, { time: formed.time as number, upColor: zc, downColor: zc }];
    }
    const withForming: StrategyOverlayResult = { ...base, candleColors };
    if (!scannerMarkersForChart.length) return withForming;
    return { ...withForming, markers: [...(withForming.markers || []), ...scannerMarkersForChart] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStrategy, paneData, ovSeriesTf, ovSeriesHtf, ovStrong, ovProbable, scannerMarkersForChart, displayPane0, tfTrends]);

  // Sniper (R129) — CLH dual-TF contrarian. Uses the SAME Timeframe-Mapping HTF
  // candles as RockBand (ovData.htf), not AvgCloud's HTF, so it matches the scan.
  // NO LOOK-AHEAD (Sniper): drop the still-forming HTF bar so the chart
  // overlay's HTF trend gate matches the backend scan (which does the same) and
  // the latest marker isn't based on a half-baked HTF candle. Sniper defaults to
  // closed-HTF (the look-ahead fix); the per-strategy flag can turn it off.
  const sniperOverlay0 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (activeStrategy !== 'sniper') return undefined;
    const candles = paneData[0];
    if (!candles?.length) return { candleColors: [], markers: scannerMarkersForChart };
    const htf = strategyClosedHtf.sniper ? dropFormingHtfBars(ovData.htf, htfInterval) : ovData.htf;
    const base = computeSniperOverlay(candles, htf, { targetRiskMultiple: 2, noWickTolerancePct: 0.05 });
    if (!scannerMarkersForChart.length) return base;
    return { ...base, markers: [...(base.markers || []), ...scannerMarkersForChart] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStrategy, paneData, ovData.htf, htfInterval, scannerMarkersForChart, strategyClosedHtf.sniper]);

  // Om — 4H swing. Its regime is the AV_Band slow-HMA hue rule, whose Effective
  // Line reads the OverView score EXCLUDING the avBand voter (`scoreBase`). That
  // series is `ovSeriesTf`, which is declared far below the generic
  // strategyOverlay0 memo, so Om cannot be fed through the registry path without
  // hitting the TDZ — same reason RockBand and Sniper are special-cased here.
  const omOverlay0 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (activeStrategy !== 'om') return undefined;
    const candles = paneData[0];
    if (!candles?.length) return { candleColors: [], markers: scannerMarkersForChart };
    const base = computeOmOverlay(candles, {
      ltfMinutes: intervals[0],
      dailyCandles,
      tfTrendSeries: ovSeriesTf,
    });
    if (!scannerMarkersForChart.length) return base;
    return { ...base, markers: [...(base.markers || []), ...scannerMarkersForChart] };
  }, [activeStrategy, paneData, intervals, dailyCandles, ovSeriesTf, scannerMarkersForChart]);

  // The overlay actually rendered on pane 0: RockBand / Sniper / Om use their own
  // score-driven overlay; every other strategy uses the registry overlay.
  const effectiveOverlay0 = activeStrategy === 'rockband' ? rockBandOverlay0
    : activeStrategy === 'sniper' ? sniperOverlay0
    : activeStrategy === 'om' ? omOverlay0
    : strategyOverlay0;

  /* A strategy's `notice` explains why it is muted or limited on this chart
     (wrong timeframe, missing higher-timeframe history, a setting that makes the
     markers disagree with the candles).

     This used to be a per-strategy branch — Bhairav, then Om bolted on beside it
     — which meant every NEW engine silently lost its notice and went quiet with
     no explanation, the exact failure the comment here warned about. It reads
     `effectiveOverlay0` now, so any strategy that sets a notice gets one.
     Bhairav is excluded because its own sim badge already carries it.

     "inactive" vs "note" is not cosmetic: a notice with no markers behind it
     means the strategy emitted nothing, while one WITH markers is a caveat about
     signals that are on screen. Saying "inactive" in the second case would be
     wrong. A notice is split into LINES because the badge draws each entry as
     one unwrapped row — a paragraph would run off the pane. */
  const strategyNoticeBadge = useMemo(() => {
    if (activeStrategy === 'bhairav') return undefined;
    const note = effectiveOverlay0?.notice;
    if (!note) return undefined;
    const name = (activeStrategy && getStrategy(activeStrategy)?.name) || 'Strategy';
    const silent = !(effectiveOverlay0?.markers && effectiveOverlay0.markers.length);
    return {
      value: 0, noAmount: true,
      sub: name + (silent ? ' — inactive on this chart' : ' — note'),
      lines: note.split('\n').filter(Boolean),
    };
  }, [activeStrategy, effectiveOverlay0]);

  const bhairavBadgeOrUndef = activeStrategy === 'bhairav' ? (bhairavSimBadge || undefined) : undefined;
  const strategyBadge = bhairavBadgeOrUndef || strategyNoticeBadge;
  const chartPnlBadge = isSynth ? (synthEnginePnlBadge || strategyBadge) : (payoffPnlBadge || strategyBadge);

  // ── Pane 1 RockBand overlay (2-split) ────────────────────────────────────────
  // In 2-split the second pane shows the same symbol at a DIFFERENT timeframe, so
  // its RockBand candle colours must come from ITS OWN chart-TF OverView series
  // (candle colour = chart-TF zone; the HTF series only drives markers). Pane 0's
  // pipeline can't be reused, so we run a parallel one here — fully gated to
  // split-mode + RockBand so it adds zero work (and no extra fetch) otherwise.
  const pane1RbActive = layoutMode === 'split' && activeStrategy === 'rockband' && ovActive;
  /* Om needs the SAME pane-1 chart-TF score series RockBand does (its AV_Band
     Effective Line reads it). Without this the second pane would draw nothing
     at all in split mode — a silent strategy, which is the failure this file
     already guards against elsewhere with `notice`. */
  const pane1OvActive = layoutMode === 'split' && (activeStrategy === 'rockband' || activeStrategy === 'om') && ovActive;
  // Sniper also needs pane-1 HTF candles (Timeframe-Mapping HTF) — broaden the
  // pane-1 HTF fetch to cover both RockBand and Sniper. Om needs the pane-1
  // BENCHMARK series too (it feeds the relative-strength voter inside the
  // OverView score its AV_Band line reads), and that arrives on the same fetch.
  const pane1HtfActive = layoutMode === 'split'
    && (activeStrategy === 'rockband' || activeStrategy === 'sniper' || activeStrategy === 'om')
    && ovActive;

  // Same mapping as pane 0 — see mappedHtfFor for why this must not be a
  // hand-copy of the ladder walk (custom intervals get no HTF at all).
  const htfInterval1 = useMemo(
    () => resolveHtfInterval(intervals[1], htfMap),
    [htfMap, intervals],
  );

  const [ovData1, setOvData1] = useState<{ tfBench: CandleRow[]; htf: CandleRow[]; htfBench: CandleRow[] }>({ tfBench: [], htf: [], htfBench: [] });
  const ovLoadRef1 = useRef(0);
  useEffect(() => {
    if (!pane1HtfActive || !symbol) { setOvData1({ tfBench: [], htf: [], htfBench: [] }); return; }
    if (exchangePending) return;   // item 2 — see the benchmark effect above
    let cancelled = false;
    const loadOv1 = async () => {
      const loadId = ++ovLoadRef1.current;
      try {
        const { symbol: bSym, exchange: bExch } = resolveBenchmark(symbol, exchange);
        const baseInt = intervals[1];
        const [tfBench, htf, htfBench] = await Promise.all([
          fetchCandles(bSym, bExch, baseInt, { fromSec: computeFromSec(bExch, baseInt), silentError: true }).catch(() => [] as CandleRow[]),
          fetchCandles(symbol, exchange, htfInterval1, { fromSec: computeFromSec(exchange, htfInterval1), silentError: true }).catch(() => [] as CandleRow[]),
          fetchCandles(bSym, bExch, htfInterval1, { fromSec: computeFromSec(bExch, htfInterval1), silentError: true }).catch(() => [] as CandleRow[]),
        ]);
        if (cancelled || ovLoadRef1.current !== loadId) return;
        setOvData1({ tfBench, htf, htfBench });
      } catch { /* leave stale; next tick retries */ }
    };
    loadOv1();
    const cadenceMs = Math.max(30, settings.liveUpdateSec || 15) * 1000;
    const id = window.setInterval(() => { if (!document.hidden) loadOv1(); }, cadenceMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [pane1HtfActive, symbol, exchange, exchangePending, intervals, htfInterval1, resolveBenchmark, fetchCandles, computeFromSec, settings.liveUpdateSec]);

  const ovSeriesTf1 = useMemo<TrendSeriesPoint[]>(() => {
    if (!pane1OvActive) return [];
    const candles = paneData[1];
    if (!candles || candles.length < 5) return [];
    return computeTimeframeTrendSeries({
      candles, benchmarkCandles: ovData1.tfBench, intervalMin: intervals[1],
      exchange, settings, weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable }, dailyCandles, weeklyCandles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane1OvActive, paneData, ovData1.tfBench, intervals, exchange, settings, ovStrong, ovProbable, dailyCandles, weeklyCandles]);

  const ovSeriesHtf1 = useMemo<TrendSeriesPoint[]>(() => {
    if (!pane1OvActive) return [];
    const candles = ovData1.htf;
    if (!candles || candles.length < 5) return [];
    return computeTimeframeTrendSeries({
      candles, benchmarkCandles: ovData1.htfBench, intervalMin: htfInterval1,
      exchange, settings, weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable }, dailyCandles, weeklyCandles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane1OvActive, ovData1.htf, ovData1.htfBench, htfInterval1, exchange, settings, ovStrong, ovProbable, dailyCandles, weeklyCandles]);

  const rockBandOverlay1 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (!pane1RbActive) return undefined;
    const candles = paneData[1];
    if (!candles?.length || !ovSeriesTf1.length) return { candleColors: [], markers: [] };
    const base = computeRockBandOverlay(candles, ovSeriesTf1, ovSeriesHtf1, { strong: ovStrong, probable: ovProbable });
    // Colour the forming bar with the latest closed-bar zone (mirrors pane 0).
    let candleColors = base.candleColors || [];
    const formed = displayPane1[displayPane1.length - 1];
    const lastClosedT = candles[candles.length - 1]?.time as number | undefined;
    const lastTf = ovSeriesTf1[ovSeriesTf1.length - 1];
    if (formed && lastClosedT != null && (formed.time as number) > lastClosedT && lastTf && Number.isFinite(lastTf.score)) {
      const zc = OVERVIEW_ZONE_COLORS[zoneForScore(lastTf.score, ovStrong, ovProbable)];
      candleColors = [...candleColors, { time: formed.time as number, upColor: zc, downColor: zc }];
    }
    return { ...base, candleColors };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane1RbActive, paneData, ovSeriesTf1, ovSeriesHtf1, ovStrong, ovProbable, displayPane1]);

  const sniperOverlay1 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (!(layoutMode === 'split' && activeStrategy === 'sniper')) return undefined;
    const candles = paneData[1];
    if (!candles?.length) return { candleColors: [], markers: [] };
    const htf = strategyClosedHtf.sniper ? dropFormingHtfBars(ovData1.htf, htfInterval1) : ovData1.htf;
    return computeSniperOverlay(candles, htf, { targetRiskMultiple: 2, noWickTolerancePct: 0.05 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutMode, activeStrategy, paneData, ovData1.htf, htfInterval1, strategyClosedHtf.sniper]);

  const omOverlay1 = useMemo<StrategyOverlayResult | undefined>(() => {
    if (activeStrategy !== 'om' || layoutMode !== 'split') return undefined;
    const candles = paneData[1];
    if (!candles?.length) return { candleColors: [], markers: [] };
    return computeOmOverlay(candles, {
      ltfMinutes: intervals[1],
      dailyCandles,
      tfTrendSeries: ovSeriesTf1,
    });
  }, [activeStrategy, layoutMode, paneData, intervals, dailyCandles, ovSeriesTf1]);

  const effectiveOverlay1 = activeStrategy === 'rockband' ? rockBandOverlay1
    : activeStrategy === 'sniper' ? sniperOverlay1
    : activeStrategy === 'om' ? omOverlay1
    : strategyOverlay1;

  /* ───── Strategy overlays for the quad option panes (CE / PE / Combined) ─────
     Mirrors the spot pane's effectiveOverlay pipeline so applying a strategy
     colours the option charts too:
       • Sniper   → needs the option's HTF candles (dual-TF CLH).
       • RockBand → needs per-bar OverView trend series computed on the option's
                    OWN candles + its OWN HTF. An option premium has no sensible
                    relative-strength benchmark or daily/weekly bias, so those
                    inputs are left empty and the score degrades to the option's
                    EMA / MACD / structure trend (relStrength + bolev → 'na').
       • Single-TF (DailyBlast / ShavedDoji) → compute on the option candles
                    directly (they ignore the aux args).
     HTF candles are fetched only while a multi-TF strategy is active in quad
     mode, so single-TF use adds no extra network traffic. */
  const optMultiTf = layoutMode === 'quad' && (activeStrategy === 'sniper' || activeStrategy === 'rockband');
  const optHtfLoadRef = useRef(0);
  useEffect(() => {
    if (!optMultiTf || (!ceSymbol && !peSymbol)) { setCeHtf([]); setPeHtf([]); return; }
    const loadId = ++optHtfLoadRef.current;
    (async () => {
      if (ceSymbol) {
        const ex = ceOptExchange || 'DELTA';
        const d = await fetchCandles(ceSymbol, ex, optHtfInterval, { fromSec: computeFromSec(ex, optHtfInterval), silentError: true });
        if (optHtfLoadRef.current !== loadId) return;
        setCeHtf(d);
        await new Promise(r => setTimeout(r, 300)); // rate limit
      } else setCeHtf([]);
      if (peSymbol) {
        const ex = peOptExchange || 'DELTA';
        const d = await fetchCandles(peSymbol, ex, optHtfInterval, { fromSec: computeFromSec(ex, optHtfInterval), silentError: true });
        if (optHtfLoadRef.current !== loadId) return;
        setPeHtf(d);
      } else setPeHtf([]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optMultiTf, ceSymbol, peSymbol, ceOptExchange, peOptExchange, optHtfInterval, fetchCandles, computeFromSec]);

  // RockBand per-bar OverView trend series for an option series (own candles,
  // empty benchmark/daily — see note above).
  const optTrendSeries = useCallback((candles: CandleRow[], intervalMin: number): TrendSeriesPoint[] => {
    if (activeStrategy !== 'rockband' || !candles || candles.length < 5) return [];
    return computeTimeframeTrendSeries({
      candles, benchmarkCandles: [], intervalMin, exchange: 'DELTA', settings,
      weights: ovWeights, thresholds: { strong: ovStrong, probable: ovProbable },
      dailyCandles: [], weeklyCandles: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStrategy, settings, ovStrong, ovProbable]);

  const buildOptOverlay = useCallback((candles: CandleRow[], htf: CandleRow[]): StrategyOverlayResult | undefined => {
    if (!activeStrategy || activeStrategy === 'none' || !candles?.length) return undefined;
    if (activeStrategy === 'sniper') {
      const h = strategyClosedHtf.sniper ? dropFormingHtfBars(htf, optHtfInterval) : htf;
      return computeSniperOverlay(candles, h, { targetRiskMultiple: 2, noWickTolerancePct: 0.05 });
    }
    if (activeStrategy === 'rockband') {
      const tfSeries = optTrendSeries(candles, optInterval);
      if (!tfSeries.length) return { candleColors: [], markers: [] };
      const htfSeries = optTrendSeries(htf, optHtfInterval);
      return computeRockBandOverlay(candles, tfSeries, htfSeries, { strong: ovStrong, probable: ovProbable });
    }
    const strat = getStrategy(activeStrategy);
    if (!strat) return undefined;
    // `htf` is already this pane's mapped-HTF series (optHtfInterval =
    // mappedHtfFor(optInterval), the same Strategies ladder pane 0 uses), so
    // hand it over: without it OM_Simple's C mark was permanently gated off on
    // the option panes and the chart said "no higher-timeframe history" about a
    // series it was holding. Raw, not de-formed — the adapter drops the forming
    // HTF bar itself, exactly as it does for the spot panes.
    return strat.computeOverlays(candles, {
      ltfMinutes: optInterval, candleType,
      mappedHtfCandles: htf, mappedHtfMinutes: optHtfInterval,
      ...omSimpleOpts,
      ...vajraOpts,
      ...clnBreakOpts,
      ...gandivOpts,
      ...lrCrossOpts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStrategy, strategyClosedHtf.sniper, optHtfInterval, optTrendSeries, optInterval, ovStrong, ovProbable, candleType, omSimpleOpts, vajraOpts, clnBreakOpts, gandivOpts, lrCrossOpts]);

  const effectiveOverlayCe = useMemo(() => buildOptOverlay(ceCandles, ceHtf), [buildOptOverlay, ceCandles, ceHtf]);
  const effectiveOverlayPe = useMemo(() => buildOptOverlay(peCandles, peHtf), [buildOptOverlay, peCandles, peHtf]);
  const effectiveOverlayCombined = useMemo(() => buildOptOverlay(combinedCandles, combinedHtf), [buildOptOverlay, combinedCandles, combinedHtf]);

  // R110 — "Bkground" is now per-candle vertical zones drawn inside TradingChart
  // (sign of the combined TF+HTF trend score), not a single whole-chart tint.
  // We just pass the toggle through; the chart consumes overviewTf/overviewHtf.

  /* ───── handlers ───── */
  const handleSymbolClick = (s: string, e: string) => {
    if (e !== 'SYNTH') {
      // Synthetic charts are session-only: never persisted as the "last symbol"
      // (a reload can't restore the in-memory synth detail) and leaving one
      // clears its state.
      localStorage.setItem('chart_lastSymbol', s);
      localStorage.setItem('chart_lastExchange', e);
      setSynth(null);
    }
    // Clear stale option state before setting new symbol
    setCeCandles([]); setPeCandles([]);    setCeExpiry(''); setCeStrike(null);
    setPeExpiry(''); setPeStrike(null);
    setOptionExpiries([]); setOptionStrikes([]);
    setNfoUnderlying(''); setSpotPrice(0);
    setTriggerMode(null); // Reset trigger picking state on symbol change
    // Reset the rate-limit hint/retry + cancel the previous symbol's in-flight fetches.
    setRateLimitHint(null);
    if (rlRef.current.timer) { clearTimeout(rlRef.current.timer); rlRef.current.timer = null; }
    rlRef.current.attempts = 0;
    try { loadAbortRef.current?.abort(); } catch { /* ignore */ }
    // Clear pane data and the live tick before fetching new symbol — otherwise
    // the OLD symbol's price-axis range is preserved while the new fetch is in
    // flight, producing a brief "blank chart with stale axis" state. Lazy
    // useMemo dependents (displayPaneFull, transformLiveWith) propagate the
    // empty arrays into TradingChart which calls setData([]) and resets fit.
    setPaneData([[], []]);
    setLatestCandles([undefined, undefined]);
    setMarkPrice(null);
    setSymbol(s);
    setExchange(e);

    // Push to browsing history (unless navigating via prev/next). Synthetic
    // charts are excluded — their state can't be restored from a history entry.
    if (!navigatingHistory.current && e !== 'SYNTH') {
      setHistory(prev => {
        const deduped = prev.filter(h => !(h.symbol === s && h.exchange === e));
        const next = [...deduped, { symbol: s, exchange: e }].slice(-20);
        localStorage.setItem('chart_history', JSON.stringify(next));
        setHistoryIdx(next.length - 1);
        return next;
      });
    }
    navigatingHistory.current = false;
  };

  /* Options Workspace is Delta BTC/ETH only, and its chart strip IS this page's spot chart —
     so entering it on any other symbol switches the chart to the BTC perp (ETH via the
     workspace's own BTC/ETH switch). */
  // The strip may also chart ONE of the workspace's option contracts ({C|P}-BTC|ETH-…).
  const workspaceContract = (exchange || '').toUpperCase() === 'DELTA' && /^[CP]-(BTC|ETH)-\d+(\.\d+)?-\d{6}$/i.test(symbol || '') ? String(symbol).toUpperCase() : null;
  const workspaceBase: 'BTC' | 'ETH' = /^ETH|^[CP]-ETH-/i.test(symbol || '') ? 'ETH' : 'BTC';
  const workspaceSymbolOk = (exchange || '').toUpperCase() === 'DELTA' && (/^(BTC|ETH)USD$/i.test(symbol || '') || !!workspaceContract);
  useEffect(() => {
    if (layoutMode === 'workspace' && !workspaceSymbolOk) handleSymbolClick('BTCUSD', 'DELTA');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutMode, workspaceSymbolOk]);

  /* Resolve a synthetic's leg broker symbols (quote() persists them server-side) and
     return the full detail. Shared by the pane click and the URL restore below. */
  const loadSynthetic = useCallback(async (id: number): Promise<SyntheticSymbolRow | null> => {
    let detail = await syntheticSymbolsService.getOne(id);
    if (detail && (detail.legs || []).some(l => !l.symbol)) {
      await syntheticSymbolsService.quote(id); // resolves + persists leg symbols (works even off-hours)
      detail = await syntheticSymbolsService.getOne(id) || detail;
    }
    if (!detail || (detail.legs || []).some(l => !l.symbol)) return null;
    return detail;
  }, []);

  /* Open a synthetic symbol's combined chart in its OWN tab. The tab URL carries
     `synthId` so the chart survives the route round-trip and a page reload — the
     in-memory detail alone did not (openChartTab navigates → ChartsPage remounts →
     `synth` was lost, which silently disabled the chart AND the trigger side-lock). */
  const openSynthetic = async (row: SyntheticSymbolRow) => {
    const detail = await loadSynthetic(row.id);
    if (!detail) {
      toast.error('Could not resolve this synthetic symbol’s option legs yet — try again in a moment.');
      return;
    }
    setSynth(detail);
    openChartTab(detail.name, 'SYNTH', { synthId: String(detail.id) });
  };

  /* Restore the synthetic from `?synthId=` — on mount, on reload, and when switching
     between synthetic tabs. Also re-syncs `symbol` to the stored (mixed-case) name. */
  const synthUrlIdRef = useRef<number | null>(null);
  useEffect(() => {
    const id = Number(new URLSearchParams(routeLocation.search).get('synthId'));
    if (!Number.isFinite(id) || id <= 0) return;
    if (synth?.id === id || synthUrlIdRef.current === id) return;
    synthUrlIdRef.current = id;
    let cancelled = false;
    (async () => {
      const detail = await loadSynthetic(id);
      if (cancelled) return;
      if (!detail) { toast.error('Could not load this synthetic symbol.'); return; }
      setSynth(detail);
      setExchange('SYNTH');
      setSymbol(prev => (prev.toUpperCase() === detail.name.toUpperCase() ? detail.name : prev));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation.search, synth?.id, loadSynthetic]);

  // Feed the latest state to the page-level Up/Down key handler (subscribed once).
  symNavRef.current = { rows: sortedSymResults, symbol, exchange, click: handleSymbolClick };

  const handleHistoryPrev = () => {
    if (historyIdx <= 0 || !history.length) return;
    const idx = historyIdx - 1;
    setHistoryIdx(idx);
    navigatingHistory.current = true;
    handleSymbolClick(history[idx].symbol, history[idx].exchange);
  };

  const handleHistoryNext = () => {
    if (historyIdx >= history.length - 1 || !history.length) return;
    const idx = historyIdx + 1;
    setHistoryIdx(idx);
    navigatingHistory.current = true;
    handleSymbolClick(history[idx].symbol, history[idx].exchange);
  };

  const setInterval = (pane: number, val: number) => {
    setIntervals(prev => { const n = [...prev]; n[pane] = val; return n; });
  };

  const handleOverlayToggle = (key: keyof Overlays) => {
    setOverlays(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Flow / HVol / Vol share ONE chip that cycles (all are mutually-exclusive lower-pane
  // views). On Delta (where Flow is available): Off → Flow → HVol → Vol → Off.
  // On other exchanges (no Flow): Off → HVol → Vol → Off.
  //   HVol = overlays.vol (weighted high-volume spikes); Vol = overlays.volume (raw volume).
  const cycleFlowVol = () => {
    const isDeltaEx = (exchange || '').toUpperCase() === 'DELTA';
    setOverlays(prev => {
      const off = { ...prev, flow: false, vol: false, volume: false };
      if (isDeltaEx) {
        if (!prev.flow && !prev.vol && !prev.volume) return { ...off, flow: true };
        if (prev.flow) return { ...off, vol: true };
        if (prev.vol) return { ...off, volume: true };
        return off; // Vol → Off
      }
      if (!prev.vol && !prev.volume) return { ...off, vol: true };
      if (prev.vol) return { ...off, volume: true };
      return off;
    });
  };

  // A custom interval will not be in INTERVAL_OPTIONS, so fall through to the
  // shared label rather than the old `v + 'm'`, which rendered 4H as "240m".
  const intervalLabel = (v: number) => intervalOptions.find(o => o.value === v)?.label || catalogIntervalLabel(v);

  /* Say so when an option pane simply has not got the history to draw at the chosen
     timeframe, instead of showing six candles and letting the user wonder. Returns a plain
     string for the pane's legend note (`symbolNote`), or undefined when there is nothing to
     report. THIN_BARS is "not enough to read a chart" on any timeframe. */
  const THIN_BARS = 60;
  const thinOptionNote = (rows: CandleRow[] | undefined, busy: boolean): string | undefined => {
    if (busy) return undefined;
    const n = rows?.length || 0;
    if (n === 0 || n >= THIN_BARS) return undefined;
    return `only ${n} bars at ${intervalLabel(optInterval)}`;
  };

  // ── Toolbar groups: professionally classified (item 7/8) ──
  //  Bands & Levels · Trend · Oscillators · Overlays · Options · Signals.
  //  A single memo builds all six so they share one dependency list.
  const {
    bandsItems, trendGroupItems, oscItems, overlayGroupItems, optionsItems, signalItems,
  } = useMemo(() => {
    // helper — an overlay-toggle chip from a key.
    const ov = (k: keyof Overlays, l: string, tip: string, color: ChipColorName = 'info'): ToolbarItem =>
      ({ key: k as string, label: l, tooltip: tip, color, active: !!overlays[k], onClick: () => handleOverlayToggle(k) });
    const isDeltaEx = (exchange || '').toUpperCase() === 'DELTA';

    // Bands & Levels — everything that paints a horizontal band / level on price.
    const bands: ToolbarItem[] = [
      ov('avgCloud', 'AvgCloud', 'Dynamic cloud between the LR_HAMA_Band Avg edge and the Combined Midline (bull/bear/neutral colour).'),
      ov('cpr', 'CPR', 'Central Pivot Range on this pane\'s CTF — TC / BC / Pivot.'),
      ov('ctfPoc', 'C-POC', 'CTF Point-of-Control — volume-profile POC band over the CTF CPR window.'),
      ov('htfPoc', 'H-POC', 'HTF Point-of-Control — volume-profile band on the HTF (CTF × 15).'),
      ov('mm', 'MM', 'Market Maker — staircase band from the body (Open/Close) of BBR-reversal / Vol-spike source candles; bull fill when it steps up, bear when down. Chart Settings → MM switches the chart-timeframe and the higher-timeframe staircase (dashed) on or off, and sets the source candle type + colours.'),
      ov('blast', 'Blast', 'Inside (HA) / Engulfing (Regular) breakout on Daily & Weekly → staircase High/Low band; a C-POC band break fires BUY ▲ / SHORT ▼ (toggle signals in Chart Settings → Blast).', 'secondary'),
      ov('bolev4h', 'BO_4H', 'Breakout-Level projector on 4-HOUR candles — faster intraday BUY/SHORT breakout levels + lookout zones.'),
      ov('bolevD', 'BO_D', 'Same breakout-level projector on DAILY CLH candles — BUY/SHORT breakout levels + lookout zones.'),
      ov('bolevW', 'BO_W', 'Same as BO_D on WEEKLY CLH candles — wider, slower breakout levels (dashed).'),
      ov('clhBand', 'CLH Band', 'CLH Band — the REAL price at which the NEXT CLH candle body would close above this body top (long breakout / short cut-off) or below its bottom (the mirror). Solved from the CLH transform itself, so it is the same on any candle type. Optional third line marks where the body would change colour. Turn on the mapped higher timeframe too, to see where the HTF flips without waiting for its candle to close. Colours / fill / opacity in Chart Settings → CLH Band.', 'warning'),
      {
        key: 'clnHtfBand', label: 'HTF Band', color: 'info', active: htfBandOn,
        onClick: () => setOverlays(p => ({ ...p, clnHtfBand: !(p.clnHtfBand ?? (settings.candle?.clnHtfBand === true)) })),
        tooltip: 'HTF Band — the higher timeframe mapped to this chart (Strategies → Timeframe Mapping), drawn as a ribbon of its CLN regression bodies behind the candles, one step per HTF bar, coloured by that bar\'s own regression direction. Context, not a signal; works with any candle type; nothing to draw on 1W. Opacity and the forming-bar option in Chart Settings → HTF Band.',
      },
      ov('profitZone', 'P-Zone', 'P-Zone — a profit-zone REFERENCE, not a tested signal. Two amber levels beyond the HTF Band in the direction it is moving (Zone 1 solid, Zone 2 dashed), set at each higher-timeframe close from the band\'s edge plus 2.5 / 4 × the average bar range, and held flat until the next close. Nothing while the HTF Band is mixed or flat; nothing on 1W. A study found booking at Zone 1 no better than booking at a random profitable moment. Settings in Chart Settings → P-Zone.', 'warning'),
      ov('clnBreakLevels', 'CLN Brk', 'CLN_Break levels — the two prices the CLN_Break rule trades: the HIGHEST of the last N CLN linear-regression body tops (long breakout) and the LOWEST of their bottoms (short breakout). Drawn from the same engine the CLN_Break strategy chip marks from, so the lines and the arrows can never disagree. The channel between them is NARROWER than one average bar range, so both are often breached on the same bar — turn on a buffer in Chart Settings → CLN Break to widen it.', 'success'),
      ov('hlDiff', 'HLDiff', 'High/Low EMA of a chosen candle type — line + fill, or High−Low spread histogram.'),
      ov('vob', 'VOB', 'Volume-Trend Order Block — supertrend cloud + volume-split order blocks + retests.'),
    ];

    // Trend — regime / trend-strength tools.
    const trend: ToolbarItem[] = [
      { key: 'overview', label: 'OverView', color: 'primary', active: overviewEnabled, onClick: () => setOverviewEnabled(p => !p), tooltip: 'OverView — 7 indicators rolled into a -100..+100 trend speedometer for the chart TF + mapped HTF.' },
      { key: 'bkground', label: 'Bkground', color: 'primary', active: bkgroundEnabled, onClick: () => setBkgroundEnabled(p => !p), tooltip: 'Bkground — tint each candle\'s background with the trend regime as vertical zones (method in Chart Settings → Bkground).' },
      ov('dbcrZone', 'DbCrZone', 'DbCrZone — Debit/Credit option-market REGIME on the underlying: DEBIT (trending / premium-expansion, negative dealer gamma + rising straddle/IV) vs CREDIT (range / premium-decay). Captured live + saved as history so it renders even after the contracts expire. Colours/opacity in Chart Settings → DbCrZone.'),
      { key: 'trend', label: 'Trend', color: 'primary', active: trendEnabled, onClick: () => setTrendEnabled(p => !p), tooltip: 'Trend — overlay chart-TF + HTF OverView trend lines, their Combined sum and a Hull MA with a filled band.' },
      ov('combinedMidline', 'CMid', 'Comprehensive Envelope Midline — mean of the combined upper/lower bands (dashed).'),
      ov('ema200', 'EMAs', 'Exponential Moving Averages (7 · 21 · 51 · 200) over LinReg(close,14) — multi-horizon trend reference.'),
    ];

    // Oscillators — sub-pane / normalised momentum studies.
    const osc: ToolbarItem[] = [
      ov('macd', 'MACD', 'MACD — EMA(12)−EMA(26) on the CSH-smoothed close with SMA(9) signal + slope-coloured band.'),
      ov('jhm', 'JHM', 'JHM (Hilega-Milega) — RSI → VWMA + EMA on a 0–100 strip with bull/bear shading.'),
      ov('wrPct', 'WR%', 'Paired Williams %R EMAs (fast/slow) on a -100..0 strip — crossover = BUY/SHORT zone.'),
      ov('dayChange', 'ChgN%', 'Rolling N-candle % change of close with an EMA + directional band, on its own scale.'),
      ov('relStrength', 'RelStrength', 'Relative Strength vs benchmark — LinReg ratio normalised to 100. Auto-resolves the benchmark.'),
      ov('bbrOsc', 'BBR', 'BBR100 oscillator — %B of the HLC3 Bollinger band on a ±100 scale (±100 = the bands). Oversold/overbought zones shaded; stepline or smooth, own pane or overlaid, in Chart Settings → BBR. Chart Settings → BBR can also draw the higher-timeframe BBR (Strategies mapping) in the same pane.'),
      ov('waveOsc', 'Wave', 'WaveTrend oscillator (wt1, optional wt2). Drawn on a scale pinned so its ±60 lines up with the BBR ±100 row — the two are comparable by rarity, not by value, so this is what makes reading them together honest. Chart Settings → Wave can also draw the higher-timeframe Wave (Strategies mapping) in the same pane.'),
      ov('synthiya', 'Synthiya', 'Synthiya — staircase PCR, ATM-IV and Relative Strength of the UNDERLYING in the lower pane (built for synthetic symbols, works on any optionable chart). Up/down step colours in Chart Settings → Synthiya.'),
      {
        key: 'flowvol',
        label: overlays.flow ? 'Flow' : overlays.vol ? 'HVol' : overlays.volume ? 'Vol' : (isDeltaEx ? 'Flow/Vol' : 'HVol/Vol'),
        color: 'info', active: !!overlays.flow || !!overlays.vol || !!overlays.volume, onClick: cycleFlowVol,
        tooltip: isDeltaEx
          ? 'Cycle Off → Flow (order-flow ribbon) → HVol (high/weighted-volume spikes + EMAs) → Vol (raw per-bar volume) → Off.'
          : 'Cycle Off → HVol (high/weighted-volume spikes + EMAs) → Vol (raw per-bar volume) → Off. Flow is Delta-only.',
      },
    ];
    if (isDeltaEx) osc.push(ov('funding' as keyof Overlays, 'Funding', 'Funding rate (% / 8h) for this Delta perpetual in a lower pane, with contrarian funding signals. Delta only.'));

    // Overlays — raw price overlays + candle-colour tints.
    const overlayGroup: ToolbarItem[] = [
      ov('bollingerEnvelope', 'BB', 'Bollinger Bands — Traditional / Liner / Custom Liner / Custom Liner Weighted variants, with fill, line style & colours in Chart Settings → Bollinger Bands.'),
      ov('lrHama', 'LR_HAMA_Band', 'Average of SLR (CSL) and HAMA clouds — independent of the display candle type.'),
      ov('avBand', 'AV_Band', 'Anchored Volume Band — HMA band off the WVol-spike effective line (bull/bear).'),
      ov('spikeColor', 'VSpk', 'Volume-spike candle colour — volume > SMA(20)+2σ (overrides strategy colours).'),
      ov('bbrColor', 'BBR tint', 'BBR100 extreme-reversal CANDLE COLOUR (overrides strategy colours). The oscillator curve is the separate BBR chip under Oscillators.'),
      ov('vpa', 'VPA', 'Volume Spread Analysis (VPA 5.0) — VSA bull/bear/warning signal markers (pick which in Settings).'),
      ov('sr', 'S/R', 'Support / Resistance levels from HLDiff peaks on the chart-TF and Strategies-mapped HTF.'),
    ];

    // Options — F&O + basket payoff overlays (the Expiry tier selector sits inline).
    const options: ToolbarItem[] = [
      ov('fno', 'FNO', 'F&O overlay — Max Pain, GEX Flip, OI-based supports/resistances.', 'secondary'),
      ov('payoffZones', 'Payoff Zones', 'Basket payoff zones — shade the profit / target / stop bands of an open option basket on its underlying.', 'secondary'),
    ];

    // Signals — BUY/SHORT plotshapes (Blast moved to Bands & Levels).
    const signalDefs: [keyof Overlays, string, string][] = [
      ['fllhh', 'FLLHH', 'Failed Lower Low / Higher High — BUY/SHORT when a completed CSL run fails to break the prior run\'s extreme. Repaint-safe.'],
      ['fbo', 'FBO', 'False Breakout — BUY on confirmed downside fakeout, SHORT on upside fakeout. Period in Chart Settings → Signals.'],
      ['wave', 'Wave ▲▼', 'WaveTrend ARROWS — BUY on bullish cross near oversold, SHORT on bearish cross near overbought. Repaint-safe. The oscillator curve is the separate Wave chip under Oscillators.'],
      ['lrc', 'LRC', 'POC breakout — close crossing the C-POC (★) / H-POC (★★) top → BUY ▲ / bottom → SHORT ▼.'],
      ['as', 'AS', 'Absolute Strength Histogram — BUY when bull line rising & bear not; SHORT when bear rising & bull not. Repaint-safe.'],
    ];
    const signals = signalDefs.map(([k, l, tip]) => ({ key: k as string, label: l, tooltip: tip, color: 'secondary' as ChipColorName, active: !!overlays[k], onClick: () => handleOverlayToggle(k) }));

    return { bandsItems: bands, trendGroupItems: trend, oscItems: osc, overlayGroupItems: overlayGroup, optionsItems: options, signalItems: signals };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlays, overviewEnabled, bkgroundEnabled, trendEnabled, exchange, layoutMode, htfBandOn, settings.candle?.clnHtfBand]);

  /* The six indicator groups, as sections of the single Indicators control.
     `id` must stay the ORIGINAL ToolbarMenuGroup groupId — the favourites
     migration in ToolbarIndicatorsGroup reads the old per-group keys by it. */
  const indicatorSections = useMemo<IndicatorSection[]>(() => [
    { id: 'bands',       label: 'Bands & Levels', items: bandsItems,        defaultFavourites: ['avgCloud', 'ctfPoc', 'htfPoc'] },
    { id: 'trendGrp',    label: 'Trend',          items: trendGroupItems,   defaultFavourites: ['overview', 'trend'] },
    { id: 'oscillators', label: 'Oscillators',    items: oscItems,          defaultFavourites: ['macd', 'relStrength'] },
    { id: 'overlaysGrp', label: 'Overlays',       items: overlayGroupItems, defaultFavourites: ['lrHama'] },
    { id: 'optionsGrp',  label: 'Options',        items: optionsItems,      defaultFavourites: ['fno'] },
    { id: 'signals',     label: 'Signals',        items: signalItems,       defaultFavourites: ['lrc', 'as'] },
  ], [bandsItems, trendGroupItems, oscItems, overlayGroupItems, optionsItems, signalItems]);

  // ── Toolbar group: CANDLE TYPE (+ Wick / Hide-candles) ──
  const candleItems = useMemo<ToolbarItem[]>(() => {
    const list: ToolbarItem[] = CANDLE_TYPES.map(ct => ({
      key: ct.value, label: ct.value === 'Regular' ? 'Reg' : ct.label, tooltip: ct.title || ct.label,
      color: 'success' as ChipColorName, active: candleType === ct.value, onClick: () => setCandleType(ct.value as CandleType),
    }));
    // LR body over ANY candle type. Under the CLN preset it is shown ON, and switching
    // it off there means "plain candles", i.e. Regular.
    const lrActive = candleType === 'CLN' || overlays.lrBody === true;
    list.push({
      key: 'lr', label: lrActive ? 'LR ✓' : 'LR', color: 'success', active: lrActive,
      tooltip: 'Linear-regression body over the current candle type (the CLN body). ' +
        'Settings → "LR body (CLN)" sets its length and whether the candles are ghosted under it.',
      onClick: () => {
        if (candleType === 'CLN') { setCandleType('Regular' as CandleType); setOverlays(p => ({ ...p, lrBody: false })); return; }
        setOverlays(p => ({ ...p, lrBody: !p.lrBody }));
      },
    });
    list.push({ key: 'wick', label: showWick ? 'Wick ✓' : 'Wick ✕', tooltip: 'Toggle candle wicks (off = body only — incl. the real-OHLC marks on smoothed candles).', color: 'success', active: showWick, onClick: () => setShowWick(w => !w) });
    list.push({ key: 'hc', label: hideCandles ? 'HC ✓' : 'HC', tooltip: 'Hide Candles — paint only indicators / signals; the price scale stays active.', color: 'success', active: hideCandles, onClick: () => setHideCandles(h => !h) });
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candleType, showWick, hideCandles, overlays.lrBody]);

  // ── Toolbar group: STRATEGY (exclusive; click active to clear) ──
  const strategyItems = useMemo<ToolbarItem[]>(() => {
    const short: Record<string, string> = { RockBand: 'RBand', DailyBlast: 'DBlast', 'Shaved Doji': 'SDoji', ThetaMiner: 'TMiner', Bhairav: 'Bhrv' };
    return strategies.map(s => ({
      key: s.id, label: short[s.name] || s.name, tooltip: s.name, color: 'warning' as ChipColorName,
      active: activeStrategy === s.id, onClick: () => setActiveStrategy(activeStrategy === s.id ? '' : s.id),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStrategy]);

  // ── Toolbar group: OPTION ACTION BUTTONS (Build one-click baskets) ──
  const optionBuildItems = useMemo<ToolbarItem[]>(() => {
    const defs: [string, string, string, boolean][] = [
      ['short_straddle', 'Straddle', 'Short Straddle', true],
      ['short_strangle', 'Strangle', 'Short Strangle', true],
      ['bear_call_spread', 'Call Cr', 'Bear Call Spread (credit)', true],
      ['bull_put_spread', 'Put Cr', 'Bull Put Spread (credit)', true],
      ['bull_call_spread', 'Call Db', 'Bull Call Spread (debit)', false],
      ['bear_put_spread', 'Put Db', 'Bear Put Spread (debit)', false],
      ['long_call', 'Call L', 'Naked long Call (debit) — one click on the strike to BUY. Bullish; loss capped at the premium paid.', false],
      ['long_put', 'Put L', 'Naked long Put (debit) — one click on the strike to BUY. Bearish; loss capped at the premium paid.', false],
      ['iron_condor', 'Iron Condor', 'Iron Condor (credit, defined risk) — short call+higher call, short put+lower put', true],
      ['iron_fly', 'Iron Fly', 'Iron Fly (credit, defined risk) — ATM short straddle + protective wings', true],
      ['call_backspread_pm', 'Call BkSp', 'Call Ratio Back-Spread (1×2, premium-matched): SELL 1 ATM call + BUY 2 OTM calls priced ≤ half the ATM premium — near-zero cost, big payoff on a strong rally, small mid-range loss. Bullish. Auto squares off ~90 min before expiry.', false],
      ['put_backspread_pm', 'Put BkSp', 'Put Ratio Back-Spread (1×2, premium-matched): SELL 1 ATM put + BUY 2 OTM puts priced ≤ half the ATM premium — near-zero cost, big payoff on a sharp sell-off. Bearish. Auto squares off ~90 min before expiry.', false],
    ];
    return defs.map(([sid, lbl, full, isCredit]) => ({ key: sid, label: lbl, tooltip: full, color: (isCredit ? 'success' : 'secondary') as ChipColorName, active: false, onClick: () => openStrategyBuilder(sid, full) }));
  // openStrategyBuilder is rebuilt when nfoUnderlying/strikes/expiry load — include
  // it so the Build chips don't keep a stale closure that falsely says "load chain first".
  }, [openStrategyBuilder]);

  // ── Toolbar group: OUTLOOK → option-basket builder (armable) ──
  const outlookItems = useMemo<ToolbarItem[]>(() => {
    const defs: [OutlookCapMode, string, string][] = [
      ['WONT_FALL_BELOW', "Won't fall ↓", "Won't fall below — click a floor level; engine ranks 'stays above' baskets"],
      ['WONT_RISE_ABOVE', "Won't rise ↑", "Won't rise above — click a ceiling level; engine ranks 'stays below' baskets"],
      ['WILL_BREAK_ABOVE', 'Break ↑', 'Will break above — click a level; engine ranks bullish breakout baskets'],
      ['WILL_BREAK_BELOW', 'Break ↓', 'Will break below — click a level; engine ranks bearish breakout baskets'],
      ['WILL_BREACH', 'Break ↕', 'Will break out either way — long-gamma / straddle-style breakout baskets'],
      ['STAY_IN_RANGE', 'In range', 'Will stay in range — click LOWER then UPPER level; engine ranks range/credit baskets'],
    ];
    return defs.map(([m, lbl, tip]) => ({ key: m, label: lbl, tooltip: tip, color: 'primary' as ChipColorName, active: outlookCapture?.mode === m, onClick: () => (outlookCapture?.mode === m ? (setOutlookCapture(null), setChartTool(null), setOutlookMode('single'), resetDraw(), setDrawPhase('entries')) : openOutlookCapture(m)) }));
  // openOutlookCapture is rebuilt when nfoUnderlying loads — it MUST be a dep, else
  // the chips keep a stale closure over the pre-load nfoUnderlying='' and every click
  // falsely reports "no option chain loaded" (the live-chain fetch widened that race).
  }, [outlookCapture, openOutlookCapture]);

  // ── Phase D — Saved chart layouts: capture / apply / reset ─────────────
  const captureLayoutState = useCallback((): SavedLayoutState => ({
    layoutMode,
    intervals: [...intervals],
    overlays: { ...overlays } as Record<string, boolean>,
    triggerLevels: { ...triggerLevels } as Record<string, number | undefined>,
  }), [layoutMode, intervals, overlays, triggerLevels]);

  const applyLayoutState = useCallback((state: SavedLayoutState) => {
    if (state.layoutMode) setLayoutMode(state.layoutMode as LayoutMode);
    if (state.intervals && Array.isArray(state.intervals)) setIntervals([...state.intervals]);
    if (state.overlays) setOverlays(state.overlays as Overlays);
    if (state.triggerLevels) setTriggerLevels(state.triggerLevels as TriggerLevels);
  }, []);

  const resetLayout = useCallback(() => {
    setLayoutMode('single');
    setIntervals([15, 60, 1440, 10080]);
    // Was a hand-maintained literal that had drifted: it turned on Bollinger /
    // EMA200 / entry zones (which no default has used for a long time) and simply
    // OMITTED a dozen keys added since — mm, avBand, vol, vob, fno, relStrength,
    // dayChange, macd, payoffZones, synthiya, dbcrZone — leaving each `undefined`
    // rather than false. Reset now means exactly the documented default set.
    setOverlays({ ...DEFAULT_OVERLAYS });
    setTriggerLevels({});
    setTriggerModeExclusive(null);
    setChartToolExclusive(null);
    presetActiveRef.current = false;
    setChartPreset(null);
  }, [setTriggerModeExclusive, setChartToolExclusive]);

  // ── Phase D — Chart-level hotkeys ─────────────────────────────────────
  // Number keys 1-9: switch the focused pane's interval to a trader-friendly
  // step. Mapping: 1=1m, 2=3m, 3=5m, 4=15m, 5=30m, 6=1H, 7=4H, 8=1D, 9=1W.
  // Each hook call must be at the top level of render (rules-of-hooks) — so
  // we unroll the 9 mappings instead of a loop.
  const _focusedIdx = focusedSplitPane === 1 ? 1 : 0;
  useHotkey('1', () => setInterval(_focusedIdx, 1));
  useHotkey('2', () => setInterval(_focusedIdx, 3));
  useHotkey('3', () => setInterval(_focusedIdx, 5));
  useHotkey('4', () => setInterval(_focusedIdx, 15));
  useHotkey('5', () => setInterval(_focusedIdx, 30));
  useHotkey('6', () => setInterval(_focusedIdx, 60));
  useHotkey('7', () => setInterval(_focusedIdx, 240));
  useHotkey('8', () => setInterval(_focusedIdx, 1440));
  useHotkey('9', () => setInterval(_focusedIdx, 10080));
  // b / c / p — toggle Bollinger / AvgCloud / CPR
  useHotkey('b', () => handleOverlayToggle('bollingerEnvelope'));
  useHotkey('c', () => handleOverlayToggle('avgCloud'));
  useHotkey('p', () => handleOverlayToggle('cpr'));
  // R68a — `R` toggles Bar Replay. Mirrors the toolbar chip's action.
  useHotkey('r', () => { void enterOrExitReplay(); });
  // n-prefix → set normal trigger mode (nb=NBUY, ns=NSHORT)
  useHotkeySequence('n', {
    b: () => setTriggerModeExclusive(triggerMode === 'nbuy' ? null : 'nbuy'),
    s: () => setTriggerModeExclusive(triggerMode === 'nshort' ? null : 'nshort'),
  }, { preventDefault: true });
  // shift+b prefix → set breakthrough trigger mode (Bb=BBUY, Bs=BSHORT)
  useHotkeySequence('shift+b', {
    b: () => setTriggerModeExclusive(triggerMode === 'bbuy' ? null : 'bbuy'),
    s: () => setTriggerModeExclusive(triggerMode === 'bshort' ? null : 'bshort'),
  }, { preventDefault: true });

  /* ───── interval selector (chip row) ───── */
  const IntervalSelect = ({ pane, label }: { pane: number; label?: string }) => {
    const items: ToolbarItem[] = intervalOptions.map(o => ({
      key: String(o.value), label: o.label, color: 'success' as ChipColorName,
      active: intervals[pane] === o.value, onClick: () => setInterval(pane, Number(o.value)),
    }));
    return (
      <ToolbarMenuGroup
        groupId="timeframes" label={label || 'TF'} items={items} isDark={isDark}
        accent={{ dark: '121,134,203', light: '63,81,181' }}
        defaultFavourites={['15', '60', '1440', '10080']}
        footer={
          <Button size="small" fullWidth startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            sx={{ fontSize: CH_TYPE.micro, textTransform: 'none', justifyContent: 'flex-start' }}
            onClick={() => setIntervalsDialogOpen(true)}>
            Custom intervals…
          </Button>
        }
      />
    );
  };

  /* FNO metrics panel — shown when FNO toggle is ON. Positioned absolutely over
     the passed-in pane. Reused across single / split / quad layouts. */
  const FnoOverlayPanel = () => {
    if (!fnoOverlay) return null;
    if (fnoSeries && fnoSeries.supported === false) {
      return (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 3, bgcolor: isDark ? 'rgba(13,17,23,0.85)' : 'rgba(255,255,255,0.9)', border: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`, borderRadius: 1, px: 1, py: 0.5, maxWidth: 220, pointerEvents: 'none' }}>
          <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary' }}>{fnoSeries.reason || 'FNO data is available for BTC & ETH only.'}</Typography>
        </Box>
      );
    }
    // The regime / Net-GEX / tier-legend box was removed (it duplicated the OverView
    // FNO section). The full read now lives in OverView (top-left) + the crosshair
    // FNO interpreter. Only the unsupported-symbol hint above remains.
    return null;
  };

  /* OverView panel overlay — primary pane only, top-left below the OHLC legend. */
  const renderOverview = () => overviewEnabled ? (
    /* Anchoring top AND bottom gives this wrapper a DEFINITE height, which is
       what lets the panel inside cap itself with `maxHeight: 100%` and scroll
       instead of running off the bottom of its pane (it was clipped mid-sentence
       in quad layout). An auto-height wrapper cannot do that: a percentage
       max-height against an auto height resolves to none.
       `pointerEvents: none` keeps the now-full-height wrapper from swallowing
       clicks meant for the candles; the panel itself re-enables them. */
    <Box sx={{
      position: 'absolute', top: 38, left: 6, bottom: 8, zIndex: 4,
      display: 'flex', alignItems: 'flex-start',
      pointerEvents: 'none',
    }}>
      <OverviewPanel
        tf={tfTrends} htf={htfTrends}
        tfLabel={intervalLabel(intervals[0])} htfLabel={intervalLabel(htfInterval)}
        ov={ovCfg as any} isDark={isDark} loadingHtf={ovLoadingHtf} weights={ovWeights as any}
        fno={fnoOutlook} fnoScore={fnoScore}
      />
    </Box>
  ) : null;

  /* Subtle inline rate-limit hint (top-center) — shown only when Delta keeps
   *  limiting the symbol you've settled on, after debounce + abort + retries. */
  const renderRateLimitHint = () => rateLimitHint ? (
    <Box sx={{
      position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 5,
      display: 'flex', alignItems: 'center', gap: 0.8,
      px: 1.2, py: 0.5, borderRadius: 999,
      bgcolor: isDark ? 'rgba(38,30,12,0.92)' : 'rgba(255,248,225,0.96)',
      border: theme => `1px solid ${isDark ? 'rgba(255,193,7,0.35)' : 'rgba(255,160,0,0.5)'}`,
      boxShadow: 2, pointerEvents: 'none', maxWidth: '80%',
    }}>
      <CircularProgress size={12} sx={{ color: '#ffa000' }} />
      <Typography sx={{ fontSize: CH_TYPE.small, fontWeight: 600, color: isDark ? '#ffca28' : '#a26a00', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {rateLimitHint}
      </Typography>
    </Box>
  ) : null;

  /* Compact per-pane trigger level chips with × remove — used in Q2/Q4 headers */
  const PaneTriggerChips = ({ levels, onRemove }: { levels: TriggerLevels; onRemove: (t: TriggerType) => void }) => {
    const entries: [TriggerType, string][] = [
      ['nbuy', '#4caf50'], ['nshort', '#f44336'], ['bbuy', '#00bcd4'], ['bshort', '#ff9800'],
    ];
    return (
      <Box sx={{ display: 'inline-flex', gap: 0.3, alignItems: 'center', ml: 0.3, overflow: 'hidden' }}>
        {entries.map(([type, color]) =>
          levels[type] != null ? (
            <Box key={type} data-trigger-chip="true" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Chip label={`${type.toUpperCase()} ${levels[type]}`} size="small"
                sx={{
                  fontSize: CH_TYPE.micro, height: 18, borderRadius: '10px 0 0 10px',
                  borderColor: color, color, bgcolor: 'transparent', border: `1px solid ${color}`,
                  '& .MuiChip-label': { px: 0.4 },
                }}
              />
              <Chip label="×" size="small" onClick={() => onRemove(type)}
                sx={{
                  cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.micro, height: 18,
                  minWidth: 14, borderColor: color, color,
                  borderRadius: '0 10px 10px 0', borderLeft: 'none',
                  border: `1px solid ${color}`, borderLeftColor: 'transparent',
                  '& .MuiChip-label': { px: 0.2 }, '&:hover': { bgcolor: `${color}33` },
                }}
              />
            </Box>
          ) : null
        )}
      </Box>
    );
  };

  /* Leg picker for a quad inspection panel (Q2 / Q4) — lets the user point the panel
     at ANY leg of a multi-leg basket (iron condor / butterfly / ratio), not just the
     two it defaulted to. Shown only when the basket has >2 legs. */
  const renderLegPicker = (which: 'ce' | 'pe') => {
    if (!basketLegsAll || basketLegsAll.length <= 2) return null;
    const cur = which === 'ce'
      ? { optionType: ceOptType, strike: ceStrike, action: ceAction }
      : { optionType: peOptType, strike: peStrike, action: peAction };
    return (
      <Box sx={{ display: 'inline-flex', gap: 0.3, alignItems: 'center', ml: 0.3, overflow: 'hidden' }}>
        {basketLegsAll.map((l, i) => {
          const active = l.optionType === cur.optionType && Number(l.strike) === Number(cur.strike) && l.action === cur.action;
          const col = l.action === 'SELL' ? C.bear : C.bull;
          return (
            <Chip key={`${l.symbol}-${i}`} size="small" onClick={() => pointLegPanel(which, l)}
              label={`${l.action[0]}${l.optionType[0]}${l.strike}`}
              sx={{
                height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, cursor: 'pointer',
                bgcolor: active ? `${col}26` : 'transparent', color: active ? col : C.dim,
                border: `1px solid ${active ? col : 'rgba(120,120,120,0.4)'}`,
                '& .MuiChip-label': { px: 0.4 },
              }} />
          );
        })}
      </Box>
    );
  };

  /* The single-layout spot chart. Also the Options Workspace's chart strip, where the
     workspace overlay (leg strikes, breakevens, profit zone) is merged into the levels. */
  const renderSingleSpot = (wsOverlay?: WsChartOverlay) => (
    <Box data-trigger-chart="true" sx={{ position: 'relative', height: '100%' }}>
      <ChartErrorBoundary label="spot-single">
        <TradingChart showAccountStats candles={displayPane0} rawCandles={paneData[0]} overlays={overlays} auxCandles={aux0} benchmarkCandles={paneBenchmarkCandles[0]} benchmarkLabel={overlays.relStrength && symbol ? resolveBenchmark(symbol, exchange).symbol : undefined} loading={paneLoading[0]} height="100%"
          latestCandle={displayPane0Live} rawLatestCandle={liveWithMark[0]} fraction={fractionView[0]} fractionLatest={fractionView[0] ? fracLive[0] : undefined} priceLevels={wsOverlay ? [...priceLevelsProp, ...wsOverlay.levels] : priceLevelsProp} fnoLevels={chartFnoLines} priceZones={wsOverlay ? (chartZones?.length ? chartZones : wsOverlay.zones) : chartZones} pnlBadge={chartPnlBadge} onPnlBadgeClick={(!isSynth && openBasket && !replay.active) ? handleBadgeClick : undefined} leftLevels={chartLeftLevelsUi} optionProfile={chartOptProfile} fnoInterp={chartFnoInterp} fnoView={fnoChart0.view} fnoSelect={fnoSelect} synthiya={synthiyaSeries} dbcrZones={overlays.dbcrZone ? dbcrZonesData : undefined}
          onChartClick={(triggerMode || chartTool) ? handleChartClickSpot : undefined}
          strategyOverlay={effectiveOverlay0}
          onPriceLevelChange={handleLevelChangeSpot}
          onDragStateChange={setIsDragging}
          hideCandles={hideCandles} showWick={showWick} candleType={candleType} overlayLeftInset={overviewEnabled && !wsOverlay ? OVERVIEW_LANE_PX : undefined}
          onPriceLevelDelete={handleLevelDeleteSpot}
          intervalMinutes={intervals[0]}
          decimalPrecision={spotDecimals}
          tickSize={spotTickSize}
          markPrice={chartMarkPrice} markPriceColor={settings.markPrice?.color}
          positionPnl={activeTrade && activeTrade.entryPrice ? { side: activeTrade.side, entryPrice: activeTrade.entryPrice, quantity: activeTrade.quantity ?? undefined, paper: !!activeTrade.isSimulation } : null}
          actualLtp={actualLtp}
          unrealizedPnl={activeTrade?.unrealizedPnl ?? null}
          pnlCurrency={activeTrade?.pnlCurrency || 'INR'}
          onCloseTrade={activeTrade ? closeActiveTrade : undefined}
          defaultVisibleBars={capOf(0)}
          tradeAmountPills={tradeAmountPills}
          flowCandles={overlays.flow ? paneFlowCandles[0] : undefined}
          fundingSeries={overlays.funding ? fundingSeries : undefined}
          overviewTf={ovSeriesTf} overviewHtf={ovSeriesHtf}
          overviewTfLabel={intervalLabel(intervals[0])} overviewHtfLabel={intervalLabel(htfInterval)}
          symbol={symbol} symbolNote={(() => { const a = symbol?.toUpperCase().startsWith('IDXN') ? aliasActualBySymbol.get(symbol.toUpperCase()) : undefined; return a ? `→ ${a.actual} · ${a.exchange}` : undefined; })()} activeStrategy={activeStrategy} rectZones={rectZones} rectDrawMode={rectDraw} selectedRectId={rectSel?.id ?? null} onRectZonesChange={setRectZones} onRectZoneSelect={handleRectZoneSelect} onRectDrawComplete={handleRectDrawComplete} syntheticLegs={isSynth ? synth?.legs : undefined} syntheticLegMarks={isSynth ? synthLegMarks : undefined}
          showTrendBand={trendEnabled}
          showBkground={bkgroundEnabled}
          replayMaxTime={replayMaxTime}
          onChartTimeClick={replaySeekOnClick}
          onLoadMore={(oldest, reason) => loadOlderPane(0, oldest, reason)}
          onVisibleBarsChange={visBarsCbs[0]} />
      </ChartErrorBoundary>
      {/* The workspace strip is short — the FNO/Overview side panels would cover it. */}
      {!wsOverlay && <FnoOverlayPanel />}
      {!wsOverlay && renderOverview()}
      {renderRateLimitHint()}
    </Box>
  );

  /* ───── quad mode strike/expiry selectors (separate CE/PE) ───── */
  const QuadSelectors = () => {
    if (layoutMode !== 'quad') return null;
    // Filter strikes to ATM ±10 (or ~20 nearest to spot)
    const filterStrikes = (selected: number | null) => {
      const ref = selected ?? spotPrice;
      if (!ref || !optionStrikes.length) return optionStrikes.slice(0, 30);
      return optionStrikes.filter(s => Math.abs(s - ref) <= (ref * 0.1));
    };
    const ceStrikes = filterStrikes(ceStrike);
    const peStrikes = filterStrikes(peStrike);

    const selectSx = { minWidth: 72, '& .MuiSelect-select': { py: 0.3, fontSize: CH_TYPE.small } };
    const labelSx = { color: 'text.secondary', fontSize: CH_TYPE.micro, mr: 0.2, fontWeight: 600 };

    const typeSx = { minWidth: 52, '& .MuiSelect-select': { py: 0.3, fontSize: CH_TYPE.small, fontWeight: 700 } };
    const actSx = { minWidth: 58, '& .MuiSelect-select': { py: 0.3, fontSize: CH_TYPE.small, fontWeight: 700 } };
    return (
      <>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.3 }} />
        {/* Leg 1 — action / type / expiry / strike (generalized) */}
        <Typography variant="caption" sx={{ ...labelSx, color: ceOptType === 'CE' ? C.callText : C.putText }}>L1</Typography>
        <Select size="small" value={ceAction} onChange={(e) => setCeAction(e.target.value as 'SELL' | 'BUY')} sx={actSx}>
          <MenuItem value="SELL">SELL</MenuItem>
          <MenuItem value="BUY">BUY</MenuItem>
        </Select>
        <Select size="small" value={ceOptType} onChange={(e) => setCeOptType(e.target.value as 'CE' | 'PE')} sx={typeSx}>
          <MenuItem value="CE">CE</MenuItem>
          <MenuItem value="PE">PE</MenuItem>
        </Select>
        <Select size="small" value={ceExpiry} onChange={(e) => setCeExpiry(e.target.value as string)} sx={{ ...selectSx, minWidth: 82 }} displayEmpty>
          {!optionExpiries.length && <MenuItem value="" disabled>—</MenuItem>}
          {optionExpiries.map(exp => <MenuItem key={exp} value={exp}>{exp}</MenuItem>)}
        </Select>
        <Select size="small" value={ceStrike ?? ''} onChange={(e) => setCeStrike(Number(e.target.value))} sx={selectSx} displayEmpty>
          {!ceStrikes.length && <MenuItem value="" disabled>—</MenuItem>}
          {ceStrikes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </Select>
        {/* Leg 2 */}
        <Typography variant="caption" sx={{ ...labelSx, color: peOptType === 'CE' ? C.callText : C.putText, ml: 0.3 }}>L2</Typography>
        <Select size="small" value={peAction} onChange={(e) => setPeAction(e.target.value as 'SELL' | 'BUY')} sx={actSx}>
          <MenuItem value="SELL">SELL</MenuItem>
          <MenuItem value="BUY">BUY</MenuItem>
        </Select>
        <Select size="small" value={peOptType} onChange={(e) => setPeOptType(e.target.value as 'CE' | 'PE')} sx={typeSx}>
          <MenuItem value="CE">CE</MenuItem>
          <MenuItem value="PE">PE</MenuItem>
        </Select>
        <Select size="small" value={peExpiry} onChange={(e) => setPeExpiry(e.target.value as string)} sx={{ ...selectSx, minWidth: 82 }} displayEmpty>
          {!optionExpiries.length && <MenuItem value="" disabled>—</MenuItem>}
          {optionExpiries.map(exp => <MenuItem key={exp} value={exp}>{exp}</MenuItem>)}
        </Select>
        <Select size="small" value={peStrike ?? ''} onChange={(e) => setPeStrike(Number(e.target.value))} sx={selectSx} displayEmpty>
          {!peStrikes.length && <MenuItem value="" disabled>—</MenuItem>}
          {peStrikes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </Select>
        {/* Timeframe for the THREE option panes (Q2 / Q3 / Q4), independent of the spot
            pane. They used to inherit intervals[0]; at an 8H spot interval a Delta option
            listed days ago has almost no history, so the panes that the quad layout exists
            for went nearly blank. "Auto" = follow the spot pane but never above 15m, and
            whatever is in force is printed in each option pane's title. */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.3 }} />
        <Tooltip title="Timeframe for the option panes (Q2/Q3/Q4). Delta option series are thin, so Auto follows the spot pane but caps at 15m.">
          <Typography variant="caption" sx={{ ...labelSx }}>Opt TF</Typography>
        </Tooltip>
        <Select size="small" value={optIntervalPref ?? 'auto'} sx={{ ...selectSx, minWidth: 78 }}
          onChange={(e) => {
            const v = e.target.value;
            setOptIntervalPref(v === 'auto' ? null : Number(v));
          }}>
          <MenuItem value="auto">Auto · {intervalLabel(Math.min(intervals[0] || OPT_INTERVAL_CAP, OPT_INTERVAL_CAP))}</MenuItem>
          {intervalOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
        {/* Re-pull the live option contracts (expiries + strikes) from the broker. */}
        <Tooltip title="Refresh option contracts from broker (latest expiries & strikes)">
          <span>
            <IconButton size="small" disabled={chainLoading}
              onClick={() => { setChainReloadNonce(n => n + 1); toast.success('Refreshing option chain…'); }}
              sx={{ p: 0.3, ml: 0.2 }}>
              {chainLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  };

  /* ───── render ───── */
  return (
    <Box sx={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    }}>

      {/* ═══ LEFT SYMBOL PANEL ═══ (width user-resizable via the right-edge grip) */}
      <Box sx={{
        width: symPanelW, minWidth: symPanelW,
        display: 'flex', flexDirection: 'column',
        borderRight: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Drag grip — widen/narrow the pane (persisted per device). */}
        <Box
          onMouseDown={startSymPanelDrag}
          title="Drag to resize the symbol pane"
          sx={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 6, zIndex: 5,
            cursor: 'col-resize',
            '&:hover': { bgcolor: isDark ? 'rgba(91,139,255,0.25)' : 'rgba(41,98,255,0.15)' },
          }}
        />
        {/* Filter chips — grouped by intent so they're easier to scan.
            Row 1: scope (ALL / Tracked / Browsed)
            Row 2: exchanges
            Row 3: long-side bullish filters (green tint)
            Row 4: short-side bearish filters (red tint)
            Row 5: open-position filter (neutral accent)
        */}
        <Box sx={{ px: 0.8, pt: 0.8, pb: 0.4 }}>
          {(() => {
            const tooltipMap: Record<string, string> = {
              Tracked: 'Show only tracked symbols',
              Browsed: 'Show recently browsed symbols (most recent first)',
              ALL: 'Show all instruments',
              MyFav: 'Symbols you marked ★ Favourite on the Instruments page (any exchange).',
              OpenPos: 'Symbols with currently open positions (excludes option-basket legs — see Baskets)',
              OpenBaskets: 'Underlyings with a running option basket (e.g. BTCUSD) — the basket legs live here, not under OpenPos',
              PendEntry: 'Symbols listed in Dashboard → Pending Entry Watch (PENDING + TRIGGERED)',
              Alerts: 'Symbols with a recent signal, newest first. Use the BUY / SHORT / COVER / SELL toggles to narrow it. TWO SOURCES, and they use DIFFERENT windows: BUY and SHORT are the Dashboard Recent-Alerts list (same period and same strategy/interval/confirmed filters you set there, latest alert per symbol); COVER and SELL come from the RockBand Signal Log over a fixed last-5-days window, because an exit for a symbol with no open trade never reaches Recent Alerts.',
              Gainers: 'Your TRACKED symbols that are UP at least the threshold % today, biggest move first. Today = last price vs the previous completed daily close, the same measurement the Dashboard heatmap uses. Only symbols you have tracked on the Instruments page are considered.',
              Losers: 'Your TRACKED symbols that are DOWN at least the threshold % today, biggest fall first. Same measurement and same tracked-only universe as Gainers.',
              LongWL: 'Symbols recently flagged LONG by your strategies',
              ShortWL: 'Symbols recently flagged SHORT by your strategies',
              LongTrig: 'Symbols with LONG-side price-level triggers',
              ShortTrig: 'Symbols with SHORT-side price-level triggers',
              AdScanBuy: 'Symbols that generated BUY in your most recent Adhoc Strategy Scan (any strategy). Replaces older results each new run.',
              AdScanShort: 'Symbols that generated SHORT in your most recent Adhoc Strategy Scan (any strategy). Replaces older results each new run.',
              NSEFNO: 'Tracked NSE F&O underlying STOCKS (NSE equity symbols that are F&O-eligible — i.e. have futures/options on NSE).',
              Global: 'Global indices & commodities (S&P 500, Nasdaq, DAX, Nikkei, Gold, Crude, …) charted via Yahoo Finance — ~15-min DELAYED, free, analysis-only (not tradable).',
              Movers: 'Possible upcoming big movers — symbols whose latest closed Daily or Weekly candle was Inside (HA) or Engulfing (Regular). Auto-populated daily at 05:31 IST from the "Crypto" watchlist; entries clear after 2 days.',
              RWA: 'Delta\'s tokenised real-world assets — tokenised US equities/ETFs (xStock, bStocks) plus tokenised metals (PAXG, XAUT, SLVON). Classified by Delta\'s own product tag, not by a symbol pattern, so it stays correct as Delta lists more. These are Delta PERPETUALS: they trade 24x7 and stream live like any other Delta symbol. Each row also names the listed instrument it tracks (NVDAXUSD · NVDA), and searching either name finds the row.',
              RWAEq: 'The LISTED instrument behind each RWA token, charted via Yahoo Finance. Rows are named by the STOCK (NVDAXUSD → NVDA) with the Delta token beside it, so either name finds the row. The three tokenised metals map to the underlying commodity: PAXG and XAUT → GOLD, SLVON → SILVER. NOT live, and cannot be made live — it is a free ~15-min-delayed feed and these instruments only trade during US market hours. The trade-off is years of daily/weekly history instead of the few months the Delta token has. Use the ↻ button on the RWA chip to rebuild this list.',
            };
            const chipLabel: Partial<Record<FilterMode, string>> = {
              AdScanBuy: 'Ad_Scan_BUY', AdScanShort: 'Ad_Scan_SHORT', NSEFNO: 'NSE F&O',
              RWA: 'Crypto RWA', RWAEq: 'RWA → Stock',
            };
            const filterItems: ToolbarItem[] = ([
              ['ALL', 'default'], ['MyFav', 'warning'], ['Tracked', 'default'], ['Browsed', 'default'],
              ['NSE', 'primary'], ['BSE', 'primary'], ['NFO', 'primary'], ['NSEFNO', 'primary'], ['MCX', 'primary'], ['CDS', 'primary'], ['NCO', 'primary'], ['DELTA', 'primary'], ['Global', 'primary'],
              ['RWA', 'primary'], ['RWAEq', 'primary'],
              ['Alerts', 'warning'],
              ['LongWL', 'success'], ['LongTrig', 'success'], ['AdScanBuy', 'success'],
              ['ShortWL', 'error'], ['ShortTrig', 'error'], ['AdScanShort', 'error'],
              ['OpenPos', 'secondary'], ['OpenBaskets', 'secondary'], ['PendEntry', 'secondary'],
            ] as [FilterMode, ChipColorName][]).map(([m, color]) => ({ key: m as string, label: chipLabel[m] || m, tooltip: tooltipMap[m] || `Filter by ${m}`, color, active: filterMode === m, onClick: () => setFilterMode(m) }));
            return (
              <>
                {/* Symbol filters — collapsible group with ★ favourites (item 17). */}
                <Box sx={{ mb: 0.5 }}>
                  <ToolbarMenuGroup groupId="symbolFilters" label="Filters" items={filterItems} isDark={isDark} dense
                    defaultFavourites={['ALL', 'Tracked', 'DELTA', 'NSE', 'OpenPos']} />
                </Box>
                {/* Labelled 'Screens', not 'Movers'. The group used to be called Movers AND
                    contain a chip called Movers, which is a different thing again (the daily
                    Inside/Engulfing Blast scan, not a %-move screen). One of the two names
                    had to go. */}
                {/* Same container + chip language as the Filters group above —
                    these are filters too, and rendering them in a different
                    palette made the panel look like two unrelated toolbars. */}
                <Box sx={{ ...chGroupSx(isDark, true), mb: 0.5 }}>
                  <Typography sx={chGroupLabelSx}>Screens</Typography>
                  {(['Gainers', 'Losers', 'Movers'] as FilterMode[]).map(m => (
                    <Tooltip key={m} title={tooltipMap[m] || m}>
                      <Chip label={m} size="small" variant="outlined"
                        onClick={() => setFilterMode(m)}
                        sx={chChipSx(C, isDark, { active: filterMode === m, dense: true })} />
                    </Tooltip>
                  ))}
                </Box>
                {/* RWA refresh — rebuilds the token→stock mapping and reloads
                    whichever of the two RWA lists is showing. One button for
                    both, because they are one dataset. */}
                {(filterMode === 'RWA' || filterMode === 'RWAEq') && (() => {
                  const unverified = rwaEquivalents.filter(e => !e.verified);
                  return (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, mt: 0.2, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" disabled={rwaRefreshing}
                        startIcon={rwaRefreshing ? <CircularProgress size={11} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                        onClick={async () => {
                          setRwaRefreshing(true);
                          try {
                            const r = await api.post<any>('/charts/rwa-symbols/refresh');
                            const d = r?.data?.data || {};
                            await loadSymbolsByMode(filterMode);
                            toast.success(`RWA list rebuilt — ${d.verified ?? 0} verified, ${d.unverified ?? 0} unverified`);
                          } catch {
                            toast.error('Could not rebuild the RWA list');
                          } finally {
                            setRwaRefreshing(false);
                          }
                        }}
                        sx={{ fontSize: CH_TYPE.micro, py: 0.2, px: 1, minWidth: 0, textTransform: 'none' }}>
                        Refresh RWA
                      </Button>
                      {unverified.length > 0 && (
                        <Tooltip arrow title={`No listed ticker could be verified for: ${unverified.map(e => e.deltaSymbol).join(', ')}. These are listed but not chartable.`}>
                          <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', cursor: 'help' }}>
                            {unverified.length} unverified
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })()}
                {/* The date field that used to sit here was a DEAD CONTROL. getMovers reads
                    only `type` and `threshold`; `date` was dropped from the handler in Round 29
                    (the bulk quote endpoint can only answer 'today vs previous close') but the
                    input stayed, so the user could pick a date, press Go, and get today's
                    numbers back with no indication the date had been ignored. */}
                {(filterMode === 'Gainers' || filterMode === 'Losers') && (
                  <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center', mb: 0.5, mt: 0.2 }}>
                    <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      Today, move ≥
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="%"
                      value={moversThreshold}
                      onChange={(e) => setMoversThreshold(e.target.value)}
                      inputProps={{ step: 0.5, min: 0.1, max: 100 }}
                      sx={{ width: 60, '& .MuiInputBase-input': { fontSize: CH_TYPE.small, py: 0.4 } }}
                    />
                    <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary' }}>%</Typography>
                    <Button size="small" variant="contained"
                      onClick={() => loadSymbolsByMode(filterMode)}
                      sx={{ fontSize: CH_TYPE.micro, py: 0.3, minWidth: 0, px: 1 }}>
                      Go
                    </Button>
                  </Box>
                )}
                {(filterMode === 'Gainers' || filterMode === 'Losers') && !!moversNote && (
                  <Typography sx={{
                    fontSize: CH_TYPE.micro, color: 'text.secondary', mb: 0.5,
                    whiteSpace: 'normal', lineHeight: 1.35,
                  }}>{moversNote}</Typography>
                )}
                {/* BUY / SHORT / COVER / SELL sub-toggles. Shown only while Alerts is the
                    active filter, so they cost no toolbar height the rest of the time. */}
                {filterMode === 'Alerts' && (
                  <Box sx={{ ...chGroupSx(isDark, true), mb: 0.5, mt: 0.2 }}>
                    <Typography sx={chGroupLabelSx}>Window</Typography>
                    {/* The SAME dashboard_alertPeriod the Dashboard Recent-Alerts dropdown
                        writes, so the two panels always describe the same window rather than
                        disagreeing about what "recent" means. */}
                    <Tooltip arrow title="How far back to look for alerts. Shared with the Dashboard Recent-Alerts panel — changing it here changes it there, and vice versa. Applies to BUY/SHORT; COVER/SELL come from the RockBand Signal Log on its own fixed 5-day window.">
                      <Select size="small" value={alertPeriod}
                        onChange={(e) => setAlertPeriod(String(e.target.value))}
                        sx={{ fontSize: CH_TYPE.micro, height: CH_SIZE.chipH, minWidth: 62,
                              "& .MuiSelect-select": { py: 0, pr: "20px !important" } }}>
                        {ALERT_PERIODS.map(pp => (
                          <MenuItem key={pp} value={pp} sx={{ fontSize: CH_TYPE.small }}>{pp}</MenuItem>
                        ))}
                      </Select>
                    </Tooltip>
                  </Box>
                )}
                {filterMode === 'Alerts' && (
                  <Box sx={{ ...chGroupSx(isDark, true), mb: 0.5 }}>
                    <Typography sx={chGroupLabelSx}>Type</Typography>
                    {ALERT_TYPES.map(t => {
                      const on = alertTypes.includes(t);
                      const bull = t === 'BUY' || t === 'COVER';
                      return (
                        <Tooltip key={t} arrow title={
                          t === 'BUY' ? 'Entry LONG signals, from Dashboard Recent Alerts.'
                          : t === 'SHORT' ? 'Entry SHORT signals, from Dashboard Recent Alerts.'
                          : t === 'COVER' ? 'COVER / COVER_FLAT — a closed short, from the RockBand Signal Log (last 5 days).'
                          : 'SELL / SELL_FLAT — a closed long, from the RockBand Signal Log (last 5 days).'
                        }>
                          <Chip label={t} size="small" variant={on ? 'filled' : 'outlined'}
                            onClick={() => toggleAlertType(t)}
                            sx={{
                              cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.micro, height: CH_SIZE.chipH,
                              '& .MuiChip-label': { px: 0.6 },
                              bgcolor: on ? (bull ? C.bullMuted : C.bearMuted) : 'transparent',
                              color: on ? (bull ? C.bull : C.bear) : 'text.disabled',
                              borderColor: on ? (bull ? C.bullBorder : C.bearBorder) : 'divider',
                            }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </>
            );
          })()}
          {/* The panel is user-resizable and narrow by default, so this row has to
              yield: the field was `fullWidth` next to a fixed 84px Select, which
              left it ~100px and clipped the placeholder to "Search s". */}
          <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center', minWidth: 0 }}>
            <TextField size="small" placeholder="Search"
              sx={{ flex: 1, minWidth: 0 }}
              value={symSearch} onChange={e => setSymSearch(e.target.value.toUpperCase())}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.3 }} />,
                endAdornment: symLoading
                  ? <CircularProgress size={12} />
                  : (symSearch ? (
                      <IconButton size="small" sx={{ p: 0.2 }} onClick={() => setSymSearch('')} title="Clear search">
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    ) : null),
                sx: { fontSize: CH_TYPE.body, height: 30 },
              }}
            />
            {/* R115 follow-up — sort dropdown. Name asc/desc work today; pct
                variants will sort once the symbol list endpoint supplies %. */}
            <Select size="small" value={symSort} onChange={e => persistSymSort(e.target.value as any)}
              sx={{ fontSize: CH_TYPE.small, height: 30, minWidth: 70, flexShrink: 0, '& .MuiSelect-select': { py: 0.5, pr: '22px !important' } }}
              title="Sort symbol list"
            >
              <MenuItem value="recent"    sx={{ fontSize: CH_TYPE.small }}>Recent ▼</MenuItem>
              {/* Time sorts are only meaningful where rows carry a timestamp. Offering
                  them on an instrument browser would be a control that silently does
                  nothing, which is worse than an absent one. */}
              {FILTER_TEMPLATE[filterMode] === 'signal' && ([
                ['time-desc', 'Time ▼'],
                ['time-asc', 'Time ▲'],
              ] as const).map(([v, label]) => (
                <MenuItem key={v} value={v} sx={{ fontSize: CH_TYPE.small }}>{label}</MenuItem>
              ))}
              <MenuItem value="name-asc"  sx={{ fontSize: CH_TYPE.small }}>Name ▲</MenuItem>
              <MenuItem value="name-desc" sx={{ fontSize: CH_TYPE.small }}>Name ▼</MenuItem>
              <MenuItem value="pct-desc"  sx={{ fontSize: CH_TYPE.small }}>% ▲</MenuItem>
              <MenuItem value="pct-asc"   sx={{ fontSize: CH_TYPE.small }}>% ▼</MenuItem>
            </Select>
          </Box>
        </Box>
        <Divider />
        <List
          dense
          ref={symListRef as any}
          /* minHeight, not just flex:1. The synthetic panes below are a sibling in this
             flex column and their content is tall (BTCUSD carries ~30 AUTO entries), so
             `flex: 1` alone left the symbol list ONE ROW high on exactly the symbols a
             trader looks at most. A per-filter row template nobody can see is worth
             nothing, so the list now claims ~6 rows before the synthetic block gets any
             space, and that block scrolls inside itself instead. */
          sx={{ flex: '1 1 auto', minHeight: 200, overflow: 'auto', py: 0, outline: 'none' }}
          // Up/Down navigation is handled by a page-level keydown listener (see
          // symNavRef) so it works regardless of which element has focus — the
          // earlier list-focus approach dropped focus on each re-render.
        >
          {sortedSymResults.map(s => {
            const shape = FILTER_TEMPLATE[filterMode] || 'market';
            // Only the RWA filters carry a second name; everywhere else this is empty.
            const pairs = (filterMode === 'RWA' || filterMode === 'RWAEq') ? rwaPairsFor(s.symbol, s.exchange) : [];
            return (
              <SymbolPaneRow
                key={`${s.symbol}-${s.exchange}`}
                item={s}
                shape={shape}
                selected={s.symbol === symbol && s.exchange === exchange}
                isDark={isDark}
                C={C}
                pct={pctFor(s.symbol, s.exchange)}
                ltp={ltpFor(s.symbol, s.exchange)}
                pairs={pairs}
                isFav={favSet.has(s.symbol.toUpperCase())}
                // Precision from the VALUE, because no instrument row travels with a pane
                // item. resolveDecimalsFor(null, n) does NOT work here: with no input it
                // takes base 2 and caps its own magnitude fallback at it, which rendered an
                // alert on a 0.00774 token as "0.01 -> 0.01" beside a -1.83% move.
                fmtPrice={(n) => formatPrice(n, decimalsForValue(n))}
                onClick={() => handleSymbolClick(s.symbol, s.exchange)}
                onToggleFav={() => toggleFavSymbol(s.symbol, s.exchange)}
              />
            );
          })}
          {symSearch.length >= 2 && !symLoading && !symResults.length && (
            <Typography sx={{ p: 1.5, fontSize: CH_TYPE.small, color: 'text.secondary', textAlign: 'center' }}>No symbols found</Typography>
          )}
          {!symResults.length && symSearch.length < 2 && (
            <Typography sx={{ p: 1.5, fontSize: CH_TYPE.small, color: 'text.secondary', textAlign: 'center' }}>No recent symbols</Typography>
          )}
        </List>
        {/* Synthetic-symbol panes (Saved / Live / Auto) — render nothing until the
            server feature flag SYNTHETIC_SYMBOLS_ENABLED is on. */}
        <Box sx={{ minHeight: 0, flexShrink: 1, overflow: 'auto' }}>
          <SyntheticSymbolPanes
            underlyingSymbol={isSynth ? (synth?.underlying?.symbol || '') : (nfoUnderlying || '')}
            activeSyntheticId={isSynth ? (synth?.id ?? null) : null}
            onOpen={openSynthetic}
            refreshNonce={synthNonce}
          />
        </Box>
      </Box>

      {/* ═══ MAIN CHART AREA ═══ */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Toolbar. `data-charts-toolbar` is read by scripts/verify-charts-ui.js
            to measure how many rows it actually occupies at each width — the
            metric this redesign is trying to move. */}
        <Box data-charts-toolbar="true" sx={{
          px: 1, py: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3,
          borderBottom: `1px solid ${isDark ? '#1e2d3d' : '#e0e0e0'}`, bgcolor: 'background.paper',
        }}>
          {/* ── Toolbar — ONE wrapping row (item 2) ──
             Row 1 + Row 2 were merged into a single flex-wrap container so chips
             pack each line fully before wrapping (a second container always forced
             a fresh line, wasting vertical space). Combined with the smaller chips
             in ToolbarMenuGroup this keeps the toolbar to ~2 rows in a typical
             setup, freeing chart height. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', rowGap: 0.4, minHeight: 28 }}>
            {/* Symbol-nav + symbol-chip + watchlist group pill (slate). Round 32:
               wrapped so the symbol cluster reads as a single group, matching
               the visual rhythm of the indicator/strategy/etc groups below. */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              px: 0.5, py: 0.15, borderRadius: 1.2,
              bgcolor: isDark ? 'rgba(176,190,197,0.14)' : 'rgba(96,125,139,0.10)',
              border: `1px solid ${isDark ? 'rgba(176,190,197,0.32)' : 'rgba(96,125,139,0.28)'}`,
            }}>
              <Tooltip title="Previous symbol">
                <span>
                  <IconButton size="small" onClick={handleHistoryPrev} disabled={historyIdx <= 0} sx={{ p: 0.3 }}>
                    <ArrowBackIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next symbol">
                <span>
                  <IconButton size="small" onClick={handleHistoryNext} disabled={historyIdx >= history.length - 1} sx={{ p: 0.3 }}>
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {symbol && (
                <>
                  <Chip label={`${symbol} · ${exchange}`} size="small"
                    sx={{ fontWeight: 700, bgcolor: C.neutralMuted, color: C.neutral, fontSize: CH_TYPE.body, height: 24, '& .MuiChip-label': { px: 0.8 } }}
                  />
                  <Tooltip title={wlLong ? 'Remove from WL Long' : 'Add to WL Long'}>
                    <IconButton size="small" onClick={() => toggleWatchlist('wlLong')} sx={{ p: 0.2 }}>
                      {wlLong ? <BookmarkIcon sx={{ fontSize: 16, color: '#4caf50' }} /> : <BookmarkBorderIcon sx={{ fontSize: 16, color: '#4caf50' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={wlShort ? 'Remove from WL Short' : 'Add to WL Short'}>
                    <IconButton size="small" onClick={() => toggleWatchlist('wlShort')} sx={{ p: 0.2 }}>
                      {wlShort ? <BookmarkIcon sx={{ fontSize: 16, color: '#f44336' }} /> : <BookmarkBorderIcon sx={{ fontSize: 16, color: '#f44336' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isFav ? 'Remove from favourites' : 'Mark as favourite ★'}>
                    <IconButton size="small" onClick={toggleFavourite} sx={{ p: 0.2 }}>
                      {isFav ? <StarIcon sx={{ fontSize: 17, color: '#ffb300' }} /> : <StarBorderIcon sx={{ fontSize: 17, color: '#ffb300' }} />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>

            {/* Layout group pill (blue). Opacities bumped in round 32 so each
               group's tint reads as a clearly distinct band of color. */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              px: 0.5, py: 0.15, borderRadius: 1.2,
              bgcolor: isDark ? 'rgba(100,181,246,0.18)' : 'rgba(33,150,243,0.13)',
              border: `1px solid ${isDark ? 'rgba(100,181,246,0.40)' : 'rgba(33,150,243,0.32)'}`,
            }}>
              <ToggleButtonGroup size="small" exclusive value={layoutMode}
                onChange={(_e, v) => { if (v) setLayoutMode(v); }}
                sx={{
                  '& .MuiToggleButton-root': { py: 0.2, px: 0.8 },
                  '& .Mui-selected': {
                    bgcolor: isDark ? 'rgba(0,200,83,0.18)' : 'rgba(0,200,83,0.12)',
                    color: isDark ? '#69f0ae' : '#1b5e20', fontWeight: 700,
                  },
                }}
              >
                <ToggleButton value="single"><Tooltip title="Single"><CropSquareIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                <ToggleButton value="split"><Tooltip title="2-Split"><ViewColumnIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                <ToggleButton value="quad"><Tooltip title="4-Quad (Options)"><ViewModuleIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                <ToggleButton value="workspace" data-testid="layout-workspace"><Tooltip title="Options Workspace — Delta BTC/ETH chain, basket builder and payoff"><SpaceDashboardIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
              <QuadSelectors />
            </Box>

            {/* Single / 2-split — pick an option to chart by Underlying/Expiry/Strike
                (no need to find the cryptic option symbol in the left pane). */}
            {layoutMode !== 'quad' && layoutMode !== 'workspace' && (
              <OptionChartPicker
                currentSymbol={symbol}
                onPick={(s, e) => handleSymbolClick(s, e)}
              />
            )}

            {/* Timeframe / Candle / Strategy — collapsible groups with ★ favourites (item 17). */}
            {layoutMode === 'split'
              ? <IntervalSelect pane={focusedSplitPane} label={`Pane ${focusedSplitPane + 1}`} />
              : <IntervalSelect pane={0} />}
            <ToolbarMenuGroup groupId="candletype" label="Candle" items={candleItems} isDark={isDark}
              accent={{ dark: '77,182,172', light: '0,150,136' }}
              defaultFavourites={['Regular', 'HA', 'lr', 'wick']} />
            <ToolbarMenuGroup groupId="strategy" label="Strategy" items={strategyItems} isDark={isDark}
              accent={{ dark: '255,183,77', light: '255,152,0' }}
              defaultFavourites={strategies.slice(0, 3).map(s => s.id)} />

            {/* AI view of this symbol. Additive: it never gates or alters a chart action, and it
                makes no model call until asked. Hidden for synthetics, which have no underlying
                candle history of their own for the model to reason about. */}
            {!!symbol && !!exchange && exchange !== 'SYNTH' && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, ml: 0.3 }}>
                <AiVerdictChip
                  assessment={aiView}
                  symbol={symbol}
                  exchange={exchange}
                  timeframe={1440}
                  askSource="charts toolbar"
                  onAssessed={setAiView}
                />
              </Box>
            )}

            {/* Option actions — Build (one-click baskets) + Outlook (basket builder),
               collapsible groups with ★ favourites (item 17). Shown when this symbol has an option chain. */}
            {nfoUnderlying && (
              <ToolbarMenuGroup groupId="optionBuild" label="Build" items={optionBuildItems} isDark={isDark}
                accent={{ dark: '38,166,154', light: '0,150,136' }} hideActiveCount
                defaultFavourites={['short_straddle', 'bear_call_spread', 'iron_condor']} />
            )}
            {nfoUnderlying && (
              <ToolbarMenuGroup groupId="outlook" label="Outlook" items={outlookItems} isDark={isDark}
                accent={{ dark: '149,117,205', light: '103,58,183' }}
                defaultFavourites={['WONT_FALL_BELOW', 'WONT_RISE_ABOVE', 'STAY_IN_RANGE']} />
            )}
            {/* Payoff-zones toggle (item 2) — the single, contextual control for the
                RUNNING basket's payoff zones + BE / TP / SL / Max-P levels. Only shown
                when a basket is open for this symbol (no duplicate in the Indicators menu). */}
            {(openBasket || campaignPayoffCard) && (
              <Tooltip arrow title={overlays.payoffZones ? 'Hide the running option basket’s / campaign’s payoff zones + break-even / take-profit / stop-loss / max-profit levels' : 'Show the running option basket’s / campaign’s payoff zones + key levels on this chart'}>
                <Chip size="small" icon={<ShowChartIcon sx={{ fontSize: 15 }} />}
                  label={overlays.payoffZones ? 'Payoff Zones ✓' : 'Payoff Zones'}
                  color={overlays.payoffZones ? 'success' : 'default'}
                  variant={overlays.payoffZones ? 'filled' : 'outlined'}
                  onClick={() => handleOverlayToggle('payoffZones' as keyof Overlays)}
                  sx={{ cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.small, height: 24 }} />
              </Tooltip>
            )}
            {/* Unified OUTLOOK draw banner — Single basket (Architect) vs Staged scale-in. Staged =
                draw the PREDEFINED entry levels per view; the dialog opens automatically when done.
                The banner PULSES while awaiting your next click so it's clear what to pick. */}
            {chartTool === 'outlook' && outlookCapture && (() => {
              const mode = outlookCapture.mode; const shape = ladderShape(mode); const N = targetStages;
              const staged = outlookMode === 'staged';
              let instr: React.ReactNode = null;
              if (staged) {
                if (drawPhase === 'entries') { const down = shape === 'dir-down'; instr = <>Click <b>E{drawnStages.length + 1}</b> of {N} — {down ? 'below' : 'above'} {drawnStages.length ? 'the previous' : 'spot'}{drawnStages.length ? ` · ${drawnStages.map((p, i) => `E${i + 1} ${Math.round(p).toLocaleString('en-IN')}`).join(', ')}` : ''}</>; }
                else if (drawPhase === 'danger') { const down = shape === 'dir-down'; instr = <>Click the <b style={{ color: '#c62828' }}>danger</b> level — {down ? 'below' : 'above'} E{drawnStages.length}</>; }
                else if (drawPhase === 'rangeUpper') { instr = <>Click the <b>{shape.startsWith('brk') ? 'breakout' : 'range'} UPPER</b> level (above spot){drawnRU != null ? ` · ${Math.round(drawnRU).toLocaleString('en-IN')}` : ''}</>; }
                else if (drawPhase === 'rangeLower') { instr = <>Click the <b>{shape.startsWith('brk') ? 'breakout' : 'range'} LOWER</b> level (below spot){drawnRL != null ? ` · ${Math.round(drawnRL).toLocaleString('en-IN')}` : ''}</>; }
                else if (drawPhase === 'upLevels') { const credit = shape === 'range'; instr = <>Click <b>E{drawnUp.length + 1}↑</b> of {N} — above the previous {credit ? '(credit added when hit)' : '(debit added when broken)'}{drawnUp.length ? ` · ${drawnUp.map((p) => Math.round(p).toLocaleString('en-IN')).join(', ')}` : ''}</>; }
                else if (drawPhase === 'dnLevels') { const credit = shape === 'range'; instr = <>Click <b>E{drawnDn.length + 1}↓</b> of {N} — below the previous {credit ? '(credit added when hit)' : '(debit added when broken)'}{drawnDn.length ? ` · ${drawnDn.map((p) => Math.round(p).toLocaleString('en-IN')).join(', ')}` : ''}</>; }
              }
              return (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', px: 1, py: 0.4, borderRadius: 1.2,
                bgcolor: isDark ? 'rgba(126,87,194,0.28)' : 'rgba(126,87,194,0.16)', border: `1.5px solid ${isDark ? 'rgba(179,157,219,0.85)' : 'rgba(126,87,194,0.7)'}`,
                ...(staged ? { animation: 'ladderPulse 1.2s ease-in-out infinite', '@keyframes ladderPulse': { '0%,100%': { boxShadow: `0 0 0 0 ${isDark ? 'rgba(179,157,219,0.55)' : 'rgba(126,87,194,0.5)'}` }, '50%': { boxShadow: `0 0 0 5px ${isDark ? 'rgba(179,157,219,0.12)' : 'rgba(126,87,194,0.12)'}` } } } : {}) }}>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>{OUTLOOK_LABEL[outlookCapture.mode]}</Typography>
                {campaignsSupported && (
                  <Tooltip arrow title="Single basket = one ranked option basket now (Strategy Architect). Staged scale-in = draw the predefined entry levels; each fires a spread anchored to that level when price reaches it (credit for range/won't-move, debit for breakouts).">
                    <ToggleButtonGroup size="small" exclusive value={outlookMode}
                      onChange={(_, v) => { if (!v) return; setOutlookMode(v); resetDraw(); setDrawPhase(v === 'staged' && shape !== 'dir-down' && shape !== 'dir-up' ? (shape === 'brk-dn' ? 'rangeLower' : 'rangeUpper') : 'entries'); setOutlookCapture(prev => prev ? { ...prev, picks: [] } : prev); }}
                      sx={{ '& .MuiToggleButton-root': { py: 0, px: 0.8, fontSize: CH_TYPE.micro, textTransform: 'none', fontWeight: 700 } }}>
                      <ToggleButton value="single">Single basket</ToggleButton>
                      <ToggleButton value="staged">Staged scale-in</ToggleButton>
                    </ToggleButtonGroup>
                  </Tooltip>
                )}
                {!staged ? (
                  <Typography variant="caption">Click {outlookCapture.clicksNeeded === 2 ? (outlookCapture.picks.length === 0 ? 'the LOWER level (1/2)' : 'the UPPER level (2/2)') : 'your level'} on the chart</Typography>
                ) : (<>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{instr}</Typography>
                  {(drawnStages.length > 0 && drawPhase === 'entries') && <Chip size="small" label="Undo" onClick={() => setDrawnStages(prev => prev.slice(0, -1))} variant="outlined" sx={{ height: 20, fontSize: CH_TYPE.micro, cursor: 'pointer' }} />}
                  {(drawnUp.length > 0 && drawPhase === 'upLevels') && <Chip size="small" label="Undo" onClick={() => setDrawnUp(prev => prev.slice(0, -1))} variant="outlined" sx={{ height: 20, fontSize: CH_TYPE.micro, cursor: 'pointer' }} />}
                  {(drawnDn.length > 0 && drawPhase === 'dnLevels') && <Chip size="small" label="Undo" onClick={() => setDrawnDn(prev => prev.slice(0, -1))} variant="outlined" sx={{ height: 20, fontSize: CH_TYPE.micro, cursor: 'pointer' }} />}
                </>)}
                <Chip size="small" label="Cancel" onClick={cancelOutlookDraw} color="error" variant="outlined" sx={{ height: 20, fontSize: CH_TYPE.micro, cursor: 'pointer' }} />
              </Box>
              );
            })()}
            {/* Multiple open baskets for this symbol → pick which one the zones overlay
                (remembered per underlying). Shown only when >1 and zones are on. */}
            {overlays.payoffZones && openBaskets.length > 1 && (
              <Typography component="span" sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, color: 'text.secondary', alignSelf: 'center', ml: 0.3 }}>Payoff:</Typography>
            )}
            {overlays.payoffZones && openBaskets.length > 1 && openBaskets.map((b: any) => (
              <Tooltip key={b.id} arrow title={`Overlay basket #${b.id} — ${b.strategy || 'basket'} (${b.tradingMode || ''})`}>
                <Chip size="small"
                  label={`#${b.id} ${b.strategy || ''}`.trim()}
                  color={openBasket?.id === b.id ? 'primary' : 'default'}
                  variant={openBasket?.id === b.id ? 'filled' : 'outlined'}
                  onClick={() => setSelectedBasketId(b.id)}
                  sx={{ cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.micro, height: 22, maxWidth: 150, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
              </Tooltip>
            ))}
            {/* P&L (top-left badge), TP & SL (left-labelled lines) now render ON the
                chart itself — see PnlBadgePrimitive + payoffLeftLevels — so they're no
                longer duplicated as toolbar chips here. */}

            {/* Triggers + manual order group pill (amber) — NBUY / NSHT / BBUY / BSHT / HVN / BUY / SHORT / Add ISL / Add Tgt / Replay.
               No pre-selector: in 4-quad the action targets whichever pane (spot /
               CE / PE) the user clicks. The chips below show the UNDERLYING's
               trigger levels; each option pane shows its own via PaneTriggerChips. */}
            {/* Dense: this is the widest group on the bar — twelve controls
                (NB/NS/BB/BS, HVN, Rect, BUY, SHORT, ISL, TGT, GoTo, R) — and at
                1366px it is what decides whether the toolbar wraps. */}
            {symbol && (
              <Box sx={chGroupSx(isDark, true)}>
                <Typography sx={chGroupLabelSx}>Levels</Typography>
                {/* Four hues became two. NB/BB are buy-side and NS/BS are
                    short-side, so colour now says WHICH SIDE — the thing that
                    matters — and the label says narrow vs broad, which is what
                    the N/B prefix was already for. */}
                {([
                  ['nbuy', 'NB', 'Narrow Buy trigger', 'bull'],
                  ['nshort', 'NS', 'Narrow Short trigger', 'bear'],
                  ['bbuy', 'BB', 'Broad Buy trigger', 'bull'],
                  ['bshort', 'BS', 'Broad Short trigger', 'bear'],
                ] as [TriggerType, string, string, ChTone][]).map(([type, label, tip, tone]) => {
                  const color = tone === 'bull' ? C.bull : C.bear;
                  return (
                  <Box key={type} data-trigger-chip="true" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                    <Tooltip arrow title={triggerLevels[type] ? `${tip} @ ${triggerLevels[type]}` : `${tip} — click to arm, then click the chart`}>
                      <Chip label={triggerLevels[type] ? `${label} ${triggerLevels[type]}` : label} size="small"
                        variant="outlined"
                        onClick={() => setTriggerModeExclusive(triggerMode === type ? null : type)}
                        sx={{
                          ...chChipSx(C, isDark, { active: triggerMode === type, tone, dense: true }),
                          ...(triggerMode !== type ? { color, borderColor: `${color}66` } : {}),
                          ...chNumeric,
                          ...(triggerLevels[type] ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {}),
                          ...(replay.active ? { opacity: 0.4 } : {}),
                        }}
                      />
                    </Tooltip>
                    {triggerLevels[type] && lvlOptCapable && (() => {
                      const att = lvlAttachment(type);
                      const nm = att ? (findTemplate(att.templateId)?.name || att.templateId) : '';
                      return (
                        <Tooltip arrow title={att
                          ? `When ${label} fires: open ${nm} (${attExpiryText(att)}, ×${att.lots}, ${att.tradingMode === 'LIVE' ? 'LIVE — real orders' : 'PAPER'})${lvlOpt?.autoExecute ? '' : ' — PAUSED: Auto-Execute is OFF, so it will only alert'}. Click to edit or remove.`
                          : `Open an option strategy when ${label} fires (instead of trading ${symbol} itself) — PAPER or LIVE`}>
                          <Chip label="⚡" size="small" variant="outlined" data-level-option={type}
                            onClick={() => setLvlOptDlg(type)}
                            sx={{
                              ...chChipSx(C, isDark, { dense: true, active: !!att, tone: att?.tradingMode === 'LIVE' ? 'bear' : tone }),
                              minWidth: 18, borderRadius: 0, borderLeft: 'none',
                              ...(att ? {} : { color, borderColor: `${color}66` }),
                              '& .MuiChip-label': { px: 0.35 },
                            }} />
                        </Tooltip>
                      );
                    })()}
                    {triggerLevels[type] && (
                      <Chip label="×" size="small"
                        onClick={() => removeTriggerLevel(type)}
                        sx={{
                          ...chChipSx(C, isDark, { dense: true }),
                          minWidth: 16, color, borderColor: `${color}66`,
                          borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none',
                          '& .MuiChip-label': { px: 0.25 },
                        }}
                        variant="outlined"
                      />
                    )}
                  </Box>
                  );
                })}

                {/* HVN capture button — multiple HVNs allowed; click chart to add.
                    Spot-only (HVN levels are keyed to the underlying). */}
                {/* HVN capture button — multiple HVNs allowed; click the underlying
                    chart to add. (HVN levels are keyed to the underlying symbol.) */}
                <Tooltip arrow title="HVN — High Volume Node. Click to arm, then click the chart to add (multiple allowed; underlying only).">
                  <Chip
                    label="HVN"
                    size="small"
                    variant="outlined"
                    onClick={() => setChartToolExclusive(chartTool === 'hvn' ? null : 'hvn')}
                    sx={chChipSx(C, isDark, { active: chartTool === 'hvn', dense: true })}
                  />
                </Tooltip>

                {/* Rectangle zone tool (item 3) — 2 clicks draw a persistent box
                    annotation (per symbol; survives re-login; visible in replay).
                    Click a zone to recolor/resize/delete; drag body/edges to move. */}
                <Tooltip arrow title="Rect zone — click to arm, then click two corners on the chart to draw a box annotation. Click a zone to edit (color/opacity/delete); drag its body to move, edges to resize. Esc cancels.">
                  <Chip
                    label="▭ Rect"
                    size="small"
                    variant="outlined"
                    onClick={() => { setRectDraw(v => !v); setChartToolExclusive(null); setRectSel(null); }}
                    sx={chChipSx(C, isDark, { active: rectDraw, dense: true })}
                  />
                </Tooltip>

                {/* ENGINE-mirror synthetic — read-only readout of the running auto-strategy
                    cycle (engine · mode · structure/tier · entry premium · booked P&L). The
                    trigger-arming chips below are hidden: the engine manages this position. */}
                {isSynth && synth?.engineCycle && (
                  <Chip
                    size="small"
                    label={[
                      synth.engineCycle.engine.toUpperCase(),
                      synth.engineCycle.mode,
                      [synth.engineCycle.structure, synth.engineCycle.tier].filter(Boolean).join(' '),
                      synth.engineCycle.entryPremium != null ? `entry ${Number(synth.engineCycle.entryPremium).toFixed(1)}` : null,
                      Number(synth.engineCycle.realizedPnl) !== 0 ? `booked ₹${Number(synth.engineCycle.realizedPnl).toFixed(0)}` : null,
                    ].filter(Boolean).join(' · ')}
                    title={'This position is opened and managed by its auto-strategy engine (Strategies → Options AT). '
                      + 'The chart shows its combined premium with read-only ENTRY and STOP lines — the STOP tightens as the engine trails. '
                      + (synth.engineCycle.exitDueAt ? `Scheduled square-off: ${new Date(synth.engineCycle.exitDueAt).toLocaleString()}.` : '')}
                    sx={{
                      fontWeight: 800, fontSize: CH_TYPE.micro, height: 20, letterSpacing: 0.3,
                      color: '#fff', bgcolor: synth.engineCycle.mode === 'LIVE' ? '#ad1457' : '#00695c',
                      '& .MuiChip-label': { px: 0.65 },
                    }}
                  />
                )}
                {/* TRADE NOW — place this synthetic's basket immediately at the live combined
                    premium (server defaults SL/Target ±50%; the lines are draggable after). */}
                {isSynth && synth && !synth.engineCycle && synth.status !== 'LIVE' && synth.status !== 'LIVE_PARTIAL' && (
                  <Chip
                    label={tradingNowBusy ? 'PLACING…' : 'TRADE NOW'}
                    size="small"
                    disabled={tradingNowBusy}
                    title={`Place this ${synth.isCredit ? 'credit (SHORT the package)' : 'debit (BUY the package)'} basket immediately at the live combined premium — no trigger lines needed. Default SL/Target ±50% land on the chart as draggable lines. Mode follows the PAPER/LIVE chip.`}
                    onClick={() => { void tradeNowSynth(); }}
                    sx={{
                      cursor: 'pointer', fontWeight: 800, fontSize: CH_TYPE.micro, height: 20, letterSpacing: 0.4,
                      color: '#fff', bgcolor: synth.isCredit ? '#c62828' : '#2e7d32',
                      '&:hover': { bgcolor: synth.isCredit ? '#b71c1c' : '#1b5e20' },
                      '& .MuiChip-label': { px: 0.65 },
                    }}
                  />
                )}
                {/* Close the LIVE position behind this synthetic straight from the chart:
                    engine-mirror cycles via the per-cycle close endpoint; basket-backed
                    LIVE synthetics via the basket close. */}
                {isSynth && ((synth?.engineCycle && synth.engineCycle.status === 'OPEN' && synth.strategyCycleId != null)
                  || (!synth?.engineCycle && synth?.basketId != null && (synth.status === 'LIVE' || synth.status === 'LIVE_PARTIAL'))) && (
                  <Chip
                    label={closingSynth ? 'CLOSING…' : 'CLOSE'}
                    size="small"
                    disabled={closingSynth}
                    title={synth?.engineCycle
                      ? `Square off this ${synth.engineCycle.mode} position now at market (closes all legs). The engine books the exit and the row leaves Synthetic · Live.`
                      : 'Close this basket now at market (closes all legs via the basket monitor).'}
                    onClick={() => { void closeSynthPosition(); }}
                    sx={{
                      cursor: 'pointer', fontWeight: 800, fontSize: CH_TYPE.micro, height: 20, letterSpacing: 0.4,
                      color: '#fff', bgcolor: '#c62828', border: '1px solid transparent',
                      '&:hover': { bgcolor: '#b71c1c' },
                      '& .MuiChip-label': { px: 0.65 },
                    }}
                  />
                )}
                {/* Synthetic charts: PAPER/LIVE arming mode for the trigger. LIVE
                    places real multi-leg orders when it fires (explicit confirm +
                    server-side margin pre-check + gates). */}
                {isSynth && !synth?.engineCycle && (
                  <Chip
                    label={synthMode}
                    size="small"
                    title={synthMode === 'PAPER'
                      ? 'Triggers arm in PAPER (simulated fills). Click to switch to LIVE — real orders when the trigger fires.'
                      : 'Triggers arm in LIVE — REAL multi-leg orders when the trigger fires (margin pre-check + kill-switch gates apply). Click to switch back to PAPER.'}
                    onClick={() => setSynthMode(m => m === 'PAPER' ? 'LIVE' : 'PAPER')}
                    sx={{
                      cursor: 'pointer', fontWeight: 800, fontSize: CH_TYPE.micro, height: 20, letterSpacing: 0.4,
                      color: '#fff', bgcolor: synthMode === 'LIVE' ? '#c62828' : '#546e7a',
                      border: synthMode === 'LIVE' ? '1.5px solid #ff8a80' : '1px solid transparent',
                      '& .MuiChip-label': { px: 0.65 },
                    }}
                  />
                )}
                {/* Manual order entry — BUY / SHORT (3-click flow opens confirm dialog).
                    Synthetic-symbol TRIGGER LOCK (plan §2): a DEBIT package can only be
                    BOUGHT, a CREDIT package can only be SHORTED — the disallowed side is
                    disabled here AND rejected server-side (fail closed). */}
                <Chip
                  label="BUY"
                  size="small"
                  disabled={isSynth && (!synth || !!synth.isCredit || !!synth.engineCycle)}
                  title={isSynth && !synth
                    ? 'Loading this synthetic symbol…'
                    : isSynth && synth?.engineCycle
                    ? 'Locked — this position is managed by its auto-strategy engine (Options AT). View-only here; manage it from Strategies → Options AT.'
                    : isSynth && synth?.isCredit
                    ? 'Locked — this synthetic symbol is a CREDIT structure: you SELL the package (SHORT trigger only). Profit comes from its premium decaying.'
                    : isSynth
                      ? 'Arm a LONG trigger on the combined premium — 3 clicks (entry → SL → target); fires the multi-leg basket when crossed (PAPER)'
                      : 'Manual BUY order — click to arm, then click the chart (opens confirm dialog)'}
                  onClick={() => { if (isSynth && (synth?.isCredit || synth?.engineCycle)) return; setChartToolExclusive(chartTool === 'buy' ? null : 'buy'); }}
                  sx={{
                    cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.small, height: 20,
                    letterSpacing: 0.3,
                    color: '#fff',
                    bgcolor: chartTool === 'buy' ? '#1b5e20' : '#2e7d32',
                    border: chartTool === 'buy' ? '1.5px solid #69f0ae' : '1px solid transparent',
                    '& .MuiChip-label': { px: 0.65 },
                    boxShadow: chartTool === 'buy'
                      ? '0 0 0 2px rgba(46,125,50,0.35), 0 0 6px rgba(105,240,174,0.5)'
                      : 'none',
                    ...(replay.active ? { opacity: 0.4 } : {}),
                    transition: 'box-shadow 120ms ease, background-color 120ms ease',
                    '&:hover': { bgcolor: chartTool === 'buy' ? '#1b5e20' : '#388e3c' },
                  }}
                />
                <Chip
                  label="SHORT"
                  size="small"
                  disabled={isSynth && (!synth || !synth.isCredit || !!synth.engineCycle)}
                  title={isSynth && !synth
                    ? 'Loading this synthetic symbol…'
                    : isSynth && synth?.engineCycle
                    ? 'Locked — this position is managed by its auto-strategy engine (Options AT). View-only here; manage it from Strategies → Options AT.'
                    : isSynth && synth && !synth.isCredit
                    ? 'Locked — this synthetic symbol is a DEBIT structure: you BUY the package (LONG trigger only). Profit comes from its value rising.'
                    : isSynth
                      ? 'Arm a SHORT trigger on the combined premium — 3 clicks (entry → SL → target); fires the multi-leg basket when crossed (PAPER)'
                      : 'Manual SHORT order — click to arm, then click the chart (opens confirm dialog)'}
                  onClick={() => { if (isSynth && synth && (!synth.isCredit || synth.engineCycle)) return; setChartToolExclusive(chartTool === 'short' ? null : 'short'); }}
                  sx={{
                    cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.small, height: 20,
                    letterSpacing: 0.3,
                    color: '#fff',
                    bgcolor: chartTool === 'short' ? '#b71c1c' : '#c62828',
                    border: chartTool === 'short' ? '1.5px solid #ff8a80' : '1px solid transparent',
                    '& .MuiChip-label': { px: 0.65 },
                    boxShadow: chartTool === 'short'
                      ? '0 0 0 2px rgba(198,40,40,0.35), 0 0 6px rgba(255,138,128,0.5)'
                      : 'none',
                    ...(replay.active ? { opacity: 0.4 } : {}),
                    transition: 'box-shadow 120ms ease, background-color 120ms ease',
                    '&:hover': { bgcolor: chartTool === 'short' ? '#b71c1c' : '#d32f2f' },
                  }}
                />
                {/* R115 Stage 5 — Add ISL / Add Tgt: arm a one-shot price-pick. */}
                {(['addIsl', 'addTgt'] as const).map(tool => {
                  const label = tool === 'addIsl' ? 'ISL' : 'TGT';
                  const armed = chartTool === tool;
                  /* These were permanently FILLED violet / cyan whether armed or
                     not, so they read as "already on" and added two more hues to
                     a toolbar that had far too many. They now follow the shared
                     rule — quiet outline until armed, filled when armed — with
                     the tone carrying the meaning: a stop is bearish, a target
                     is bullish. */
                  const tone: ChTone = tool === 'addIsl' ? 'bear' : 'bull';
                  const disabled = !activeTrade || (activeTrade.status !== 'OPEN' && activeTrade.status !== 'DRAFT');
                  return (
                    <Tooltip key={tool} arrow title={disabled ? `No open trade for this symbol — ${tool === 'addIsl' ? 'Add Initial Stop-Loss' : 'Add Target'} needs an open/draft trade` : `${tool === 'addIsl' ? 'Add Initial Stop-Loss' : 'Add Target'} — click to arm, then click the chart`}>
                      <span>
                        <Chip
                          label={label}
                          size="small"
                          disabled={disabled}
                          onClick={() => setChartToolExclusive(armed ? null : tool)}
                          variant="outlined"
                          sx={{
                            ...chChipSx(C, isDark, { active: armed, tone, dense: true }),
                            ...(armed ? {} : { color: tone === 'bear' ? C.bear : C.bull }),
                            letterSpacing: 0.3,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: (disabled || replay.active) ? 0.45 : 1,
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                })}
                {/* GoTo date — jump the chart to a historical date (no dragging). */}
                <Tooltip arrow title="GoTo date — jump the chart to a chosen date/time instead of dragging back. Exit the replay bar to return to live.">
                  <Chip
                    label="GoTo"
                    size="small"
                    disabled={replayDeepLoading}
                    onClick={(e) => {
                      if (!gotoVal) {
                        const d = new Date(Date.now() - 30 * 864e5);
                        const pad = (n: number) => String(n).padStart(2, '0');
                        setGotoVal(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                      }
                      setGotoAnchorEl(e.currentTarget);
                    }}
                    variant="outlined"
                    sx={{ ...chChipSx(C, isDark, { dense: true }), letterSpacing: 0.3 }}
                  />
                </Tooltip>
                <Popover open={Boolean(gotoAnchorEl)} anchorEl={gotoAnchorEl} onClose={() => setGotoAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, width: 240 }}>
                    <Typography sx={{ fontSize: CH_TYPE.small, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>Go to date / time (IST)</Typography>
                    <TextField type="datetime-local" size="small" value={gotoVal} onChange={(e) => setGotoVal(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <Button variant="contained" size="small" disabled={replayDeepLoading || !gotoVal}
                      onClick={() => {
                        const ms = Date.parse(`${gotoVal}:00+05:30`);
                        if (!Number.isFinite(ms)) { toast.error('Pick a valid date/time'); return; }
                        if (ms > Date.now()) { toast.error('Pick a date in the past'); return; }
                        setGotoAnchorEl(null);
                        setChartTool(null);
                        goToDate(Math.floor(ms / 1000));
                      }}>
                      {replayDeepLoading ? <CircularProgress size={16} /> : 'Go'}
                    </Button>
                    <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary' }}>Loads history around that date (frozen). Use the Replay bar to step/play forward, or Exit to return to live.</Typography>
                  </Box>
                </Popover>
                {/* Bar Replay (R68a) — single-pane only. */}
                <Tooltip arrow title={replay.active ? 'Bar Replay — ON (click to exit)' : 'Bar Replay — step the chart bar-by-bar'}>
                <Chip
                  icon={<ReplayIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
                  label={replay.active ? 'ON' : 'R'}
                  size="small"
                  onClick={() => { void enterOrExitReplay(); }}
                  sx={{
                    cursor: 'pointer', fontWeight: 700, fontSize: CH_TYPE.small, height: 20,
                    letterSpacing: 0.3, color: '#fff',
                    bgcolor: replay.active ? '#1565c0' : '#37474f',
                    border: replay.active ? '1.5px solid #82b1ff' : '1px solid transparent',
                    '& .MuiChip-label': { px: 0.5 },
                    '& .MuiChip-icon': { ml: 0.4, mr: -0.2 },
                    boxShadow: replay.active
                      ? '0 0 0 2px rgba(21,101,192,0.35), 0 0 6px rgba(130,177,255,0.5)'
                      : 'none',
                    transition: 'box-shadow 120ms ease, background-color 120ms ease',
                    '&:hover': { bgcolor: replay.active ? '#1565c0' : '#455a64' },
                  }}
                />
                </Tooltip>
              </Box>
            )}

            {/* Indicator groups — six labelled containers collapsed into ONE.
                They are all the same kind of thing ("what is drawn on the
                chart"), so six containers with six triggers bought nothing and
                cost a whole toolbar row. Pinned ★ favourites still render inline
                and carry over from the old per-group keys. */}
            <ToolbarIndicatorsGroup isDark={isDark} sections={indicatorSections} />

            {/* FNO expiry-tier quick selector (item #5) — flip Day0 / Week0 / Month0
                option profiles right from the toolbar when FNO is on (mirrors Chart
                Settings → FNO → Expiry tiers; persists to the active template). */}
            {fnoOverlay && (
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.6, py: 0.2, borderRadius: 1.2,
                bgcolor: isDark ? 'rgba(38,166,154,0.16)' : 'rgba(38,166,154,0.10)',
                border: `1px solid ${isDark ? 'rgba(38,166,154,0.42)' : 'rgba(38,166,154,0.36)'}`,
                opacity: fnoTierBusy ? 0.6 : 1, transition: 'opacity .15s',
              }}>
                <Typography component="span" sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, color: 'text.secondary' }}>Expiry</Typography>
                {(() => {
                  // Single-select: exactly one active. If none/multiple were stored, treat D0 as the active one.
                  const active: 'D' | 'W' | 'M' = fcfg.tierM && !(fcfg.tierD !== false) && !(fcfg.tierW !== false) ? 'M'
                    : (fcfg.tierW !== false) && !(fcfg.tierD !== false) ? 'W' : 'D';
                  return ([
                    ['D', 'D0', 'Day 0 — nearest LIVE daily expiry'],
                    ['W', 'W0', 'Week 0 — nearest weekly expiry'],
                    ['M', 'M0', 'Month 0 — nearest monthly expiry'],
                  ] as [('D'|'W'|'M'), string, string][]).map(([t, lbl, tip]) => {
                  const on = !fnoPin && active === t;
                  return (
                  <Tooltip key={t} arrow title={`${tip}. Rolls to the next contract when it settles.`}>
                    <Chip size="small" label={lbl}
                      color={on ? 'success' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={() => { if (!on) onFnoSelect(t); }}
                      sx={{ cursor: 'pointer', fontWeight: 800, fontSize: CH_TYPE.micro, height: 22 }} />
                  </Tooltip>
                  ); });
                })()}
                {/* Every listed expiry by date (2026-11-03, owner): the Options Workspace has its own
                    pill row, so the dates show here in the other layouts. A date PINS that contract;
                    clicking the pinned one again (or ×) goes back to the rolling tier. */}
                {layoutMode !== 'workspace' && fnoSelect && (() => {
                  const dates = fnoSelect.options.filter(o => o.group === 'pinned');
                  if (!dates.length) return null;
                  return (
                    <Box data-testid="fno-tb-dates" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, ml: 0.3, pl: 0.5, borderLeft: `1px solid ${isDark ? 'rgba(38,166,154,0.42)' : 'rgba(38,166,154,0.36)'}`,
                      maxWidth: { xs: 260, md: 520, xl: 760 }, overflowX: 'auto', scrollbarWidth: 'thin' }}>
                      {dates.map(o => {
                        const on = fnoPin === o.value;
                        const monthly = /^monthly/.test(o.sub || '');
                        const left = (o.sub || '').split(' · ')[1] || '';
                        return (
                          <Tooltip key={o.value} arrow title={on ? `Pinned: the FNO lines, panes and option profile show only the ${o.label} contract. Click again to go back to the rolling ${fnoRollTier}0.` : `Pin ${o.label} (${o.sub}) — the FNO lines, panes and option profile show only this contract. ${o.title || ''}`}>
                            <Box component="button" type="button" data-testid={`fno-tb-exp-${o.value}`} aria-pressed={on}
                              onClick={() => { if (on) setFnoPin(null); else onFnoSelect(o.value); }}
                              sx={{ all: 'unset', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.7, height: 20, borderRadius: 1,
                                fontSize: CH_TYPE.micro, fontWeight: 800, flexShrink: 0,
                                border: '1px solid', borderColor: on ? 'warning.main' : 'transparent',
                                bgcolor: on ? (isDark ? 'rgba(255,167,38,0.22)' : 'rgba(255,152,0,0.16)') : 'transparent',
                                color: on ? 'text.primary' : 'text.secondary', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' } }}>
                              {on && <span>📌</span>}{o.label}
                              {left && left !== 'settled' && <Box component="span" sx={{ fontWeight: 600, fontSize: '0.92em', opacity: 0.7 }}>{left}</Box>}
                              {monthly && <Box component="span" sx={{ fontSize: '0.85em', px: 0.3, borderRadius: 0.5, border: '1px solid', borderColor: 'info.main', color: 'info.main', lineHeight: 1.2 }}>M</Box>}
                              {on && <Box component="span" sx={{ ml: 0.1, opacity: 0.7 }}>×</Box>}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  );
                })()}
                {fnoPin && layoutMode === 'workspace' && (
                  <Tooltip arrow title="This expiry is pinned: the FNO lines and panes show only this contract. × goes back to the rolling tier. Pick another from the expiry pills above the chart.">
                    <Chip size="small" color="warning" label={`📌 ${fmtExpiry(fnoPin)}`} onDelete={() => setFnoPin(null)}
                      sx={{ fontWeight: 800, fontSize: CH_TYPE.micro, height: 22 }} />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Action buttons group pill (green) — refresh / saved layouts / snapshot
               / settings / help. Packs inline in the merged toolbar (item 2) so it
               doesn't force a wasted right-aligned line. */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.3,
              px: 0.5, py: 0.15, borderRadius: 1.2,
              bgcolor: isDark ? 'rgba(129,199,132,0.18)' : 'rgba(76,175,80,0.13)',
              border: `1px solid ${isDark ? 'rgba(129,199,132,0.40)' : 'rgba(76,175,80,0.32)'}`,
            }}>
              {/* The cadence is settings.liveUpdateSec, not a constant. This tooltip used to be
                  the literal string "Live updates active (15s)", so it lied for anyone who changed
                  the setting — and it was the only place on the page that states the poll rate. */}
              {isLive && symbol && (
                <Tooltip title={`Live updates active — polling every ${Math.max(3, settings.liveUpdateSec)}s. Change it in Chart Settings (gear) → Live update cadence.`}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%', bgcolor: '#4caf50',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                  }} />
                </Tooltip>
              )}
              <Tooltip title="Refresh all">
                <IconButton size="small" onClick={() => { loadSpotPanes(); }} sx={{ p: 0.4 }}>
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <SavedChartLayouts
                captureState={captureLayoutState}
                applyState={applyLayoutState}
                resetLayout={resetLayout}
              />
              <SnapshotButton filenameHint={symbol ? `chart-${symbol.toLowerCase()}` : 'chart'} />
              <Tooltip title="Chart Settings">
                <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ p: 0.4 }}>
                  <SettingsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help">
                <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ p: 0.4 }}>
                  <HelpOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

          </Box>
        </Box>

        {/* Chart panes — fill remaining height. The panes wrapper is
            position:relative so the Bar Replay controller can be absolutely
            positioned over it, regardless of layout mode. */}
        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
          {/* R143 Plan A — NSE without a Zerodha link: offer free (Yahoo) data
              for charting / replay instead of a blocking error. */}
          {nseFreeOffer && !freeDataActive && (
            <Box sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 12, maxWidth: 560 }}>
              <Alert severity="info" variant="filled"
                sx={{ alignItems: 'center', boxShadow: 4, fontSize: CH_TYPE.body, py: 0.5 }}
                action={
                  <Button color="inherit" size="small" variant="outlined" onClick={() => { setFreeDataActive(true); setNseFreeOffer(false); }}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Load free data</Button>
                }>
                Live NSE data needs Zerodha. Load <strong>free (Yahoo)</strong> data for charting / replay — daily history is deep, intraday ~60 days. Delayed &amp; analysis-only (not tradable).
              </Alert>
            </Box>
          )}
          {/* Free / delayed data badge (NSE free mode or Global symbols). */}
          {(freeDataActive || (exchange || '').toUpperCase() === 'GLOBAL') && (
            <Box sx={{ position: 'absolute', top: 8, right: 12, zIndex: 11, pointerEvents: 'none' }}>
              <Chip size="small" label={(exchange || '').toUpperCase() === 'GLOBAL' ? 'Yahoo · delayed (free)' : 'Free data (Yahoo) · delayed'}
                sx={{ bgcolor: 'rgba(255,167,38,0.92)', color: '#1a1a1a', fontWeight: 700, fontSize: CH_TYPE.micro, height: 20 }} />
            </Box>
          )}
          {/* R68b — Bar Replay controller overlays all layouts (single,
              split, quad). The playhead is anchored to paneData[0] and
              synced across every TradingChart instance via replayMaxTime. */}
          {replay.active && (paneData[0]?.length ?? 0) > 0 && (
            <ReplayController
              totalBars={paneData[0].length}
              currentIdx={replay.currentIdx}
              playing={replay.playing}
              speed={replay.speed}
              isDark={isDark}
              currentTimeLabel={(() => {
                const t = paneData[0][replay.currentIdx]?.time;
                if (!t) return '';
                const d = new Date(t * 1000);
                return d.toISOString().slice(0, 16).replace('T', ' ') + ' IST';
              })()}
              currentIsoLocal={(() => {
                const t = paneData[0][replay.currentIdx]?.time;
                if (!t) return '';
                return new Date(t * 1000).toISOString().slice(0, 16);
              })()}
              onPlayPause={() => setReplay(r => ({ ...r, playing: !r.playing }))}
              onStepBack={() => setReplay(r => ({ ...r, playing: false, currentIdx: Math.max(0, r.currentIdx - replayStepBars) }))}
              onStepForward={() => setReplay(r => {
                const total = paneData[0]?.length || 0;
                return { ...r, playing: false, currentIdx: Math.min(total - 1, r.currentIdx + replayStepBars) };
              })}
              stepLabel={replayStepLabel}
              stepOptions={replayStepOptions}
              onStepLabelChange={setReplayStepLabel}
              onRestart={() => setReplay(r => ({ ...r, playing: false, currentIdx: 0 }))}
              onJumpToEnd={exitReplay}
              onSpeedChange={(s) => setReplay(r => ({ ...r, speed: s }))}
              onSeek={(idx) => setReplay(r => ({ ...r, playing: false, currentIdx: Math.max(0, Math.min((paneData[0]?.length || 1) - 1, idx)) }))}
              onClose={exitReplay}
              source={replay.source}
              deepLoaded={replay.deep}
              deepLoading={replayDeepLoading}
              onSourceChange={(s) => loadReplayDeep(s)}
              onPickDateTime={(isoLocal) => {
                // datetime-local strings are wall-clock (no timezone). We
                // display IST in the readout, so interpret the picked time
                // as IST and convert to the IST-shifted epoch seconds the
                // candle grid uses (UTC + 19800).
                const utcMs = Date.parse(isoLocal + 'Z');
                if (!Number.isFinite(utcMs)) return;
                seekReplayToTime(Math.floor(utcMs / 1000));
              }}
            />
          )}

          {layoutMode === 'single' && renderSingleSpot()}

          {layoutMode === 'workspace' && (
            <OptionsWorkspace
              base={workspaceBase}
              onBaseChange={b => handleSymbolClick(`${b}USD`, 'DELTA')}
              chartContract={workspaceContract}
              onChartContract={sym => handleSymbolClick(sym || `${workspaceBase}USD`, 'DELTA')}
              onExpiryPick={fnoOverlay ? (iso => setFnoPin(iso)) : undefined}
              fnoExpiry={fnoOverlay ? fnoShownExpiry : null}
              renderChart={ov => (workspaceSymbolOk ? renderSingleSpot(ov) : null)} />
          )}

          {layoutMode === 'split' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%', gap: '1px', bgcolor: isDark ? '#1e2d3d' : '#e0e0e0' }}>
              {[0, 1].map(i => (
                <Box key={i}
                  onMouseDown={() => setFocusedSplitPane(i as 0 | 1)}
                  sx={{
                    bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative',
                    boxShadow: focusedSplitPane === i ? `inset 0 0 0 2px ${isDark ? '#3f6efb' : '#1976d2'}` : 'none',
                  }}>
                  <Typography variant="body2" sx={{ ...chPaneTitleSx, color: 'text.primary', px: 1, pt: 0.35, pb: 0.2 }}>
                    {symbol} · {intervalLabel(intervals[i])}{focusedSplitPane === i ? ' · ●' : ''}
                  </Typography>
                  {i === 0 && <FnoOverlayPanel />}
                  {i === 0 && renderOverview()}
                  {i === 0 && renderRateLimitHint()}
                  <Box data-trigger-chart={i === 0 ? 'true' : undefined} sx={{ flex: 1, minHeight: 0 }}>
                    <ChartErrorBoundary label={`spot-split-${i}`}>
                      <TradingChart showAccountStats={i === 0} candles={i === 0 ? displayPane0 : displayPane1} rawCandles={paneData[i] || []} overlays={overlays} auxCandles={i === 0 ? aux0 : aux1} benchmarkCandles={paneBenchmarkCandles[i]} benchmarkLabel={overlays.relStrength && symbol ? resolveBenchmark(symbol, exchange).symbol : undefined} loading={paneLoading[i]} height="100%"
                        onDragStateChange={i === 0 ? setIsDragging : undefined}
                        hideCandles={hideCandles} showWick={showWick} candleType={candleType} overlayLeftInset={overviewEnabled ? OVERVIEW_LANE_PX : undefined}
                        latestCandle={i === 0 ? displayPane0Live : displayPane1Live}
                        rawLatestCandle={liveWithMark[i]}
                        fraction={fractionView[i]} fractionLatest={fractionView[i] ? fracLive[i] : undefined}
                        // Both panes show the same symbol — pane 1 needs the
                        // SL / TSL / Target / trigger lines too. Without
                        // onPriceLevelChange the lines render read-only on
                        // pane 1, which is the desired behaviour (drag-edit
                        // happens on pane 0's primary view).
                        priceLevels={priceLevelsProp} fnoLevels={i === 0 ? chartFnoLines : chartFnoLines1} priceZones={chartZones} pnlBadge={chartPnlBadge} onPnlBadgeClick={(!isSynth && openBasket && !replay.active) ? handleBadgeClick : undefined} leftLevels={chartLeftLevelsUi} optionProfile={chartOptProfile} fnoInterp={chartFnoInterp} fnoView={i === 0 ? fnoChart0.view : chartFnoView1} fnoSelect={fnoSelect} synthiya={synthiyaSeries} dbcrZones={overlays.dbcrZone ? dbcrZonesData : undefined}
                        onChartClick={i === 0 && (triggerMode || chartTool) ? handleChartClickSpot : undefined}
                        strategyOverlay={i === 0 ? effectiveOverlay0 : effectiveOverlay1}
                        onPriceLevelChange={i === 0 ? handleLevelChangeSpot : undefined}
                        onPriceLevelDelete={i === 0 ? handleLevelDeleteSpot : undefined}
                        intervalMinutes={intervals[i]}
                        decimalPrecision={spotDecimals}
                        tickSize={spotTickSize}
                        markPrice={chartMarkPrice} markPriceColor={settings.markPrice?.color}
                        positionPnl={activeTrade && activeTrade.entryPrice ? { side: activeTrade.side, entryPrice: activeTrade.entryPrice, quantity: activeTrade.quantity ?? undefined, paper: !!activeTrade.isSimulation } : null}
                        actualLtp={actualLtp}
                        unrealizedPnl={activeTrade?.unrealizedPnl ?? null}
                        pnlCurrency={activeTrade?.pnlCurrency || 'INR'}
                        onLoadMore={(oldest, reason) => loadOlderPane(i as 0 | 1, oldest, reason)}
                        defaultVisibleBars={capOf(i)}
                        onVisibleBarsChange={visBarsCbs[i]}
                        flowCandles={overlays.flow ? paneFlowCandles[i] : undefined}
                        fundingSeries={overlays.funding ? fundingSeries : undefined}
                        overviewTf={i === 0 ? ovSeriesTf : undefined} overviewHtf={i === 0 ? ovSeriesHtf : undefined}
                        overviewTfLabel={intervalLabel(intervals[0])} overviewHtfLabel={intervalLabel(htfInterval)}
                  symbol={symbol} symbolNote={(() => { const a = symbol?.toUpperCase().startsWith('IDXN') ? aliasActualBySymbol.get(symbol.toUpperCase()) : undefined; return a ? `→ ${a.actual} · ${a.exchange}` : undefined; })()} activeStrategy={activeStrategy} rectZones={rectZones} rectDrawMode={rectDraw} selectedRectId={rectSel?.id ?? null} onRectZonesChange={setRectZones} onRectZoneSelect={handleRectZoneSelect} onRectDrawComplete={handleRectDrawComplete} syntheticLegs={isSynth ? synth?.legs : undefined} syntheticLegMarks={isSynth ? synthLegMarks : undefined}
                        showTrendBand={i === 0 ? trendEnabled : undefined}
                        showBkground={i === 0 ? bkgroundEnabled : undefined}
                        replayMaxTime={replayMaxTime}
                        onChartTimeClick={replaySeekOnClick}
                        crosshairGroup={crosshairGroup} />
                    </ChartErrorBoundary>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {layoutMode === 'quad' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', height: '100%', gap: '1px', bgcolor: isDark ? '#1e2d3d' : '#e0e0e0' }}>
              {/* Q1: Underlying Spot */}
              <Box sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                <Typography variant="body2" sx={{ ...chPaneTitleSx, color: 'text.primary', px: 1, pt: 0.35, pb: 0.2 }}>
                  {symbol} (Spot) · {intervalLabel(intervals[0])}
                </Typography>
                <FnoOverlayPanel />
                {renderOverview()}
                {renderRateLimitHint()}
                <Box data-trigger-chart="true" sx={{ flex: 1, minHeight: 0 }}>
                  <ChartErrorBoundary label="spot-quad-Q1">
                    <TradingChart showAccountStats candles={displayPane0} rawCandles={paneData[0]} overlays={overlays} auxCandles={aux0} benchmarkCandles={paneBenchmarkCandles[0]} benchmarkLabel={overlays.relStrength && symbol ? resolveBenchmark(symbol, exchange).symbol : undefined} loading={paneLoading[0]} height="100%"
                      onDragStateChange={setIsDragging}
                  hideCandles={hideCandles} showWick={showWick} candleType={candleType} overlayLeftInset={overviewEnabled ? OVERVIEW_LANE_PX : undefined}
                      latestCandle={displayPane0Live} rawLatestCandle={liveWithMark[0]}
                      fraction={fractionView[0]} fractionLatest={fractionView[0] ? fracLive[0] : undefined}
                      priceLevels={priceLevelsProp} fnoLevels={chartFnoLines} priceZones={chartZones} pnlBadge={chartPnlBadge} onPnlBadgeClick={(!isSynth && openBasket && !replay.active) ? handleBadgeClick : undefined} leftLevels={chartLeftLevelsUi} optionProfile={chartOptProfile} fnoInterp={chartFnoInterp} fnoView={fnoChart0.view} fnoSelect={fnoSelect} synthiya={synthiyaSeries} dbcrZones={overlays.dbcrZone ? dbcrZonesData : undefined} onChartClick={(triggerMode || chartTool) ? handleChartClickSpot : undefined}
                      strategyOverlay={effectiveOverlay0}
                      onPriceLevelChange={handleLevelChangeSpot}
                      onPriceLevelDelete={handleLevelDeleteSpot}
                      intervalMinutes={intervals[0]}
                      decimalPrecision={spotDecimals}
                      tickSize={spotTickSize}
                      markPrice={chartMarkPrice} markPriceColor={settings.markPrice?.color}
                      positionPnl={activeTrade && activeTrade.entryPrice ? { side: activeTrade.side, entryPrice: activeTrade.entryPrice, quantity: activeTrade.quantity ?? undefined, paper: !!activeTrade.isSimulation } : null}
                      actualLtp={actualLtp}
                      unrealizedPnl={activeTrade?.unrealizedPnl ?? null}
                      pnlCurrency={activeTrade?.pnlCurrency || 'INR'}
                      onLoadMore={(oldest, reason) => loadOlderPane(0, oldest, reason)}
                      defaultVisibleBars={capOf(0)}
                      onVisibleBarsChange={visBarsCbs[0]}
                      flowCandles={overlays.flow ? paneFlowCandles[0] : undefined}
                  fundingSeries={overlays.funding ? fundingSeries : undefined}
                      overviewTf={ovSeriesTf} overviewHtf={ovSeriesHtf}
                      overviewTfLabel={intervalLabel(intervals[0])} overviewHtfLabel={intervalLabel(htfInterval)}
                  symbol={symbol} symbolNote={(() => { const a = symbol?.toUpperCase().startsWith('IDXN') ? aliasActualBySymbol.get(symbol.toUpperCase()) : undefined; return a ? `→ ${a.actual} · ${a.exchange}` : undefined; })()} activeStrategy={activeStrategy} rectZones={rectZones} rectDrawMode={rectDraw} selectedRectId={rectSel?.id ?? null} onRectZonesChange={setRectZones} onRectZoneSelect={handleRectZoneSelect} onRectDrawComplete={handleRectDrawComplete} syntheticLegs={isSynth ? synth?.legs : undefined} syntheticLegMarks={isSynth ? synthLegMarks : undefined}
                      showTrendBand={trendEnabled}
                      showBkground={bkgroundEnabled}
                      replayMaxTime={replayMaxTime}
                      onChartTimeClick={replaySeekOnClick}
                      crosshairGroup={crosshairGroup} />
                  </ChartErrorBoundary>
                </Box>
              </Box>
              {/* Q2: Leg 1 (generalized — call or put, sell or buy) */}
              <Box sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, pt: 0.2, flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography variant="body2" sx={{ ...chPaneTitleSx, color: ceOptType === 'CE' ? C.callText : C.putText }}>
                    <span style={{ color: ceAction === 'SELL' ? C.bear : C.bull }}>{ceAction}</span> {nfoUnderlying} {ceStrike} {ceOptType} · {ceExpiry} · {intervalLabel(optInterval)}
                  </Typography>
                  {renderLegPicker('ce')}
                  <PaneTriggerChips levels={ceTriggerLevels} onRemove={removeCeTriggerLevel} />
                </Box>
                <Box data-trigger-chart="true" sx={{ flex: 1, minHeight: 0 }}>
                  <ChartErrorBoundary label="ce-Q2">
                    <TradingChart candles={displayCe} rawCandles={ceCandles} fraction={optFractionView[0]} overlays={overlays} loading={ceLoading} height="100%"
                      hideCandles={hideCandles} showWick={showWick} candleType={candleType} overlayLeftInset={overviewEnabled ? OVERVIEW_LANE_PX : undefined}
                      priceLevels={ceLevelsProp}
                      onChartClick={ceInstrumentId && (triggerMode || chartTool === 'buy' || chartTool === 'short') ? handleChartClickCe : undefined}
                      strategyOverlay={effectiveOverlayCe}
                      onPriceLevelChange={ceInstrumentId ? handleLevelChangeCe : undefined}
                      intervalMinutes={optInterval}
                      symbolNote={thinOptionNote(ceCandles, ceLoading)}
                      decimalPrecision={ceDecimals}
                      actualLtp={ceLtp ?? (ceCandles.length ? Number(ceCandles[ceCandles.length - 1].close) : null)}
                      defaultVisibleBars={capOf(1)}
                      onVisibleBarsChange={visBarsCbs[1]}
                      replayMaxTime={replayMaxTime}
                      crosshairGroup={crosshairGroup} />
                  </ChartErrorBoundary>
                </Box>
              </Box>
              {/* Q3: Combined (CE+PE) candlestick */}
              <Box sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ ...chPaneHeaderSx(isDark), flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography variant="body2" sx={{ ...chPaneTitleSx, color: 'text.primary' }}>
                    {q3IsLocked
                      ? `Synthetic (${openBasket?.tradingMode === 'PAPER' ? 'PAPER' : 'LIVE'} basket${basketLegsAll && basketLegsAll.length > 2 ? `, ${basketLegsAll.length} legs` : ''}) · ${basketLegsAll!.map(l => `${l.action[0]}${l.optionType[0]}${l.strike}`).join(' / ')} · ${intervalLabel(optInterval)}`
                      : `Synthetic · ${ceAction[0]}${ceOptType[0]}${ceStrike ?? '—'} / ${peAction[0]}${peOptType[0]}${peStrike ?? '—'} · ${intervalLabel(optInterval)}`}
                  </Typography>
                  {/* Phase C — open-position P&L strip (drag the TP/SL lines below to adjust).
                     P&L = (entry − current) net premium, valid for credit AND debit baskets
                     because both are computed with the signed (SELL +, BUY −) convention. */}
                  {openBasketChart && q3Cur != null && (() => {
                    const cur = q3Cur; // current signed net premium (per unit)
                    const entry = openBasketChart.entryPerUnit;
                    const pnl = entry != null
                      ? (entry - cur) * openBasketChart.q * openBasketChart.cv * USD_TO_INR : null;
                    const isCredit = entry != null ? entry >= 0 : true;
                    return (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, ml: 'auto' }}>
                        {openBasket?.tradingMode === 'PAPER' && <Chip size="small" label="PAPER" sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, bgcolor: C.warnMuted, color: C.warn }} />}
                        <Chip size="small" label={`${isCredit ? 'CREDIT' : 'DEBIT'} ×${openBasketChart.q}`} sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 700, bgcolor: isCredit ? C.bearMuted : C.bullMuted, color: isCredit ? C.bear : C.bull }} />
                        <Typography sx={{ fontSize: CH_TYPE.small, fontWeight: 700, color: 'text.secondary' }}>net {cur.toFixed(2)}</Typography>
                        {pnl != null && (
                          <Typography sx={{ fontSize: CH_TYPE.small, fontWeight: 800, color: pnl >= 0 ? C.bull : C.bear }}>
                            {pnl >= 0 ? '+' : ''}₹{Math.round(pnl).toLocaleString('en-IN')}
                          </Typography>
                        )}
                        <Tooltip title="Drag the dashed TP / SL lines on this chart to adjust the position's exit levels (per-unit combined premium)."><span style={{ fontSize: CH_TYPE.small, cursor: 'help', opacity: 0.6 }}>ⓘ</span></Tooltip>
                      </Box>
                    );
                  })()}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <ChartErrorBoundary label="combined-Q3">
                    <TradingChart candles={q3DisplayCandles} rawCandles={q3RawCandles} overlays={overlays} loading={ceLoading || peLoading} height="100%"
                      hideCandles={hideCandles} showWick={showWick} candleType={candleType} overlayLeftInset={overviewEnabled ? OVERVIEW_LANE_PX : undefined}
                      strategyOverlay={effectiveOverlayCombined}
                      priceLevels={combinedLevelsProp}
                      onPriceLevelChange={openBasket ? handleCombinedLevelChange : undefined}
                      intervalMinutes={optInterval}
                      symbolNote={thinOptionNote(q3RawCandles, ceLoading || peLoading)}
                      decimalPrecision={Math.max(ceDecimals, peDecimals)}
                      actualLtp={q3Cur}
                      defaultVisibleBars={capOf(2)}
                      onVisibleBarsChange={visBarsCbs[2]}
                      replayMaxTime={replayMaxTime}
                      crosshairGroup={crosshairGroup} />
                  </ChartErrorBoundary>
                </Box>
              </Box>
              {/* Q4: Leg 2 (generalized — call or put, sell or buy) */}
              <Box sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, pt: 0.2, flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography variant="body2" sx={{ ...chPaneTitleSx, color: peOptType === 'CE' ? C.callText : C.putText }}>
                    <span style={{ color: peAction === 'SELL' ? C.bear : C.bull }}>{peAction}</span> {nfoUnderlying} {peStrike} {peOptType} · {peExpiry} · {intervalLabel(optInterval)}
                  </Typography>
                  {renderLegPicker('pe')}
                  <PaneTriggerChips levels={peTriggerLevels} onRemove={removePeTriggerLevel} />
                </Box>
                <Box data-trigger-chart="true" sx={{ flex: 1, minHeight: 0 }}>
                  <ChartErrorBoundary label="pe-Q4">
                    <TradingChart candles={displayPe} rawCandles={peCandles} fraction={optFractionView[1]} overlays={overlays} loading={peLoading} height="100%"
                      hideCandles={hideCandles} showWick={showWick} candleType={candleType} overlayLeftInset={overviewEnabled ? OVERVIEW_LANE_PX : undefined}
                      priceLevels={peLevelsProp}
                      onChartClick={peInstrumentId && (triggerMode || chartTool === 'buy' || chartTool === 'short') ? handleChartClickPe : undefined}
                      strategyOverlay={effectiveOverlayPe}
                      onPriceLevelChange={peInstrumentId ? handleLevelChangePe : undefined}
                      intervalMinutes={optInterval}
                      symbolNote={thinOptionNote(peCandles, peLoading)}
                      decimalPrecision={peDecimals}
                      actualLtp={peLtp ?? (peCandles.length ? Number(peCandles[peCandles.length - 1].close) : null)}
                      defaultVisibleBars={capOf(3)}
                      onVisibleBarsChange={visBarsCbs[3]}
                      replayMaxTime={replayMaxTime}
                      crosshairGroup={crosshairGroup} />
                  </ChartErrorBoundary>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <ChartsHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ChartSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <CustomIntervalsDialog
        open={intervalsDialogOpen}
        onClose={() => setIntervalsDialogOpen(false)}
        onChanged={setIntervalVocab}
      />

      {/* Chart-side BUY/SHORT capture status — small floating banner while in
          capture flow. Anchored to the BOTTOM-centre so it never covers the
          timeframe / higher-TF controls at the top of the chart (the user needs
          to switch timeframes while picking SL/Target levels). */}
      {orderCapture.stage !== 'idle' && orderCapture.stage !== 'confirming' && (
        <Box sx={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1300,
          bgcolor: orderCapture.side === 'BUY' ? '#2e7d32' : '#c62828',
          color: '#fff', px: 2, py: 0.6, borderRadius: 2,
          fontSize: CH_TYPE.body, fontWeight: 700, letterSpacing: 0.3,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          {orderCapture.side} · click chart to set
          {orderCapture.stage === 'awaiting_entry' && (orderCapture.entryCount > 1 ? ` Entry ${orderCapture.entries.length + 1} of ${orderCapture.entryCount}` : ' Entry')}
          {orderCapture.stage === 'awaiting_sl' && ' Stop-Loss'}
          {orderCapture.stage === 'awaiting_target' && ' Target'}
          <Button size="small" onClick={cancelQuickOrder}
            sx={{ color: '#fff', fontSize: CH_TYPE.small, minWidth: 'auto', textTransform: 'none', ml: 1 }}>
            Cancel
          </Button>
        </Box>
      )}

      {/* Strategy click-to-build capture banner — bottom-centre, mirrors the
          BUY/SHORT banner. Shows the current role to pick on the Spot chart. */}
      {strategyCapture && (
        <Box sx={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1300, bgcolor: '#00897b', color: '#fff', px: 2, py: 0.6, borderRadius: 2,
          fontSize: CH_TYPE.body, fontWeight: 700, letterSpacing: 0.3,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 1,
        }}>
          {strategyCapture.label} · click Spot to set {strategyCapture.roles[strategyCapture.picks.length] || 'level'} ({strategyCapture.picks.length + 1} of {strategyCapture.clicksNeeded})
          <Button size="small" onClick={cancelStrategyCapture}
            sx={{ color: '#fff', fontSize: CH_TYPE.small, minWidth: 'auto', textTransform: 'none', ml: 1 }}>
            Cancel
          </Button>
        </Box>
      )}

      {/* Confirmation modal — extracted to OrderConfirmDialog in Round 67a.
          Adds a Trade Sizing strip (Segment/Mode/Investment/Leverage/Lot/Auto-qty/
          Required vs Available margin). Behavior of submit & quantity edit is
          unchanged. */}
      {lvlOptDlg && instrumentId && (
        <LevelOptionDialog
          open
          onClose={() => setLvlOptDlg(null)}
          instrumentId={instrumentId}
          symbol={symbol}
          levelKey={lvlOptDlg}
          levelValue={triggerLevels[lvlOptDlg] ?? null}
          existing={lvlAttachment(lvlOptDlg)}
          autoExecute={!!lvlOpt?.autoExecute}
          onSaved={reloadLvlOpt}
        />
      )}
      <OrderConfirmDialog
        open={orderCapture.stage === 'confirming'}
        symbol={orderCapture.targetSymbol || symbol}
        exchange={exchange}
        // Synthetic charts ARM a trigger — surface the PAPER/LIVE decision in the
        // dialog itself (the toolbar chip alone was missed and trades armed PAPER).
        synthMode={isSynth ? synthMode : undefined}
        onChangeSynthMode={isSynth ? setSynthMode : undefined}
        // Normal charts: the same PAPER / LIVE choice for the order itself.
        tradeMode={isSynth ? undefined : chartTradeMode}
        onChangeTradeMode={isSynth ? undefined : setChartTradeMode}
        side={orderCapture.side}
        // Blended average across all legs (== E1 for a single entry) drives the
        // price line + the Trade-Sizing margin estimate.
        entry={orderStages && orderStages.length ? orderStages[orderStages.length - 1].avg : orderCapture.entries[0]}
        sl={orderCapture.sl}
        target={orderCapture.target}
        quantity={(orderCapture.quantities || []).reduce((a, b) => a + b, 0) || undefined}
        sizedTotal={orderCapture.sizedTotal}
        orderType={orderCapture.orderType}
        submitting={orderCapture.submitting}
        decimals={orderCapture.targetDecimals ?? spotDecimals}
        isDark={isDark}
        // For a CE/PE leg, suppress the spot-perp margin estimate (it would be
        // wrong); the user confirms the contract quantity explicitly.
        instrumentDetail={orderCapture.targetPane && orderCapture.targetPane !== 'spot' ? null : instrumentDetail}
        hasOptionChain={hasOptionChain && (!orderCapture.targetPane || orderCapture.targetPane === 'spot')}
        riskReward={orderRiskReward}
        warning={orderCaptureWarning}
        info={orderCaptureInfo}
        entries={orderCapture.entries}
        quantities={orderCapture.quantities}
        stages={orderStages}
        onClose={cancelQuickOrder}
        onChangeQuantity={(q) => setOrderCapture(prev => {
          // For multi-entry the field edits the TOTAL; re-split by the same rule.
          if (q == null) return { ...prev, quantities: undefined, sizedTotal: undefined };
          if (prev.entries.length <= 1) return { ...prev, quantities: [q], sizedTotal: q };
          return { ...prev, quantities: splitQuantity(q, prev.entries.length), sizedTotal: q };
        })}
        onChangeOrderType={(t) => setOrderCapture(prev => ({ ...prev, orderType: t }))}
        onSubmit={submitQuickOrder}
      />

      {/* In-chart strategy builder — preview, adjust strikes/lots, approve & execute */}
      <BasketReviewDialog
        open={builderConfig != null}
        underlyingSymbol={nfoUnderlying}
        strategyId={builderConfig?.strategyId ?? null}
        strategyLabel={builderConfig?.label ?? ''}
        spotPrice={Number(actualLtp) || spotPrice || 0}
        initialLegs={builderConfig?.legs ?? null}
        sellInvestmentInr={instrumentDetail?.optInvestmentAmountSell ?? 0}
        buyInvestmentInr={instrumentDetail?.optInvestmentAmountBuy ?? 0}
        leverage={instrumentDetail?.leverage ?? 1}
        onClose={() => { setBuilderConfig(null); setDialogCard(null); }}
        onExecuted={() => { setBuilderConfig(null); setDialogCard(null); }}
        onCardChange={setDialogCard}
      />

      {/* Basket adjustment review — opened by clicking the on-chart P&L badge. Reuses the
          full BasketAdjustPanel (ready-made roll candidates, editable strike/expiry, adjusted
          payoff, approve & place). */}
      <Dialog open={adjustOpen && !!openBasket} onClose={() => setAdjustOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
          Adjust basket {openBasket ? `#${openBasket.id} · ${openBasket.strategy || 'basket'}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {openBasket && (
            <BasketAdjustPanel
              basketId={openBasket.id}
              strategy={openBasket.strategy}
              tradingMode={openBasket.tradingMode}
              contractValue={openBasket.legFills?.[0]?.contractValue}
              onAdjusted={() => setAdjustOpen(false)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => openBasket && squareOffBasket(openBasket)} color="error" variant="outlined" disabled={squaring} sx={{ mr: 'auto' }}>
            {squaring ? 'Squaring off…' : 'Square off basket'}
          </Button>
          <Button onClick={() => setAdjustOpen(false)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Staged-campaign ADJUST — opened by the on-chart campaign badge (a tranche that belongs
          to a campaign) or the toolbar campaign chip. Full CampaignAdjustPanel + square-off-all. */}
      <Dialog open={campaignAdjustOpen && campaignAdjustId != null} onClose={() => setCampaignAdjustOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Adjust staged campaign {campaignAdjustId != null ? `#${campaignAdjustId}` : ''}</DialogTitle>
        <DialogContent dividers>
          {campaignAdjustId != null && (
            <CampaignAdjustPanel
              campaignId={campaignAdjustId}
              symbol={(nfoUnderlying || symbol) ? `${(nfoUnderlying || symbol).toUpperCase().replace(/(USDT|USDC|USD|INR|PERP)$/, '')}USD` : undefined}
              tradingMode={(openCampaigns.find((c: any) => c.id === campaignAdjustId)?.tradingMode) || 'PAPER'}
              onAdjusted={() => setCampaignAdjustOpen(false)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => campaignAdjustId != null && squareOffCampaign(campaignAdjustId)} color="error" variant="outlined" disabled={squaring} sx={{ mr: 'auto' }}>
            {squaring ? 'Squaring off…' : 'Square off whole campaign'}
          </Button>
          <Button onClick={() => setCampaignAdjustOpen(false)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Staged-campaign CREATE — launched from the toolbar "Stage campaign" chip, seeded with
          the charted underlying (levels auto-seed from spot; user tweaks them). */}
      <StagedCampaignDialog
        key={stageSeedKey}
        open={stageDlgOpen}
        onClose={() => { setStageDlgOpen(false); setStageSeed(null); }}
        seed={stageSeed || { underlyingSymbol: (nfoUnderlying || symbol) ? `${(nfoUnderlying || symbol).toUpperCase().replace(/(USDT|USDC|USD|INR|PERP)$/, '')}USD` : 'BTCUSD', spot: Number(actualLtp) || spotPrice || 0 }}
        onCreated={() => { setStageDlgOpen(false); setStageSeed(null); }}
      />

      {/* Outlook → ranked option baskets (item 5) — seeded Strategy Architect */}
      <OutlookBuilderDialog
        open={outlookDialog != null}
        underlyingSymbol={nfoUnderlying || symbol}
        mode={(outlookDialog?.mode ?? null) as any}
        level={outlookDialog?.level ?? null}
        level2={outlookDialog?.level2 ?? null}
        onClose={() => setOutlookDialog(null)}
      />

      {/* Rect zone editor (item 3) — anchored at the click point: color swatches,
          opacity, delete. Del/Backspace also deletes (guarded when typing). */}
      <Popover
        open={rectSel != null}
        onClose={() => setRectSel(null)}
        anchorReference="anchorPosition"
        anchorPosition={rectSel ? { top: rectSel.y, left: rectSel.x } : undefined}
      >
        {(() => {
          const z = rectZones.find(r => r.id === rectSel?.id);
          if (!z) return null;
          const SWATCHES = ['#2196f3', '#26a69a', '#ef5350', '#ffb74d', '#ab47bc', '#78909c', '#fdd835', '#ec407a'];
          const patch = (p: Partial<RectZone>) => setRectZones(zs => zs.map(r => r.id === z.id ? { ...r, ...p } : r));
          return (
            <Box sx={{ p: 1.2, width: 210 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.6 }}>Rect zone</Typography>
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
                {SWATCHES.map(c => (
                  <Box key={c} onClick={() => patch({ color: c })}
                    sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: c, cursor: 'pointer',
                      border: z.color === c ? '2px solid' : '1px solid', borderColor: z.color === c ? 'text.primary' : 'divider' }} />
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Opacity {(Math.round(z.opacity * 100))}%</Typography>
              <Slider size="small" min={5} max={60} value={Math.round(z.opacity * 100)}
                onChange={(_, v) => patch({ opacity: Number(v) / 100 })} sx={{ mb: 0.5 }} />
              <Button size="small" color="error" variant="outlined" fullWidth
                onClick={() => { setRectZones(zs => zs.filter(r => r.id !== z.id)); setRectSel(null); }}>
                Delete zone
              </Button>
            </Box>
          );
        })()}
      </Popover>
    </Box>
  );
};

const ChartsPageWithProvider: React.FC = () => (
  <ChartSettingsProvider>
    <ChartsPage />
  </ChartSettingsProvider>
);

export default ChartsPageWithProvider;
